import React, { useState, useEffect, useRef } from 'react';
import Plot from 'react-plotly.js';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import html2canvas from 'html2canvas';
import * as XLSX from 'xlsx';
import { parseHtmlToTableData, parseMarkdownToTableData } from './htmlToTable';
import katex from 'katex';
import './App.css';
import ShopifyView from './ShopifyView';
import TranslationSetupDialog from './components/TranslationSetupDialog';
import { translateText, getColumnTranslationConfig, setColumnTranslationConfig } from './utils/translation';
import WarehouseView from './WarehouseView';
import ShareTableModal from './ShareTableModal';
import SharedTablesView from './SharedTablesView';
import SharingWithOthersView from './SharingWithOthersView';
import FileExplorer from './components/FileExplorer';
import {
  createContextPath,
  extendPath,
  extractLegacyParams,
  createCellContext,
  pathToString,
  buildApiUrl,
  getDepth
} from './contextPath';
// Import table traversal utilities for recursive operations
import {
  traverseAndUpdate,
  updateColumnHeaderAtPath,
  updateRowHeaderAtPath,
  updateNameAtPath,
  toggleExpandedAtPath,
  toggleShowHeadersAtPath,
  toggleShowRowHeadersAtPath,
  insertRowAtPath,
  insertColumnAtPath,
  deleteRowAtPath,
  deleteColumnAtPath,
  moveRowAtPath,
  moveColumnAtPath
} from './utils/tableTraversal';
// Import test helpers for debugging (temporary)
import './testHelpers';
// Import authentication helper
import { getAuthHeaders } from './api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

// Simple markdown parser for basic formatting (bold, italic, links)
// Returns HTML string with basic markdown converted
const parseSimpleMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;

  let html = text;

  // Escape HTML to prevent XSS
  html = html
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  // Convert inline code: `code` - before other formatting
  html = html.replace(/`([^`]+)`/g, '<code style="font-family: monospace; font-size: 0.9em; background: rgba(127, 127, 127, 0.2); padding: 0.1em 0.3em; border-radius: 3px;">$1</code>');

  // Convert markdown links: [text](url) - inherit font size
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" style="color: #3b82f6; text-decoration: underline; font-size: inherit; font-family: inherit;">$1</a>');

  // Convert bold: **text** or __text__ - inherit font size
  html = html.replace(/\*\*([^*]+)\*\*/g, '<strong style="font-size: inherit; font-family: inherit;">$1</strong>');
  html = html.replace(/__([^_]+)__/g, '<strong style="font-size: inherit; font-family: inherit;">$1</strong>');

  // Convert italic: *text* or _text_ - inherit font size
  html = html.replace(/\*([^*]+)\*/g, '<em style="font-size: inherit; font-family: inherit; font-style: italic;">$1</em>');
  html = html.replace(/_([^_]+)_/g, '<em style="font-size: inherit; font-family: inherit; font-style: italic;">$1</em>');

  // Preserve line breaks
  html = html.replace(/\n/g, '<br/>');

  return html;
};

// LaTeX rendering component for column headers
// Supports inline math: $formula$ or display math: $$formula$$
const LatexText = ({ text }) => {
  if (!text) {
    return <span>{text}</span>;
  }

  try {
    // Check if text contains LaTeX ($ or $$)
    if (text.includes('$')) {
      let html = text;

      // Replace display math $$...$$ first
      html = html.replace(/\$\$(.*?)\$\$/g, (match, formula) => {
        try {
          return katex.renderToString(formula, {
            displayMode: true,
            throwOnError: false,
            output: 'html'
          });
        } catch (e) {
          console.error('[LatexText] Error rendering display math:', e);
          return match; // Return original if rendering fails
        }
      });

      // Replace inline math $...$
      html = html.replace(/\$(.*?)\$/g, (match, formula) => {
        try {
          return katex.renderToString(formula, {
            displayMode: false,
            throwOnError: false,
            output: 'html'
          });
        } catch (e) {
          console.error('[LatexText] Error rendering inline math:', e);
          return match; // Return original if rendering fails
        }
      });

      return <span style={{ fontSize: 'inherit', fontWeight: 'inherit' }} dangerouslySetInnerHTML={{ __html: html }} />;
    }
  } catch (error) {
    console.error('[LatexText] LaTeX rendering error:', error);
  }

  // No LaTeX, just show plain text
  return <span style={{ fontSize: 'inherit', fontWeight: 'inherit' }}>{text}</span>;
};

// Editable column header with LaTeX rendering
// Shows LaTeX when not editing, switches to plain text input when clicked
const EditableLatexHeader = ({ value, onSave, fontSize, onSort, textAlign = 'center' }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef(null);

  // Update editValue when value prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    setIsEditing(false);
    if (editValue !== value && onSave) {
      // Normalize empty strings to null so fallback values work correctly
      const normalizedValue = editValue.trim() === '' ? null : editValue;
      onSave(normalizedValue);
    }
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            handleSave();
          } else if (e.key === 'Escape') {
            setEditValue(value);
            setIsEditing(false);
          }
        }}
        style={{
          border: 'none',
          background: 'transparent',
          fontWeight: 600,
          fontSize: fontSize,
          padding: '2px 4px',
          margin: 0,
          boxSizing: 'border-box',
          width: '100%',
          outline: 'none',
          textAlign: textAlign,
          fontFamily: 'inherit',
          lineHeight: 'inherit'
        }}
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <div
      onClick={() => {
        setIsEditing(true);
      }}
      className="edit-trigger"
      style={{
        cursor: 'text',
        userSelect: 'none',
        textAlign: textAlign,
        minWidth: '20px',
        minHeight: '1em'
      }}
      title="Click to edit"
    >
      {value && value.trim() !== '' ? <LatexText text={value} /> : <span style={{ opacity: 0.4 }}>(empty)</span>}
    </div>
  );
};

// Uncontrolled textarea that syncs its DOM value when `cellValue` changes from the outside
// (e.g. row move, paste, undo). Mirrors the prop→state sync pattern used by EditableLatexHeader,
// but keeps typing uncontrolled so keystrokes don't round-trip through React state.
const EditableCellTextarea = React.forwardRef(function EditableCellTextarea(
  { cellValue, density, ...rest },
  forwardedRef
) {
  const innerRef = useRef(null);
  const setRef = (el) => {
    innerRef.current = el;
    if (typeof forwardedRef === 'function') forwardedRef(el);
    else if (forwardedRef) forwardedRef.current = el;
  };

  useEffect(() => {
    const ta = innerRef.current;
    if (!ta) return;
    if (document.activeElement === ta) return; // don't clobber the user's in-progress edit
    const next = cellValue || '';
    if (ta.value !== next) {
      ta.value = next;
      ta.style.height = 'auto';
      if (density === 'sub-compact') {
        const minHeight = parseFloat(getComputedStyle(ta).fontSize) * 1.1;
        ta.style.height = Math.max(ta.scrollHeight, minHeight) + 'px';
      } else {
        ta.style.height = ta.scrollHeight + 'px';
      }
    }
  }, [cellValue, density]);

  return <textarea ref={setRef} defaultValue={cellValue || ''} {...rest} />;
});

// Dropdown that replaces individual subtable toggle arrows.
// Shows a single button; clicking opens a popover listing each subtable by name
// with a toggle, plus Show All / Hide All controls.
const SubtableDropdown = ({ subtables, onToggle, onShowAll, onHideAll, onAdd, readOnly, theme }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const expandedCount = subtables.filter(st => st.expanded !== false).length;
  const totalCount = subtables.length;

  const isDark = theme === 'dark';

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
        className="btn-toggle-inline"
        title={`${expandedCount}/${totalCount} subtables shown`}
        style={{
          fontSize: '0.7rem',
          padding: '2px 4px',
          lineHeight: 1.2,
          minWidth: '28px',
          textAlign: 'center',
          whiteSpace: 'nowrap',
        }}
      >
        <span style={{ fontSize: '0.6rem' }}>{expandedCount > 0 ? '▼' : '▶'}</span>
        <span style={{ marginLeft: '2px' }}>{totalCount}</span>
      </button>
      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            zIndex: 9999,
            background: isDark ? '#2d2d2d' : '#fff',
            border: `1px solid ${isDark ? '#555' : '#ccc'}`,
            borderRadius: '6px',
            boxShadow: isDark
              ? '0 4px 12px rgba(0,0,0,0.5)'
              : '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: '180px',
            maxWidth: '280px',
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '4px 0',
            fontSize: '0.8rem',
            color: isDark ? '#e0e0e0' : '#333',
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Show All / Hide All / Done row */}
          <div style={{
            display: 'flex',
            gap: '4px',
            padding: '4px 8px 6px',
            borderBottom: `1px solid ${isDark ? '#444' : '#eee'}`,
            alignItems: 'center',
          }}>
            <button
              onClick={() => {
                const toShow = subtables.filter(st => st.expanded === false).map(st => st.id);
                if (toShow.length > 0 && onShowAll) onShowAll(toShow);
              }}
              style={{
                flex: 1,
                fontSize: '0.7rem',
                padding: '3px 6px',
                border: `1px solid ${isDark ? '#666' : '#ccc'}`,
                borderRadius: '3px',
                background: isDark ? '#3a3a3a' : '#f5f5f5',
                color: isDark ? '#ccc' : '#555',
                cursor: 'pointer',
              }}
            >
              Show All
            </button>
            <button
              onClick={() => {
                const toHide = subtables.filter(st => st.expanded !== false).map(st => st.id);
                if (toHide.length > 0 && onHideAll) onHideAll(toHide);
              }}
              style={{
                flex: 1,
                fontSize: '0.7rem',
                padding: '3px 6px',
                border: `1px solid ${isDark ? '#666' : '#ccc'}`,
                borderRadius: '3px',
                background: isDark ? '#3a3a3a' : '#f5f5f5',
                color: isDark ? '#ccc' : '#555',
                cursor: 'pointer',
              }}
            >
              Hide All
            </button>
            <button
              onClick={() => setOpen(false)}
              style={{
                fontSize: '0.75rem',
                padding: '2px 6px',
                border: 'none',
                borderRadius: '3px',
                background: isDark ? '#555' : '#ddd',
                color: isDark ? '#ccc' : '#555',
                cursor: 'pointer',
                fontWeight: 600,
                lineHeight: 1.2,
              }}
              title="Close"
            >
              ✕
            </button>
          </div>
          {/* Subtable list */}
          {subtables.map((subtable) => {
            const isExpanded = subtable.expanded !== false;
            return (
              <div
                key={subtable.id}
                onClick={() => onToggle(subtable.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  background: 'transparent',
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = isDark ? '#3a3a3a' : '#f0f0f0'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <span style={{
                  fontSize: '0.75rem',
                  color: isExpanded
                    ? (isDark ? '#7cb3ff' : '#4a90d9')
                    : (isDark ? '#888' : '#aaa'),
                  flexShrink: 0,
                }}>
                  {isExpanded ? '▼' : '▶'}
                </span>
                <span style={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  fontWeight: isExpanded ? 500 : 400,
                  opacity: isExpanded ? 1 : 0.65,
                }}>
                  {subtable.name || '(untitled)'}
                </span>
              </div>
            );
          })}
          {/* Add subtable option */}
          {!readOnly && (
            <div
              onClick={() => { onAdd(); setOpen(false); }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 8px',
                cursor: 'pointer',
                borderTop: `1px solid ${isDark ? '#444' : '#eee'}`,
                color: isDark ? '#7cb3ff' : '#4a90d9',
                transition: 'background 0.1s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = isDark ? '#3a3a3a' : '#f0f0f0'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <span style={{ fontSize: '0.75rem', flexShrink: 0 }}>+</span>
              <span>Add subtable</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Helper function for authenticated fetch calls
const authFetch = async (url, options = {}) => {
  const response = await fetch(url, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers
    }
  });

  if (response.status === 401) {
    // Token expired - redirect to login
    localStorage.removeItem('token');
    window.location.reload();
    throw new Error('Session expired. Please login again.');
  }

  return response;
};

// Helper function to check if a table is read-only (shared with read permission)
const isTableReadOnly = (table) => {
  return table.isShared && table.sharedPermission === 'read';
};

// Color helpers (used by custom color scheme)
const hexToRgb = (hex) => {
  if (!hex) return { r: 0, g: 0, b: 0 };
  const clean = hex.replace('#', '').trim();
  const expanded = clean.length === 3
    ? clean.split('').map(c => c + c).join('')
    : clean;
  const num = parseInt(expanded, 16);
  if (Number.isNaN(num)) return { r: 0, g: 0, b: 0 };
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
};

const rgbToHsl = ({ r, g, b }) => {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h /= 6;
  }
  return { h: h * 360, s: s * 100, l: l * 100 };
};

const hslToHex = (h, s, l) => {
  h /= 360; s /= 100; l /= 100;
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  let r, g, b;
  if (s === 0) { r = g = b = l; }
  else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1/3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1/3);
  }
  const toHex = (x) => Math.round(x * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

const deriveCustomPalette = (hex) => {
  const rgb = hexToRgb(hex);
  const { h, s, l } = rgbToHsl(rgb);
  return {
    light: hslToHex(h, Math.min(s, 60), 95),
    lightAlt: hslToHex(h, Math.min(s, 60), 88),
    medium: hslToHex(h, Math.min(s, 70), 77),
    primary: hex,
    secondary: hslToHex(h, s, Math.max(l - 12, 20)),
    dark: hslToHex(h, s, Math.max(l - 25, 20)),
    darkBg: hslToHex(h, Math.min(s, 80), 22),
    darkBorder: hslToHex(h, Math.min(s, 75), Math.min(l + 10, 70)),
    rgb: `${rgb.r}, ${rgb.g}, ${rgb.b}`
  };
};

// Helper function to calculate depth-based background color
const getDepthBackground = (depth, theme, colorScheme, depthShadingEnabled, isParentRow = false, customColor = null) => {
  if (!depthShadingEnabled || depth === 0) {
    return undefined; // Return undefined to let CSS handle it
  }

  // Calculate progressive opacity based on depth (10% per level, max 60%)
  let opacity = Math.min(0.10 * depth, 0.6);

  // Make parent rows more visible
  if (isParentRow) {
    opacity = 0.35; // Fixed much higher opacity for parent rows
  }

  // Color scheme RGB values
  const colorSchemes = {
    blue: '33, 150, 243',      // #2196F3
    green: '76, 175, 80',      // #4CAF50
    purple: '156, 39, 176',    // #9C27B0
    orange: '255, 152, 0',     // #FF9800
    grey: '158, 158, 158'      // #9E9E9E
  };

  let rgb;
  if (colorScheme === 'custom' && customColor) {
    const { r, g, b } = hexToRgb(customColor);
    rgb = `${r}, ${g}, ${b}`;
  } else {
    rgb = colorSchemes[colorScheme] || colorSchemes.blue;
  }

  if (theme === 'dark') {
    // For dark theme, use the color scheme with moderate opacity
    return `rgba(${rgb}, ${opacity * 0.7})`;
  } else {
    // For light theme, use the color scheme with stronger opacity
    return `rgba(${rgb}, ${opacity * 0.6})`;
  }
};

function App() {
  // Theme state (Phase 1)
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('theme');
    return saved || 'light';
  });
  const [colorScheme, setColorScheme] = useState(() => {
    const saved = localStorage.getItem('colorScheme');
    return saved || 'blue';
  });
  const [customColor, setCustomColor] = useState(() => {
    const saved = localStorage.getItem('customColor');
    return saved || '#2A9DBF';
  });
  const [companyLogo, setCompanyLogo] = useState(() => {
    return localStorage.getItem('companyLogo') || null;
  });
  const [logoBgEnabled, setLogoBgEnabled] = useState(() => {
    return localStorage.getItem('logoBgEnabled') === 'true';
  });
  const [logoBgColor, setLogoBgColor] = useState(() => {
    return localStorage.getItem('logoBgColor') || '#ffffff';
  });
  const [toolbarOpen, setToolbarOpen] = useState(() => {
    const saved = localStorage.getItem('toolbarOpen');
    return saved === null ? true : saved === 'true';
  });

  // Graph paper background toggle
  const [graphPaper, setGraphPaper] = useState(() => {
    const saved = localStorage.getItem('graphPaper');
    return saved === 'true';
  });

  // Show default cell placeholders (RxCy) toggle
  const [showDefaultCellPlaceholders, setShowDefaultCellPlaceholders] = useState(() => {
    const saved = localStorage.getItem('showDefaultCellPlaceholders');
    return saved === null ? true : saved === 'true'; // Default to true (show placeholders)
  });

  // Metallic finish toggle
  const [metallicFinish, setMetallicFinish] = useState(() => {
    const saved = localStorage.getItem('metallicFinish');
    return saved === 'true';
  });

  // Density state (Phase 2)
  const [density, setDensity] = useState(() => {
    const saved = localStorage.getItem('density');
    return saved || 'normal';
  });

  // Border style state (Phase 2)
  const [borderStyle, setBorderStyle] = useState(() => {
    const saved = localStorage.getItem('borderStyle');
    return saved || 'bordered';
  });

  // Depth shading state (Phase 2)
  const [depthShading, setDepthShading] = useState(() => {
    const saved = localStorage.getItem('depthShading');
    return saved ? JSON.parse(saved) : false;
  });

  // Modal and dropdown states
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showFileDropdown, setShowFileDropdown] = useState(false);

  // Loading states
  const [isCreatingTable, setIsCreatingTable] = useState(false);

  // Cell input backgrounds state
  const [showCellBackgrounds, setShowCellBackgrounds] = useState(() => {
    const saved = localStorage.getItem('showCellBackgrounds');
    return saved ? JSON.parse(saved) : true;
  });

  // Subtable connector lines state
  const [showConnectorLines, setShowConnectorLines] = useState(() => {
    const saved = localStorage.getItem('showConnectorLines');
    return saved ? JSON.parse(saved) : true;
  });

  // State to track which table/subtable/nested table has expanded controls in minimalist mode
  const [expandedControls, setExpandedControls] = useState({});

  // State to track which tables are collapsed (hidden content, only showing header)
  const [collapsedTables, setCollapsedTables] = useState(() => {
    const saved = localStorage.getItem('collapsedTables');
    return saved ? JSON.parse(saved) : {};
  });

  // Persist collapsedTables to localStorage
  useEffect(() => {
    localStorage.setItem('collapsedTables', JSON.stringify(collapsedTables));
  }, [collapsedTables]);

  // State to track which tables have subtables enabled (showing + column and controls)
  const [subtablesEnabled, setSubtablesEnabled] = useState(() => {
    const saved = localStorage.getItem('subtablesEnabled');
    return saved ? JSON.parse(saved) : {};
  });

  // Persist subtablesEnabled to localStorage
  useEffect(() => {
    localStorage.setItem('subtablesEnabled', JSON.stringify(subtablesEnabled));
  }, [subtablesEnabled]);

  // File management state
  const [availableFiles, setAvailableFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState('tables_data.json');
  const [showFileMenu, setShowFileMenu] = useState(false);
  const [explorerOpen, setExplorerOpen] = useState(false); // File explorer sidebar state

  // Tab state
  const [tabs, setTabs] = useState([]);
  const [activeTabId, setActiveTabId] = useState(null);
  const [editingTabId, setEditingTabId] = useState(null); // Track which tab is being renamed
  const [editingTableNameValue, setEditingTableNameValue] = useState(''); // Track the current value being edited

  // Table state (for active tab)
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTableRows, setNewTableRows] = useState(3);
  const [newTableCols, setNewTableCols] = useState(3);
  const [newTableName, setNewTableName] = useState('');
  const [showSubtableModal, setShowSubtableModal] = useState(null); // {tableId, rowIndex, parentSubtableId}
  const [selectedCell, setSelectedCell] = useState(null); // {tableId, rowIdx, colIdx} for keyboard shortcuts
  const [showContextMenu, setShowContextMenu] = useState(false); // Whether to show the context menu
  const [selectedHeaderCell, setSelectedHeaderCell] = useState(null); // {tableId, rowIdx, colIdx, contextPath} for header cell context menu
  const [focusedElement, setFocusedElement] = useState(null); // {type, tableId, rowIdx, colIdx, subtableId, nestedTableId} for expanded elements
  const [openDatePickers, setOpenDatePickers] = useState({}); // Track which date pickers are open
  const [showTableReferenceModal, setShowTableReferenceModal] = useState(null); // {tableId, rowIdx, colIdx, subtableId, nestedTableId}
  const [availableTables, setAvailableTables] = useState([]); // Tables available for referencing
  const [deleteWarning, setDeleteWarning] = useState(null); // {tableId, tableName, references}
  const [showRowMenu, setShowRowMenu] = useState(null); // {tableId, rowIdx} for row insert menu
  const [showColMenu, setShowColMenu] = useState(null); // {tableId, colIdx} for column insert menu
  const [showColBorderSubmenu, setShowColBorderSubmenu] = useState(null); // {tableId, colIdx} for which column's border submenu is open
  const [showTranslationDialog, setShowTranslationDialog] = useState(null); // {tableId, colIdx} for which column's translation dialog is open
  const [tableAIAssistant, setTableAIAssistant] = useState({}); // {[tableId]: {expanded: bool, loading: bool, conversationHistory: [], input: ''}}
  const [borderSubmenuTimeout, setBorderSubmenuTimeout] = useState(null); // Timeout for closing border submenu
  const [showRowBorderSubmenu, setShowRowBorderSubmenu] = useState(null); // {tableId, rowIdx} for which row's border submenu is open
  const [rowBorderSubmenuTimeout, setRowBorderSubmenuTimeout] = useState(null); // Timeout for closing row border submenu
  const [showTableMenu, setShowTableMenu] = useState(null); // tableId for table-level menu
  const [showSubtableColMenu, setShowSubtableColMenu] = useState(null); // {subtableId, colIdx} for subtable column insert menu
  const [showNestedTableColMenu, setShowNestedTableColMenu] = useState(null); // {nestedTableId, colIdx} for nested table column insert menu
  const [editingTableName, setEditingTableName] = useState(null); // tableId of table whose name is being edited
  const [editingTableDescription, setEditingTableDescription] = useState(null); // tableId of table whose description is being edited
  const [editingTableDescriptionValue, setEditingTableDescriptionValue] = useState(''); // Track the current description value being edited
  const [editingSubtableName, setEditingSubtableName] = useState(null); // subtableId of subtable whose name is being edited
  const [editingSubtableNameValue, setEditingSubtableNameValue] = useState(''); // Track the current subtable name value being edited
  const [editingNestedTableName, setEditingNestedTableName] = useState(null); // nestedTableId of nested table whose name is being edited
  const [editingNestedTableNameValue, setEditingNestedTableNameValue] = useState(''); // Track the current nested table name value being edited
  const [showSubtableRowMenu, setShowSubtableRowMenu] = useState(null); // {tableId, subtableId, rowIdx} for subtable row insert menu
  const [showNestedTableRowMenu, setShowNestedTableRowMenu] = useState(null); // {tableId, nestedTableId, rowIdx} for nested table row insert menu
  const creatingSpecialCell = useRef(false); // Flag to prevent onBlur when creating charts/nested tables/etc.
  const fileExplorerRef = useRef(null); // Ref to FileExplorer component for triggering refresh
  const [showChartModal, setShowChartModal] = useState(null); // {tableId, rowIdx, colIdx, subtableId, nestedTableId}
  const [capturingScreenshot, setCapturingScreenshot] = useState(false); // Track screenshot capture in progress

  // Simple Markdown renderer for description text
  const renderMarkdown = (text) => {
    if (!text) return '';

    let html = text;

    // Bold: **text** or __text__
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__(.+?)__/g, '<strong>$1</strong>');

    // Italic: *text* or _text_
    html = html.replace(/\*(.+?)\*/g, '<em>$1</em>');
    html = html.replace(/_(.+?)_/g, '<em>$1</em>');

    // Code: `text`
    html = html.replace(/`(.+?)`/g, '<code>$1</code>');

    // Links: [text](url)
    html = html.replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');

    // Line breaks
    html = html.replace(/\n/g, '<br>');

    return html;
  };

  // Cell editing state - tracks pending edits before persistence
  const [editingCells, setEditingCells] = useState({}); // Key: `${tableId}-${rowIdx}-${colIdx}`, Value: pending cell value

  // View mode state (Shopify integration)
  const [viewMode, setViewMode] = useState('tables'); // 'tables' or 'shopify'

  // Table sharing state
  const [showShareModal, setShowShareModal] = useState(null); // table object to share
  const [showSharedTablesView, setShowSharedTablesView] = useState(false);
  const [showSharingWithOthersView, setShowSharingWithOthersView] = useState(false);
  const [sharedTables, setSharedTables] = useState([]); // Tables shared with current user
  const [tableShares, setTableShares] = useState({}); // Map of tableId -> array of shares (who we're sharing with)
  const [showShareDropdown, setShowShareDropdown] = useState(null); // Track which table's share dropdown is open
  const [hiddenSharedTableIds, setHiddenSharedTableIds] = useState(() => {
    // Load hidden table IDs from localStorage
    const saved = localStorage.getItem('hiddenSharedTables');
    return saved ? JSON.parse(saved) : [];
  });

  // Ref for the tables container to capture screenshot
  const tablesContainerRef = useRef(null);

  // Ref for debouncing resize saves
  const resizeDebounceTimerRef = useRef(null);

  // Clipboard state for copy/paste
  const [copiedRow, setCopiedRow] = useState(null); // {data: [...], source: {tableId, type}}
  const [copiedColumn, setCopiedColumn] = useState(null); // {data: [...], numRows, source: {tableId, type}}

  // Undo/Redo state
  const [history, setHistory] = useState([]); // Array of past states
  const [historyIndex, setHistoryIndex] = useState(-1); // Current position in history
  const [isUndoRedoAction, setIsUndoRedoAction] = useState(false); // Flag to prevent history recording during undo/redo

  // Claude table generation state
  const [claudePrompt, setClaudePrompt] = useState('');
  const [generatingTable, setGeneratingTable] = useState(false);

  // Apply theme, density, and border style to document root
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    document.documentElement.setAttribute('data-color', colorScheme);
    document.documentElement.setAttribute('data-density', density);
    document.documentElement.setAttribute('data-border', borderStyle);
    document.documentElement.setAttribute('data-cell-bg', showCellBackgrounds ? 'show' : 'hide');
    document.documentElement.setAttribute('data-connector-lines', showConnectorLines ? 'show' : 'hide');
    document.documentElement.setAttribute('data-graph-paper', graphPaper ? 'show' : 'hide');
    document.documentElement.setAttribute('data-metallic', metallicFinish ? 'show' : 'hide');

    // Apply (or clear) the custom color CSS variables on the root
    const rootEl = document.documentElement;
    const customVars = [
      '--accent-primary', '--accent-secondary', '--accent-gradient',
      '--accent-light-bg', '--accent-light-border', '--accent-border',
      '--accent-shadow', '--accent-shadow-light'
    ];
    if (colorScheme === 'custom') {
      const p = deriveCustomPalette(customColor);
      rootEl.style.setProperty('--accent-primary', p.primary);
      rootEl.style.setProperty('--accent-secondary', p.secondary);
      rootEl.style.setProperty('--accent-gradient', `linear-gradient(135deg, ${p.primary} 0%, ${p.secondary} 100%)`);
      rootEl.style.setProperty('--accent-light-bg', theme === 'dark' ? p.darkBg : p.light);
      rootEl.style.setProperty('--accent-light-border', theme === 'dark' ? p.primary : p.lightAlt);
      rootEl.style.setProperty('--accent-border', theme === 'dark' ? p.darkBorder : p.primary);
      rootEl.style.setProperty('--accent-shadow', `rgba(${p.rgb}, 0.4)`);
      rootEl.style.setProperty('--accent-shadow-light', `rgba(${p.rgb}, 0.1)`);
    } else {
      customVars.forEach(v => rootEl.style.removeProperty(v));
    }

    // Force browser to recalculate styles by triggering a reflow
    void document.documentElement.offsetHeight;

    localStorage.setItem('theme', theme);
    localStorage.setItem('colorScheme', colorScheme);
    localStorage.setItem('customColor', customColor);
    if (companyLogo) {
      localStorage.setItem('companyLogo', companyLogo);
    } else {
      localStorage.removeItem('companyLogo');
    }
    localStorage.setItem('logoBgEnabled', String(logoBgEnabled));
    localStorage.setItem('logoBgColor', logoBgColor);
    localStorage.setItem('toolbarOpen', String(toolbarOpen));
    localStorage.setItem('density', density);
    localStorage.setItem('borderStyle', borderStyle);
    localStorage.setItem('depthShading', JSON.stringify(depthShading));
    localStorage.setItem('showCellBackgrounds', JSON.stringify(showCellBackgrounds));
    localStorage.setItem('showConnectorLines', JSON.stringify(showConnectorLines));
    localStorage.setItem('graphPaper', JSON.stringify(graphPaper));
    localStorage.setItem('showDefaultCellPlaceholders', JSON.stringify(showDefaultCellPlaceholders));
    localStorage.setItem('metallicFinish', JSON.stringify(metallicFinish));
  }, [theme, colorScheme, customColor, companyLogo, logoBgEnabled, logoBgColor, toolbarOpen, density, borderStyle, depthShading, showCellBackgrounds, showConnectorLines, graphPaper, showDefaultCellPlaceholders, metallicFinish]);

  // Note: fetchTableShares() is called when:
  // 1. App loads (at end of fetchTabs())
  // 2. Share modal closes (after sharing/unsharing operations)

  // Close share dropdown when clicking outside
  useEffect(() => {
    if (!showShareDropdown) return;

    const handleClickOutside = () => {
      setShowShareDropdown(null);
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showShareDropdown]);

  // Close file menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFileMenu && !event.target.closest('.theme-control')) {
        setShowFileMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showFileMenu]);

  // Load tabs and files on mount
  useEffect(() => {
    let initialized = false;
    const initializeApp = async () => {
      if (initialized) return;
      initialized = true;

      try {
        const data = await fetchAvailableFiles(); // Fetch available files first
        // Load the file that the backend says is current
        if (data && data.current) {
          await loadFile(data.current, false); // Don't show alert on startup
          setLoading(false); // Clear loading state
        } else {
          // If no files available, just fetch tabs
          await fetchTabs();
        }
      } catch (error) {
        console.error('[initializeApp] Error initializing app:', error);
        // Try to load tabs anyway
        await fetchTabs();
      }
    };
    initializeApp();
  }, []);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showContextMenu) {
        setShowContextMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showContextMenu]);

  // Close insert row/column menus when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (showRowMenu) {
        setShowRowMenu(null);
      }
      if (showColMenu) {
        setShowColMenu(null);
      }
      if (showSubtableColMenu) {
        setShowSubtableColMenu(null);
      }
      if (showNestedTableColMenu) {
        setShowNestedTableColMenu(null);
      }
      if (showSubtableRowMenu) {
        setShowSubtableRowMenu(null);
      }
      if (showNestedTableRowMenu) {
        setShowNestedTableRowMenu(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showRowMenu, showColMenu, showSubtableColMenu, showNestedTableColMenu, showSubtableRowMenu, showNestedTableRowMenu]);

  // Keyboard shortcuts for cell operations
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Only process shortcuts if a cell is selected and Alt/Option key is pressed
      if (!selectedCell || !e.altKey) return;

      // Use e.code to get physical key (works across different keyboard layouts and macOS)
      const code = e.code;

      // Prevent default browser behavior for our shortcuts
      if (['KeyC', 'KeyT', 'KeyR', 'KeyD'].includes(code)) {
        e.preventDefault();
        e.stopPropagation();
      }

      const { tableId, rowIdx, colIdx, subtableId, nestedTableId } = selectedCell;
      const isSubtable = selectedCell.isSubtable || false;

      console.log('[KEYBOARD SHORTCUT] Selected cell:', {
        selectedCell,
        code,
        pathString: selectedCell.contextPath ? pathToString(selectedCell.contextPath) : 'NO PATH'
      });

      switch (code) {
        case 'KeyC': // Alt+C - Create Chart
          if (selectedCell.contextPath) {
            createChart(selectedCell.contextPath, rowIdx, colIdx);
          }
          break;

        case 'KeyT': // Alt+T - Add Nested Table
          if (selectedCell.contextPath) {
            createNestedTable(selectedCell.contextPath, rowIdx, colIdx);
          }
          break;

        case 'KeyR': // Alt+R - Add Table Reference
          openTableReferenceModal(tableId, rowIdx, colIdx, subtableId, nestedTableId);
          break;

        case 'KeyD': // Alt+D - Add Date Picker
          createDatePicker(tableId, rowIdx, colIdx, subtableId, nestedTableId);
          break;

        case 'KeyP': // Alt+P - Add Color Picker
          createColorPicker(tableId, rowIdx, colIdx, subtableId, nestedTableId);
          break;

        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [selectedCell]);

  // Keyboard shortcuts for focused elements (Alt+X to delete)
  useEffect(() => {
    const handleKeyDown = async (e) => {
      if (!focusedElement) return;

      // Alt+X - Delete the focused element
      if (e.altKey && e.code === 'KeyX') {
        e.preventDefault();
        e.stopPropagation();

        const { type, tableId, rowIdx, colIdx, subtableId, nestedTableId, contextPath: storedContextPath } = focusedElement;

        // Construct contextPath if not stored (for backward compatibility)
        const contextPath = storedContextPath || createContextPath(tableId, subtableId, nestedTableId);

        // ✅ UPDATED: Use updateCellGeneric with contextPath for unlimited depth support
        await updateCellGeneric(tableId, rowIdx, colIdx, null, { contextPath });

        // Clear focus after deletion
        setFocusedElement(null);

        // Also update selectedCell to point to the now-empty cell
        // This ensures arrow key navigation works correctly after deletion
        setSelectedCell({
          tableId,
          rowIdx,
          colIdx,
          subtableId,
          nestedTableId: null, // The nested table is gone, so clear this
          isSubtable: !!subtableId
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [focusedElement]);

  // Arrow key navigation for moving between cells
  useEffect(() => {
    const handleArrowKeys = (e) => {
      if (!selectedCell) {
        return;
      }

      // Only handle arrow keys
      if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;

      // Don't interfere if user is actively typing in a text area or contentEditable
      // (but DO handle arrow keys in regular input fields since those are our table cells)
      if (e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) return;

      // Check if the focused element matches the selected cell context
      // This prevents navigation in parent tables when focused in nested tables
      if (e.target.tagName === 'INPUT') {
        const focusedNestedTableId = e.target.getAttribute('data-nested-table-id');
        const focusedSubtableId = e.target.getAttribute('data-subtable-id');

        // If selectedCell is in a nested table but focused element is not (or vice versa), ignore
        if (selectedCell.nestedTableId !== focusedNestedTableId) {
          return;
        }

        // If selectedCell is in a subtable but focused element is not (or vice versa), ignore
        if (selectedCell.subtableId !== focusedSubtableId) {
          return;
        }
      }

      e.preventDefault();
      e.stopPropagation();

      const { tableId, rowIdx, colIdx, subtableId, nestedTableId } = selectedCell;

      // Find the current table to get its dimensions
      let currentTable = null;
      let numRows = 0;
      let numCols = 0;

      if (nestedTableId) {
        // Find nested table - could be in main table or in a subtable
        for (const table of tables) {
          const findNestedTable = (rows) => {
            for (const row of rows) {
              for (const cell of row) {
                if (cell && typeof cell === 'object' && cell.type === 'nested_table' && cell.id === nestedTableId) {
                  return cell;
                }
              }
            }
            return null;
          };

          // First try to find in main table rows
          currentTable = findNestedTable(table.rows);

          // If not found and we have a subtableId, search in subtables
          if (!currentTable && subtableId && table.row_subtables) {
            for (const rowKey in table.row_subtables) {
              const subtablesInRow = table.row_subtables[rowKey];
              const foundSubtable = subtablesInRow.find(st => st.id === subtableId);
              if (foundSubtable) {
                currentTable = findNestedTable(foundSubtable.rows);
                if (currentTable) break;
              }
            }
          }

          if (currentTable) break;
        }

        // If we found the nested table and also have a subtableId, find the subtable within the nested table
        if (currentTable && subtableId) {
          if (currentTable.row_subtables) {
            let foundSubtable = null;
            for (const rowKey in currentTable.row_subtables) {
              const subtablesInRow = currentTable.row_subtables[rowKey];
              foundSubtable = subtablesInRow.find(st => st.id === subtableId);
              if (foundSubtable) {
                currentTable = foundSubtable;
                break;
              }
            }
          }
        }

        if (currentTable) {
          numRows = currentTable.num_rows;
          numCols = currentTable.num_cols;
        }
      } else if (subtableId) {
        // Find subtable - subtables are stored in row_subtables[rowKey] as an array
        for (const table of tables) {
          if (table.row_subtables) {
            // Search through all row keys
            for (const rowKey in table.row_subtables) {
              const subtablesInRow = table.row_subtables[rowKey];
              const foundSubtable = subtablesInRow.find(st => st.id === subtableId);
              if (foundSubtable) {
                currentTable = foundSubtable;
                numRows = currentTable.num_rows;
                numCols = currentTable.num_cols;
                break;
              }
            }
            if (currentTable) break;
          }
        }
      } else {
        // Main table
        currentTable = tables.find(t => t.id === tableId);
        if (currentTable) {
          numRows = currentTable.num_rows;
          numCols = currentTable.num_cols;
        }
      }

      if (!currentTable) {
        return;
      }

      // Calculate new position based on arrow key
      let newRowIdx = rowIdx;
      let newColIdx = colIdx;

      switch (e.key) {
        case 'ArrowUp':
          newRowIdx = Math.max(0, rowIdx - 1);
          break;
        case 'ArrowDown':
          newRowIdx = Math.min(numRows - 1, rowIdx + 1);
          break;
        case 'ArrowLeft':
          newColIdx = Math.max(0, colIdx - 1);
          break;
        case 'ArrowRight':
          newColIdx = Math.min(numCols - 1, colIdx + 1);
          break;
      }

      // Update selected cell if position changed
      if (newRowIdx !== rowIdx || newColIdx !== colIdx) {
        const newSelection = {
          tableId,
          rowIdx: newRowIdx,
          colIdx: newColIdx,
          subtableId,
          nestedTableId,
          isSubtable: selectedCell.isSubtable
        };
        setSelectedCell(newSelection);

        // Focus the input element in the new cell
        // Use setTimeout to ensure the DOM has updated
        setTimeout(() => {
          // Build a selector to find the input in the new cell
          let selector;
          if (nestedTableId && subtableId) {
            // Subtable inside a nested table - need both attributes
            selector = `input[data-nested-table-id="${nestedTableId}"][data-subtable-id="${subtableId}"][data-row="${newRowIdx}"][data-col="${newColIdx}"]`;
          } else if (nestedTableId) {
            selector = `input[data-nested-table-id="${nestedTableId}"][data-row="${newRowIdx}"][data-col="${newColIdx}"]`;
          } else if (subtableId) {
            selector = `input[data-subtable-id="${subtableId}"][data-row="${newRowIdx}"][data-col="${newColIdx}"]`;
          } else {
            // Main table - exclude subtable and nested table inputs
            selector = `input[data-table-id="${tableId}"][data-row="${newRowIdx}"][data-col="${newColIdx}"]:not([data-subtable-id]):not([data-nested-table-id])`;
          }

          const inputElement = document.querySelector(selector);
          if (inputElement) {
            inputElement.focus();
          }
        }, 0);
      }
    };

    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
  }, [selectedCell, tables]);

  // Undo/Redo keyboard shortcuts
  useEffect(() => {
    const handleUndoRedo = (e) => {
      // Check if user is typing in an input field - don't intercept undo there
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      // Cmd+Z (Mac) or Ctrl+Z (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }

      // Cmd+Shift+Z (Mac) or Ctrl+Y (Windows/Linux) for redo
      if (((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'z') ||
          (e.ctrlKey && e.key === 'y')) {
        e.preventDefault();
        redo();
        return;
      }
    };

    document.addEventListener('keydown', handleUndoRedo);
    return () => document.removeEventListener('keydown', handleUndoRedo);
  }, [history, historyIndex]);

  // Track state changes and save to history
  useEffect(() => {
    if (tabs.length > 0 && !isUndoRedoAction) {
      saveToHistory();
    }
  }, [tabs]);

  const fetchTabs = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/tabs`);
      const tabsData = await response.json();
      setTabs(tabsData.tabs);
      setActiveTabId(tabsData.activeTabId);

      // Set tables for active tab
      const activeTab = tabsData.tabs.find(tab => tab.id === tabsData.activeTabId);
      let ownedTables = [];
      if (activeTab) {
        ownedTables = JSON.parse(JSON.stringify(activeTab.tables));
      }

      // Variable to hold final combined tables for share fetching
      let finalTables = ownedTables;

      // Fetch shared tables
      try {
        const sharedResponse = await authFetch(`${API_URL}/api/shared-tables`);
        const sharedData = await sharedResponse.json();

        // Transform shared tables with metadata
        const sharedTablesWithMeta = sharedData.map(item => ({
          ...item.table,
          isShared: true,
          sharedBy: item.owner_username,
          sharedPermission: item.permission,
          shareId: item.share_id
        }));

        // Filter out hidden shared tables
        const visibleSharedTables = sharedTablesWithMeta.filter(
          table => !hiddenSharedTableIds.includes(table.id)
        );

        // Combine owned and visible shared tables
        const combinedTables = [...ownedTables, ...visibleSharedTables];
        finalTables = combinedTables;
        setTables(combinedTables);
        setSharedTables(sharedTablesWithMeta);
      } catch (sharedError) {
        console.error('[fetchTabs] Error fetching shared tables:', sharedError);
        // If shared tables fail, still show owned tables
        setTables(ownedTables);
      }

      setLoading(false);

      // Fetch table shares after tables are loaded (pass tables directly since state isn't updated yet)
      await fetchTableShares(finalTables);
    } catch (error) {
      console.error('[fetchTabs] Error fetching tabs:', error);
      setLoading(false);
    }
  };

  // File Management Functions
  const fetchAvailableFiles = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/files`);
      const data = await response.json();
      setAvailableFiles(data.files);
      setCurrentFile(data.current);
      return data; // Return data for use in initializeApp
    } catch (error) {
      console.error('Error fetching available files:', error);
      return null;
    }
  };

  const loadFile = async (filename, showAlert = false) => {
    try {
      // Clear existing state before loading new file
      setTabs([]);
      setActiveTabId(null);
      setTables([]);
      setSharedTables([]);

      const response = await authFetch(`${API_URL}/api/files/load/${filename}`, {
        method: 'POST'
      });
      const data = await response.json();
      setCurrentFile(data.current);

      // Update tabs from the response
      if (data.tabs) {
        setTabs(data.tabs.tabs || []);
        setActiveTabId(data.tabs.activeTabId);

        // Set tables for active tab (just like fetchTabs does)
        const activeTab = data.tabs.tabs.find(tab => tab.id === data.tabs.activeTabId);
        let ownedTables = [];
        if (activeTab) {
          ownedTables = JSON.parse(JSON.stringify(activeTab.tables));
        }

        // Fetch shared tables
        try {
          const sharedResponse = await authFetch(`${API_URL}/api/shared-tables`);
          const sharedData = await sharedResponse.json();

          // Transform shared tables with metadata
          const sharedTablesWithMeta = sharedData.map(item => ({
            ...item.table,
            isShared: true,
            sharedBy: item.owner_username,
            sharedPermission: item.permission,
            shareId: item.share_id
          }));

          // Filter out hidden shared tables
          const visibleSharedTables = sharedTablesWithMeta.filter(
            table => !hiddenSharedTableIds.includes(table.id)
          );

          // Combine owned and visible shared tables
          const combinedTables = [...ownedTables, ...visibleSharedTables];
          setTables(combinedTables);
          setSharedTables(sharedTablesWithMeta);
        } catch (sharedError) {
          console.error('[loadFile] Error fetching shared tables:', sharedError);
          // If shared tables fail, still show owned tables
          setTables(ownedTables);
        }
      }
      if (showAlert) {
        alert(`Loaded ${filename}`);
      }
    } catch (error) {
      console.error('Error loading file:', error);
      if (showAlert) {
        alert('Error loading file');
      }
    }
  };

  const saveCurrentFile = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/files/save`, {
        method: 'POST'
      });
      const data = await response.json();
      alert(data.message);
    } catch (error) {
      console.error('Error saving file:', error);
      alert('Error saving file');
    }
  };

  const createNewFile = async () => {
    const filename = prompt('Enter new file name (without .json extension):');
    if (!filename) return;

    try {
      const response = await authFetch(`${API_URL}/api/files/create/${filename}`, {
        method: 'POST'
      });
      const data = await response.json();

      // Clear existing state before loading new file
      setTabs([]);
      setActiveTabId(null);
      setTables([]);
      setSharedTables([]);

      // Refresh available files list
      await fetchAvailableFiles();

      // Reload tabs after creating new file
      await fetchTabs();

      alert(data.message);
    } catch (error) {
      console.error('Error creating file:', error);
      const errorData = await error.response?.json();
      alert(errorData?.detail || 'Error creating file');
    }
  };

  const renameFile = async () => {
    const newFilename = prompt(`Rename "${currentFile}" to:`, currentFile.replace('.json', ''));
    if (!newFilename || newFilename === currentFile.replace('.json', '')) return;

    try {
      const response = await authFetch(`${API_URL}/api/files/rename`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          old_filename: currentFile,
          new_filename: newFilename
        })
      });
      const data = await response.json();

      // Refresh available files list
      await fetchAvailableFiles();

      // Load the renamed file
      await loadFile(data.new_filename);

      alert(data.message);
    } catch (error) {
      console.error('Error renaming file:', error);
      const errorData = await error.response?.json();
      alert(errorData?.detail || 'Error renaming file');
    }
  };

  const duplicateFile = async () => {
    const newFilename = prompt(`Duplicate "${currentFile}" as:`, currentFile.replace('.json', '') + '_copy');
    if (!newFilename) return;

    try {
      const response = await authFetch(`${API_URL}/api/files/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          filename: currentFile,
          new_filename: newFilename
        })
      });
      const data = await response.json();

      // Refresh available files list
      await fetchAvailableFiles();

      alert(data.message);
    } catch (error) {
      console.error('Error duplicating file:', error);
      const errorData = await error.response?.json();
      alert(errorData?.detail || 'Error duplicating file');
    }
  };

  const deleteFile = async () => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${currentFile}"?\n\nThis action cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const response = await authFetch(`${API_URL}/api/files/${currentFile}`, {
        method: 'DELETE'
      });
      const data = await response.json();

      // Refresh available files list
      await fetchAvailableFiles();

      // If switched to another file, load it
      if (data.switched_to) {
        await loadFile(data.switched_to);
      }

      alert(data.message);
    } catch (error) {
      console.error('Error deleting file:', error);
      const errorData = await error.response?.json();
      alert(errorData?.detail || 'Error deleting file');
    }
  };

  const importFromExcel = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        // Read the Excel file
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });

        console.log('[importFromExcel] Workbook loaded:', {
          sheetNames: workbook.SheetNames,
          numSheets: workbook.SheetNames.length
        });

        // Convert first sheet to table
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to array of arrays (includes headers as first row)
        const rawData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        console.log('[importFromExcel] Raw data:', rawData);

        if (!rawData || rawData.length === 0) {
          alert('Excel file is empty');
          return;
        }

        // Parse the data
        const headers = rawData[0] || [];
        const rows = rawData.slice(1).filter(row => row.some(cell => cell !== null && cell !== undefined && cell !== ''));

        console.log('[importFromExcel] Parsed:', {
          headers,
          numRows: rows.length,
          numCols: headers.length
        });

        // Calculate column widths based on header text length
        // Use generous sizing to ensure headers fit
        const columnWidths = headers.map(header => {
          const headerText = String(header || '');
          const minWidth = 150; // Minimum column width
          const charWidth = 10; // Pixels per character (increased for better fit)
          const padding = 60; // Extra padding for cell padding, borders, and sort icons
          const calculatedWidth = (headerText.length * charWidth) + padding;
          return Math.max(minWidth, calculatedWidth);
        });

        // Create table structure
        const newTable = {
          id: `imported-${Date.now()}`,
          name: firstSheetName || 'Imported Table',
          rows: rows.map(row => {
            // Pad row to match header length
            const paddedRow = Array(headers.length).fill(null);
            row.forEach((cell, i) => {
              if (i < headers.length) {
                // Convert cell value
                paddedRow[i] = convertExcelCell(cell);
              }
            });
            return paddedRow;
          }),
          num_rows: rows.length,
          num_cols: headers.length,
          row_subtables: {},
          column_widths: columnWidths,
          row_heights: null,
          row_header_width: null,
          show_headers: headers.length > 0,
          column_headers: headers.map(h => h || ''),
          show_row_headers: true,
          row_headers: rows.map((_, i) => `Row ${i + 1}`),
          sort_column: null,
          sort_direction: null,
          keep_done_at_bottom: false
        };

        console.log('[importFromExcel] Created table:', newTable);

        // Send to backend to add to current tab
        authFetch(`${API_URL}/api/tables/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            table: newTable,
            tab_id: activeTabId
          })
        })
          .then(response => response.json())
          .then(tabsData => {
            // Update state with new tables
            setTabs(tabsData.tabs);
            const activeTab = tabsData.tabs.find(tab => tab.id === activeTabId);
            if (activeTab) {
              setTables(JSON.parse(JSON.stringify(activeTab.tables)));
            }
            alert(`Imported table "${newTable.name}" with ${rows.length} rows and ${headers.length} columns`);
          })
          .catch(error => {
            console.error('[importFromExcel] Error adding table:', error);
            alert(`Error adding imported table: ${error.message}`);
          });

        // Reset file input
        event.target.value = '';
      } catch (error) {
        console.error('[importFromExcel] Error:', error);
        alert(`Error importing Excel file: ${error.message}`);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Import from HTML or Markdown file (hierarchical document → table with subtables)
  const importFromHierarchical = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const text = e.target.result;
        const fileName = file.name.toLowerCase();
        let newTable;

        if (fileName.endsWith('.md') || fileName.endsWith('.markdown')) {
          newTable = parseMarkdownToTableData(text);
        } else {
          // Treat as HTML (covers .html, .htm, and anything else)
          newTable = parseHtmlToTableData(text);
        }

        // Debug: log the full structure before sending
        console.log('[importFromHierarchical] Parsed table:', JSON.stringify(newTable, null, 2));
        console.log('[importFromHierarchical] Summary:', {
          name: newTable.name,
          rows: newTable.num_rows,
          cols: newTable.num_cols,
          rowHeaders: newTable.row_headers,
          subtableRowKeys: Object.keys(newTable.row_subtables || {}),
          subtableCounts: Object.fromEntries(
            Object.entries(newTable.row_subtables || {}).map(
              ([k, v]) => [k, v.length + ' subtables: ' + v.map(s => s.name).join(', ')]
            )
          )
        });

        // Send to backend via the existing table import endpoint
        authFetch(`${API_URL}/api/tables/import`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            table: newTable,
            tab_id: activeTabId
          })
        })
          .then(response => {
            console.log('[importFromHierarchical] Response status:', response.status);
            if (!response.ok) {
              return response.text().then(text => { throw new Error(`Server error ${response.status}: ${text}`); });
            }
            return response.json();
          })
          .then(tabsData => {
            // Check what the server actually stored
            const activeTab = tabsData.tabs.find(tab => tab.id === activeTabId);
            if (activeTab) {
              const imported = activeTab.tables[activeTab.tables.length - 1];
              console.log('[importFromHierarchical] Server stored subtables:', Object.keys(imported.row_subtables || {}));
              setTables(JSON.parse(JSON.stringify(activeTab.tables)));
            }
            setTabs(tabsData.tabs);

            const subtableCount = Object.values(newTable.row_subtables || {})
              .reduce((sum, arr) => sum + arr.length, 0);
            alert(
              `Imported "${newTable.name}"\n\n` +
              `${newTable.num_rows} top-level sections\n` +
              `${subtableCount} subtables\n\n` +
              `Source: ${file.name}`
            );
          })
          .catch(error => {
            console.error('[importFromHierarchical] Error:', error);
            alert(`Error importing: ${error.message}`);
          });

        event.target.value = '';
      } catch (error) {
        console.error('[importFromHierarchical] Error:', error);
        alert(`Error parsing ${file.name}: ${error.message}`);
        event.target.value = '';
      }
    };

    reader.readAsText(file);
  };

  // Export all user data as backup
  const exportBackup = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/export?filename=${currentFile}`);
      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link with original filename included
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      // Remove .json extension from currentFile and add it to backup name
      const baseFilename = currentFile.replace('.json', '');
      a.download = `backup_${baseFilename}_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert('Backup downloaded successfully!');
    } catch (error) {
      console.error('Export error:', error);
      alert(`Error exporting backup: ${error.message}`);
    }
  };

  // Import backup data
  const importBackup = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        // Parse the JSON backup file
        const backupData = JSON.parse(e.target.result);

        // Confirm with user before importing (this will overwrite current data)
        const confirmed = window.confirm(
          `Import backup file?\n\n` +
          `This will replace your current data with the backup.\n` +
          `Your current data will be automatically backed up first.\n\n` +
          `Tabs in backup: ${backupData.tabs?.length || 0}\n` +
          `Tables in backup: ${backupData.tabs?.reduce((sum, tab) => sum + (tab.tables?.length || 0), 0) || 0}\n\n` +
          `Continue?`
        );

        if (!confirmed) {
          event.target.value = '';
          return;
        }

        // Send to backend
        const response = await authFetch(`${API_URL}/api/import?filename=${currentFile}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(backupData)
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Import failed');
        }

        const result = await response.json();

        // Reload data from server - force reload the current file
        await loadFile(currentFile);

        alert(
          `Backup imported successfully!\n\n` +
          `Tabs: ${result.tabs_count}\n` +
          `Tables: ${result.tables_count}\n\n` +
          `Your previous data was backed up to: ${result.backup_created}`
        );

        // Reset file input
        event.target.value = '';
      } catch (error) {
        console.error('Import error:', error);
        alert(`Error importing backup: ${error.message}`);
        event.target.value = '';
      }
    };

    reader.readAsText(file);
  };

  // Export current file
  const exportFile = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/files/export/${currentFile}`);
      if (!response.ok) {
        throw new Error('Export failed');
      }

      // Get the blob from response
      const blob = await response.blob();

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = currentFile;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      alert(`File "${currentFile}" downloaded successfully!`);
    } catch (error) {
      console.error('Export error:', error);
      alert(`Error exporting file: ${error.message}`);
    }
  };

  // Import file
  const importFile = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      // For file uploads, we need to let the browser set Content-Type with boundary
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/files/import`, {
        method: 'POST',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
          // Do NOT set Content-Type for FormData - browser will set it with boundary
        },
        body: formData
      });

      if (response.status === 401) {
        localStorage.removeItem('token');
        window.location.reload();
        throw new Error('Session expired. Please login again.');
      }

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Import failed');
      }

      const result = await response.json();

      // Refresh file list
      await fetchAvailableFiles();

      alert(`File "${result.filename}" imported successfully! You can now switch to it using the file selector.`);

      // Reset file input
      event.target.value = '';
    } catch (error) {
      console.error('Import error:', error);
      alert(`Error importing file: ${error.message}`);
      event.target.value = '';
    }
  };

  // Helper function to convert Excel cell values
  const convertExcelCell = (cell) => {
    if (cell === null || cell === undefined || cell === '') {
      return null;
    }

    // Check if it's a date (Excel dates are numbers)
    if (typeof cell === 'number' && cell > 40000 && cell < 60000) {
      // Likely an Excel date (days since 1900-01-01)
      const excelEpoch = new Date(1900, 0, 1);
      const date = new Date(excelEpoch.getTime() + (cell - 2) * 24 * 60 * 60 * 1000);
      return {
        type: 'date',
        value: date.toISOString().split('T')[0]
      };
    }

    // Check for priority keywords
    const cellStr = String(cell).toLowerCase();
    if (cellStr === 'high' || cellStr === 'medium' || cellStr === 'low') {
      return {
        type: 'priority',
        value: cellStr
      };
    }

    // Check for done/checkbox
    if (cellStr === '✓' || cellStr === 'done' || cellStr === 'true' || cellStr === 'yes') {
      return {
        type: 'done',
        done: true
      };
    }
    if (cellStr === '✗' || cellStr === 'false' || cellStr === 'no') {
      return {
        type: 'done',
        done: false
      };
    }

    // Return as plain text/number
    return cell;
  };

  const fetchTables = async () => {
    // Keep this for backward compatibility, just refresh tabs
    await fetchTabs();
  };

  // Fetch shares for all tables in current tab
  const fetchTableShares = async (tablesToCheck = null) => {
    const targetTables = tablesToCheck || tables;
    if (!targetTables || targetTables.length === 0) return;

    try {
      const sharesMap = {};

      // Fetch shares for each table
      for (const table of targetTables) {
        if (table.isShared) continue; // Skip tables shared with us

        try {
          const response = await authFetch(`${API_URL}/api/shares/table/${table.id}`);
          if (response.ok) {
            const shares = await response.json();
            if (shares.length > 0) {
              sharesMap[table.id] = shares;
            }
          } else if (response.status === 404) {
            // Table not found in backend yet - this can happen with optimistic updates
            // Silently ignore, will retry on next state change
          }
        } catch (err) {
          console.error(`Error fetching shares for table ${table.id}:`, err);
        }
      }

      setTableShares(sharesMap);
    } catch (err) {
      console.error('Error fetching table shares:', err);
    }
  };

  // Save current state to history (for undo/redo)
  const saveToHistory = () => {
    if (isUndoRedoAction) return; // Don't save during undo/redo

    const currentState = {
      tabs: JSON.parse(JSON.stringify(tabs)),
      activeTabId,
      timestamp: Date.now()
    };

    setHistory(prev => {
      // Remove any "future" states if we're not at the end
      const newHistory = prev.slice(0, historyIndex + 1);
      // Add new state
      newHistory.push(currentState);
      // Keep only last 50 states to avoid memory issues
      return newHistory.slice(-50);
    });

    setHistoryIndex(prev => Math.min(prev + 1, 49));
  };

  // Undo function
  const undo = () => {
    if (historyIndex <= 0) {
      console.log('[undo] No more history to undo');
      return;
    }

    const previousState = history[historyIndex - 1];
    setIsUndoRedoAction(true);

    // Restore previous state
    setTabs(JSON.parse(JSON.stringify(previousState.tabs)));
    setActiveTabId(previousState.activeTabId);

    // Update tables for active tab
    const activeTab = previousState.tabs.find(tab => tab.id === previousState.activeTabId);
    if (activeTab) {
      setTables(JSON.parse(JSON.stringify(activeTab.tables)));
    }

    setHistoryIndex(prev => prev - 1);

    setTimeout(() => setIsUndoRedoAction(false), 100);
    console.log('[undo] Restored state from history index', historyIndex - 1);
  };

  // Redo function
  const redo = () => {
    if (historyIndex >= history.length - 1) {
      console.log('[redo] No more history to redo');
      return;
    }

    const nextState = history[historyIndex + 1];
    setIsUndoRedoAction(true);

    // Restore next state
    setTabs(JSON.parse(JSON.stringify(nextState.tabs)));
    setActiveTabId(nextState.activeTabId);

    // Update tables for active tab
    const activeTab = nextState.tabs.find(tab => tab.id === nextState.activeTabId);
    if (activeTab) {
      setTables(JSON.parse(JSON.stringify(activeTab.tables)));
    }

    setHistoryIndex(prev => prev + 1);

    setTimeout(() => setIsUndoRedoAction(false), 100);
    console.log('[redo] Restored state from history index', historyIndex + 1);
  };

  const createTab = async (name = 'New Tab') => {
    try {
      const response = await authFetch(`${API_URL}/api/tabs?name=${encodeURIComponent(name)}`, { method: 'POST' });
      const tabsData = await response.json();
      setTabs(tabsData.tabs);
      setActiveTabId(tabsData.activeTabId);

      // Update tables for active tab
      const activeTab = tabsData.tabs.find(tab => tab.id === tabsData.activeTabId);
      if (activeTab) {
        setTables(JSON.parse(JSON.stringify(activeTab.tables)));
      }
    } catch (error) {
      console.error('Error creating tab:', error);
    }
  };

  const deleteTab = async (tabId) => {
    try {
      const response = await authFetch(`${API_URL}/api/tabs/${tabId}`, { method: 'DELETE' });
      const tabsData = await response.json();
      setTabs(tabsData.tabs);
      setActiveTabId(tabsData.activeTabId);

      // Update tables for new active tab
      const activeTab = tabsData.tabs.find(tab => tab.id === tabsData.activeTabId);
      if (activeTab) {
        setTables(JSON.parse(JSON.stringify(activeTab.tables)));
      }
    } catch (error) {
      console.error('Error deleting tab:', error);
      alert('Cannot delete the last tab');
    }
  };

  const renameTab = async (tabId, newName) => {
    try {
      await authFetch(`${API_URL}/api/tabs/${tabId}?name=${encodeURIComponent(newName)}`, { method: 'PUT' });
      await fetchTabs();
    } catch (error) {
      console.error('Error renaming tab:', error);
    }
  };

  const switchTab = async (tabId) => {
    try {
      const response = await authFetch(`${API_URL}/api/tabs/active/${tabId}`, { method: 'PUT' });
      const tabsData = await response.json();
      setTabs(tabsData.tabs);
      setActiveTabId(tabsData.activeTabId);

      // Update tables for newly active tab
      const activeTab = tabsData.tabs.find(tab => tab.id === tabId);
      if (activeTab) {
        setTables(JSON.parse(JSON.stringify(activeTab.tables)));
      }
    } catch (error) {
      console.error('Error switching tab:', error);
    }
  };

  const captureTableScreenshot = async (tableId, tableName) => {
    const tableElement = document.querySelector(`[data-table-id="${tableId}"]`);
    if (!tableElement) {
      alert('Table not found');
      return;
    }

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `${tableName}_${timestamp}.png`;

      // Temporarily expand all collapsed subtables for capture
      const allExpandButtons = tableElement.querySelectorAll('.expand-nested-table-btn, .subtable-toggle-btn');
      const originalExpandedStates = [];

      // Store original states and expand everything
      allExpandButtons.forEach((btn, index) => {
        const isExpanded = btn.textContent.includes('▼') || btn.textContent.includes('Collapse');
        originalExpandedStates[index] = isExpanded;
        if (!isExpanded) {
          btn.click();
        }
      });

      // Wait for DOM to update after expanding
      await new Promise(resolve => setTimeout(resolve, 300));

      // Store original overflow styles for all elements
      const originalStyles = new Map();
      const allTableWrappers = tableElement.querySelectorAll('.table-wrapper, .table-container');

      // Store and remove overflow from table element itself
      const tableOriginalStyles = {
        overflow: tableElement.style.overflow,
        overflowX: tableElement.style.overflowX,
        overflowY: tableElement.style.overflowY,
        maxWidth: tableElement.style.maxWidth,
        maxHeight: tableElement.style.maxHeight
      };
      tableElement.style.overflow = 'visible';
      tableElement.style.overflowX = 'visible';
      tableElement.style.overflowY = 'visible';
      tableElement.style.maxWidth = 'none';
      tableElement.style.maxHeight = 'none';

      // Store and remove overflow from all table wrappers and containers
      allTableWrappers.forEach(element => {
        originalStyles.set(element, {
          overflow: element.style.overflow,
          overflowX: element.style.overflowX,
          overflowY: element.style.overflowY,
          maxWidth: element.style.maxWidth,
          maxHeight: element.style.maxHeight
        });
        element.style.overflow = 'visible';
        element.style.overflowX = 'visible';
        element.style.overflowY = 'visible';
        element.style.maxWidth = 'none';
        element.style.maxHeight = 'none';
      });

      // Wait for layout to adjust
      await new Promise(resolve => setTimeout(resolve, 200));

      // Get the actual full dimensions of the table content
      const rect = tableElement.getBoundingClientRect();
      const fullWidth = Math.max(tableElement.scrollWidth, tableElement.offsetWidth, rect.width);
      const fullHeight = Math.max(tableElement.scrollHeight, tableElement.offsetHeight, rect.height);

      // Capture the table with full dimensions
      const canvas = await html2canvas(tableElement, {
        allowTaint: true,
        useCORS: true,
        scrollY: 0,
        scrollX: 0,
        x: 0,
        y: 0,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        scale: 2,
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--bg-primary') || '#ffffff',
        onclone: (clonedDoc) => {
          const clonedTable = clonedDoc.querySelector(`[data-table-id="${tableId}"]`);
          if (clonedTable) {
            clonedTable.style.overflow = 'visible';
            clonedTable.style.overflowX = 'visible';
            clonedTable.style.overflowY = 'visible';
            clonedTable.style.maxWidth = 'none';
            clonedTable.style.maxHeight = 'none';
            clonedTable.style.height = 'auto';
            clonedTable.style.width = 'auto';

            const clonedWrappers = clonedTable.querySelectorAll('.table-wrapper, .table-container');
            clonedWrappers.forEach(wrapper => {
              wrapper.style.overflow = 'visible';
              wrapper.style.overflowX = 'visible';
              wrapper.style.overflowY = 'visible';
              wrapper.style.maxWidth = 'none';
              wrapper.style.maxHeight = 'none';
            });
          }
        }
      });

      // Restore original overflow styles for table element
      tableElement.style.overflow = tableOriginalStyles.overflow;
      tableElement.style.overflowX = tableOriginalStyles.overflowX;
      tableElement.style.overflowY = tableOriginalStyles.overflowY;
      tableElement.style.maxWidth = tableOriginalStyles.maxWidth;
      tableElement.style.maxHeight = tableOriginalStyles.maxHeight;

      // Restore original overflow styles for all wrappers
      allTableWrappers.forEach(element => {
        const original = originalStyles.get(element);
        if (original) {
          element.style.overflow = original.overflow;
          element.style.overflowX = original.overflowX;
          element.style.overflowY = original.overflowY;
          element.style.maxWidth = original.maxWidth;
          element.style.maxHeight = original.maxHeight;
        }
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);

        // Restore original expanded/collapsed states
        allExpandButtons.forEach((btn, index) => {
          const isCurrentlyExpanded = btn.textContent.includes('▼') || btn.textContent.includes('Collapse');
          if (isCurrentlyExpanded !== originalExpandedStates[index]) {
            btn.click();
          }
        });
      });
    } catch (error) {
      console.error('Error capturing table screenshot:', error);
      alert('Failed to capture screenshot. Please try again.');
    }
  };

  // Generic screenshot capture for any table element (subtables, nested tables, etc.)
  const captureElementScreenshot = async (selector, elementName, type = 'table') => {
    const element = document.querySelector(selector);
    if (!element) {
      alert(`${type} not found`);
      return;
    }

    try {
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `${elementName}_${timestamp}.png`;

      // Temporarily expand all collapsed subtables for capture
      const allExpandButtons = element.querySelectorAll('.expand-nested-table-btn, .subtable-toggle-btn');
      const originalExpandedStates = [];

      // Store original states and expand everything
      allExpandButtons.forEach((btn, index) => {
        const isExpanded = btn.textContent.includes('▼') || btn.textContent.includes('Collapse');
        originalExpandedStates[index] = isExpanded;
        if (!isExpanded) {
          btn.click();
        }
      });

      // Wait for DOM to update after expanding
      await new Promise(resolve => setTimeout(resolve, 300));

      // Store original overflow styles
      const originalStyles = new Map();
      const allTableWrappers = element.querySelectorAll('.table-wrapper, .table-container, .subtable-container, .nested-table-wrapper');

      // Store and remove overflow from element itself
      const elementOriginalStyles = {
        overflow: element.style.overflow,
        overflowX: element.style.overflowX,
        overflowY: element.style.overflowY,
        maxWidth: element.style.maxWidth,
        maxHeight: element.style.maxHeight
      };
      element.style.overflow = 'visible';
      element.style.overflowX = 'visible';
      element.style.overflowY = 'visible';
      element.style.maxWidth = 'none';
      element.style.maxHeight = 'none';

      // Store and remove overflow from all wrappers and containers
      allTableWrappers.forEach(elem => {
        originalStyles.set(elem, {
          overflow: elem.style.overflow,
          overflowX: elem.style.overflowX,
          overflowY: elem.style.overflowY,
          maxWidth: elem.style.maxWidth,
          maxHeight: elem.style.maxHeight
        });
        elem.style.overflow = 'visible';
        elem.style.overflowX = 'visible';
        elem.style.overflowY = 'visible';
        elem.style.maxWidth = 'none';
        elem.style.maxHeight = 'none';
      });

      // Wait for layout to adjust
      await new Promise(resolve => setTimeout(resolve, 200));

      // Get the actual full dimensions
      const rect = element.getBoundingClientRect();
      const fullWidth = Math.max(element.scrollWidth, element.offsetWidth, rect.width);
      const fullHeight = Math.max(element.scrollHeight, element.offsetHeight, rect.height);

      // Capture the element with full dimensions
      const canvas = await html2canvas(element, {
        allowTaint: true,
        useCORS: true,
        scrollY: 0,
        scrollX: 0,
        x: 0,
        y: 0,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        scale: 2,
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--bg-primary') || '#ffffff',
        onclone: (clonedDoc) => {
          const clonedElement = clonedDoc.querySelector(selector);
          if (clonedElement) {
            clonedElement.style.overflow = 'visible';
            clonedElement.style.overflowX = 'visible';
            clonedElement.style.overflowY = 'visible';
            clonedElement.style.maxWidth = 'none';
            clonedElement.style.maxHeight = 'none';
            clonedElement.style.height = 'auto';
            clonedElement.style.width = 'auto';

            const clonedWrappers = clonedElement.querySelectorAll('.table-wrapper, .table-container, .subtable-container, .nested-table-wrapper');
            clonedWrappers.forEach(wrapper => {
              wrapper.style.overflow = 'visible';
              wrapper.style.overflowX = 'visible';
              wrapper.style.overflowY = 'visible';
              wrapper.style.maxWidth = 'none';
              wrapper.style.maxHeight = 'none';
            });
          }
        }
      });

      // Restore original overflow styles
      element.style.overflow = elementOriginalStyles.overflow;
      element.style.overflowX = elementOriginalStyles.overflowX;
      element.style.overflowY = elementOriginalStyles.overflowY;
      element.style.maxWidth = elementOriginalStyles.maxWidth;
      element.style.maxHeight = elementOriginalStyles.maxHeight;

      allTableWrappers.forEach(elem => {
        const original = originalStyles.get(elem);
        if (original) {
          elem.style.overflow = original.overflow;
          elem.style.overflowX = original.overflowX;
          elem.style.overflowY = original.overflowY;
          elem.style.maxWidth = original.maxWidth;
          elem.style.maxHeight = original.maxHeight;
        }
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);

        // Restore original expanded/collapsed states
        allExpandButtons.forEach((btn, index) => {
          const isCurrentlyExpanded = btn.textContent.includes('▼') || btn.textContent.includes('Collapse');
          if (isCurrentlyExpanded !== originalExpandedStates[index]) {
            btn.click();
          }
        });
      });
    } catch (error) {
      console.error(`Error capturing ${type} screenshot:`, error);
      alert(`Failed to capture screenshot. Please try again.`);
    }
  };

  const captureTabScreenshot = async () => {
    if (!tablesContainerRef.current) {
      alert('No content to capture');
      return;
    }

    try {
      setCapturingScreenshot(true);

      // Get the current tab name for the filename
      const activeTab = tabs.find(tab => tab.id === activeTabId);
      const tabName = activeTab ? activeTab.name : 'tab';
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      const filename = `${tabName}_${timestamp}.png`;

      // Temporarily expand all collapsed nested tables and subtables for capture
      const container = tablesContainerRef.current;
      const allExpandButtons = container.querySelectorAll('.expand-nested-table-btn, .subtable-toggle-btn');
      const originalExpandedStates = [];

      // Store original states and expand everything
      allExpandButtons.forEach((btn, index) => {
        const isExpanded = btn.textContent.includes('▼') || btn.textContent.includes('Collapse');
        originalExpandedStates[index] = isExpanded;
        if (!isExpanded) {
          btn.click();
        }
      });

      // Wait for DOM to update after expanding
      await new Promise(resolve => setTimeout(resolve, 500));

      // Store original overflow styles for all elements
      const originalStyles = new Map();

      // Get all elements that might have overflow restrictions
      const allTableWrappers = container.querySelectorAll('.table-wrapper, .table-container');

      // Store and remove overflow from all table wrappers and containers
      allTableWrappers.forEach(element => {
        originalStyles.set(element, {
          overflow: element.style.overflow,
          overflowX: element.style.overflowX,
          overflowY: element.style.overflowY,
          maxWidth: element.style.maxWidth,
          maxHeight: element.style.maxHeight
        });
        element.style.overflow = 'visible';
        element.style.overflowX = 'visible';
        element.style.overflowY = 'visible';
        element.style.maxWidth = 'none';
        element.style.maxHeight = 'none';
      });

      // Store and remove overflow from main container
      const containerOriginal = {
        overflow: container.style.overflow,
        maxHeight: container.style.maxHeight,
        maxWidth: container.style.maxWidth
      };
      container.style.overflow = 'visible';
      container.style.maxHeight = 'none';
      container.style.maxWidth = 'none';

      // Wait a bit for layout to adjust
      await new Promise(resolve => setTimeout(resolve, 200));

      // Get the actual full dimensions of the content
      const rect = container.getBoundingClientRect();
      const fullWidth = Math.max(container.scrollWidth, container.offsetWidth, rect.width);
      const fullHeight = Math.max(container.scrollHeight, container.offsetHeight, rect.height);

      // Capture the entire container with all content including overflow
      const canvas = await html2canvas(container, {
        allowTaint: true,
        useCORS: true,
        scrollY: 0,
        scrollX: 0,
        x: 0,
        y: 0,
        width: fullWidth,
        height: fullHeight,
        windowWidth: fullWidth,
        windowHeight: fullHeight,
        scale: 2, // Higher quality
        backgroundColor: getComputedStyle(document.documentElement).getPropertyValue('--bg-primary') || '#ffffff',
        onclone: (clonedDoc) => {
          // In the cloned document, ensure all content is visible
          const clonedContainer = clonedDoc.querySelector('[data-screenshot-container]');
          if (clonedContainer) {
            clonedContainer.style.overflow = 'visible';
            clonedContainer.style.maxHeight = 'none';
            clonedContainer.style.maxWidth = 'none';
            clonedContainer.style.height = 'auto';
            clonedContainer.style.width = 'auto';

            // Remove overflow from all table wrappers and containers in the clone
            const clonedWrappers = clonedContainer.querySelectorAll('.table-wrapper, .table-container');
            clonedWrappers.forEach(wrapper => {
              wrapper.style.overflow = 'visible';
              wrapper.style.overflowX = 'visible';
              wrapper.style.overflowY = 'visible';
              wrapper.style.maxWidth = 'none';
              wrapper.style.maxHeight = 'none';
            });
          }
        }
      });

      // Restore original overflow styles for all elements
      allTableWrappers.forEach(element => {
        const original = originalStyles.get(element);
        if (original) {
          element.style.overflow = original.overflow;
          element.style.overflowX = original.overflowX;
          element.style.overflowY = original.overflowY;
          element.style.maxWidth = original.maxWidth;
          element.style.maxHeight = original.maxHeight;
        }
      });

      // Restore main container styles
      container.style.overflow = containerOriginal.overflow;
      container.style.maxHeight = containerOriginal.maxHeight;
      container.style.maxWidth = containerOriginal.maxWidth;

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);

        // Restore original expanded/collapsed states
        allExpandButtons.forEach((btn, index) => {
          const isCurrentlyExpanded = btn.textContent.includes('▼') || btn.textContent.includes('Collapse');
          if (isCurrentlyExpanded !== originalExpandedStates[index]) {
            btn.click();
          }
        });

        setCapturingScreenshot(false);
      });
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      alert('Failed to capture screenshot. Please try again.');
      setCapturingScreenshot(false);
    }
  };

  const createTable = async () => {
    console.log('[createTable] FUNCTION CALLED');
    // Generate a unique name if user didn't provide one
    const name = newTableName.trim() || `Table ${tables.length + 1}`;
    setIsCreatingTable(true);
    console.log('[createTable] About to call API');
    try {
      const response = await authFetch(
        `${API_URL}/api/tables?num_rows=${newTableRows}&num_cols=${newTableCols}&name=${encodeURIComponent(name)}`,
        { method: 'POST' }
      );
      const newTable = await response.json();

      // Insert new table at the end of owned tables, before shared tables
      const ownedTables = tables.filter(t => !t.isShared);
      const sharedTables = tables.filter(t => t.isShared);
      setTables([...ownedTables, newTable, ...sharedTables]);
      setNewTableName('');

      // Refresh FileExplorer to show the new table
      console.log('[createTable] Refreshing FileExplorer, ref exists:', !!fileExplorerRef.current);
      if (fileExplorerRef.current) {
        console.log('[createTable] Calling fileExplorerRef.current.refresh()');
        fileExplorerRef.current.refresh();
      }
    } catch (error) {
      console.error('Error creating table:', error);
    } finally {
      setIsCreatingTable(false);
    }
  };

  const generateTableWithClaude = async () => {
    if (!claudePrompt.trim()) {
      alert('Please enter a description for the table you want to generate.');
      return;
    }

    setGeneratingTable(true);
    try {
      const response = await authFetch(`${API_URL}/api/claude/generate-table`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          prompt: claudePrompt,
          tab_id: activeTabId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to generate table');
      }

      const result = await response.json();
      console.log('Generated table:', result);

      // Refresh tables to show the new one
      await fetchTables();

      // Refresh FileExplorer to show the new table
      if (fileExplorerRef.current) {
        fileExplorerRef.current.refresh();
      }

      // Clear the prompt
      setClaudePrompt('');

      alert(`Successfully generated table: ${result.table.name}`);
    } catch (error) {
      console.error('Error generating table with Claude:', error);
      alert(`Failed to generate table: ${error.message}`);
    } finally {
      setGeneratingTable(false);
    }
  };

  const deleteTable = async (tableId) => {
    try {
      const response = await authFetch(`${API_URL}/api/tables/${tableId}`, { method: 'DELETE' });
      const result = await response.json();

      if (result.can_delete) {
        // Table was successfully deleted
        setTables(tables.filter(t => t.id !== tableId));

        // Refresh FileExplorer to remove the deleted table
        if (fileExplorerRef.current) {
          fileExplorerRef.current.refresh();
        }
      } else {
        // Table has references, show warning modal
        const tableToDelete = tables.find(t => t.id === tableId);
        setDeleteWarning({
          tableId: tableId,
          tableName: tableToDelete?.name || 'Unknown Table',
          references: result.references || []
        });
      }
    } catch (error) {
      console.error('Error deleting table:', error);
    }
  };

  const moveTable = async (tableId, direction) => {
    try {
      const response = await authFetch(`${API_URL}/api/tables/${tableId}/move?direction=${direction}`, { method: 'POST' });
      if (!response.ok) {
        throw new Error(`Failed to move table: ${response.statusText}`);
      }
      await fetchTables();
    } catch (error) {
      console.error('Error moving table:', error);
      alert(`Error moving table: ${error.message}`);
    }
  };

  // ============================================================
  // SORTING HELPERS
  // ============================================================

  // Extract sortable value from a cell based on its type
  const getCellSortValue = (cell) => {
    if (cell === null || cell === undefined || cell === '') {
      return { value: null, type: 'null' };
    }

    // Handle special cell types
    if (typeof cell === 'object') {
      // Priority picker: high > medium > low
      if (cell.type === 'priority') {
        const priorityOrder = { 'high': 1, 'medium': 2, 'low': 3 };
        return { value: priorityOrder[cell.value] || 999, type: 'priority' };
      }

      // Done picker: sort alphabetically by value
      if (cell.type === 'done') {
        const doneOrder = { 'thumbs-up': 1, 'checkmark': 2, 'animated-check': 3 };
        return { value: doneOrder[cell.value] || 999, type: 'done' };
      }

      // Date picker: sort by timestamp
      if (cell.type === 'date') {
        const timestamp = cell.value ? new Date(cell.value).getTime() : 0;
        return { value: timestamp, type: 'date' };
      }

      // Color picker: sort by hex value
      if (cell.type === 'color') {
        return { value: cell.value || '', type: 'color' };
      }

      // Chart, nested table, table reference: not sortable, treat as null
      if (cell.type === 'chart' || cell.type === 'nested_table' || cell.type === 'table_reference') {
        return { value: null, type: 'complex' };
      }

      // Unknown object type
      return { value: JSON.stringify(cell), type: 'string' };
    }

    // Try to parse as number
    const num = parseFloat(cell);
    if (!isNaN(num) && isFinite(num)) {
      return { value: num, type: 'number' };
    }

    // Default to string
    return { value: String(cell).toLowerCase(), type: 'string' };
  };

  // Sort rows based on column and direction
  // Returns array of objects: [{ row, originalIndex }, ...]
  const sortRows = (rows, columnIndex, direction, doneColumn = null) => {
    // Create array with original indices
    const rowsWithIndices = rows.map((row, idx) => ({ row, originalIndex: idx }));

    // If doneColumn is set, separate done rows from non-done rows
    let doneRows = [];
    let nonDoneRows = rowsWithIndices;

    if (doneColumn !== null && doneColumn !== undefined) {
      // Check if row has a done cell in the specified column
      const hasDoneInColumn = (row) => {
        const cell = row[doneColumn];
        return cell && cell.type === 'done' && cell.value;
      };

      doneRows = rowsWithIndices.filter(({ row }) => hasDoneInColumn(row));
      nonDoneRows = rowsWithIndices.filter(({ row }) => !hasDoneInColumn(row));
    }

    // Sort non-done rows if sorting is applied
    if (columnIndex !== null && columnIndex !== undefined && direction !== null) {
      const sorted = nonDoneRows.sort((a, b) => {
        const cellA = a.row[columnIndex];
        const cellB = b.row[columnIndex];

        const valueA = getCellSortValue(cellA);
        const valueB = getCellSortValue(cellB);

        // Handle nulls based on sort direction
        // Ascending: nulls at end (1), Descending: nulls at beginning (-1)
        if (valueA.value === null && valueB.value === null) return 0;
        if (valueA.value === null) return direction === 'asc' ? 1 : -1;
        if (valueB.value === null) return direction === 'asc' ? -1 : 1;

        // Compare based on type
        let comparison = 0;
        if (valueA.type === 'number' && valueB.type === 'number') {
          comparison = valueA.value - valueB.value;
        } else if (valueA.type === 'date' && valueB.type === 'date') {
          comparison = valueA.value - valueB.value;
        } else {
          // String comparison (works for color hex values too)
          const strA = String(valueA.value);
          const strB = String(valueB.value);
          comparison = strA.localeCompare(strB);
        }

        return direction === 'asc' ? comparison : -comparison;
      });

      // Return sorted non-done rows + done rows at bottom
      return [...sorted, ...doneRows];
    }

    // No sorting applied, just return non-done rows + done rows at bottom
    return [...nonDoneRows, ...doneRows];
  };

  // Toggle sort for a table/subtable/nested table
  const toggleSort = async (columnIndex, tableId, subtableId = null, nestedTableId = null) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    let currentSortColumn, currentSortDirection;
    let endpoint;

    if (nestedTableId) {
      // Find nested table
      endpoint = `${API_URL}/api/tables/${tableId}/nested-table/${nestedTableId}/sort`;
      // Search for nested table in table structure
      const findNestedTable = (rows) => {
        for (const row of rows) {
          for (const cell of row) {
            if (cell && typeof cell === 'object' && cell.type === 'nested_table' && cell.id === nestedTableId) {
              return cell;
            }
          }
        }
        return null;
      };
      // Search in main table rows first
      let nestedTable = findNestedTable(table.rows);
      // If not found, search in subtables
      if (!nestedTable) {
        const searchSubtables = (subtables) => {
          for (const rowKey in subtables) {
            for (const st of subtables[rowKey]) {
              const found = findNestedTable(st.rows);
              if (found) return found;
              const foundInSub = searchSubtables(st.row_subtables || {});
              if (foundInSub) return foundInSub;
            }
          }
          return null;
        };
        nestedTable = searchSubtables(table.row_subtables);
      }
      currentSortColumn = nestedTable?.sort_column;
      currentSortDirection = nestedTable?.sort_direction;
    } else if (subtableId) {
      // Find subtable
      endpoint = `${API_URL}/api/tables/${tableId}/subtable/${subtableId}/sort`;
      // Search for subtable in table structure
      const findSubtable = (subtables) => {
        for (const rowKey in subtables) {
          for (const st of subtables[rowKey]) {
            if (st.id === subtableId) return st;
            const found = findSubtable(st.row_subtables || {});
            if (found) return found;
          }
        }
        return null;
      };
      const subtable = findSubtable(table.row_subtables);
      currentSortColumn = subtable?.sort_column;
      currentSortDirection = subtable?.sort_direction;
    } else {
      // Main table
      endpoint = `${API_URL}/api/tables/${tableId}/sort`;
      currentSortColumn = table.sort_column;
      currentSortDirection = table.sort_direction;
    }

    // Cycle through sort states: none → asc → desc → none
    let newDirection;
    if (currentSortColumn === columnIndex) {
      if (currentSortDirection === 'asc') {
        newDirection = 'desc';
      } else if (currentSortDirection === 'desc') {
        newDirection = null; // Clear sort
        columnIndex = null;
      } else {
        newDirection = 'asc';
      }
    } else {
      newDirection = 'asc';
    }

    // Update backend
    try {
      await authFetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sort_column: columnIndex,
          sort_direction: newDirection
        })
      });

      // Refresh tables to get updated sort state
      await fetchTables();
    } catch (error) {
      console.error('Error updating sort:', error);
    }
  };

  // ============================================================
  // CELL EDITING HELPERS - Manage local state and persistence
  // ============================================================

  // Get the cell key for tracking edits
  const getCellKey = (tableId, rowIdx, colIdx, contextPath) => {
    // Properly serialize the context path by extracting IDs from path segments
    if (contextPath && contextPath.path && contextPath.path.length > 0) {
      const pathStr = contextPath.path.map(segment => `${segment.type}-${segment.id}`).join('-');
      return `${tableId}-${pathStr}-${rowIdx}-${colIdx}`;
    }
    return `${tableId}-${rowIdx}-${colIdx}`;
  };

  // Persist cell value to backend (called on blur or Enter key)
  const persistCellValue = async (tableId, rowIdx, colIdx, value, options = {}) => {
    const { contextPath } = options;
    const cellKey = getCellKey(tableId, rowIdx, colIdx, contextPath);

    // Persist to backend FIRST
    await updateCellGeneric(tableId, rowIdx, colIdx, value, options);

    // THEN clear from editing state (after backend update completes)
    // This prevents the flash where old value shows briefly
    setEditingCells(prev => {
      const next = { ...prev };
      delete next[cellKey];
      return next;
    });
  };

  // ============================================================
  // UNIFIED UPDATE CELL FUNCTION - Handles all table/subtable/nested cell updates
  // ============================================================
  const updateCellGeneric = async (tableId, rowIdx, colIdx, value, options = {}) => {
    const { subtableId, nestedTableId, contextPath } = options;

    // CORRECT APPROACH: Always use the single endpoint with path in JSON body
    const endpoint = `/api/tables/${tableId}/cell?row=${rowIdx}&col=${colIdx}`;

    // Extract path array from contextPath for sending to backend
    const pathArray = contextPath && contextPath.path ? contextPath.path : [];

    const contextLabel = nestedTableId ? 'nested table cell' : (subtableId ? 'subtable cell' : 'cell');

    try {
      const response = await authFetch(`${API_URL}${endpoint}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          value,
          path: pathArray  // Send path as JSON in body, not in URL
        })
      });

      // Wait for response to complete
      if (!response.ok) {
        console.error(`Update ${contextLabel} failed:`, response.status, await response.text());
        return;
      }

      // Parse response if available (but don't log it)
      try {
        await response.json();
      } catch (e) {
        // Response not JSON, proceeding anyway
      }

      // State update strategy depends on context
      // If contextPath has depth > 0, we're in nested structure - always refresh
      const isNested = contextPath && getDepth(contextPath) > 0;

      // Check if this table has active sorting
      const currentTable = tables.find(t => t.id === tableId);
      const hasSorting = currentTable && currentTable.sort_column !== null && currentTable.sort_column !== undefined;

      // Check if this is a chart, done picker, or delete operation - these can be updated optimistically everywhere
      const isChart = value && typeof value === 'object' && value.type === 'chart';
      const isDonePicker = value && typeof value === 'object' && value.type === 'done';
      const isDelete = value === null || value === '' || value === undefined;

      console.log('[updateCellGeneric] isDelete:', isDelete, 'isDonePicker:', isDonePicker, 'isNested:', isNested, 'subtableId:', subtableId, 'nestedTableId:', nestedTableId);

      if ((isNested || nestedTableId || subtableId || hasSorting) && !isChart && !isDonePicker && !isDelete) {
        // Nested structures OR sorted tables need full refresh from server (except charts)
        const contextType = hasSorting ? 'sorted table' : (isNested ? `nested (depth ${getDepth(contextPath)})` : (nestedTableId ? 'nested table' : 'subtable'));
        console.log(`updateCellGeneric: ${contextType} context, refreshing tables`);
        await fetchTables();
        console.log('updateCellGeneric: refresh complete');
      } else if (isChart || isDonePicker || isDelete) {
        // Charts, done pickers, and deletes can be updated optimistically everywhere - update via context path
        const updateType = isChart ? 'chart' : (isDonePicker ? 'done picker' : 'delete');
        console.log('[updateCellGeneric] Using optimistic update for', updateType, 'contextPath:', contextPath);
        setTables(prevTables => prevTables.map(table => {
          if (table.id === tableId) {
            const updatedTable = JSON.parse(JSON.stringify(table));

            // Navigate using context path
            if (contextPath && contextPath.path && contextPath.path.length > 0) {
              let current = updatedTable;
              const path = contextPath.path;

              console.log('[updateCellGeneric] Navigating path with', path.length, 'segments');
              for (let i = 0; i < path.length; i++) {
                const segment = path[i];
                if (segment.type === 'subtable') {
                  // Search through all rows to find the subtable by ID
                  let foundSubtable = null;
                  if (current.row_subtables) {
                    for (const rowKey in current.row_subtables) {
                      foundSubtable = current.row_subtables[rowKey].find(st => st.id === segment.id);
                      if (foundSubtable) break;
                    }
                  }
                  if (!foundSubtable) {
                    console.warn('[updateCellGeneric] Subtable not found at segment', i, segment);
                    return table; // Path not found
                  }
                  current = foundSubtable;
                } else if (segment.type === 'nested') {
                  // Search through all cells to find the nested table by ID
                  let foundNested = null;
                  if (current.rows) {
                    for (const row of current.rows) {
                      for (const cell of row) {
                        if (cell && typeof cell === 'object' && cell.type === 'nested_table' && cell.id === segment.id) {
                          foundNested = cell;
                          break;
                        }
                      }
                      if (foundNested) break;
                    }
                  }
                  if (!foundNested) {
                    console.warn('[updateCellGeneric] Nested table not found at segment', i, segment);
                    return table; // Path not found
                  }
                  current = foundNested;
                }
              }

              // Now current is the parent object, update the final cell
              if (current.rows && current.rows[rowIdx] && current.rows[rowIdx][colIdx] !== undefined) {
                const currentCellValue = current.rows[rowIdx][colIdx];

                // IMPORTANT: Don't overwrite if cell has been changed to a nested table
                // This prevents race condition where optimistic nested table creation is overwritten by stale updateCell response
                if (currentCellValue && typeof currentCellValue === 'object' && currentCellValue.type === 'nested_table') {
                  console.warn('[updateCellGeneric] Cell is nested table, not updating');
                  return table; // Don't update - cell now contains nested table
                }

                // Clear row height if we're removing a done picker
                if (currentCellValue && typeof currentCellValue === 'object' && currentCellValue.type === 'done' &&
                    (value === null || value === '' || value === undefined)) {
                  if (current.row_heights && current.row_heights[rowIdx]) {
                    console.log('[updateCellGeneric] Clearing row height for done picker removal');
                    current.row_heights[rowIdx] = null;
                  }
                }

                console.log('[updateCellGeneric] Setting cell[' + rowIdx + '][' + colIdx + '] to', value);
                current.rows[rowIdx][colIdx] = value;
                return updatedTable;
              } else {
                console.warn('[updateCellGeneric] Cell not found at final position', rowIdx, colIdx);
              }
            } else {
              // No context path - update in main table
              if (updatedTable.rows && updatedTable.rows[rowIdx]) {
                const currentCellValue = updatedTable.rows[rowIdx][colIdx];

                // IMPORTANT: Don't overwrite if cell has been changed to a nested table
                // This prevents race condition where optimistic nested table creation is overwritten by stale updateCell response
                if (currentCellValue && typeof currentCellValue === 'object' && currentCellValue.type === 'nested_table') {
                  return table; // Don't update - cell now contains nested table
                }

                // Clear row height if we're removing a done picker
                if (currentCellValue && typeof currentCellValue === 'object' && currentCellValue.type === 'done' &&
                    (value === null || value === '' || value === undefined)) {
                  if (updatedTable.row_heights && updatedTable.row_heights[rowIdx]) {
                    console.log('[updateCellGeneric] Clearing row height for done picker removal (main table)');
                    updatedTable.row_heights[rowIdx] = null;
                  }
                }

                updatedTable.rows[rowIdx][colIdx] = value;
                return updatedTable;
              }
            }

            return table;
          }
          return table;
        }));
      } else {
        // Regular cells without sorting can update local state
        setTables(prevTables => prevTables.map(table => {
          if (table.id === tableId) {
            const newRows = [...table.rows];
            const currentCellValue = newRows[rowIdx][colIdx];

            // IMPORTANT: Don't overwrite if cell has been changed to a nested table or other object
            // This prevents race condition where optimistic nested table creation is overwritten by stale updateCell response
            if (currentCellValue && typeof currentCellValue === 'object' && currentCellValue.type === 'nested_table') {
              return table; // Don't update - cell now contains nested table
            }

            // Clear row height if we're removing a done picker
            let clearedRowHeights = null;
            if (currentCellValue && typeof currentCellValue === 'object' && currentCellValue.type === 'done' &&
                (value === null || value === '' || value === undefined)) {
              if (table.row_heights && table.row_heights[rowIdx]) {
                console.log('[updateCellGeneric] Clearing row height for done picker removal (regular cell)');
                clearedRowHeights = [...table.row_heights];
                clearedRowHeights[rowIdx] = null;
              }
            }

            newRows[rowIdx][colIdx] = value;
            return {
              ...table,
              rows: newRows,
              ...(clearedRowHeights && { row_heights: clearedRowHeights })
            };
          }
          return table;
        }));
      }

      // Auto-translate if this cell is a source for translation columns
      if (value && typeof value === 'string') {
        handleAutoTranslate(tableId, rowIdx, colIdx, value);
      }
    } catch (error) {
      console.error(`Error updating ${contextLabel}:`, error);
    }
  };

  // Backwards-compatible wrapper functions
  const updateCell = (tableId, rowIdx, colIdx, value) =>
    updateCellGeneric(tableId, rowIdx, colIdx, value, {});

  // ✅ REMOVED: updateSubtableCell and updateNestedTableCell
  // These functions have been replaced by updateCellGeneric with contextPath support
  // All call sites now use updateCellGeneric for unlimited depth support

  // ==================== Translation Functions ====================

  const handleAutoTranslate = async (tableId, rowIdx, sourceColIdx, sourceText) => {
    const table = tables.find(t => t.id === tableId);
    if (!table || !table.translation_columns) return;

    const token = localStorage.getItem('token');
    if (!token) return;

    // Find all columns that translate from this source column
    for (const targetColIdxStr of Object.keys(table.translation_columns)) {
      const targetColIdx = parseInt(targetColIdxStr);
      const config = table.translation_columns[targetColIdx];

      if (config.sourceColumn === sourceColIdx) {
        // This column translates from the source column that just changed
        console.log('[AutoTranslate] Translating row', rowIdx, 'col', sourceColIdx, '→', targetColIdx);

        if (!sourceText || !sourceText.trim()) {
          // Clear translation if source is empty
          await updateCellGeneric(tableId, rowIdx, targetColIdx, '');
          continue;
        }

        try {
          const translation = await translateText(
            sourceText,
            config.sourceLang,
            config.targetLang,
            token
          );

          // Update the translation column
          await updateCellGeneric(tableId, rowIdx, targetColIdx, translation);
        } catch (error) {
          console.error('[AutoTranslate] Error:', error);
        }
      }
    }
  };

  const handleSetupTranslation = async (tableId, colIdx, config) => {
    console.log('[Translation Setup]', tableId, colIdx, config);

    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    // Update table with new translation config
    const updatedTable = setColumnTranslationConfig(table, colIdx, config);

    // Update tables state
    setTables(prevTables => prevTables.map(t =>
      t.id === tableId ? updatedTable : t
    ));

    // If config is not null, translate existing cells
    if (config) {
      const sourceColIdx = config.sourceColumn;
      const token = localStorage.getItem('token');

      console.log('[Translation Setup] Translating existing rows from column', sourceColIdx, 'to', colIdx);

      // Translate all existing rows
      for (let rowIdx = 0; rowIdx < table.num_rows; rowIdx++) {
        const row = table.rows[rowIdx];
        const sourceText = row[sourceColIdx];

        // Only translate if source has text and target is empty/undefined
        if (sourceText && typeof sourceText === 'string' && sourceText.trim() && !row[colIdx]) {
          try {
            const translation = await translateText(
              sourceText,
              config.sourceLang,
              config.targetLang,
              token
            );

            console.log('[Translation Setup] Row', rowIdx, ':', sourceText.substring(0, 30), '→', translation.substring(0, 30));

            // Update the translation column
            await updateCellGeneric(tableId, rowIdx, colIdx, translation);

            // Small delay to avoid overwhelming the API
            await new Promise(resolve => setTimeout(resolve, 100));
          } catch (error) {
            console.error('[Translation Setup] Error translating row', rowIdx, ':', error);
          }
        }
      }
    }

    // Save the updated table with translation config to backend
    try {
      await authFetch(`${API_URL}/api/tables/${tableId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedTable)
      });

      console.log('[Translation Setup] Saved translation config to backend');
    } catch (error) {
      console.error('[Translation Setup] Error saving to backend:', error);
    }
  };

  // ==================== End Translation Functions ====================

  // ==================== AI Assistant Functions ====================

  const handleAIAssistantPrompt = async (tableId, prompt) => {
    console.log('[AI Assistant] Processing prompt for table:', tableId, 'Prompt:', prompt);

    // Update state to show loading
    setTableAIAssistant(prev => ({
      ...prev,
      [tableId]: {
        ...prev[tableId],
        loading: true,
        input: ''
      }
    }));

    try {
      const token = localStorage.getItem('token');
      const conversationHistory = tableAIAssistant[tableId]?.conversationHistory || [];

      // Call backend AI assistant endpoint
      const response = await fetch(`${API_URL}/api/tables/${tableId}/ai-assistant`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: prompt,
          conversation_history: conversationHistory,
          filename: currentFile
        })
      });

      if (!response.ok) {
        throw new Error(`AI Assistant error: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('[AI Assistant] Response:', data);

      // Execute tool calls
      for (const tool of data.tool_calls || []) {
        console.log('[AI Assistant] Executing tool:', tool.name, tool.input);

        switch (tool.name) {
          case 'add_nested_table':
            // Create a nested table in a cell (not a subtable)
            const contextPath = {
              tableId: tableId,
              path: []
            };
            await createNestedTable(
              contextPath,
              tool.input.row_index,
              tool.input.col_index,
              tool.input.num_rows || 3,
              tool.input.num_cols || 2
            );
            break;

          case 'insert_row':
            // Map 'before'/'after' to 'above'/'below' for backend
            const rowPosition = tool.input.position === 'before' ? 'above' : 'below';
            await insertRow(tableId, tool.input.index, rowPosition);
            break;

          case 'insert_column':
            await insertColumn(tableId, tool.input.index, tool.input.position === 'left' ? 'left' : 'right');
            // If header is provided, update it
            if (tool.input.header) {
              const newColIdx = tool.input.position === 'left' ? tool.input.index : tool.input.index + 1;
              await updateColumnHeader(tableId, newColIdx, tool.input.header);
            }
            break;

          case 'update_cell':
            await updateCellGeneric(tableId, tool.input.row_index, tool.input.col_index, tool.input.value);
            break;

          case 'update_column_header':
            await updateColumnHeader(tableId, tool.input.col_index, tool.input.header);
            break;

          case 'delete_row':
            await deleteRow(tableId, tool.input.index);
            break;

          case 'delete_column':
            await deleteColumn(tableId, tool.input.index);
            break;

          case 'setup_translation':
            await handleSetupTranslation(
              tableId,
              tool.input.target_column,
              {
                sourceColumn: tool.input.source_column,
                sourceLang: tool.input.source_lang || 'NL',
                targetLang: tool.input.target_lang || 'EN'
              }
            );
            break;

          case 'add_subtable':
            await addSubtable(
              tableId,
              tool.input.row_index,
              tool.input.num_rows || 2,
              tool.input.num_cols || 2,
              null, // parentSubtableId
              null  // nestedTableId
            );
            // If a name was provided, update the subtable name after creation
            // Note: We'd need to get the subtable ID from addSubtable response to update the name
            // For now, the subtable will be created with default name "New Subtable"
            // User can rename it manually or we can enhance this later
            break;

          default:
            console.warn('[AI Assistant] Unknown tool:', tool.name);
        }

        // Small delay between operations
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // Update conversation history
      const newHistory = [
        ...conversationHistory,
        { role: 'user', content: prompt },
        { role: 'assistant', content: data.assistant_message || 'Done!' }
      ];

      setTableAIAssistant(prev => ({
        ...prev,
        [tableId]: {
          ...prev[tableId],
          loading: false,
          conversationHistory: newHistory
        }
      }));

      // Note: No need to call saveCurrentFile() here because each individual operation
      // (insertRow, updateCell, handleSetupTranslation, etc.) already saves to backend
      // via their own API endpoints. Calling saveCurrentFile() would show unwanted modal.

    } catch (error) {
      console.error('[AI Assistant] Error:', error);
      alert(`AI Assistant error: ${error.message}`);

      setTableAIAssistant(prev => ({
        ...prev,
        [tableId]: {
          ...prev[tableId],
          loading: false
        }
      }));
    }
  };

  // ==================== End AI Assistant Functions ====================

  const addSubtable = async (tableId, rowIndex, numRows = 2, numCols = 2, parentSubtableId = null, nestedTableId = null) => {
    // Generate temporary ID for optimistic update
    const tempId = `temp-subtable-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

    // Create optimistic subtable structure
    const optimisticSubtable = {
      id: tempId,
      name: 'New Subtable',
      num_rows: numRows,
      num_cols: numCols,
      rows: Array(numRows).fill(null).map(() => Array(numCols).fill("")),
      column_headers: Array(numCols).fill(null).map((_, i) => `Column ${i + 1}`),
      row_headers: Array(numRows).fill(null).map((_, i) => `Row ${i + 1}`),
      show_headers: false,
      show_row_headers: false,
      expanded: true,
      row_subtables: {},
      _optimistic: true, // Flag to show loading indicator
    };

    // STEP 1: IMMEDIATELY update UI with optimistic data
    setTables(prevTables => prevTables.map(t => {
      if (t.id !== tableId) return t;

      const updatedTable = JSON.parse(JSON.stringify(t)); // Deep clone

      if (nestedTableId) {
        // Add to nested table
        const addToNestedTable = (cell) => {
          if (cell && typeof cell === 'object' && cell.type === 'nested_table' && cell.id === nestedTableId) {
            const rowKey = `row-${rowIndex}`;
            if (!cell.row_subtables) cell.row_subtables = {};
            if (!cell.row_subtables[rowKey]) cell.row_subtables[rowKey] = [];
            cell.row_subtables[rowKey].push(optimisticSubtable);
            return true;
          }
          return false;
        };

        // Search through all cells
        for (let r = 0; r < updatedTable.rows.length; r++) {
          for (let c = 0; c < updatedTable.rows[r].length; c++) {
            if (addToNestedTable(updatedTable.rows[r][c])) break;
          }
        }
      } else if (parentSubtableId) {
        // Add to parent subtable (nested subtable)
        const rowKey = `row-${rowIndex}`;

        // Recursive function to find and update parent subtable
        const addToParentSubtable = (subtables) => {
          if (!subtables) return false;

          for (const key in subtables) {
            for (let i = 0; i < subtables[key].length; i++) {
              const subtable = subtables[key][i];
              if (subtable.id === parentSubtableId) {
                // Found the parent subtable!
                if (!subtable.row_subtables) subtable.row_subtables = {};
                if (!subtable.row_subtables[rowKey]) subtable.row_subtables[rowKey] = [];
                subtable.row_subtables[rowKey].push(optimisticSubtable);
                return true;
              }
              // Recursively search in this subtable's children
              if (subtable.row_subtables && addToParentSubtable(subtable.row_subtables)) {
                return true;
              }
            }
          }
          return false;
        };

        // Search for parent subtable in the table's row_subtables
        if (!addToParentSubtable(updatedTable.row_subtables)) {
          console.error('Parent subtable not found:', parentSubtableId);
        }
      } else {
        // Add to main table
        const rowKey = `row-${rowIndex}`;
        if (!updatedTable.row_subtables) updatedTable.row_subtables = {};
        if (!updatedTable.row_subtables[rowKey]) updatedTable.row_subtables[rowKey] = [];
        updatedTable.row_subtables[rowKey].push(optimisticSubtable);
      }

      return updatedTable;
    }));

    // Close modal immediately for better UX
    setShowSubtableModal(null);

    // STEP 2: Send request to backend (async, non-blocking)
    try {
      let url;
      if (parentSubtableId) {
        url = `${API_URL}/api/tables/${tableId}/subtable/${parentSubtableId}/subtable?row_index=${rowIndex}&num_rows=${numRows}&num_cols=${numCols}`;
      } else if (nestedTableId) {
        url = `${API_URL}/api/tables/${tableId}/subtable?row_index=${rowIndex}&num_rows=${numRows}&num_cols=${numCols}&nested_table_id=${nestedTableId}`;
      } else {
        url = `${API_URL}/api/tables/${tableId}/subtable?row_index=${rowIndex}&num_rows=${numRows}&num_cols=${numCols}`;
      }

      const response = await authFetch(url, { method: 'POST' });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Add subtable failed:', response.status, errorText);

        // STEP 3a: ROLLBACK optimistic update on failure
        setTables(prevTables => prevTables.map(t => {
          if (t.id !== tableId) return t;

          const rolledBackTable = JSON.parse(JSON.stringify(t));

          // Remove the optimistic subtable
          const removeOptimistic = (obj) => {
            if (obj.row_subtables) {
              Object.keys(obj.row_subtables).forEach(key => {
                obj.row_subtables[key] = obj.row_subtables[key].filter(s => s.id !== tempId);
              });
            }
          };

          removeOptimistic(rolledBackTable);

          // Also check nested tables
          for (let r = 0; r < rolledBackTable.rows.length; r++) {
            for (let c = 0; c < rolledBackTable.rows[r].length; c++) {
              const cell = rolledBackTable.rows[r][c];
              if (cell && typeof cell === 'object' && cell.type === 'nested_table') {
                removeOptimistic(cell);
              }
            }
          }

          return rolledBackTable;
        }));

        alert(`Failed to add subtable: ${errorText}`);
        return;
      }

      // STEP 3b: Replace optimistic subtable with real data
      const updatedTable = await response.json();

      // Find and replace just the subtable that was created
      setTables(prevTables => prevTables.map(t => {
        if (t.id !== tableId) return t;

        // Use the complete updated table from backend instead of trying to merge
        // This ensures all IDs and structure match exactly
        const newTable = JSON.parse(JSON.stringify(updatedTable));

        // Preserve shared table metadata
        newTable.isShared = t.isShared;
        newTable.sharedBy = t.sharedBy;
        newTable.sharedPermission = t.sharedPermission;
        newTable.shareId = t.shareId;

        return newTable;
      }));

    } catch (error) {
      console.error('Error adding subtable:', error);

      // ROLLBACK on error
      setTables(prevTables => prevTables.map(t => {
        if (t.id !== tableId) return t;

        const rolledBackTable = JSON.parse(JSON.stringify(t));
        const rowKey = `row-${rowIndex}`;

        if (rolledBackTable.row_subtables && rolledBackTable.row_subtables[rowKey]) {
          rolledBackTable.row_subtables[rowKey] = rolledBackTable.row_subtables[rowKey].filter(s => s.id !== tempId);
        }

        return rolledBackTable;
      }));

      alert(`Error: ${error.message}`);
    }
  };

  const addSubtableToNestedTable = async (tableId, nestedTableId, rowIndex, numRows = 2, numCols = 4) => {
    try {
      const url = `${API_URL}/api/tables/${tableId}/subtable?row_index=${rowIndex}&num_rows=${numRows}&num_cols=${numCols}&nested_table_id=${nestedTableId}`;

      const response = await authFetch(url, { method: 'POST' });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to add subtable to nested table:', response.status, errorText);
        alert(`Failed to add subtable: ${errorText}`);
        return;
      }

      const updatedTable = await response.json();
      setTables(tables.map(t => {
        if (t.id === tableId) {
          // Preserve shared table metadata when updating
          return {
            ...updatedTable,
            isShared: t.isShared,
            sharedBy: t.sharedBy,
            sharedPermission: t.sharedPermission,
            shareId: t.shareId
          };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error adding subtable to nested table:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // ✅ UPDATED: Delete subtable at any depth using contextPath
  const deleteSubtable = async (contextPath) => {
    try {
      // STEP 1: Optimistically remove structure from UI
      setTables(prevTables => prevTables.map(table => {
        if (table.id !== contextPath.tableId) return table;

        // Use backend's delete_structure_at_path logic
        // Navigate to parent and remove the target structure
        const path = contextPath.path;
        if (!path || path.length === 0) return table; // Can't delete root

        const targetSegment = path[path.length - 1];
        const parentPath = { tableId: contextPath.tableId, path: path.slice(0, -1) };

        // Function to delete from parent
        const deleteFromParent = (parentStructure) => {
          const updated = { ...parentStructure };

          if (targetSegment.type === 'subtable') {
            // Remove subtable from row_subtables
            if (updated.row_subtables) {
              updated.row_subtables = { ...updated.row_subtables };
              for (const rowKey in updated.row_subtables) {
                updated.row_subtables[rowKey] = updated.row_subtables[rowKey].filter(
                  st => st.id !== targetSegment.id
                );
              }
            }
          } else if (targetSegment.type === 'nested') {
            // Remove nested table from rows (replace with empty string)
            if (updated.rows) {
              updated.rows = updated.rows.map(row =>
                row.map(cell => {
                  if (cell && typeof cell === 'object' &&
                      cell.type === 'nested_table' &&
                      cell.id === targetSegment.id) {
                    return "";
                  }
                  return cell;
                })
              );
            }
          }

          return updated;
        };

        return traverseAndUpdate(table, parentPath, deleteFromParent);
      }));

      // STEP 2: Send delete request to backend
      const response = await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/delete-structure`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete structure failed:', response.status, errorText);
        // STEP 3: Rollback on error
        await fetchTables();
        alert(`Failed to delete structure: ${errorText}`);
        return;
      }

      // If successful, optimistic update already shows correct state
    } catch (error) {
      console.error('Error deleting structure:', error);
      // Rollback on error
      await fetchTables();
      alert(`Error: ${error.message}`);
    }
  };

  // Alias for backward compatibility
  const deleteNestedTable = deleteSubtable;

  const insertRow = async (tableId, position, direction) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/insert-row?position=${position}&direction=${direction}`,
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Insert row failed:', response.status, errorText);
        alert(`Failed to insert row: ${errorText}`);
        return;
      }

      const updatedTable = await response.json();
      setTables(tables.map(t => {
        if (t.id === tableId) {
          // Preserve shared table metadata when updating
          return {
            ...updatedTable,
            isShared: t.isShared,
            sharedBy: t.sharedBy,
            sharedPermission: t.sharedPermission,
            shareId: t.shareId
          };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error inserting row:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const insertColumn = async (tableId, position, direction) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/insert-column?position=${position}&direction=${direction}`,
        { method: 'POST' }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Insert column failed:', response.status, errorText);
        alert(`Failed to insert column: ${errorText}`);
        return;
      }

      const updatedTable = await response.json();
      setTables(tables.map(t => {
        if (t.id === tableId) {
          // Preserve shared table metadata when updating
          return {
            ...updatedTable,
            isShared: t.isShared,
            sharedBy: t.sharedBy,
            sharedPermission: t.sharedPermission,
            shareId: t.shareId
          };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error inserting column:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const insertSubtableRow = async (contextPath, position, direction) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        insertRowAtPath(structure, position, direction)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/insert-row`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, position, direction })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error inserting row:', error);
      await fetchTables();  // Rollback on error
    }
  };

  const insertSubtableColumn = async (contextPath, position, direction) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        insertColumnAtPath(structure, position, direction)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/insert-column`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, position, direction })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error inserting column:', error);
      await fetchTables();  // Rollback on error
    }
  };

  const insertNestedTableRow = async (contextPath, position, direction) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        insertRowAtPath(structure, position, direction)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/insert-row`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, position, direction })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error inserting row:', error);
      await fetchTables();  // Rollback on error
    }
  };

  const insertNestedTableColumn = async (contextPath, position, direction) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        insertColumnAtPath(structure, position, direction)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/insert-column`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, position, direction })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error inserting column:', error);
      await fetchTables();  // Rollback on error
    }
  };

  // Delete row/column functions
  const deleteRow = async (tableId, position) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/delete-row?position=${position}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete row failed:', response.status, errorText);
        alert(`Failed to delete row: ${errorText}`);
        return;
      }

      const updatedTable = await response.json();
      setTables(tables.map(t => {
        if (t.id === tableId) {
          // Preserve shared table metadata when updating
          return {
            ...updatedTable,
            isShared: t.isShared,
            sharedBy: t.sharedBy,
            sharedPermission: t.sharedPermission,
            shareId: t.shareId
          };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error deleting row:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const deleteColumn = async (tableId, position) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/delete-column?position=${position}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Delete column failed:', response.status, errorText);
        alert(`Failed to delete column: ${errorText}`);
        return;
      }

      const updatedTable = await response.json();
      setTables(tables.map(t => {
        if (t.id === tableId) {
          // Preserve shared table metadata when updating
          return {
            ...updatedTable,
            isShared: t.isShared,
            sharedBy: t.sharedBy,
            sharedPermission: t.sharedPermission,
            shareId: t.shareId
          };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error deleting column:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const setColumnBorderStyle = async (tableId, colIdx, borderStyle) => {
    try {
      // Optimistic update
      setTables(prevTables => prevTables.map(t => {
        if (t.id === tableId) {
          const column_border_styles = { ...(t.column_border_styles || {}) };
          if (borderStyle === 'none') {
            delete column_border_styles[colIdx];
          } else {
            column_border_styles[colIdx] = borderStyle;
          }
          return { ...t, column_border_styles };
        }
        return t;
      }));

      // Send to backend
      await authFetch(`${API_URL}/api/tables/${tableId}/column-border-style`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ col_idx: colIdx, border_style: borderStyle })
      });
    } catch (error) {
      console.error('Error setting column border style:', error);
      await fetchTables();
    }
  };

  const setColumnBorderStyleAtPath = async (contextPath, colIdx, borderStyle) => {
    try {
      // Optimistic update using recursive traversal
      setTables(prevTables => prevTables.map(table => {
        if (table.id !== contextPath.tableId) return table;
        return traverseAndUpdate(table, contextPath, (structure) => {
          const updated = { ...structure };

          // Initialize column_border_styles if it doesn't exist
          if (!updated.column_border_styles) {
            updated.column_border_styles = {};
          } else {
            // Deep copy the existing styles
            updated.column_border_styles = { ...updated.column_border_styles };
          }

          if (borderStyle === 'none') {
            delete updated.column_border_styles[colIdx];
          } else {
            updated.column_border_styles[colIdx] = borderStyle;
          }

          return updated;
        });
      }));

      // Send to backend
      await authFetch(`${API_URL}/api/tables/${contextPath.tableId}/column-border-style-at-path`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contextPath, col_idx: colIdx, border_style: borderStyle })
      });
    } catch (error) {
      console.error('Error setting column border style at path:', error);
      await fetchTables();
    }
  };

  // Row border style functions (similar to column border functions)
  const setRowBorderStyle = async (tableId, rowIdx, borderStyle) => {
    try {
      // Optimistic update
      setTables(prevTables => prevTables.map(t => {
        if (t.id === tableId) {
          const row_border_styles = { ...(t.row_border_styles || {}) };
          if (borderStyle === 'none') {
            delete row_border_styles[rowIdx];
          } else {
            row_border_styles[rowIdx] = borderStyle;
          }
          return { ...t, row_border_styles };
        }
        return t;
      }));

      // Send to backend
      await authFetch(`${API_URL}/api/tables/${tableId}/row-border-style`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ row_idx: rowIdx, border_style: borderStyle })
      });
    } catch (error) {
      console.error('Error setting row border style:', error);
      await fetchTables();
    }
  };

  const setRowBorderStyleAtPath = async (contextPath, rowIdx, borderStyle) => {
    try {
      // Optimistic update using recursive traversal
      setTables(prevTables => prevTables.map(table => {
        if (table.id !== contextPath.tableId) return table;
        return traverseAndUpdate(table, contextPath, (structure) => {
          const updated = { ...structure };

          // Initialize row_border_styles if it doesn't exist
          if (!updated.row_border_styles) {
            updated.row_border_styles = {};
          } else {
            // Deep copy the existing styles
            updated.row_border_styles = { ...updated.row_border_styles };
          }

          if (borderStyle === 'none') {
            delete updated.row_border_styles[rowIdx];
          } else {
            updated.row_border_styles[rowIdx] = borderStyle;
          }

          return updated;
        });
      }));

      // Send to backend
      await authFetch(`${API_URL}/api/tables/${contextPath.tableId}/row-border-style-at-path`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contextPath, row_idx: rowIdx, border_style: borderStyle })
      });
    } catch (error) {
      console.error('Error setting row border style at path:', error);
      await fetchTables();
    }
  };

  const deleteSubtableRow = async (contextPath, position) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        deleteRowAtPath(structure, position)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/delete-row`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, position })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error deleting row:', error);
      await fetchTables();  // Rollback on error
    }
  };

  const deleteSubtableColumn = async (contextPath, position) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        deleteColumnAtPath(structure, position)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/delete-column`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, position })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error deleting column:', error);
      await fetchTables();  // Rollback on error
    }
  };

  const deleteNestedTableRow = async (contextPath, position) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        deleteRowAtPath(structure, position)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/delete-row`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, position })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error deleting row:', error);
      await fetchTables();  // Rollback on error
    }
  };

  const deleteNestedTableColumn = async (contextPath, position) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        deleteColumnAtPath(structure, position)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/delete-column`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, position })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error deleting column:', error);
      await fetchTables();  // Rollback on error
    }
  };

  // Move row/column functions
  const moveColumn = async (tableId, fromIndex, direction) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/move-column?from_index=${fromIndex}&direction=${direction}`,
        { method: 'PUT' }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Move column failed:', response.status, errorText);
        alert(`Failed to move column: ${errorText}`);
        return;
      }

      const updatedTable = await response.json();
      console.log('[moveColumn] Updated table received:', updatedTable);
      console.log('[moveColumn] Column headers from backend:', JSON.stringify(updatedTable.column_headers));
      console.log('[moveColumn] First row from backend:', JSON.stringify(updatedTable.rows[0]));

      // Update the table in tables state
      setTables(prevTables => {
        console.log('[moveColumn] Previous tables state:', prevTables);
        const oldTable = prevTables.find(t => t.id === tableId);
        console.log('[moveColumn] Old table column_headers:', JSON.stringify(oldTable?.column_headers));

        const newTables = prevTables.map(t => {
          if (t.id === tableId) {
            // Create a deep copy to force React to see it as new
            const newTable = {
              ...updatedTable,
              rows: updatedTable.rows.map(row => [...row]),
              column_headers: updatedTable.column_headers ? [...updatedTable.column_headers] : null,
              column_widths: updatedTable.column_widths ? [...updatedTable.column_widths] : null,
              isShared: t.isShared,
              sharedBy: t.sharedBy,
              sharedPermission: t.sharedPermission,
              shareId: t.shareId
            };
            console.log('[moveColumn] New table column_headers:', JSON.stringify(newTable.column_headers));
            console.log('[moveColumn] New table first row:', JSON.stringify(newTable.rows[0]));
            return newTable;
          }
          return t;
        });
        return newTables;
      });

      // Also update the table in tabs state
      setTabs(prevTabs => prevTabs.map(tab => {
        if (tab.id === activeTabId) {
          return {
            ...tab,
            tables: tab.tables.map(t => {
              if (t.id === tableId) {
                return {
                  ...updatedTable,
                  rows: updatedTable.rows.map(row => [...row]),
                  column_headers: updatedTable.column_headers ? [...updatedTable.column_headers] : null,
                  column_widths: updatedTable.column_widths ? [...updatedTable.column_widths] : null
                };
              }
              return t;
            })
          };
        }
        return tab;
      }));
    } catch (error) {
      console.error('Error moving column:', error);
      alert(`Error: ${error.message}`);
    }
  };

  const moveRow = async (tableId, fromIndex, direction) => {
    try {
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/move-row?from_index=${fromIndex}&direction=${direction}`,
        { method: 'PUT' }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Move row failed:', response.status, errorText);
        alert(`Failed to move row: ${errorText}`);
        return;
      }

      const updatedTable = await response.json();
      console.log('[moveRow] Updated table received:', updatedTable.row_headers);

      // Update the table in tables state
      setTables(prevTables => prevTables.map(t => {
        if (t.id === tableId) {
          // Preserve shared table metadata when updating
          return {
            ...updatedTable,
            rows: updatedTable.rows.map(row => [...row]),
            row_headers: updatedTable.row_headers ? [...updatedTable.row_headers] : null,
            isShared: t.isShared,
            sharedBy: t.sharedBy,
            sharedPermission: t.sharedPermission,
            shareId: t.shareId
          };
        }
        return t;
      }));

      // Also update the table in tabs state
      setTabs(prevTabs => prevTabs.map(tab => {
        if (tab.id === activeTabId) {
          return {
            ...tab,
            tables: tab.tables.map(t => {
              if (t.id === tableId) {
                return {
                  ...updatedTable,
                  rows: updatedTable.rows.map(row => [...row]),
                  row_headers: updatedTable.row_headers ? [...updatedTable.row_headers] : null
                };
              }
              return t;
            })
          };
        }
        return tab;
      }));
    } catch (error) {
      console.error('Error moving row:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Merge cells: merge a cell with the cell to its right
  const mergeCellRight = async (contextPath, rowIdx, colIdx) => {
    const { tableId } = contextPath;

    // First check if the target cell is already merged
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    let validationFailed = false;
    traverseAndUpdate(table, contextPath, (target) => {
      const mergedCells = target.merged_cells || {};
      const currentKey = `${rowIdx},${colIdx}`;
      const currentMerge = mergedCells[currentKey] || { rowspan: 1, colspan: 1 };

      // Calculate the target column
      const targetCol = colIdx + currentMerge.colspan;

      // Check if target column is out of bounds
      if (targetCol >= target.num_cols) {
        alert('Cannot merge: target cell is out of bounds');
        validationFailed = true;
        return target;
      }

      // Check if any target cell is already part of a merge
      for (let row = rowIdx; row < rowIdx + currentMerge.rowspan; row++) {
        const targetKey = `${row},${targetCol}`;

        // Check if target cell is a merge origin
        if (mergedCells[targetKey]) {
          alert('Cannot merge: target cell is already merged');
          validationFailed = true;
          return target;
        }

        // Check if target cell is covered by another merge
        for (const [key, merge] of Object.entries(mergedCells)) {
          const [mergeRow, mergeCol] = key.split(',').map(Number);
          if (row >= mergeRow && row < mergeRow + merge.rowspan &&
              targetCol >= mergeCol && targetCol < mergeCol + merge.colspan) {
            alert('Cannot merge: target cell is already merged');
            validationFailed = true;
            return target;
          }
        }
      }

      return target;
    });

    // If validation failed, return early
    if (validationFailed) return;

    // Optimistic update
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== tableId) return table;

      const updatedTable = JSON.parse(JSON.stringify(table));
      const result = traverseAndUpdate(updatedTable, contextPath, (target) => {
        const mergedCells = target.merged_cells || {};
        const key = `${rowIdx},${colIdx}`;

        // Get current span (or default to 1,1)
        const currentMerge = mergedCells[key] || { rowspan: 1, colspan: 1 };

        // Increase colspan by 1
        mergedCells[key] = {
          rowspan: currentMerge.rowspan,
          colspan: currentMerge.colspan + 1
        };

        return { ...target, merged_cells: mergedCells };
      });

      return result;
    }));

    // Send to backend
    try {
      await authFetch(`${API_URL}/api/tables/${tableId}/merge-cell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contextPath, row_index: rowIdx, col_index: colIdx, direction: 'right' })
      });
    } catch (error) {
      console.error('[mergeCellRight] Error:', error);
      await fetchTables();
    }
  };

  // Merge cells: merge a cell with the cell below it
  const mergeCellDown = async (contextPath, rowIdx, colIdx) => {
    const { tableId } = contextPath;

    // First check if the target cell is already merged
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    let validationFailed = false;
    traverseAndUpdate(table, contextPath, (target) => {
      const mergedCells = target.merged_cells || {};
      const currentKey = `${rowIdx},${colIdx}`;
      const currentMerge = mergedCells[currentKey] || { rowspan: 1, colspan: 1 };

      // Calculate the target row
      const targetRow = rowIdx + currentMerge.rowspan;

      // Check if target row is out of bounds
      if (targetRow >= target.num_rows) {
        alert('Cannot merge: target cell is out of bounds');
        validationFailed = true;
        return target;
      }

      // Check if any target cell is already part of a merge
      for (let col = colIdx; col < colIdx + currentMerge.colspan; col++) {
        const targetKey = `${targetRow},${col}`;

        // Check if target cell is a merge origin
        if (mergedCells[targetKey]) {
          alert('Cannot merge: target cell is already merged');
          validationFailed = true;
          return target;
        }

        // Check if target cell is covered by another merge
        for (const [key, merge] of Object.entries(mergedCells)) {
          const [mergeRow, mergeCol] = key.split(',').map(Number);
          if (targetRow >= mergeRow && targetRow < mergeRow + merge.rowspan &&
              col >= mergeCol && col < mergeCol + merge.colspan) {
            alert('Cannot merge: target cell is already merged');
            validationFailed = true;
            return target;
          }
        }
      }

      return target;
    });

    // If validation failed, return early
    if (validationFailed) return;

    // Optimistic update
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== tableId) return table;

      const updatedTable = JSON.parse(JSON.stringify(table));
      const result = traverseAndUpdate(updatedTable, contextPath, (target) => {
        const mergedCells = target.merged_cells || {};
        const key = `${rowIdx},${colIdx}`;

        // Get current span (or default to 1,1)
        const currentMerge = mergedCells[key] || { rowspan: 1, colspan: 1 };

        // Increase rowspan by 1
        mergedCells[key] = {
          rowspan: currentMerge.rowspan + 1,
          colspan: currentMerge.colspan
        };

        return { ...target, merged_cells: mergedCells };
      });

      return result;
    }));

    // Send to backend
    try {
      await authFetch(`${API_URL}/api/tables/${tableId}/merge-cell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contextPath, row_index: rowIdx, col_index: colIdx, direction: 'down' })
      });
    } catch (error) {
      console.error('[mergeCellDown] Error:', error);
      await fetchTables();
    }
  };

  // Unmerge cells: split a merged cell back to individual cells
  const unmergeCells = async (contextPath, rowIdx, colIdx) => {
    const { tableId } = contextPath;

    // Optimistic update
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== tableId) return table;

      const updatedTable = JSON.parse(JSON.stringify(table));
      const result = traverseAndUpdate(updatedTable, contextPath, (target) => {
        const mergedCells = target.merged_cells || {};
        const key = `${rowIdx},${colIdx}`;

        // Remove the merge entry
        delete mergedCells[key];

        return { ...target, merged_cells: mergedCells };
      });

      return result;
    }));

    // Send to backend
    try {
      await authFetch(`${API_URL}/api/tables/${tableId}/unmerge-cell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contextPath, row_index: rowIdx, col_index: colIdx })
      });
    } catch (error) {
      console.error('[unmergeCells] Error:', error);
      await fetchTables();
    }
  };

  // Add a header row level
  const addHeaderRow = async (tableId) => {
    try {
      await authFetch(`${API_URL}/api/tables/${tableId}/add-header-row`, {
        method: 'POST'
      });
      await fetchTables();
    } catch (error) {
      console.error('[addHeaderRow] Error:', error);
      alert(`Error adding header row: ${error.message}`);
    }
  };

  // Remove a header row level
  const removeHeaderRow = async (tableId, headerRowIndex) => {
    console.log('[removeHeaderRow] Called with tableId:', tableId, 'headerRowIndex:', headerRowIndex);
    try {
      const response = await authFetch(`${API_URL}/api/tables/${tableId}/remove-header-row`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ header_row_index: headerRowIndex })
      });
      console.log('[removeHeaderRow] Response:', response);
      await fetchTables();
    } catch (error) {
      console.error('[removeHeaderRow] Error:', error);
      alert(`Error removing header row: ${error.message}`);
    }
  };

  // ===== ContextPath-based header row operations for subtables/nested tables =====

  const addHeaderRowAtPath = async (contextPath) => {
    // Optimistic update
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) => {
        const headerRows = structure.header_rows || [];
        const newHeaderRow = Array(structure.num_cols || 0).fill('');

        // Shift existing merges down by 1
        const updatedMerges = {};
        if (structure.header_rows_merged) {
          for (const [key, merge] of Object.entries(structure.header_rows_merged)) {
            const [rowIdx, colIdx] = key.split(',');
            const newRowIdx = parseInt(rowIdx) + 1;
            updatedMerges[`${newRowIdx},${colIdx}`] = merge;
          }
        }

        return {
          ...structure,
          header_rows: [newHeaderRow, ...headerRows],
          header_rows_merged: Object.keys(updatedMerges).length > 0 ? updatedMerges : structure.header_rows_merged
        };
      });
    }));

    try {
      await authFetch(`${API_URL}/api/tables/${contextPath.tableId}/add-header-row-at-path`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contextPath })
      });
    } catch (error) {
      console.error('[addHeaderRowAtPath] Error:', error);
      await fetchTables(); // Rollback
    }
  };

  const removeHeaderRowAtPath = async (contextPath, headerRowIndex) => {
    // Optimistic update
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) => {
        const headerRows = [...(structure.header_rows || [])];
        headerRows.splice(headerRowIndex, 1);

        // Update merged cell indices
        const updatedMerges = {};
        if (structure.header_rows_merged) {
          for (const [key, merge] of Object.entries(structure.header_rows_merged)) {
            const [rowIdx, colIdx] = key.split(',').map(Number);

            // Skip merges in deleted row
            if (rowIdx === headerRowIndex) continue;

            // Shift up rows below deleted row
            const newRowIdx = rowIdx > headerRowIndex ? rowIdx - 1 : rowIdx;
            updatedMerges[`${newRowIdx},${colIdx}`] = merge;
          }
        }

        return {
          ...structure,
          header_rows: headerRows.length > 0 ? headerRows : null,
          header_rows_merged: Object.keys(updatedMerges).length > 0 ? updatedMerges : null
        };
      });
    }));

    try {
      await authFetch(`${API_URL}/api/tables/${contextPath.tableId}/remove-header-row-at-path`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ contextPath, header_row_index: headerRowIndex })
      });
    } catch (error) {
      console.error('[removeHeaderRowAtPath] Error:', error);
      await fetchTables(); // Rollback
    }
  };

  // Context-path based header cell merge functions (for subtables/nested)
  const mergeHeaderCellRightAtPath = async (contextPath, headerRowIdx, colIdx) => {
    // Optimistic update
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) => {
        const mergedCells = structure.header_rows_merged || {};
        const key = `${headerRowIdx},${colIdx}`;
        const currentMerge = mergedCells[key] || { rowspan: 1, colspan: 1 };

        return {
          ...structure,
          header_rows_merged: {
            ...mergedCells,
            [key]: {
              rowspan: currentMerge.rowspan,
              colspan: currentMerge.colspan + 1
            }
          }
        };
      });
    }));

    try {
      await authFetch(`${API_URL}/api/tables/${contextPath.tableId}/merge-header-cell-at-path`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contextPath,
          header_row_index: headerRowIdx,
          col_index: colIdx,
          direction: 'right'
        })
      });
    } catch (error) {
      console.error('[mergeHeaderCellRightAtPath] Error:', error);
      await fetchTables();
    }
  };

  const mergeHeaderCellDownAtPath = async (contextPath, headerRowIdx, colIdx) => {
    // Optimistic update
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) => {
        const mergedCells = structure.header_rows_merged || {};
        const key = `${headerRowIdx},${colIdx}`;
        const currentMerge = mergedCells[key] || { rowspan: 1, colspan: 1 };

        return {
          ...structure,
          header_rows_merged: {
            ...mergedCells,
            [key]: {
              rowspan: currentMerge.rowspan + 1,
              colspan: currentMerge.colspan
            }
          }
        };
      });
    }));

    try {
      await authFetch(`${API_URL}/api/tables/${contextPath.tableId}/merge-header-cell-at-path`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contextPath,
          header_row_index: headerRowIdx,
          col_index: colIdx,
          direction: 'down'
        })
      });
    } catch (error) {
      console.error('[mergeHeaderCellDownAtPath] Error:', error);
      await fetchTables();
    }
  };

  const unmergeHeaderCellAtPath = async (contextPath, headerRowIdx, colIdx) => {
    // Optimistic update
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) => {
        const mergedCells = { ...(structure.header_rows_merged || {}) };
        const key = `${headerRowIdx},${colIdx}`;
        delete mergedCells[key];

        return {
          ...structure,
          header_rows_merged: Object.keys(mergedCells).length > 0 ? mergedCells : null
        };
      });
    }));

    try {
      await authFetch(`${API_URL}/api/tables/${contextPath.tableId}/unmerge-header-cell-at-path`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contextPath,
          header_row_index: headerRowIdx,
          col_index: colIdx
        })
      });
    } catch (error) {
      console.error('[unmergeHeaderCellAtPath] Error:', error);
      await fetchTables();
    }
  };

  // Merge header cell with cell to its right
  const mergeHeaderCellRight = async (tableId, headerRowIdx, colIdx) => {
    const table = tables.find(t => t.id === tableId);
    if (!table || !table.header_rows?.[headerRowIdx]) return;

    const headerRow = table.header_rows[headerRowIdx];
    const mergedCells = table.header_rows_merged || {};
    const currentKey = `${headerRowIdx},${colIdx}`;
    const currentMerge = mergedCells[currentKey] || { rowspan: 1, colspan: 1 };
    const targetCol = colIdx + currentMerge.colspan;

    // Validation
    if (targetCol >= headerRow.length) {
      alert('Cannot merge: target cell is out of bounds');
      return;
    }

    // Check if target cells are already part of another merge
    for (let c = targetCol; c < targetCol + 1; c++) {
      const targetKey = `${headerRowIdx},${c}`;
      if (mergedCells[targetKey] && targetKey !== currentKey) {
        alert('Cannot merge: target cell is already merged');
        return;
      }
    }

    // Optimistic update
    setTables(prevTables => prevTables.map(t => {
      if (t.id !== tableId) return t;
      const updatedMerged = { ...t.header_rows_merged };
      updatedMerged[currentKey] = {
        rowspan: currentMerge.rowspan,
        colspan: currentMerge.colspan + 1
      };
      return { ...t, header_rows_merged: updatedMerged };
    }));

    // Send to backend
    try {
      await authFetch(`${API_URL}/api/tables/${tableId}/merge-header-cell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header_row_index: headerRowIdx,
          col_index: colIdx,
          direction: 'right'
        })
      });
    } catch (error) {
      console.error('[mergeHeaderCellRight] Error:', error);
      await fetchTables(); // Rollback
    }
  };

  // Merge header cell with cell below
  const mergeHeaderCellDown = async (tableId, headerRowIdx, colIdx) => {
    const table = tables.find(t => t.id === tableId);
    if (!table || !table.header_rows?.[headerRowIdx]) return;

    const mergedCells = table.header_rows_merged || {};
    const currentKey = `${headerRowIdx},${colIdx}`;
    const currentMerge = mergedCells[currentKey] || { rowspan: 1, colspan: 1 };
    const targetRow = headerRowIdx + currentMerge.rowspan;

    // Validation
    if (targetRow >= table.header_rows.length) {
      alert('Cannot merge: target cell is out of bounds');
      return;
    }

    // Check if target cells are already part of another merge
    for (let c = colIdx; c < colIdx + currentMerge.colspan; c++) {
      const targetKey = `${targetRow},${c}`;
      if (mergedCells[targetKey]) {
        alert('Cannot merge: target cell is already merged');
        return;
      }
    }

    // Optimistic update
    setTables(prevTables => prevTables.map(t => {
      if (t.id !== tableId) return t;
      const updatedMerged = { ...t.header_rows_merged };
      updatedMerged[currentKey] = {
        rowspan: currentMerge.rowspan + 1,
        colspan: currentMerge.colspan
      };
      return { ...t, header_rows_merged: updatedMerged };
    }));

    // Send to backend
    try {
      await authFetch(`${API_URL}/api/tables/${tableId}/merge-header-cell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header_row_index: headerRowIdx,
          col_index: colIdx,
          direction: 'down'
        })
      });
    } catch (error) {
      console.error('[mergeHeaderCellDown] Error:', error);
      await fetchTables(); // Rollback
    }
  };

  // Unmerge a header cell
  const unmergeHeaderCell = async (tableId, headerRowIdx, colIdx) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) return;

    const mergedCells = table.header_rows_merged || {};
    const currentKey = `${headerRowIdx},${colIdx}`;

    if (!mergedCells[currentKey]) {
      alert('This cell is not merged');
      return;
    }

    // Optimistic update
    setTables(prevTables => prevTables.map(t => {
      if (t.id !== tableId) return t;
      const updatedMerged = { ...t.header_rows_merged };
      delete updatedMerged[currentKey];
      return { ...t, header_rows_merged: updatedMerged };
    }));

    // Send to backend
    try {
      await authFetch(`${API_URL}/api/tables/${tableId}/unmerge-header-cell`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          header_row_index: headerRowIdx,
          col_index: colIdx
        })
      });
    } catch (error) {
      console.error('[unmergeHeaderCell] Error:', error);
      await fetchTables(); // Rollback
    }
  };

  const moveSubtableColumn = async (contextPath, fromIndex, direction) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        moveColumnAtPath(structure, fromIndex, direction)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/move-column`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, from_index: fromIndex, direction })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error moving column:', error);
      await fetchTables();  // Rollback on error
    }
  };

  const moveSubtableRow = async (contextPath, fromIndex, direction) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        moveRowAtPath(structure, fromIndex, direction)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/move-row`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, from_index: fromIndex, direction })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error moving row:', error);
      await fetchTables();  // Rollback on error
    }
  };

  const moveNestedTableColumn = async (contextPath, fromIndex, direction) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        moveColumnAtPath(structure, fromIndex, direction)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/move-column`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, from_index: fromIndex, direction })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error moving column:', error);
      await fetchTables();  // Rollback on error
    }
  };

  const moveNestedTableRow = async (contextPath, fromIndex, direction) => {
    // Optimistic update using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;
      return traverseAndUpdate(table, contextPath, (structure) =>
        moveRowAtPath(structure, fromIndex, direction)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/move-row`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, from_index: fromIndex, direction })
        }
      );
      // No fetchTables() on success - optimistic update already applied
    } catch (error) {
      console.error('Error moving row:', error);
      await fetchTables();  // Rollback on error
    }
  };

  // ============================================================
  // COPY/PASTE FUNCTIONS - Copy and paste rows/columns
  // ============================================================

  // Helper function to generate new IDs for nested objects to avoid reference issues
  const regenerateIds = (value) => {
    if (!value || typeof value !== 'object') {
      return value;
    }

    // Deep clone first
    const cloned = JSON.parse(JSON.stringify(value));

    // Recursively regenerate IDs for nested tables, subtables, and charts
    const regenerateIdsRecursive = (obj) => {
      if (!obj || typeof obj !== 'object') {
        return obj;
      }

      // If it's an array, process each element
      if (Array.isArray(obj)) {
        return obj.map(item => regenerateIdsRecursive(item));
      }

      // If it has an ID and a type (nested table, chart, etc.), regenerate the ID
      if (obj.id && obj.type) {
        obj.id = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }

      // Recursively process all properties
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          obj[key] = regenerateIdsRecursive(obj[key]);
        }
      }

      return obj;
    };

    return regenerateIdsRecursive(cloned);
  };

  // Copy a row from main table
  const copyRow = (tableId, rowIndex) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) {
      console.error('[copyRow] Table not found:', tableId);
      return;
    }

    const rowData = table.rows[rowIndex];
    console.log('[copyRow] Copying row:', { tableId, rowIndex, rowData });

    // Deep copy the row data to avoid reference issues
    const copiedData = JSON.parse(JSON.stringify(rowData));

    setCopiedRow({
      data: copiedData,
      source: { tableId, type: 'main' }
    });
    setCopiedColumn(null); // Clear column clipboard
    console.log('[copyRow] Row copied to clipboard');
  };

  // Copy a row from subtable or nested table
  const copySubtableRow = (contextPath, rowIndex) => {
    const table = tables.find(t => t.id === contextPath.tableId);
    if (!table) return;

    // ✅ UPDATED: Use traverseAndUpdate pattern to find structure at any depth
    const findStructureByPath = (tbl, ctxPath) => {
      let current = tbl;
      for (const segment of ctxPath.path) {
        if (segment.type === 'subtable') {
          let found = null;
          for (const rowKey in current.row_subtables || {}) {
            for (const st of current.row_subtables[rowKey]) {
              if (st.id === segment.id) {
                found = st;
                break;
              }
            }
            if (found) break;
          }
          if (!found) return null;
          current = found;
        } else if (segment.type === 'nested') {
          let found = null;
          for (const row of current.rows || []) {
            for (const cell of row) {
              if (cell && cell.type === 'nested_table' && cell.id === segment.id) {
                found = cell;
                break;
              }
            }
            if (found) break;
          }
          if (!found) return null;
          current = found;
        }
      }
      return current;
    };

    const structure = findStructureByPath(table, contextPath);
    if (!structure) return;

    setCopiedRow({
      data: structure.rows[rowIndex],
      source: { contextPath, type: 'structure' }
    });
    setCopiedColumn(null);
  };

  // Alias for backward compatibility
  const copyNestedTableRow = copySubtableRow;

  // Copy a column from main table
  const copyColumn = (tableId, colIndex) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) {
      console.error('[copyColumn] Table not found:', tableId);
      return;
    }

    const columnData = table.rows.map(row => row[colIndex]);
    console.log('[copyColumn] Copying column:', { tableId, colIndex, columnData });

    // Deep copy the column data to avoid reference issues
    const copiedData = JSON.parse(JSON.stringify(columnData));

    setCopiedColumn({
      data: copiedData,
      numRows: table.num_rows,
      source: { tableId, type: 'main' }
    });
    setCopiedRow(null); // Clear row clipboard
    console.log('[copyColumn] Column copied to clipboard');
  };

  // ✅ UPDATED: Copy a column - works at unlimited depth
  const copySubtableColumn = (contextPath, colIndex) => {
    const table = tables.find(t => t.id === contextPath.tableId);
    if (!table) return;

    // ✅ UPDATED: Use traverseAndUpdate pattern to find structure at any depth
    const findStructureByPath = (tbl, ctxPath) => {
      let current = tbl;
      for (const segment of ctxPath.path) {
        if (segment.type === 'subtable') {
          let found = null;
          for (const rowKey in current.row_subtables || {}) {
            for (const st of current.row_subtables[rowKey]) {
              if (st.id === segment.id) {
                found = st;
                break;
              }
            }
            if (found) break;
          }
          if (!found) return null;
          current = found;
        } else if (segment.type === 'nested') {
          let found = null;
          for (const row of current.rows || []) {
            for (const cell of row) {
              if (cell && cell.type === 'nested_table' && cell.id === segment.id) {
                found = cell;
                break;
              }
            }
            if (found) break;
          }
          if (!found) return null;
          current = found;
        }
      }
      return current;
    };

    const structure = findStructureByPath(table, contextPath);
    if (!structure) return;

    const columnData = structure.rows.map(row => row[colIndex]);
    setCopiedColumn({
      data: columnData,
      numRows: structure.num_rows,
      source: { contextPath, type: 'structure' }
    });
    setCopiedRow(null);
  };

  // Alias for backward compatibility
  const copyNestedTableColumn = copySubtableColumn;

  // Paste a row (overwrites existing row contents)
  const pasteRow = async (tableId, position) => {
    if (!copiedRow) return;

    console.log('[pasteRow] Starting paste:', { tableId, position, copiedRow });

    try {
      const table = tables.find(t => t.id === tableId);
      if (!table) {
        console.error('[pasteRow] Table not found:', tableId);
        return;
      }

      console.log('[pasteRow] Found table:', table.name, 'num_cols:', table.num_cols);
      console.log('[pasteRow] Copied data:', copiedRow.data);

      // Update each cell in the row with the copied data
      for (let colIdx = 0; colIdx < copiedRow.data.length && colIdx < table.num_cols; colIdx++) {
        const cellValue = copiedRow.data[colIdx];
        console.log(`[pasteRow] Pasting cell [${position}][${colIdx}]:`, cellValue);

        // Regenerate IDs for nested objects to avoid reference issues
        const valueWithNewIds = regenerateIds(cellValue);

        // Use updateCell to properly handle all cell types
        await updateCell(tableId, position, colIdx, valueWithNewIds);
      }

      console.log('[pasteRow] Paste complete');
    } catch (error) {
      console.error('Error pasting row:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // ✅ UPDATED: Paste a row (overwrites existing row contents) - works at unlimited depth
  const pasteSubtableRow = async (contextPath, position) => {
    if (!copiedRow) return;

    try {
      const table = tables.find(t => t.id === contextPath.tableId);
      if (!table) return;

      // ✅ UPDATED: Use traverseAndUpdate pattern to find structure at any depth
      const findStructureByPath = (tbl, ctxPath) => {
        let current = tbl;
        for (const segment of ctxPath.path) {
          if (segment.type === 'subtable') {
            let found = null;
            for (const rowKey in current.row_subtables || {}) {
              for (const st of current.row_subtables[rowKey]) {
                if (st.id === segment.id) {
                  found = st;
                  break;
                }
              }
              if (found) break;
            }
            if (!found) return null;
            current = found;
          } else if (segment.type === 'nested') {
            let found = null;
            for (const row of current.rows || []) {
              for (const cell of row) {
                if (cell && cell.type === 'nested_table' && cell.id === segment.id) {
                  found = cell;
                  break;
                }
              }
              if (found) break;
            }
            if (!found) return null;
            current = found;
          }
        }
        return current;
      };

      const structure = findStructureByPath(table, contextPath);
      if (!structure) return;

      // Update each cell in the row with the copied data
      for (let colIdx = 0; colIdx < copiedRow.data.length && colIdx < structure.num_cols; colIdx++) {
        const cellValue = copiedRow.data[colIdx];
        // Regenerate IDs for nested objects to avoid reference issues
        const valueWithNewIds = regenerateIds(cellValue);
        // ✅ UPDATED: Use updateCellGeneric with contextPath for unlimited depth support
        await updateCellGeneric(contextPath.tableId, position, colIdx, valueWithNewIds, { contextPath });
      }
    } catch (error) {
      console.error('Error pasting row:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Alias for backward compatibility
  const pasteNestedTableRow = pasteSubtableRow;

  // Paste a column (overwrites existing column contents)
  const pasteColumn = async (tableId, position) => {
    if (!copiedColumn) return;

    try {
      const table = tables.find(t => t.id === tableId);
      if (!table) return;

      // Update each cell in the column with the copied data
      for (let rowIdx = 0; rowIdx < copiedColumn.data.length && rowIdx < table.num_rows; rowIdx++) {
        const cellValue = copiedColumn.data[rowIdx];
        // Regenerate IDs for nested objects to avoid reference issues
        const valueWithNewIds = regenerateIds(cellValue);
        // Use updateCell to properly handle all cell types
        await updateCell(tableId, rowIdx, position, valueWithNewIds);
      }
    } catch (error) {
      console.error('Error pasting column:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // ✅ UPDATED: Paste a column (overwrites existing column contents) - works at unlimited depth
  const pasteSubtableColumn = async (contextPath, position) => {
    if (!copiedColumn) return;

    try {
      const table = tables.find(t => t.id === contextPath.tableId);
      if (!table) return;

      // ✅ UPDATED: Use traverseAndUpdate pattern to find structure at any depth
      const findStructureByPath = (tbl, ctxPath) => {
        let current = tbl;
        for (const segment of ctxPath.path) {
          if (segment.type === 'subtable') {
            let found = null;
            for (const rowKey in current.row_subtables || {}) {
              for (const st of current.row_subtables[rowKey]) {
                if (st.id === segment.id) {
                  found = st;
                  break;
                }
              }
              if (found) break;
            }
            if (!found) return null;
            current = found;
          } else if (segment.type === 'nested') {
            let found = null;
            for (const row of current.rows || []) {
              for (const cell of row) {
                if (cell && cell.type === 'nested_table' && cell.id === segment.id) {
                  found = cell;
                  break;
                }
              }
              if (found) break;
            }
            if (!found) return null;
            current = found;
          }
        }
        return current;
      };

      const structure = findStructureByPath(table, contextPath);
      if (!structure) return;

      // Update each cell in the column with the copied data
      for (let rowIdx = 0; rowIdx < copiedColumn.data.length && rowIdx < structure.num_rows; rowIdx++) {
        const cellValue = copiedColumn.data[rowIdx];
        // Regenerate IDs for nested objects to avoid reference issues
        const valueWithNewIds = regenerateIds(cellValue);
        // ✅ UPDATED: Use updateCellGeneric with contextPath for unlimited depth support
        await updateCellGeneric(contextPath.tableId, rowIdx, position, valueWithNewIds, { contextPath });
      }
    } catch (error) {
      console.error('Error pasting column:', error);
      alert(`Error: ${error.message}`);
    }
  };

  // Alias for backward compatibility
  const pasteNestedTableColumn = pasteSubtableColumn;

  // ✅ UPDATED: Toggle expanded state at any depth using contextPath
  const toggleSubtable = async (contextPath) => {
    try {
      console.log('[toggleSubtable] Starting toggle:', contextPath);

      // STEP 1: Optimistically toggle expanded state in UI
      setTables(prevTables => prevTables.map(table => {
        if (table.id !== contextPath.tableId) return table;
        return traverseAndUpdate(table, contextPath, (structure) => ({
          ...structure,
          expanded: !structure.expanded
        }));
      }));

      // STEP 2: Send toggle request to backend
      const response = await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/toggle-expanded`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[toggleSubtable] Toggle failed:', response.status, errorText);
        await fetchTables(); // Rollback
        alert(`Failed to toggle: ${errorText}`);
        return;
      }

      console.log('[toggleSubtable] Successfully toggled expanded state');
      // Optimistic update already applied, no fetchTables needed
    } catch (error) {
      console.error('[toggleSubtable] Error toggling:', error);
      await fetchTables(); // Rollback
      alert(`Error: ${error.message}`);
    }
  };


  // Bulk show/hide: single optimistic UI update, then sequential backend calls.
  // Avoids race conditions from parallel saves and visual shaking from sequential re-renders.
  const bulkSetExpanded = async (subtableIds, targetExpanded, buildContextPath) => {
    if (subtableIds.length === 0) return;

    // STEP 1: Single optimistic update — flip all at once
    setTables(prevTables => {
      let updated = [...prevTables];
      for (const id of subtableIds) {
        const cp = buildContextPath(id);
        updated = updated.map(table => {
          if (table.id !== cp.tableId) return table;
          return traverseAndUpdate(table, cp, (structure) => ({
            ...structure,
            expanded: targetExpanded,
          }));
        });
      }
      return updated;
    });

    // STEP 2: Sequential backend calls (no race conditions)
    for (const id of subtableIds) {
      try {
        const cp = buildContextPath(id);
        await authFetch(
          `${API_URL}/api/tables/${cp.tableId}/update-by-path/toggle-expanded`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contextPath: cp }),
          }
        );
      } catch (err) {
        console.error('[bulkSetExpanded] Error toggling subtable:', id, err);
      }
    }
  };

  // Collect all subtables (with their IDs and expanded state) from a table's row_subtables.
  const getAllTableSubtables = (table) => {
    const result = [];
    if (!table.row_subtables) return result;
    for (const rowKey of Object.keys(table.row_subtables)) {
      const subs = table.row_subtables[rowKey];
      if (Array.isArray(subs)) {
        for (const st of subs) {
          result.push(st);
        }
      }
    }
    return result;
  };

  const createNestedTable = async (contextPath, rowIdx, colIdx, numRows = 3, numCols = 3) => {
    const { tableId } = contextPath;

    // Generate temporary ID for optimistic update
    const tempId = `temp-nested-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const stableKey = `${tableId}-${contextPath.path.join('-')}-${rowIdx}-${colIdx}-${Date.now()}`;

    // Create optimistic nested table structure
    const optimisticNestedTable = {
      id: tempId,
      _stableKey: stableKey,
      type: 'nested_table',
      name: 'Nested Table',
      num_rows: numRows,
      num_cols: numCols,
      rows: Array(numRows).fill(null).map(() => Array(numCols).fill("")),
      column_headers: Array(numCols).fill(null).map((_, i) => `Column ${i + 1}`),
      row_headers: Array(numRows).fill(null).map((_, i) => `Row ${i + 1}`),
      show_headers: false,
      show_row_headers: false,
      expanded: true,
      row_subtables: {},
      _optimistic: true,
    };

    // STEP 1: Optimistic update - place nested table in cell
    setTables(prevTables => {
      const newTables = prevTables.map(t => {
        if (t.id !== tableId) return t;

        const updatedTable = JSON.parse(JSON.stringify(t));

        const result = traverseAndUpdate(updatedTable, contextPath, (target) => {
          // traverseAndUpdate expects the callback to return the updated structure
          if (target.rows && target.rows[rowIdx] && target.rows[rowIdx][colIdx] !== undefined) {
            const updated = { ...target };
            updated.rows = updated.rows.map((row, rIdx) =>
              rIdx === rowIdx
                ? row.map((cell, cIdx) => (cIdx === colIdx ? optimisticNestedTable : cell))
                : row
            );
            return updated;
          }
          return target; // No change
        });

        return result;
      });

      return newTables;
    });

    setShowContextMenu(false);

    // STEP 2: Send to backend
    try {
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/update-by-path/create-nested-table`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contextPath,
            row_index: rowIdx,
            col_index: colIdx,
            num_rows: numRows,
            num_cols: numCols
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Create nested table failed:', response.status, errorText);

        // ROLLBACK optimistic update
        setTables(prevTables => prevTables.map(t => {
          if (t.id !== tableId) return t;

          const rolledBackTable = JSON.parse(JSON.stringify(t));
          traverseAndUpdate(rolledBackTable, contextPath, (target) => {
            if (target.rows && target.rows[rowIdx] && target.rows[rowIdx][colIdx]) {
              const cell = target.rows[rowIdx][colIdx];
              if (cell && typeof cell === 'object' && cell.id === tempId) {
                target.rows[rowIdx][colIdx] = "";
                return true;
              }
            }
            return false;
          });

          return rolledBackTable;
        }));

        alert(`Failed to create nested table: ${errorText}`);
        return;
      }

      // STEP 3: Replace temp ID with real ID from backend
      const updatedTable = await response.json();

      // Extract real ID from backend response
      let realId = null;
      const backendResult = traverseAndUpdate(updatedTable, contextPath, (target) => {
        if (target.rows && target.rows[rowIdx] && target.rows[rowIdx][colIdx]) {
          const cell = target.rows[rowIdx][colIdx];
          if (cell && typeof cell === 'object' && cell.type === 'nested_table') {
            realId = cell.id;
            return true;
          }
        }
        return false;
      });

      if (!realId) {
        console.error('Could not find real nested table ID in backend response');
        return;
      }

      // Update optimistic nested table with real ID
      setTables(prevTables => prevTables.map(t => {
        if (t.id !== tableId) return t;

        const newTable = JSON.parse(JSON.stringify(t));
        traverseAndUpdate(newTable, contextPath, (target) => {
          if (target.rows && target.rows[rowIdx] && target.rows[rowIdx][colIdx]) {
            const cell = target.rows[rowIdx][colIdx];
            if (cell && typeof cell === 'object' && cell.id === tempId) {
              cell.id = realId;
              delete cell._optimistic;
              return true;
            }
          }
          return false;
        });

        return newTable;
      }));

    } catch (error) {
      console.error('Error creating nested table:', error);

      // ROLLBACK on error
      setTables(prevTables => prevTables.map(t => {
        if (t.id !== tableId) return t;

        const rolledBackTable = JSON.parse(JSON.stringify(t));
        traverseAndUpdate(rolledBackTable, contextPath, (target) => {
          if (target.rows && target.rows[rowIdx] && target.rows[rowIdx][colIdx]) {
            const cell = target.rows[rowIdx][colIdx];
            if (cell && typeof cell === 'object' && cell.id === tempId) {
              target.rows[rowIdx][colIdx] = "";
              return true;
            }
          }
          return false;
        });

        return rolledBackTable;
      }));

      alert(`Error: ${error.message}`);
    }
  };

  // ✅ REPLACED: Old toggleNestedTable replaced with alias to toggleSubtable
  // Both subtables and nested tables now use the same contextPath-based toggle function
  const toggleNestedTable = toggleSubtable;


  // Resize functions
  // ============================================================
  // UNIFIED RESIZE FUNCTION - Handles all table/subtable/nested resize operations
  // ============================================================
  const resizeElement = async (dimension, index, size, options = {}) => {
    const { tableId, subtableId, nestedTableId, skipRefresh } = options;

    // Build endpoint based on context
    // Order matters: nested table comes before subtable in the path
    let endpoint = `/api/tables/${tableId}`;
    if (nestedTableId) endpoint += `/nested-table/${nestedTableId}`;
    if (subtableId) endpoint += `/subtable/${subtableId}`;
    endpoint += `/resize/${dimension}`;

    // Build query params based on dimension type
    const indexParam = dimension === 'column' ? 'col_index' : 'row_index';
    const sizeParam = dimension === 'column' ? 'width' : 'height';
    endpoint += `?${indexParam}=${index}&${sizeParam}=${Math.round(size)}`;

    try {
      await authFetch(`${API_URL}${endpoint}`, { method: 'PUT' });

      // Call fetchTables to reflect the change in state.
      // The optimistic resize path (resizeElementOptimized) already updated local
      // state during the drag, so it passes skipRefresh to avoid a full-table refetch.
      // That refetch is not just redundant there — its in-flight GET snapshots the
      // server BEFORE an in-progress (uncommitted) cell edit is persisted, and when it
      // lands late it overwrites the whole table, wiping the edit. See the resize/save race.
      if (!skipRefresh) {
        fetchTables();
      }
    } catch (error) {
      console.error(`Error resizing ${dimension}:`, error);
    }
  };

  // New optimized resize: updates local state during drag, persists on mouseup
  const resizeElementOptimized = (dimension, index, options = {}) => {
    const { tableId, subtableId, nestedTableId } = options;

    // Helper to update a table's dimensions (works on any table object)
    const updateTableDimensions = (tbl) => {
      const updatedTable = { ...tbl };

      if (dimension === 'column') {
        if (!updatedTable.column_widths) {
          updatedTable.column_widths = Array(updatedTable.num_cols).fill(150);
        }
        updatedTable.column_widths = [...updatedTable.column_widths];
        updatedTable.column_widths[index] = newSize;
      } else {
        if (!updatedTable.row_heights) {
          updatedTable.row_heights = Array(updatedTable.num_rows).fill(40);
        }
        updatedTable.row_heights = [...updatedTable.row_heights];
        updatedTable.row_heights[index] = newSize;
      }

      return updatedTable;
    };

    // Store current size in closure so updateLocalState can access it
    let newSize = 0;

    // Helper to update local state (column_widths or row_heights in tables state)
    const updateLocalState = (size) => {
      newSize = size;

      setTables(currentTables => {
        return currentTables.map(table => {
          if (table.id !== tableId) return table;

          // Case 1: Nested table (inside cells)
          if (nestedTableId) {
            const updatedTable = { ...table };

            // Update rows array immutably
            updatedTable.rows = table.rows.map(row => {
              return row.map(cell => {
                if (cell && typeof cell === 'object' && cell.type === 'nested_table' && cell.id === nestedTableId) {
                  return updateTableDimensions(cell);
                }
                return cell;
              });
            });

            // Also check subtables if subtableId provided
            if (subtableId && updatedTable.row_subtables) {
              updatedTable.row_subtables = { ...updatedTable.row_subtables };
              for (const rowKey in updatedTable.row_subtables) {
                updatedTable.row_subtables[rowKey] = updatedTable.row_subtables[rowKey].map(subtable => {
                  if (subtable.id === subtableId) {
                    // Update nested table within this subtable
                    const updatedSubtable = { ...subtable };
                    updatedSubtable.rows = subtable.rows.map(row => {
                      return row.map(cell => {
                        if (cell && typeof cell === 'object' && cell.type === 'nested_table' && cell.id === nestedTableId) {
                          return updateTableDimensions(cell);
                        }
                        return cell;
                      });
                    });
                    return updatedSubtable;
                  }
                  return subtable;
                });
              }
            }

            return updatedTable;
          }

          // Case 2: Subtable (inside row_subtables)
          else if (subtableId) {
            const updatedTable = { ...table };

            if (updatedTable.row_subtables) {
              updatedTable.row_subtables = { ...updatedTable.row_subtables };
              for (const rowKey in updatedTable.row_subtables) {
                updatedTable.row_subtables[rowKey] = updatedTable.row_subtables[rowKey].map(subtable => {
                  if (subtable.id === subtableId) {
                    return updateTableDimensions(subtable);
                  }
                  return subtable;
                });
              }
            }

            return updatedTable;
          }

          // Case 3: Main table
          else {
            return updateTableDimensions(table);
          }
        });
      });
    };

    // Helper to persist to backend.
    // skipRefresh: local state is already updated optimistically via updateLocalState
    // during the drag, so we must NOT trigger a full-table refetch here. That refetch
    // races with (and clobbers) any uncommitted in-progress cell edit. See resizeElement.
    const persistToBackend = async (finalSize) => {
      await resizeElement(dimension, index, finalSize, { ...options, skipRefresh: true });
    };

    return {
      onResizePreview: updateLocalState,
      onResizeCommit: persistToBackend
    };
  };

  // Backwards-compatible wrapper functions
  const resizeTableColumn = (tableId, colIndex, width) =>
    resizeElement('column', colIndex, width, { tableId });

  const resizeTableRow = (tableId, rowIndex, height) =>
    resizeElement('row', rowIndex, height, { tableId });

  // ✅ UPDATED: Resize column at any depth using contextPath
  const resizeSubtableColumn = async (contextPath, colIndex, width) => {
    try {
      console.log('[resizeSubtableColumn] Starting resize:', { contextPath, colIndex, width });

      // STEP 1: Optimistically resize column in UI
      setTables(prevTables => prevTables.map(table => {
        if (table.id !== contextPath.tableId) return table;
        return traverseAndUpdate(table, contextPath, (structure) => {
          const updated = { ...structure };

          // Initialize column_widths array if needed
          if (!updated.column_widths || !Array.isArray(updated.column_widths)) {
            updated.column_widths = Array(updated.num_cols || 0).fill(null);
          } else {
            updated.column_widths = [...updated.column_widths];
          }

          // Extend array if needed
          while (updated.column_widths.length <= colIndex) {
            updated.column_widths.push(null);
          }

          updated.column_widths[colIndex] = Math.round(width);
          return updated;
        });
      }));

      // STEP 2: Send resize request to backend
      const response = await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/resize-column`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, col_index: colIndex, width: Math.round(width) })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[resizeSubtableColumn] Resize failed:', response.status, errorText);
        await fetchTables(); // Rollback
        return;
      }

      console.log('[resizeSubtableColumn] Successfully resized column');
      // Optimistic update already applied, no fetchTables needed
    } catch (error) {
      console.error('[resizeSubtableColumn] Error resizing:', error);
      await fetchTables(); // Rollback
    }
  };

  // ✅ UPDATED: Resize row at any depth using contextPath
  const resizeSubtableRow = async (contextPath, rowIndex, height) => {
    try {
      console.log('[resizeSubtableRow] Starting resize:', { contextPath, rowIndex, height });

      // STEP 1: Optimistically resize row in UI
      setTables(prevTables => prevTables.map(table => {
        if (table.id !== contextPath.tableId) return table;
        return traverseAndUpdate(table, contextPath, (structure) => {
          const updated = { ...structure };

          // Initialize row_heights array if needed
          if (!updated.row_heights || !Array.isArray(updated.row_heights)) {
            updated.row_heights = Array(updated.num_rows || 0).fill(null);
          } else {
            updated.row_heights = [...updated.row_heights];
          }

          // Extend array if needed
          while (updated.row_heights.length <= rowIndex) {
            updated.row_heights.push(null);
          }

          updated.row_heights[rowIndex] = Math.round(height);
          return updated;
        });
      }));

      // STEP 2: Send resize request to backend
      const response = await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/resize-row`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, row_index: rowIndex, height: Math.round(height) })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[resizeSubtableRow] Resize failed:', response.status, errorText);
        await fetchTables(); // Rollback
        return;
      }

      console.log('[resizeSubtableRow] Successfully resized row');
      // Optimistic update already applied, no fetchTables needed
    } catch (error) {
      console.error('[resizeSubtableRow] Error resizing:', error);
      await fetchTables(); // Rollback
    }
  };

  // Aliases for backward compatibility
  const resizeNestedTableColumn = resizeSubtableColumn;
  const resizeNestedTableRow = resizeSubtableRow;

  // ============================================================
  // UNIFIED TOGGLE HEADERS FUNCTION
  // ============================================================
  const toggleHeaders = async (headerType, options = {}) => {
    // ✅ REFACTORED: Now accepts contextPath
    const { tableId, subtableId, nestedTableId, contextPath } = options;

    // Build endpoint based on context (for backward compatibility with legacy API)
    let endpoint;
    if (contextPath) {
      // Use contextPath to extract legacy params
      const legacyParams = extractLegacyParams(contextPath);
      endpoint = `/api/tables/${legacyParams.tableId}`;
      if (legacyParams.subtableId) endpoint += `/subtable/${legacyParams.subtableId}`;
      if (legacyParams.nestedTableId) endpoint += `/nested-table/${legacyParams.nestedTableId}`;
    } else {
      // Fallback to legacy parameter style
      endpoint = `/api/tables/${tableId}`;
      if (subtableId) endpoint += `/subtable/${subtableId}`;
      if (nestedTableId) endpoint += `/nested-table/${nestedTableId}`;
    }
    endpoint += headerType === 'row' ? '/toggle-row-headers' : '/toggle-headers';

    // Optimistic update: immediately update local state using recursive traversal
    setTables(prevTables => prevTables.map(table => {
      // Determine which tableId to check
      const targetTableId = contextPath ? contextPath.tableId : tableId;
      if (table.id !== targetTableId) return table;

      // If using contextPath, use traverseAndUpdate for recursive search
      if (contextPath && contextPath.path && contextPath.path.length > 0) {
        return traverseAndUpdate(table, contextPath, (structure) =>
          headerType === 'row'
            ? toggleShowRowHeadersAtPath(structure)
            : toggleShowHeadersAtPath(structure)
        );
      }

      // Otherwise, update main table headers (no nesting)
      if (!subtableId && !nestedTableId) {
        const newShowHeaders = headerType === 'column' ? !table.show_headers : table.show_headers;
        const newShowRowHeaders = headerType === 'row' ? !table.show_row_headers : table.show_row_headers;

        const updatedTable = {
          ...table,
          show_headers: newShowHeaders,
          show_row_headers: newShowRowHeaders
        };

        // Initialize column_headers if toggling column headers on
        if (headerType === 'column' && newShowHeaders && !table.column_headers) {
          updatedTable.column_headers = Array(table.num_cols).fill(null).map((_, i) => `Column ${i + 1}`);
        }

        // Initialize row_headers if toggling row headers on
        if (headerType === 'row' && newShowRowHeaders && !table.row_headers) {
          updatedTable.row_headers = Array(table.num_rows).fill(null).map((_, i) => `Row ${i + 1}`);
        }

        return updatedTable;
      }

      // Update subtable headers (stored in row_subtables)
      if (subtableId) {
        const updatedTable = { ...table };

        // Helper function to recursively update subtable (returns new subtables object)
        const updateInSubtables = (subtables) => {
          if (!subtables) return null;

          const newSubtables = {};
          let found = false;

          for (const rowKey in subtables) {
            newSubtables[rowKey] = subtables[rowKey].map(subtable => {
              if (subtable.id === subtableId) {
                // Found it - update headers
                found = true;
                const newShowHeaders = headerType === 'column' ? !subtable.show_headers : subtable.show_headers;
                const newShowRowHeaders = headerType === 'row' ? !subtable.show_row_headers : subtable.show_row_headers;

                // Initialize headers if toggling on and they don't exist
                const updatedSubtable = {
                  ...subtable,
                  show_headers: newShowHeaders,
                  show_row_headers: newShowRowHeaders
                };

                // Initialize column_headers if toggling column headers on
                if (headerType === 'column' && newShowHeaders && !subtable.column_headers) {
                  updatedSubtable.column_headers = Array(subtable.num_cols).fill(null).map((_, i) => `Column ${i + 1}`);
                }

                // Initialize row_headers if toggling row headers on
                if (headerType === 'row' && newShowRowHeaders && !subtable.row_headers) {
                  updatedSubtable.row_headers = Array(subtable.num_rows).fill(null).map((_, i) => `Row ${i + 1}`);
                }

                return updatedSubtable;
              }
              // Recursively check nested subtables
              if (subtable.row_subtables) {
                const updatedNestedSubtables = updateInSubtables(subtable.row_subtables);
                if (updatedNestedSubtables) {
                  found = true;
                  return {
                    ...subtable,
                    row_subtables: updatedNestedSubtables
                  };
                }
              }
              return subtable;
            });
          }

          return found ? newSubtables : null;
        };

        if (updatedTable.row_subtables) {
          const updatedSubtables = updateInSubtables(updatedTable.row_subtables);
          if (updatedSubtables) {
            updatedTable.row_subtables = updatedSubtables;
          }
        }

        return updatedTable;
      }

      // Update nested table headers (stored in table.rows cells)
      if (nestedTableId) {
        const updatedTable = { ...table };

        // Helper function to recursively update nested table
        const updateInRows = (rows) => {
          if (!rows) return false;

          for (let i = 0; i < rows.length; i++) {
            for (let j = 0; j < rows[i].length; j++) {
              const cell = rows[i][j];
              if (cell && typeof cell === 'object' && cell.type === 'nested_table' && cell.id === nestedTableId) {
                const newShowHeaders = headerType === 'column' ? !cell.show_headers : cell.show_headers;
                const newShowRowHeaders = headerType === 'row' ? !cell.show_row_headers : cell.show_row_headers;

                const updatedCell = {
                  ...cell,
                  show_headers: newShowHeaders,
                  show_row_headers: newShowRowHeaders
                };

                // Initialize column_headers if toggling column headers on
                if (headerType === 'column' && newShowHeaders && !cell.column_headers) {
                  updatedCell.column_headers = Array(cell.num_cols).fill(null).map((_, i) => `Column ${i + 1}`);
                }

                // Initialize row_headers if toggling row headers on
                if (headerType === 'row' && newShowRowHeaders && !cell.row_headers) {
                  updatedCell.row_headers = Array(cell.num_rows).fill(null).map((_, i) => `Row ${i + 1}`);
                }

                rows[i][j] = updatedCell;
                return true;
              }
            }
          }
          return false;
        };

        if (updatedTable.rows) {
          updatedTable.rows = JSON.parse(JSON.stringify(updatedTable.rows));
          updateInRows(updatedTable.rows);
        }

        return updatedTable;
      }

      return table;
    }));

    // Then sync with backend
    try {
      if (contextPath) {
        // ✅ UPDATED: Use new contextPath-based API endpoint for unlimited depth support
        const apiEndpoint = headerType === 'row'
          ? `${API_URL}/api/tables/${contextPath.tableId}/toggle-by-path/row-headers`
          : `${API_URL}/api/tables/${contextPath.tableId}/toggle-by-path/headers`;

        await authFetch(apiEndpoint, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath })
        });
      } else {
        // Legacy parameters - use old endpoint
        await authFetch(`${API_URL}${endpoint}`, { method: 'PUT' });
      }
    } catch (error) {
      console.error(`Error toggling ${headerType} headers:`, error);
      // Revert on error by fetching fresh data
      fetchTables();
    }
  };

  // ✅ REFACTORED: Wrapper functions now support contextPath
  const toggleTableHeaders = (tableId) =>
    toggleHeaders('column', { tableId });

  const toggleTableRowHeaders = (tableId) =>
    toggleHeaders('row', { tableId });

  const toggleSubtableHeaders = (tableIdOrContextPath, subtableId) => {
    // Support both legacy (tableId, subtableId) and new (contextPath) signatures
    if (typeof tableIdOrContextPath === 'object' && tableIdOrContextPath.path) {
      // New: contextPath passed
      return toggleHeaders('column', { contextPath: tableIdOrContextPath });
    }
    // Legacy: (tableId, subtableId) passed
    return toggleHeaders('column', { tableId: tableIdOrContextPath, subtableId });
  };

  const toggleSubtableRowHeaders = (tableIdOrContextPath, subtableId) => {
    if (typeof tableIdOrContextPath === 'object' && tableIdOrContextPath.path) {
      return toggleHeaders('row', { contextPath: tableIdOrContextPath });
    }
    return toggleHeaders('row', { tableId: tableIdOrContextPath, subtableId });
  };

  const toggleNestedTableHeaders = (tableIdOrContextPath, nestedTableId) => {
    if (typeof tableIdOrContextPath === 'object' && tableIdOrContextPath.path) {
      return toggleHeaders('column', { contextPath: tableIdOrContextPath });
    }
    return toggleHeaders('column', { tableId: tableIdOrContextPath, nestedTableId });
  };

  const toggleNestedTableRowHeaders = (tableIdOrContextPath, nestedTableId) => {
    if (typeof tableIdOrContextPath === 'object' && tableIdOrContextPath.path) {
      return toggleHeaders('row', { contextPath: tableIdOrContextPath });
    }
    return toggleHeaders('row', { tableId: tableIdOrContextPath, nestedTableId });
  };

  // Header update functions (keep as-is, not duplicated)
  const updateColumnHeader = async (tableId, colIndex, label) => {
    // Optimistically update the UI immediately
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== tableId) return table;
      const updatedTable = { ...table };
      if (!updatedTable.column_headers) {
        updatedTable.column_headers = Array(updatedTable.num_cols).fill(null).map((_, i) => `Column ${i + 1}`);
      }
      updatedTable.column_headers = [...updatedTable.column_headers];
      updatedTable.column_headers[colIndex] = label;
      return updatedTable;
    }));

    try {
      await authFetch(
        `${API_URL}/api/tables/${tableId}/update-header?col_index=${colIndex}&label=${encodeURIComponent(label || '')}`,
        { method: 'PUT' }
      );
      // Don't fetch tables - we already updated optimistically
    } catch (error) {
      console.error('Error updating header:', error);
      // On error, refetch to restore correct state
      await fetchTables();
    }
  };

  const updateRowHeader = async (tableId, rowIndex, label) => {
    // Optimistically update the UI immediately
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== tableId) return table;
      const updatedTable = { ...table };
      if (!updatedTable.row_headers) {
        updatedTable.row_headers = Array(updatedTable.num_rows).fill(null).map((_, i) => `Row ${i + 1}`);
      }
      updatedTable.row_headers = [...updatedTable.row_headers];
      updatedTable.row_headers[rowIndex] = label;
      return updatedTable;
    }));

    try {
      await authFetch(
        `${API_URL}/api/tables/${tableId}/update-row-header?row_index=${rowIndex}&label=${encodeURIComponent(label || '')}`,
        { method: 'PUT' }
      );
      // Don't fetch tables - we already updated optimistically
    } catch (error) {
      console.error('Error updating row header:', error);
      // On error, refetch to restore correct state
      await fetchTables();
    }
  };

  const updateSubtableColumnHeader = async (contextPath, colIndex, label) => {
    // ✅ REFACTORED: Now uses contextPath with recursive traversal
    // Optimistically update the UI immediately with RECURSIVE search
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;

      // Use recursive traversal helper to find and update at any depth
      return traverseAndUpdate(table, contextPath, (structure) =>
        updateColumnHeaderAtPath(structure, colIndex, label)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint that supports unlimited depth
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/column-header`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contextPath,
            col_index: colIndex,
            label
          })
        }
      );
      // ✅ KEEP: Don't fetch tables - we already updated optimistically
    } catch (error) {
      console.error('Error updating subtable column header:', error);
      // ✅ KEEP: On error, refetch to restore correct state
      await fetchTables();
    }
  };

  const updateSubtableRowHeader = async (contextPath, rowIndex, label) => {
    // ✅ REFACTORED: Now uses contextPath with recursive traversal
    // Optimistically update the UI immediately with RECURSIVE search
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;

      // Use recursive traversal helper to find and update at any depth
      return traverseAndUpdate(table, contextPath, (structure) =>
        updateRowHeaderAtPath(structure, rowIndex, label)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint (works for subtables AND nested tables)
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/row-header`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contextPath,
            row_index: rowIndex,
            label
          })
        }
      );
      // ✅ KEEP: Don't fetch tables - we already updated optimistically
    } catch (error) {
      console.error('Error updating subtable row header:', error);
      // ✅ KEEP: On error, refetch to restore correct state
      await fetchTables();
    }
  };

  const updateNestedTableColumnHeader = async (contextPath, colIndex, label) => {
    // ✅ REFACTORED: Now uses contextPath with recursive traversal
    // Optimistically update the UI immediately with RECURSIVE search
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;

      // Use recursive traversal helper to find and update at any depth
      return traverseAndUpdate(table, contextPath, (structure) =>
        updateColumnHeaderAtPath(structure, colIndex, label)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint (same as subtables)
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/column-header`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contextPath,
            col_index: colIndex,
            label
          })
        }
      );
      // ✅ KEEP: Don't fetch tables - we already updated optimistically
    } catch (error) {
      console.error('Error updating nested table column header:', error);
      // ✅ KEEP: On error, refetch to restore correct state
      await fetchTables();
    }
  };

  const updateNestedTableRowHeader = async (contextPath, rowIndex, label) => {
    // ✅ REFACTORED: Now uses contextPath with recursive traversal
    // Optimistically update the UI immediately with RECURSIVE search
    setTables(prevTables => prevTables.map(table => {
      if (table.id !== contextPath.tableId) return table;

      // Use recursive traversal helper to find and update at any depth
      return traverseAndUpdate(table, contextPath, (structure) =>
        updateRowHeaderAtPath(structure, rowIndex, label)
      );
    }));

    try {
      // ✅ UPDATED: Use new contextPath-based API endpoint (same as subtables)
      await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/row-header`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contextPath,
            row_index: rowIndex,
            label
          })
        }
      );
      // ✅ KEEP: Don't fetch tables - we already updated optimistically
    } catch (error) {
      console.error('Error updating nested table row header:', error);
      // ✅ KEEP: On error, refetch to restore correct state
      await fetchTables();
    }
  };

  const updateTableName = async (tableId, name) => {
    try {
      console.log('[updateTableName] Starting update:', { tableId, name });

      // STEP 1: Optimistic update - apply changes immediately
      setTables(prevTables => prevTables.map(t =>
        t.id === tableId ? { ...t, name } : t
      ));
      setTabs(prevTabs => prevTabs.map(tab => {
        if (tab.id === activeTabId) {
          return {
            ...tab,
            tables: tab.tables.map(t => t.id === tableId ? { ...t, name } : t)
          };
        }
        return tab;
      }));

      // STEP 2: Send to backend
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/name`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name })
        }
      );

      if (response.ok) {
        console.log('[updateTableName] Successfully updated name');
        // Optimistic update already applied
      } else {
        console.error('[updateTableName] Failed to update table name. Status:', response.status);
        await fetchTables(); // Rollback on error
      }
    } catch (error) {
      console.error('[updateTableName] Error updating table name:', error);
      await fetchTables(); // Rollback on error
    }
  };

  const updateTableDescription = async (tableId, description) => {
    try {
      console.log('[updateTableDescription] Updating description:', { tableId, description });

      // STEP 1: Optimistic update - apply changes immediately
      setTables(prevTables => {
        const updated = prevTables.map(t =>
          t.id === tableId ? { ...t, description } : t
        );
        console.log('[updateTableDescription] Updated tables:', updated.find(t => t.id === tableId)?.description);
        return updated;
      });
      setTabs(prevTabs => prevTabs.map(tab => {
        if (tab.id === activeTabId) {
          return {
            ...tab,
            tables: tab.tables.map(t => t.id === tableId ? { ...t, description } : t)
          };
        }
        return tab;
      }));

      // STEP 2: Send to backend
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/description`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ description })
        }
      );

      if (!response.ok) {
        console.error('[updateTableDescription] Failed to update table description. Status:', response.status);
        await fetchTables(); // Rollback on error
      } else {
        console.log('[updateTableDescription] Successfully saved description');
      }
    } catch (error) {
      console.error('[updateTableDescription] Error updating table description:', error);
      await fetchTables(); // Rollback on error
    }
  };

  const updateTableDescriptionWidth = async (tableId, width) => {
    try {
      // STEP 1: Optimistic update - apply changes immediately
      setTables(prevTables => prevTables.map(t =>
        t.id === tableId ? { ...t, description_width: width } : t
      ));
      setTabs(prevTabs => prevTabs.map(tab => {
        if (tab.id === activeTabId) {
          return {
            ...tab,
            tables: tab.tables.map(t => t.id === tableId ? { ...t, description_width: width } : t)
          };
        }
        return tab;
      }));

      // STEP 2: Send to backend
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/description-width`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ width })
        }
      );

      if (!response.ok) {
        console.error('[updateTableDescriptionWidth] Failed to update description width. Status:', response.status);
        await fetchTables(); // Rollback on error
      }
    } catch (error) {
      console.error('[updateTableDescriptionWidth] Error updating description width:', error);
      await fetchTables(); // Rollback on error
    }
  };

  // ✅ UPDATED: Update name at any depth using contextPath
  const updateSubtableName = async (contextPath, name) => {
    try {
      console.log('[updateSubtableName] Starting update:', { contextPath, name });

      // STEP 1: Optimistically update name in UI
      setTables(prevTables => prevTables.map(table => {
        if (table.id !== contextPath.tableId) return table;
        return traverseAndUpdate(table, contextPath, (structure) => ({
          ...structure,
          name
        }));
      }));

      // STEP 2: Send update to backend
      const response = await authFetch(
        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/update-name`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ contextPath, name })
        }
      );

      if (response.ok) {
        console.log('[updateSubtableName] Successfully updated name');
        // Optimistic update already applied, no fetchTables needed
      } else {
        console.error('[updateSubtableName] Failed to update name. Status:', response.status);
        await fetchTables(); // Rollback on error
      }
    } catch (error) {
      console.error('[updateSubtableName] Error updating name:', error);
      await fetchTables(); // Rollback on error
    }
  };

  // Alias for backward compatibility
  const updateNestedTableName = updateSubtableName;

  // ✅ REMOVED: Old deleteNestedTable function replaced with alias at line 2834

  // Create chart directly with default data
  const createChart = async (contextPath, rowIdx, colIdx, xData = [1, 2, 3], yData = [1, 4, 2]) => {
    console.log('[createChart] Called with:', { contextPath, rowIdx, colIdx, xData, yData });
    const { tableId } = contextPath;

    const chartCell = {
      type: 'chart',
      xData,
      yData,
      chartType: 'line',
      title: 'Line Chart',
      expanded: true,
      showDataTable: false,
      _optimistic: true,
    };

    console.log('[createChart] Created chartCell:', chartCell);

    // STEP 1: Optimistic update
    setTables(prevTables => {
      const newTables = prevTables.map(t => {
        if (t.id !== tableId) return t;

        const updatedTable = JSON.parse(JSON.stringify(t));
        const result = traverseAndUpdate(updatedTable, contextPath, (target) => {
          if (target.rows && target.rows[rowIdx] && target.rows[rowIdx][colIdx] !== undefined) {
            target.rows[rowIdx][colIdx] = chartCell;
            return target;
          }
          return target;
        });

        return result;
      });

      return newTables;
    });

    setShowContextMenu(false);

    // STEP 2: Send to backend
    try {
      console.log('[createChart] Sending request to backend...');
      const response = await authFetch(
        `${API_URL}/api/tables/${tableId}/update-by-path/create-chart`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            contextPath,
            row_index: rowIdx,
            col_index: colIdx,
            x_data: xData,
            y_data: yData
          })
        }
      );

      console.log('[createChart] Response received, status:', response.status, 'ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[createChart] Create chart failed:', response.status, errorText);

        // ROLLBACK optimistic update
        console.log('[createChart] Rolling back optimistic update due to failed response');
        setTables(prevTables => prevTables.map(t => {
          if (t.id !== tableId) return t;

          const rolledBackTable = JSON.parse(JSON.stringify(t));
          return traverseAndUpdate(rolledBackTable, contextPath, (target) => {
            if (target.rows && target.rows[rowIdx] && target.rows[rowIdx][colIdx]) {
              target.rows[rowIdx][colIdx] = "";
            }
            return target;
          });
        }));

        alert(`Failed to create chart: ${errorText}`);
        return;
      }

      // STEP 3: Confirm with backend response
      const updatedTable = await response.json();

      // Remove _optimistic flag (backend confirmed success)
      setTables(prevTables => prevTables.map(t => {
        if (t.id !== tableId) return t;

        const confirmedTable = JSON.parse(JSON.stringify(t));
        return traverseAndUpdate(confirmedTable, contextPath, (target) => {
          if (target.rows && target.rows[rowIdx] && target.rows[rowIdx][colIdx]) {
            const cell = target.rows[rowIdx][colIdx];
            if (cell && typeof cell === 'object' && cell.type === 'chart') {
              delete cell._optimistic;
            }
          }
          return target;
        });
      }));

    } catch (error) {
      console.error('[createChart] Exception caught:', error);
      console.error('[createChart] Error details:', error.message, error.stack);

      // ROLLBACK on error
      setTables(prevTables => prevTables.map(t => {
        if (t.id !== tableId) return t;

        const rolledBackTable = JSON.parse(JSON.stringify(t));
        return traverseAndUpdate(rolledBackTable, contextPath, (target) => {
          if (target.rows && target.rows[rowIdx] && target.rows[rowIdx][colIdx]) {
            target.rows[rowIdx][colIdx] = "";
          }
          return target;
        });
      }));

      alert(`Error: ${error.message}`);
    }
  };

  // Create chart with X/Y data
  const createChartWithData = (tableId, rowIdx, colIdx, xData, yData, title, subtableId = null, nestedTableId = null) => {
    const chartCell = {
      type: 'chart',
      xData: xData,  // Array of X values
      yData: yData,  // Array of Y values
      chartType: 'line',
      title: title || 'Chart',
      expanded: false,
      showDataTable: false  // Collapsible data table state
    };

    // ✅ UPDATED: Use updateCellGeneric with contextPath for unlimited depth support
    const contextPath = createContextPath(tableId, subtableId, nestedTableId);
    updateCellGeneric(tableId, rowIdx, colIdx, chartCell, { contextPath });
  };


  // Toggle chart expanded/collapsed
  const toggleChart = (tableId, rowIdx, colIdx, currentCell, subtableId = null, nestedTableId = null, contextPath = null) => {
    const updatedCell = {
      ...currentCell,
      expanded: !currentCell.expanded
    };

    // Use updateCellGeneric with context path for deep nesting support
    updateCellGeneric(tableId, rowIdx, colIdx, updatedCell, { subtableId, nestedTableId, contextPath });
  };

  // Helper functions to manage date picker open/close state
  const getDatePickerKey = (tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null) => {
    return `${tableId}-${rowIdx}-${colIdx}-${subtableId || 'none'}-${nestedTableId || 'none'}`;
  };

  const toggleDatePicker = (tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null) => {
    const key = getDatePickerKey(tableId, rowIdx, colIdx, subtableId, nestedTableId);
    setOpenDatePickers(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const closeDatePicker = (tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null) => {
    const key = getDatePickerKey(tableId, rowIdx, colIdx, subtableId, nestedTableId);
    setOpenDatePickers(prev => ({
      ...prev,
      [key]: false
    }));
  };

  const isDatePickerOpen = (tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null) => {
    const key = getDatePickerKey(tableId, rowIdx, colIdx, subtableId, nestedTableId);
    return openDatePickers[key] || false;
  };

  // Create a datepicker in a cell
  const createDatePicker = (tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null, contextPath = null) => {
    const dateCell = {
      type: 'date',
      value: new Date().toISOString(), // Current date/time as default
      displayFormat: 'MM/dd/yyyy h:mm aa' // US format with time
    };

    // Use updateCellGeneric with context path for deep nesting support
    updateCellGeneric(tableId, rowIdx, colIdx, dateCell, { subtableId, nestedTableId, contextPath });

    setShowContextMenu(false); // Close menu
  };

  // Create a color picker in a cell
  const createColorPicker = (tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null, contextPath = null) => {
    const colorCell = {
      type: 'color',
      value: '#3b82f6' // Default blue color
    };

    // Use updateCellGeneric with context path for deep nesting support
    updateCellGeneric(tableId, rowIdx, colIdx, colorCell, { subtableId, nestedTableId, contextPath });

    setShowContextMenu(false); // Close menu
  };

  const createPriorityPicker = (tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null, contextPath = null) => {
    const priorityCell = {
      type: 'priority',
      value: 'medium' // Default to medium priority
    };

    // Use updateCellGeneric with context path for deep nesting support
    updateCellGeneric(tableId, rowIdx, colIdx, priorityCell, { subtableId, nestedTableId, contextPath });

    setShowContextMenu(false); // Close menu
  };

  const createDonePicker = (tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null, contextPath = null) => {
    const doneCell = {
      type: 'done',
      value: 'checkmark' // Default to checkmark
    };

    // Use updateCellGeneric with context path for deep nesting support
    updateCellGeneric(tableId, rowIdx, colIdx, doneCell, { subtableId, nestedTableId, contextPath });

    setShowContextMenu(false); // Close menu
  };

  // Open table reference modal
  const openTableReferenceModal = async (tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null, contextPath = null) => {
    // Fetch available tables for this table
    try {
      const response = await authFetch(`${API_URL}/api/tables/available-for-reference/${tableId}`);
      const tables = await response.json();
      console.log('Available tables:', tables);
      // Ensure tables is an array
      setAvailableTables(Array.isArray(tables) ? tables : []);
      setShowTableReferenceModal({ tableId, rowIdx, colIdx, subtableId, nestedTableId, contextPath });
      setShowContextMenu(false); // Close context menu
    } catch (error) {
      console.error('Error fetching available tables:', error);
      setAvailableTables([]); // Set to empty array on error
    }
  };

  // Create table reference
  const createTableReference = async (referencedTableId, referencedTableName) => {
    if (!showTableReferenceModal) return;

    const { tableId, rowIdx, rowIndex, colIdx, subtableId, nestedTableId, contextPath, isSubtableReference, parentSubtableId } = showTableReferenceModal;

    // Check if this is a subtable reference (not a cell reference)
    if (isSubtableReference) {
      try {
        // Use rowIndex for subtable references (from showSubtableModal), rowIdx for cell references
        const actualRowIndex = rowIndex !== undefined ? rowIndex : rowIdx;

        let url;
        if (parentSubtableId) {
          // Adding reference to a subtable (nested)
          url = `${API_URL}/api/tables/${tableId}/subtable/${parentSubtableId}/subtable-reference?row_index=${actualRowIndex}&referenced_table_id=${referencedTableId}`;
        } else if (nestedTableId) {
          // Adding reference to a nested table
          url = `${API_URL}/api/tables/${tableId}/subtable-reference?row_index=${actualRowIndex}&referenced_table_id=${referencedTableId}&nested_table_id=${nestedTableId}`;
        } else {
          // Adding reference to main table
          url = `${API_URL}/api/tables/${tableId}/subtable-reference?row_index=${actualRowIndex}&referenced_table_id=${referencedTableId}`;
        }

        console.log('[DEBUG] Creating subtable reference with:', { tableId, actualRowIndex, referencedTableId, parentSubtableId, nestedTableId, url });
        const response = await authFetch(url, { method: 'POST' });

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Add subtable reference failed:', response.status, errorText);
          alert(`Failed to add subtable reference: ${errorText}`);
          setShowTableReferenceModal(null);
          return;
        }

        const updatedTable = await response.json();
        setTables(tables.map(t => {
          if (t.id === tableId) {
            // Preserve shared table metadata when updating
            return {
              ...updatedTable,
              isShared: t.isShared,
              sharedBy: t.sharedBy,
              sharedPermission: t.sharedPermission,
              shareId: t.shareId
            };
          }
          return t;
        }));
        setShowTableReferenceModal(null);
      } catch (error) {
        console.error('Error adding subtable reference:', error);
        alert(`Error: ${error.message}`);
        setShowTableReferenceModal(null);
      }
      return;
    }

    // Otherwise, create a cell reference (existing behavior)
    const referenceCell = {
      type: 'table_reference',
      table_id: referencedTableId,
      table_name: referencedTableName,
      expanded: false
    };

    console.log('[createTableReference] Creating reference with context:', {
      tableId, rowIdx, colIdx, subtableId, nestedTableId, contextPath,
      pathString: contextPath ? pathToString(contextPath) : 'no path'
    });

    // Use updateCellGeneric with context path for deep nesting support
    await updateCellGeneric(tableId, rowIdx, colIdx, referenceCell, { contextPath });

    setShowTableReferenceModal(null); // Close modal
    fetchTables(); // Refresh to get updated data
  };

  // Toggle table reference expansion
  const toggleTableReference = (tableId, rowIdx, colIdx, currentCell, options = {}) => {
    const { subtableId = null, nestedTableId = null, contextPath = null } = options;

    const updatedCell = {
      ...currentCell,
      expanded: !currentCell.expanded
    };

    // Use contextPath if provided, otherwise create from legacy parameters
    const actualContextPath = contextPath || createContextPath(tableId, subtableId, nestedTableId);

    console.log('[toggleTableReference] Context path details:', {
      tableId,
      rowIdx,
      colIdx,
      subtableId,
      nestedTableId,
      contextPath,
      actualContextPath,
      pathString: pathToString(actualContextPath)
    });

    updateCellGeneric(tableId, rowIdx, colIdx, updatedCell, { contextPath: actualContextPath });
  };

  // Update datepicker value
  const updateDatePicker = (tableId, rowIdx, colIdx, currentCell, newDate, subtableId = null, nestedTableId = null) => {
    const updatedCell = {
      ...currentCell,
      value: newDate.toISOString()
    };

    // ✅ UPDATED: Use updateCellGeneric with contextPath for unlimited depth support
    const contextPath = createContextPath(tableId, subtableId, nestedTableId);
    updateCellGeneric(tableId, rowIdx, colIdx, updatedCell, { contextPath });
  };

  // Resize handle component
  // Now supports two modes:
  // 1. onResize only: backward compatible, calls on every mousemove
  // 2. onResizePreview + onResizeCommit: preview during drag, commit on mouseup
  const ResizeHandle = ({ direction, onResize, onResizePreview, onResizeCommit }) => {
    const handleMouseDown = (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Commit any in-progress cell edit before starting the resize.
      // Cells are uncontrolled (EditableCellTextarea): typing lives only in the DOM
      // until a blur/Enter fires persistCellValue. The e.preventDefault() above stops
      // the focused textarea from blurring on its own, so without this the edit would
      // stay uncommitted and could be lost. Blur it explicitly to fire its onBlur →
      // persistCellValue. Cheap: runs only on resize mousedown, only when a text field
      // is focused — never during the drag.
      const active = document.activeElement;
      if (active && (active.tagName === 'TEXTAREA' || active.tagName === 'INPUT') &&
          typeof active.blur === 'function') {
        active.blur();
      }

      // Prevent text selection during drag
      const originalUserSelect = document.body.style.userSelect;
      const originalCursor = document.body.style.cursor;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = direction === 'col' ? 'col-resize' : 'row-resize';

      const startPos = direction === 'col' ? e.clientX : e.clientY;
      const startSize = direction === 'col' ?
        e.target.parentElement.offsetWidth :
        e.target.parentElement.offsetHeight;

      let currentSize = startSize;

      const handleMouseMove = (moveEvent) => {
        moveEvent.preventDefault();
        const currentPos = direction === 'col' ? moveEvent.clientX : moveEvent.clientY;
        const delta = currentPos - startPos;
        const newSize = Math.max(30, startSize + delta);
        currentSize = newSize;

        // Use preview callback if provided, otherwise fall back to onResize
        if (onResizePreview) {
          onResizePreview(newSize);
        } else if (onResize) {
          onResize(newSize);
        }
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove, { passive: false });
        document.removeEventListener('mouseup', handleMouseUp, { passive: false });

        // Restore original styles
        document.body.style.userSelect = originalUserSelect;
        document.body.style.cursor = originalCursor;

        // Call commit callback with final size if provided
        if (onResizeCommit) {
          onResizeCommit(currentSize);
        }
      };

      document.addEventListener('mousemove', handleMouseMove, { passive: false });
      document.addEventListener('mouseup', handleMouseUp, { passive: false });
    };

    return (
      <div
        className={`resize-handle-${direction}`}
        onMouseDown={handleMouseDown}
      />
    );
  };

  // Recursive component to render subtables
  // NEW: Now accepts contextPath for proper deep nesting tracking
  const renderSubtablesRecursive = (tableId, subtable, depth = 1, parentPath = null) => {
    const isExpanded = subtable.expanded !== false; // Default to true if not specified

    // Check if this is a reference subtable
    if (subtable.is_reference && subtable.referenced_table_id) {
      // Find the referenced table
      const referencedTable = tables.find(t => t.id === subtable.referenced_table_id);
      if (!referencedTable) {
        return (
          <div key={subtable.id} className="subtable-container" style={{ marginLeft: `${depth * 2}rem` }}>
            <div className="subtable-header">
              <span style={{ color: '#ef4444' }}>⚠️ Referenced table not found</span>
            </div>
          </div>
        );
      }

      // Render the referenced table in place of the subtable
      const table = tables.find(t => t.id === tableId);
      const readOnly = isTableReadOnly(table);

      // Build context path for this reference subtable
      const contextPath = parentPath
        ? extendPath(parentPath, { type: 'subtable', id: subtable.id })
        : extendPath(createContextPath(tableId, null, null), { type: 'subtable', id: subtable.id });

      return (
        <div
          key={subtable.id}
          className="subtable-container"
          style={{
            marginLeft: '4rem',
            backgroundColor: borderStyle === 'minimalist' ? undefined : getDepthBackground(depth, theme, colorScheme, depthShading, false, customColor)
          }}
        >
          {renderControlHeader({
            isExpanded,
            title: `🔗 ${subtable.name}`,
            showToggle: false,
            controlId: borderStyle !== 'minimalist' && isExpanded ? `ref-subtable-${subtable.id}` : null,
            controlButtons: borderStyle !== 'minimalist' && isExpanded ? [
                {
                  label: 'Col',
                  onClick: () => {}, // Read-only, can't toggle
                  active: false,
                  title: 'Column headers (read-only reference)'
                },
                {
                  label: referencedTable.show_row_headers ? 'Row ✓' : 'Row',
                  onClick: () => {}, // Read-only, can't toggle
                  active: referencedTable.show_row_headers,
                  title: 'Row headers (read-only reference)'
                },
                ...(!readOnly ? [{
                  label: '🗑',
                  onClick: () => deleteSubtable(contextPath),
                  active: false,
                  title: 'Remove reference',
                  danger: true
                }] : [])
            ] : []
          })}
          {isExpanded && (
            <>
              {borderStyle === 'minimalist' && !referencedTable.show_headers && renderMinimalistControlsBar(`ref-subtable-${subtable.id}`, [
                {
                  label: 'Col',
                  onClick: () => {}, // Read-only, can't toggle
                  active: false,
                  title: 'Column headers (read-only reference)'
                },
                {
                  label: referencedTable.show_row_headers ? 'Row ✓' : 'Row',
                  onClick: () => {}, // Read-only, can't toggle
                  active: referencedTable.show_row_headers,
                  title: 'Row headers (read-only reference)'
                },
                ...(!readOnly ? [{
                  label: '🗑',
                  onClick: () => deleteSubtable(contextPath),
                  active: false,
                  title: 'Remove reference',
                  danger: true
                }] : [])
              ])}
              {borderStyle === 'minimalist' && (
                <div style={{
                  fontSize: '0.75rem',
                  color: '#666',
                  fontStyle: 'italic',
                  marginBottom: '4px',
                  marginLeft: '4px'
                }}>
                  📎 Reference to: {referencedTable.table_name || 'Untitled Table'}
                </div>
              )}
              <div className="nested-table-wrapper reference-table-wrapper">
                <table className="nested-table-inner">
                  {referencedTable.show_headers && renderTableHeaderRow({
                    columnHeaders: referencedTable.column_headers,
                    numCols: referencedTable.num_cols,
                    showRowHeaders: referencedTable.show_row_headers,
                    showControls: false,
                    theme: 'default',
                    sizing: 'medium',
                    controlsId: `ref-subtable-${subtable.id}`,
                    translationColumns: referencedTable.translation_columns,
                    extraButtons: borderStyle === 'minimalist' ? [
                      {
                        label: referencedTable.show_headers ? 'Col ✓' : 'Col',
                        onClick: () => {}, // Read-only
                        active: referencedTable.show_headers,
                        title: 'Column headers (read-only reference)'
                      },
                      {
                        label: referencedTable.show_row_headers ? 'Row ✓' : 'Row',
                        onClick: () => {}, // Read-only
                        active: referencedTable.show_row_headers,
                        title: 'Row headers (read-only reference)'
                      },
                      ...(!readOnly ? [{
                        label: '🗑',
                        onClick: () => deleteSubtable(contextPath),
                        active: false,
                        title: 'Remove reference',
                        danger: true
                      }] : [])
                    ] : null
                  })}
                <tbody>
                  {referencedTable.rows.map((refRow, refRowIdx) => {
                    const rowSubtables = referencedTable.row_subtables?.[`row-${refRowIdx}`] || [];

                    return (
                    <React.Fragment key={refRowIdx}>
                      <tr>
                        {/* No row controls for referenced tables (showControls: false in header) */}
                        {referencedTable.show_row_headers && (
                          <td className="table-row-header-cell" style={{ padding: '0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>
                            <LatexText text={(referencedTable.row_headers || [])[refRowIdx] || `Row ${refRowIdx + 1}`} />
                          </td>
                        )}
                        {refRow.map((refCell, refColIdx) => (
                          <td
                            key={refColIdx}
                            className="reference-table-cell"
                          >
                            {renderCell(referencedTable.id, refCell, refRowIdx, refColIdx, false, null, null)}
                          </td>
                        ))}
                      </tr>
                      {rowSubtables.filter(st => st.expanded !== false).length > 0 && (
                        <tr>
                          <td
                            colSpan={referencedTable.num_cols + (referencedTable.show_row_headers ? 1 : 0) + 1}
                            style={{ padding: 0, border: 'none' }}
                          >
                            {rowSubtables
                              .filter(st => st.expanded !== false)
                              .map((st) =>
                                renderSubtablesRecursive(referencedTable.id, st, 1, null)
                              )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      );
    }

    // Build context path for this subtable
    // Always extend the parent path (even if it's empty)
    const contextPath = parentPath
      ? extendPath(parentPath, { type: 'subtable', id: subtable.id })
      : extendPath(createContextPath(tableId, null, null), { type: 'subtable', id: subtable.id });

    // Extract legacy parameters for backward compatibility with existing functions
    const { subtableId, nestedTableId } = extractLegacyParams(contextPath);

    // Check if table is read-only
    const table = tables.find(t => t.id === tableId);
    const readOnly = isTableReadOnly(table);

    return (
      <div
        key={`${subtable.id}-${JSON.stringify(subtable.column_border_styles || {})}`}
        className="subtable-container"
        style={{
          marginLeft: '4rem',
          backgroundColor: borderStyle === 'minimalist' ? undefined : getDepthBackground(depth, theme, colorScheme, depthShading, false, customColor)
        }}
      >
        {renderControlHeader({
          isExpanded,
          title: subtable.name,
          showToggle: false,
          isEditing: editingSubtableName === subtable.id,
          editingValue: editingSubtableNameValue,
          onEditStart: () => {
            setEditingSubtableName(subtable.id);
            setEditingSubtableNameValue(subtable.name);
          },
          onEditChange: (e) => setEditingSubtableNameValue(e.target.value),
          onEditBlur: () => {
            const newName = editingSubtableNameValue.trim();
            if (newName && newName !== subtable.name) {
              // Update state first (synchronous)
              setTables(prevTables => prevTables.map(table => {
                if (table.id !== contextPath.tableId) return table;
                return traverseAndUpdate(table, contextPath, (structure) => ({
                  ...structure,
                  name: newName
                }));
              }));
              // Exit edit mode after state update
              setEditingSubtableName(null);
              // Send to backend
              const sendToBackend = async () => {
                try {
                  const response = await authFetch(
                    `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/update-name`,
                    {
                      method: 'PUT',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({ contextPath, name: newName })
                    }
                  );
                  if (!response.ok) {
                    await fetchTables();
                  }
                } catch (error) {
                  await fetchTables();
                }
              };
              sendToBackend();
            } else {
              setEditingSubtableName(null);
            }
          },
          onEditKeyDown: (e) => {
            if (e.key === 'Enter') {
              const newName = editingSubtableNameValue.trim();
              if (newName && newName !== subtable.name) {
                // Update state first (synchronous)
                setTables(prevTables => prevTables.map(table => {
                  if (table.id !== contextPath.tableId) return table;
                  return traverseAndUpdate(table, contextPath, (structure) => ({
                    ...structure,
                    name: newName
                  }));
                }));
                // Exit edit mode after state update
                setEditingSubtableName(null);
                // Send to backend (don't do optimistic update again)
                const sendToBackend = async () => {
                  try {
                    const response = await authFetch(
                      `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/update-name`,
                      {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ contextPath, name: newName })
                      }
                    );
                    if (!response.ok) {
                      await fetchTables(); // Rollback on error
                    }
                  } catch (error) {
                    await fetchTables(); // Rollback on error
                  }
                };
                sendToBackend();
              } else {
                setEditingSubtableName(null);
              }
            } else if (e.key === 'Escape') {
              setEditingSubtableName(null);
            }
          },
          controlId: borderStyle !== 'minimalist' ? `subtable-${subtable.id}` : null,
          controlButtons: borderStyle !== 'minimalist' ? [
              {
                label: '📷',
                onClick: () => captureElementScreenshot(`.subtable-container:has([data-subtable-id="${subtable.id}"])`, subtable.name, 'subtable'),
                active: true,
                title: 'Capture screenshot of this subtable'
              },
              {
                label: subtable.show_headers ? 'Col ✓' : 'Col',
                // ✅ UPDATED: Now passes contextPath
                onClick: () => toggleSubtableHeaders(contextPath),
                active: subtable.show_headers,
                title: subtable.show_headers ? 'Hide column headers' : 'Show column headers'
              },
              {
                label: subtable.show_row_headers ? 'Row ✓' : 'Row',
                // ✅ UPDATED: Now passes contextPath
                onClick: () => toggleSubtableRowHeaders(contextPath),
                active: subtable.show_row_headers,
                title: subtable.show_row_headers ? 'Hide row headers' : 'Show row headers'
              },
              ...(!readOnly && subtable.show_headers ? [{
                label: '+Hdr Row',
                onClick: () => addHeaderRowAtPath(contextPath),
                active: false,
                title: 'Add header row level above existing headers',
                style: {
                  marginLeft: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  background: 'var(--accent-primary)',
                  color: 'white',
                  fontWeight: 600
                }
              }] : []),
              {
                label: subtablesEnabled[subtable.id] ? 'Subtables ✓' : 'Subtables',
                onClick: () => setSubtablesEnabled(prev => ({ ...prev, [subtable.id]: !prev[subtable.id] })),
                active: subtablesEnabled[subtable.id],
                title: subtablesEnabled[subtable.id] ? 'Hide subtables column and controls' : 'Show subtables column and controls'
              },
              ...(!readOnly ? [
                {
                  label: '🗑',
                  onClick: () => deleteSubtable(contextPath),
                  active: false,
                  title: 'Delete subtable',
                  danger: true
                }
              ] : [])
          ] : []
        })}
        {isExpanded && (
          <>
            {borderStyle === 'minimalist' && !subtable.show_headers && renderMinimalistControlsBar(`subtable-${subtable.id}`, [
              {
                label: '📷',
                onClick: () => captureElementScreenshot(`.subtable-container:has([data-subtable-id="${subtable.id}"])`, subtable.name, 'subtable'),
                active: true,
                title: 'Capture screenshot of this subtable'
              },
              {
                label: subtable.show_headers ? 'Col ✓' : 'Col',
                // ✅ UPDATED: Now passes contextPath
                onClick: () => toggleSubtableHeaders(contextPath),
                active: subtable.show_headers,
                title: subtable.show_headers ? 'Hide column headers' : 'Show column headers'
              },
              {
                label: subtable.show_row_headers ? 'Row ✓' : 'Row',
                // ✅ UPDATED: Now passes contextPath
                onClick: () => toggleSubtableRowHeaders(contextPath),
                active: subtable.show_row_headers,
                title: subtable.show_row_headers ? 'Hide row headers' : 'Show row headers'
              },
              ...(!readOnly && subtable.show_headers ? [{
                label: '+Hdr Row',
                onClick: () => addHeaderRowAtPath(contextPath),
                active: false,
                title: 'Add header row level above existing headers',
                style: {
                  marginLeft: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  background: 'var(--accent-primary)',
                  color: 'white',
                  fontWeight: 600
                }
              }] : []),
              {
                label: subtablesEnabled[subtable.id] ? 'Subtables ✓' : 'Subtables',
                onClick: () => setSubtablesEnabled(prev => ({ ...prev, [subtable.id]: !prev[subtable.id] })),
                active: subtablesEnabled[subtable.id],
                title: subtablesEnabled[subtable.id] ? 'Hide subtables column and controls' : 'Show subtables column and controls'
              },
              ...(!readOnly ? [
                {
                  label: '🗑',
                  onClick: () => deleteSubtable(contextPath),
                  active: false,
                  title: 'Delete subtable',
                  danger: true
                }
              ] : [])
            ])}
            <table className="subtable" data-subtable-id={subtable.id}>
              {subtable.show_headers && (
                <thead>
                  {/* Render additional header rows if they exist */}
                  {subtable.header_rows && subtable.header_rows.map((headerRow, headerRowIdx) => (
                    <tr key={`header-row-${headerRowIdx}`}>
                      {/* Only render corner cells in first header row, with rowspan to cover all header rows + main header row */}
                      {headerRowIdx === 0 && subtablesEnabled[subtable.id] && subtable.show_row_headers && (() => {
                        const allSubs = getAllTableSubtables(subtable);
                        const anyExp = allSubs.some(st => st.expanded !== false);
                        return (
                        <th className="row-controls" rowSpan={(subtable.header_rows?.length || 0) + 1} colSpan={2}>
                          {allSubs.length > 0 && (
                            <button
                              onClick={() => {
                                const ids = allSubs.filter(st => anyExp ? st.expanded !== false : st.expanded === false).map(st => st.id);
                                if (ids.length > 0) bulkSetExpanded(ids, !anyExp, (id) => extendPath(contextPath, { type: 'subtable', id }));
                              }}
                              title={anyExp ? 'Hide all subtables' : 'Show all subtables'}
                              style={{ fontSize: '0.65rem', padding: '2px 5px', border: `1px solid ${theme === 'dark' ? '#666' : '#ccc'}`, borderRadius: '3px', background: theme === 'dark' ? '#3a3a3a' : '#f5f5f5', color: theme === 'dark' ? '#ccc' : '#555', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              {anyExp ? '▼ Hide all' : '▶ Show all'}
                            </button>
                          )}
                        </th>
                        );
                      })()}
                      {headerRowIdx === 0 && subtablesEnabled[subtable.id] && !subtable.show_row_headers && (() => {
                        const allSubs = getAllTableSubtables(subtable);
                        const anyExp = allSubs.some(st => st.expanded !== false);
                        return (
                        <th className="row-controls" rowSpan={(subtable.header_rows?.length || 0) + 1}>
                          {allSubs.length > 0 && (
                            <button
                              onClick={() => {
                                const ids = allSubs.filter(st => anyExp ? st.expanded !== false : st.expanded === false).map(st => st.id);
                                if (ids.length > 0) bulkSetExpanded(ids, !anyExp, (id) => extendPath(contextPath, { type: 'subtable', id }));
                              }}
                              title={anyExp ? 'Hide all subtables' : 'Show all subtables'}
                              style={{ fontSize: '0.65rem', padding: '2px 5px', border: `1px solid ${theme === 'dark' ? '#666' : '#ccc'}`, borderRadius: '3px', background: theme === 'dark' ? '#3a3a3a' : '#f5f5f5', color: theme === 'dark' ? '#ccc' : '#555', cursor: 'pointer', whiteSpace: 'nowrap' }}
                            >
                              {anyExp ? '▼ Hide all' : '▶ Show all'}
                            </button>
                          )}
                        </th>
                        );
                      })()}
                      {headerRowIdx === 0 && !subtablesEnabled[subtable.id] && subtable.show_row_headers && (
                        <th className="table-row-header-cell" rowSpan={(subtable.header_rows?.length || 0) + 1}></th>
                      )}
                      {headerRow.map((headerCell, colIdx) => {
                        const colWidth = subtable.column_widths?.[colIdx] || 150;
                        const mergeInfo = subtable.header_rows_merged?.[`${headerRowIdx},${colIdx}`];
                        const colspan = mergeInfo?.colspan || 1;
                        const rowspan = mergeInfo?.rowspan || 1;

                        // Skip cells covered by merges
                        let isCovered = false;
                        if (subtable.header_rows_merged) {
                          for (const [key, merge] of Object.entries(subtable.header_rows_merged)) {
                            const [mergeRow, mergeCol] = key.split(',').map(Number);
                            if (mergeRow !== headerRowIdx || mergeCol !== colIdx) {
                              if (headerRowIdx >= mergeRow && headerRowIdx < mergeRow + merge.rowspan &&
                                  colIdx >= mergeCol && colIdx < mergeCol + merge.colspan) {
                                isCovered = true;
                                break;
                              }
                            }
                          }
                        }

                        if (isCovered) return null;

                        return (
                          <th
                            key={`header-${headerRowIdx}-${colIdx}`}
                            className="table-column-header-cell"
                            colSpan={colspan}
                            rowSpan={rowspan}
                            onContextMenu={(e) => {
                              if (!readOnly) {
                                e.preventDefault();
                                setSelectedHeaderCell({
                                  contextPath,
                                  headerRowIdx,
                                  colIdx,
                                  x: e.clientX,
                                  y: e.clientY
                                });
                              }
                            }}
                            style={{
                              width: `${colWidth * colspan}px`,
                              minWidth: `${colWidth * colspan}px`,
                              position: 'relative',
                              cursor: !readOnly ? 'context-menu' : 'default',
                              textAlign: 'center',
                              ...(borderStyle !== 'minimalist' && {
                                fontWeight: 600,
                                padding: '0.75rem',
                                fontSize: '0.85rem'
                              }),
                              ...getColumnBorderStyle(subtable.column_border_styles, colIdx)
                            }}
                          >
                            {readOnly ? (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{headerCell || ''}</div>
                            ) : (
                              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  value={headerCell || ''}
                                  onChange={(e) => {
                                  const newValue = e.target.value;
                                  // Optimistic update
                                  setTables(prevTables => prevTables.map(table => {
                                    if (table.id !== contextPath.tableId) return table;
                                    return traverseAndUpdate(table, contextPath, (structure) => {
                                      const updatedHeaderRows = [...(structure.header_rows || [])];
                                      updatedHeaderRows[headerRowIdx] = [...updatedHeaderRows[headerRowIdx]];
                                      updatedHeaderRows[headerRowIdx][colIdx] = newValue;
                                      return { ...structure, header_rows: updatedHeaderRows };
                                    });
                                  }));

                                  // Send to backend
                                  authFetch(`${API_URL}/api/tables/${contextPath.tableId}/update-header-row-cell-at-path`, {
                                    method: 'PUT',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      contextPath,
                                      header_row_index: headerRowIdx,
                                      col_index: colIdx,
                                      value: newValue
                                    })
                                  }).catch(error => {
                                    console.error('Error updating header row cell:', error);
                                    fetchTables();
                                  });
                                }}
                                  className={borderStyle === 'minimalist' ? 'minimalist-header-input' : ''}
                                  style={{
                                    border: 'none',
                                    background: 'transparent',
                                    padding: 0,
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                    color: 'inherit',
                                    fontWeight: 'inherit',
                                    outline: 'none',
                                    width: '100%',
                                    boxSizing: 'border-box',
                                    textAlign: 'center'
                                  }}
                                />
                              </div>
                            )}
                          </th>
                        );
                      })}
                      {!readOnly && (
                        <th style={{ border: 'none', background: 'transparent', width: '20px', padding: '2px' }}>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeHeaderRowAtPath(contextPath, headerRowIdx);
                            }}
                            title="Remove this header row"
                            style={{
                              padding: '2px 4px',
                              fontSize: '0.65rem',
                              background: 'transparent',
                              color: 'var(--text-secondary)',
                              border: 'none',
                              cursor: 'pointer',
                              opacity: 0.5,
                              transition: 'opacity 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                          >
                            ×
                          </button>
                        </th>
                      )}
                    </tr>
                  ))}
                  {/* Render regular header row inside the same thead */}
                  {renderTableHeaderRow({
              columnHeaders: subtable.column_headers,
              numCols: subtable.num_cols,
              showRowHeaders: subtable.show_row_headers,
              showControls: !!subtablesEnabled[subtable.id],
              skipCornerCells: !!subtable.header_rows, // Skip if header rows exist (they're rowspanned from above)
              returnRowOnly: true, // Return just <tr> to include in same <thead>
              theme: 'default',
              sizing: 'medium',
              isEditable: !readOnly,
              onHeaderEdit: (colIdx, newValue) => {
                // ✅ UPDATED: Now passes contextPath instead of legacy params
                updateSubtableColumnHeader(contextPath, colIdx, newValue);
              },
              showHeaderMenu: !readOnly,
              onInsertColumn: (colIdx, direction) => {
                // Convert 'before'/'after' to 'left'/'right' for backend
                const backendDirection = direction === 'before' ? 'left' : 'right';
                insertSubtableColumn(contextPath, colIdx, backendDirection);
                setShowSubtableColMenu(null);
              },
              onMoveColumn: (colIdx, direction) => {
                moveSubtableColumn(contextPath, colIdx, direction);
              },
              onDeleteColumn: (colIdx) => {
                deleteSubtableColumn(contextPath, colIdx);
              },
              onCopyColumn: (colIdx) => {
                copySubtableColumn(tableId, subtable.id, colIdx);
              },
              onPasteColumn: (colIdx) => {
                pasteSubtableColumn(tableId, subtable.id, colIdx);
              },
              copiedColumn: copiedColumn,
              showColMenu: showSubtableColMenu,
              setShowColMenu: setShowSubtableColMenu,
              menuIdentifier: { subtableId: subtable.id },
              rowHeaderWidth: subtable.row_header_width || 120,
              onRowHeaderResize: !readOnly ? (newWidth) => {
                // Manual resize (legacy API - only works for depth <= 1)
                const depth = getDepth(contextPath);
                if (depth <= 1) {
                  authFetch(`http://localhost:8000/api/tables/${tableId}/subtable/${subtable.id}/row-header-width`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ width: newWidth })
                  }).then(() => fetchTables());
                }
                // Note: Manual resize skipped for depth > 1
              } : null,
              sortColumn: subtable.sort_column,
              sortDirection: subtable.sort_direction,
              onSort: (colIdx) => toggleSort(colIdx, tableId, subtable.id),
              controlsId: `subtable-${subtable.id}`,
              doneColumn: subtable.done_column,
              onSetDoneColumn: !readOnly ? async (colIdx) => {
                try {
                  console.log('[Subtable Done Column] Setting done column:', colIdx, 'for subtable:', subtable.id, 'current:', subtable.done_column);

                  // Optimistic update: immediately update local state
                  setTables(prevTables => prevTables.map(t => {
                    if (t.id !== tableId) return t;

                    // Deep clone to ensure new references
                    const updatedTable = JSON.parse(JSON.stringify(t));

                    // Recursive function to find and update subtable done column
                    const updateSubtableDoneColumn = (subtables) => {
                      if (!subtables) return false;

                      for (const rowKey in subtables) {
                        for (let i = 0; i < subtables[rowKey].length; i++) {
                          const st = subtables[rowKey][i];
                          if (st.id === subtable.id) {
                            // Toggle: if clicking same column, unset; otherwise set to new column
                            const currentDoneColumn = st.done_column;
                            const newDoneColumn = currentDoneColumn === colIdx ? null : colIdx;
                            console.log('[Subtable Done Column] Optimistic update: current:', currentDoneColumn, '-> new:', newDoneColumn);
                            st.done_column = newDoneColumn;
                            return true;
                          }
                          // Recursively search in nested subtables
                          if (st.row_subtables && updateSubtableDoneColumn(st.row_subtables)) {
                            return true;
                          }
                        }
                      }
                      return false;
                    };

                    const found = updateSubtableDoneColumn(updatedTable.row_subtables);

                    if (!found) {
                      console.warn('[Subtable Done Column] Subtable not found in optimistic update:', subtable.id);
                    }

                    // Return updated table even if not found (to be safe)
                    return updatedTable;
                  }));

                  // Then sync with backend
                  await authFetch(`${API_URL}/api/tables/${tableId}/subtable/${subtable.id}/done-column`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ col_idx: colIdx })
                  });
                } catch (error) {
                  console.error('[Subtable Done Column] Error:', error);
                  // Revert on error
                  await fetchTables();
                }
              } : null,
              columnBorderStyles: subtable.column_border_styles,
              onSetColumnBorderStyle: !readOnly ? (colIdx, borderStyle) => {
                setColumnBorderStyleAtPath(contextPath, colIdx, borderStyle);
              } : null,
              showColBorderSubmenu: showColBorderSubmenu,
              setShowColBorderSubmenu: setShowColBorderSubmenu,
              borderSubmenuTimeout: borderSubmenuTimeout,
              setBorderSubmenuTimeout: setBorderSubmenuTimeout,
              translationColumns: subtable.translation_columns,
              allSubtablesForToggle: getAllTableSubtables(subtable),
              onBulkExpand: (ids) => bulkSetExpanded(ids, true, (id) => extendPath(contextPath, { type: 'subtable', id })),
              onBulkCollapse: (ids) => bulkSetExpanded(ids, false, (id) => extendPath(contextPath, { type: 'subtable', id })),
              extraButtons: borderStyle === 'minimalist' ? [
                {
                  label: subtable.show_headers ? 'Col ✓' : 'Col',
                  // ✅ UPDATED: Now passes contextPath
                onClick: () => toggleSubtableHeaders(contextPath),
                  active: subtable.show_headers,
                  title: subtable.show_headers ? 'Hide column headers' : 'Show column headers'
                },
                {
                  label: subtable.show_row_headers ? 'Row ✓' : 'Row',
                  // ✅ UPDATED: Now passes contextPath
                onClick: () => toggleSubtableRowHeaders(contextPath),
                  active: subtable.show_row_headers,
                  title: subtable.show_row_headers ? 'Hide row headers' : 'Show row headers'
                },
                ...(!readOnly ? [{
                  label: '🗑',
                  onClick: () => deleteSubtable(contextPath),
                  active: false,
                  title: 'Delete subtable',
                  danger: true
                }] : [])
              ] : null
            })}
                </thead>
              )}
            <tbody>
              {sortRows(subtable.rows, subtable.sort_column, subtable.sort_direction, subtable.done_column).map((rowData, displayIdx) => {
              const { row: subRow, originalIndex: originalSubRowIdx } = rowData;
              const subRowIdx = originalSubRowIdx; // Alias for compatibility
              const rowKey = `row-${originalSubRowIdx}`;
              const nestedSubtables = subtable.row_subtables?.[rowKey] || [];
              const hasNestedSubtables = nestedSubtables.length > 0;
              const hasExpandedSubtables = nestedSubtables.some(st => st.expanded !== false);
              const parentRowBg = depthShading && hasExpandedSubtables
                ? getDepthBackground(1, theme, colorScheme, true, true, customColor)
                : undefined;

              return (
                <React.Fragment key={originalSubRowIdx}>
                  <tr>
                    {subtablesEnabled[subtable.id] && renderRowControls({
                      subtables: nestedSubtables,
                      onToggleSubtable: (id) => toggleSubtable(extendPath(contextPath, { type: 'subtable', id })),
                      onShowAllSubtables: (ids) => bulkSetExpanded(ids, true, (id) => extendPath(contextPath, { type: 'subtable', id })),
                      onHideAllSubtables: (ids) => bulkSetExpanded(ids, false, (id) => extendPath(contextPath, { type: 'subtable', id })),
                      onAddSubtable: () => setShowSubtableModal({ tableId, rowIndex: subRowIdx, parentSubtableId: subtable.id }),
                      readOnly,
                      backgroundColor: parentRowBg,
                      parentId: subtable.id,
                      rowBorderStyles: subtable.row_border_styles,
                      rowIndex: originalSubRowIdx
                    })}
                    {subtable.show_row_headers && (
                      <td
                        className="table-row-header-cell"
                        style={{
                          fontWeight: 600,
                          padding: '0.4rem',
                          width: `${subtable.row_header_width || 120}px`,
                          minWidth: `${subtable.row_header_width || 120}px`,
                          fontSize: '0.85rem',
                          position: 'relative',
                          verticalAlign: 'middle',
                          ...getRowBorderStyle(subtable.row_border_styles, originalSubRowIdx)
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          {readOnly ? (
                            <div style={{ flex: 1, fontSize: '0.85rem', fontWeight: 600 }}>
                              {(subtable.row_headers || [])[subRowIdx] ? (
                                <LatexText text={(subtable.row_headers || [])[subRowIdx]} />
                              ) : (
                                <span style={{ opacity: 0.4 }}>(empty)</span>
                              )}
                            </div>
                          ) : (
                            <div style={{ flex: 1 }}>
                              <EditableLatexHeader
                                value={(subtable.row_headers || [])[subRowIdx]}
                                onSave={async (newValue) => {
                                  const currentValue = (subtable.row_headers || [])[subRowIdx];
                                  if (newValue !== currentValue) {
                                    // ✅ UPDATED: Now passes contextPath instead of legacy params
                                    await updateSubtableRowHeader(contextPath, subRowIdx, newValue);
                                  }

                                  // Auto-resize row header column to fit content
                                  const canvas = document.createElement('canvas');
                                  const context = canvas.getContext('2d');
                                  context.font = '600 0.85rem sans-serif';

                                  const allHeaders = (subtable.row_headers || subtable.rows.map((_, i) => `Row ${i + 1}`)).map((header, i) =>
                                    i === subRowIdx ? newValue : header
                                  );

                                  const maxWidth = allHeaders.reduce((max, header) => {
                                    const width = context.measureText(header || '').width;
                                    return Math.max(max, width);
                                  }, 0);

                                  const newWidth = Math.max(80, Math.min(300, Math.ceil(maxWidth + 80)));

                                  // Auto-resize row header width (legacy API - only works for depth <= 1)
                                  const depth = getDepth(contextPath);
                                  if (depth <= 1) {
                                    await authFetch(`${API_URL}/api/tables/${tableId}/subtable/${subtable.id}/row-header-width`, {
                                      method: 'PUT',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ width: newWidth })
                                    });
                                    await fetchTables();
                                  }
                                  // Note: Width auto-resize skipped for depth > 1 (minor cosmetic limitation)
                                }}
                                fontSize="0.85rem"
                                onSort={null}
                                textAlign='left'
                              />
                            </div>
                          )}
                          <div style={{ position: 'relative' }}>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowSubtableRowMenu(
                                  showSubtableRowMenu?.tableId === tableId &&
                                  showSubtableRowMenu?.subtableId === subtable.id &&
                                  showSubtableRowMenu?.rowIdx === subRowIdx
                                    ? null
                                    : { tableId, subtableId: subtable.id, rowIdx: subRowIdx }
                                );
                              }}
                              className="btn-insert-menu"
                              title="Insert row"
                            >
                              ≡
                            </button>
                            {showSubtableRowMenu?.tableId === tableId &&
                             showSubtableRowMenu?.subtableId === subtable.id &&
                             showSubtableRowMenu?.rowIdx === subRowIdx && (
                              <div className="insert-menu" style={{ overflow: 'visible' }} onClick={(e) => e.stopPropagation()}>
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                insertSubtableRow(contextPath, subRowIdx, 'above');
                                setShowSubtableRowMenu(null);
                              }}
                            >
                              ↑ Insert Above
                            </button>
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                insertSubtableRow(contextPath, subRowIdx, 'below');
                                setShowSubtableRowMenu(null);
                              }}
                            >
                              ↓ Insert Below
                            </button>
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                moveSubtableRow(contextPath, subRowIdx, 'up');
                                setShowSubtableRowMenu(null);
                              }}
                              disabled={subtable.sort_column !== null || subRowIdx === 0}
                              style={{ opacity: (subtable.sort_column !== null || subRowIdx === 0) ? 0.5 : 1 }}
                              title={subtable.sort_column !== null ? "Cannot move rows when sorting is active" : ""}
                            >
                              ⬆ Move Up
                            </button>
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                moveSubtableRow(contextPath, subRowIdx, 'down');
                                setShowSubtableRowMenu(null);
                              }}
                              disabled={subtable.sort_column !== null || subRowIdx === subtable.num_rows - 1}
                              style={{ opacity: (subtable.sort_column !== null || subRowIdx === subtable.num_rows - 1) ? 0.5 : 1 }}
                              title={subtable.sort_column !== null ? "Cannot move rows when sorting is active" : ""}
                            >
                              ⬇ Move Down
                            </button>
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                copySubtableRow(tableId, subtable.id, subRowIdx);
                                setShowSubtableRowMenu(null);
                              }}
                            >
                              📋 Copy Row
                            </button>
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                pasteSubtableRow(tableId, subtable.id, subRowIdx);
                                setShowSubtableRowMenu(null);
                              }}
                              disabled={!copiedRow}
                              style={{ opacity: copiedRow ? 1 : 0.5 }}
                              title={copiedRow ? "Paste copied row below this row" : "No row copied"}
                            >
                              📄 Paste Row
                            </button>
                            {/* Row Border Styles */}
                            <div style={{ borderTop: '1px solid var(--border-primary)', margin: '4px 0', paddingTop: '4px' }} />
                            <div
                              style={{ position: 'relative', overflow: 'visible' }}
                              onMouseEnter={() => {
                                if (rowBorderSubmenuTimeout) {
                                  clearTimeout(rowBorderSubmenuTimeout);
                                  setRowBorderSubmenuTimeout(null);
                                }
                                setShowRowBorderSubmenu({ tableId, subtableId: subtable.id, rowIdx: subRowIdx });
                              }}
                              onMouseLeave={() => {
                                const timeout = setTimeout(() => {
                                  setShowRowBorderSubmenu(null);
                                }, 300);
                                setRowBorderSubmenuTimeout(timeout);
                              }}
                            >
                              <button
                                className="insert-menu-item"
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  background: showRowBorderSubmenu?.tableId === tableId && showRowBorderSubmenu?.subtableId === subtable.id && showRowBorderSubmenu?.rowIdx === subRowIdx ? 'var(--bg-hover)' : undefined
                                }}
                              >
                                <span>🎨 Border Style</span>
                                <span>▶</span>
                              </button>
                              {(showRowBorderSubmenu?.tableId === tableId && showRowBorderSubmenu?.subtableId === subtable.id && showRowBorderSubmenu?.rowIdx === subRowIdx) && (
                                <div
                                  className="insert-menu"
                                  style={{
                                    position: 'absolute',
                                    left: '100%',
                                    top: 0,
                                    marginLeft: '2px',
                                    minWidth: '150px',
                                    zIndex: 10000
                                  }}
                                  onMouseEnter={() => {
                                    if (rowBorderSubmenuTimeout) {
                                      clearTimeout(rowBorderSubmenuTimeout);
                                      setRowBorderSubmenuTimeout(null);
                                    }
                                  }}
                                  onMouseLeave={() => {
                                    const timeout = setTimeout(() => {
                                      setShowRowBorderSubmenu(null);
                                    }, 300);
                                    setRowBorderSubmenuTimeout(timeout);
                                  }}
                                >
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      setRowBorderStyleAtPath(contextPath, subRowIdx, 'none');
                                      setShowSubtableRowMenu(null);
                                      setShowRowBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: !subtable.row_border_styles?.[subRowIdx] ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    None
                                  </button>
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      setRowBorderStyleAtPath(contextPath, subRowIdx, 'double-top');
                                      setShowSubtableRowMenu(null);
                                      setShowRowBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: subtable.row_border_styles?.[subRowIdx] === 'double-top' ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    ═ Double Above
                                  </button>
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      setRowBorderStyleAtPath(contextPath, subRowIdx, 'double-bottom');
                                      setShowSubtableRowMenu(null);
                                      setShowRowBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: subtable.row_border_styles?.[subRowIdx] === 'double-bottom' ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    ═ Double Below
                                  </button>
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      setRowBorderStyleAtPath(contextPath, subRowIdx, 'thick-top');
                                      setShowSubtableRowMenu(null);
                                      setShowRowBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: subtable.row_border_styles?.[subRowIdx] === 'thick-top' ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    ▬ Thick Above
                                  </button>
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      setRowBorderStyleAtPath(contextPath, subRowIdx, 'thick-bottom');
                                      setShowSubtableRowMenu(null);
                                      setShowRowBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: subtable.row_border_styles?.[subRowIdx] === 'thick-bottom' ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    ▬ Thick Below
                                  </button>
                                </div>
                              )}
                            </div>
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                if (window.confirm('Delete this row?')) {
                                  deleteSubtableRow(contextPath, subRowIdx);
                                  setShowSubtableRowMenu(null);
                                }
                              }}
                              style={{ color: '#ef4444' }}
                            >
                              🗑 Delete Row
                            </button>
                          </div>
                            )}
                          </div>
                        </div>
                      </td>
                    )}
                    {subRow.map((subCell, subColIdx) => {
                      // Check if cell contains a nested table (expanded or collapsed)
                      const hasNestedTable = subCell && typeof subCell === 'object' && subCell.type === 'nested_table';

                      // Check if ANY cell in this row has a nested table, expanded chart, or done picker
                      const rowHasNestedTable = subRow.some(c => c && typeof c === 'object' && c.type === 'nested_table');
                      const rowHasExpandedChart = subRow.some(c => c && typeof c === 'object' && c.type === 'chart' && c.expanded);
                      const rowHasDonePicker = subRow.some(c => c && typeof c === 'object' && c.type === 'done');
                      const rowHasSpecialContent = rowHasNestedTable || rowHasExpandedChart || rowHasDonePicker;

                      // Always use stored column width to ensure resize handles remain accessible
                      // For expanded nested tables/charts, the content will overflow/expand as needed via CSS
                      const colWidth = subtable.column_widths?.[subColIdx] || 150;
                      const rowHeight = rowHasSpecialContent ? null : (subtable.row_heights?.[subRowIdx] || 40);

                      // Create unique key that changes when nested table is toggled
                      const cellKey = hasNestedTable
                        ? `${subColIdx}-${subCell.id}-${subCell.expanded ? 'exp' : 'col'}`
                        : subColIdx;

                      return (
                        <td
                          key={cellKey}
                          data-subtable-col={`${subtable.id}-${subColIdx}`}
                          data-subtable-row={`${subtable.id}-${subRowIdx}`}
                          style={{
                            width: `${colWidth}px`,
                            minWidth: `${colWidth}px`,
                            ...(rowHeight && density !== 'sub-compact' && { height: `${rowHeight}px` }),
                            ...getColumnBorderStyle(subtable.column_border_styles, subColIdx),
                            ...getRowBorderStyle(subtable.row_border_styles, originalSubRowIdx)
                          }}
                          onContextMenu={(e) => {
                            const table = tables.find(t => t.id === tableId);
                            if (isTableReadOnly(table)) {
                              e.preventDefault();
                              return;
                            }
                            e.preventDefault();
                            e.stopPropagation();
                            const cellContext = createCellContext(contextPath, subRowIdx, subColIdx);
                            console.log('[SUBTABLE CELL RIGHT-CLICK]', {
                              rowIdx: subRowIdx,
                              colIdx: subColIdx,
                              contextPath,
                              pathString: pathToString(contextPath),
                              cellContext
                            });
                            setSelectedCell(cellContext);
                            setShowContextMenu(true);
                          }}
                          onClick={(e) => {
                            // Only set context if clicking directly on the cell, not on nested content
                            if (e.target === e.currentTarget || e.target.closest('td') === e.currentTarget) {
                              const cellContext = createCellContext(contextPath, subRowIdx, subColIdx);
                              console.log('[SUBTABLE CELL CLICK]', {
                                rowIdx: subRowIdx,
                                colIdx: subColIdx,
                                contextPath,
                                pathString: pathToString(contextPath),
                                cellContext
                              });
                              setSelectedCell(cellContext);
                            }
                          }}
                        >
                          {renderCell(tableId, subCell, subRowIdx, subColIdx, contextPath)}
                          {/* Column resize handle */}
                          {!readOnly && (
                            <ResizeHandle
                              direction="col"
                              {...resizeElementOptimized('column', subColIdx, { tableId, subtableId: subtable.id, nestedTableId })}
                            />
                          )}
                          {/* Row resize handle */}
                          {!readOnly && subRowIdx < subtable.rows.length - 1 && (
                            <ResizeHandle
                              direction="row"
                              {...resizeElementOptimized('row', subRowIdx, { tableId, subtableId: subtable.id, nestedTableId })}
                            />
                          )}
                        </td>
                      );
                    })}
                  </tr>
                  {nestedSubtables.filter(st => st.expanded).map((nestedSubtable) => (
                    <tr key={nestedSubtable.id} className="subtable-row">
                      <td colSpan={subRow.length + 1 + (subtable.show_row_headers ? 1 : 0)} style={{ padding: 0 }}>
                        <div style={{ width: 0, overflow: 'visible' }}>
                          <div style={{ width: 'max-content' }}>
                            {renderSubtablesRecursive(tableId, nestedSubtable, depth + 1, contextPath)}
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            </tbody>
          </table>
          </>
        )}
      </div>
    );
  };

  // Helper function to render minimalist controls bar when headers are hidden
  const renderMinimalistControlsBar = (id, buttons) => {
    // Now works in all modes, not just minimalist

    return (
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        marginBottom: '0.5rem',
        padding: '0.25rem 0.5rem',
        background: 'color-mix(in srgb, var(--accent-primary) 60%, var(--accent-light-bg) 40%)',
        borderRadius: '4px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              maxWidth: expandedControls[id] ? '500px' : '0px',
              opacity: expandedControls[id] ? 1 : 0,
              overflow: 'hidden',
              transition: 'max-width 0.3s ease, opacity 0.3s ease',
              marginRight: '0.25rem'
            }}
          >
            {buttons.map((btn, idx) => (
              <button
                key={idx}
                onClick={btn.onClick}
                className="minimalist-header-btn"
                title={btn.title}
                style={{
                  marginLeft: '0.5rem',
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  background: btn.danger ? 'var(--accent-primary)' : (btn.active ? 'var(--accent-primary)' : 'rgba(0,0,0,0.1)'),
                  color: btn.danger ? 'white' : (btn.active ? 'white' : 'var(--text-primary)'),
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  flexShrink: 0
                }}
              >
                {btn.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => setExpandedControls(prev => ({ ...prev, [id]: !prev[id] }))}
            className="minimalist-header-btn"
            title={expandedControls[id] ? 'Hide controls' : 'Show controls'}
            style={{
              padding: '0.25rem 0.5rem',
              fontSize: '0.75rem',
              background: 'rgba(0,0,0,0.1)',
              color: 'var(--text-primary)',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: 600,
              flexShrink: 0,
              lineHeight: '1',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {expandedControls[id] ? '◀' : <span style={{ transform: 'rotate(90deg)', display: 'inline-block', fontSize: '1rem' }}>≡</span>}
          </button>
        </div>
      </div>
    );
  };

  // ============================================================
  // UNIFIED NESTED TABLE RENDERER - Context Path System
  // ============================================================
  // Replaces renderNestedTableCell and renderNestedTableCellMain with a unified function
  // that uses context paths to support unlimited nesting depth
  const renderNestedTable = (tableId, cell, rowIdx, colIdx, parentContextPath = null) => {
    // Build context path for this nested table
    // Always extend the parent path (even if it's empty)
    const contextPath = parentContextPath
      ? extendPath(parentContextPath, { type: 'nested', id: cell.id })
      : extendPath(createContextPath(tableId, null, null), { type: 'nested', id: cell.id });

    // Calculate depth from context path (count nested and subtable entries)
    const depth = contextPath?.path?.filter(p => p.type === 'nested' || p.type === 'subtable').length || 0;

    // Extract legacy parameters for backward compatibility with existing API calls
    const { subtableId, nestedTableId } = extractLegacyParams(contextPath);

    // Check if table is read-only
    const table = tables.find(t => t.id === tableId);
    const readOnly = isTableReadOnly(table);

    if (cell.expanded) {
      // Show full table when expanded - recursively render cells to support nested tables in nested tables
      // Use stable key if available, otherwise fall back to ID, and include column_border_styles to force re-render
      const reactKey = `${cell._stableKey || cell.id}-${JSON.stringify(cell.column_border_styles || {})}`;

      return (
        <div
          key={reactKey}
          className="nested-table-wrapper"
          style={{ backgroundColor: getDepthBackground(depth, theme, colorScheme, depthShading, false, customColor) }}
          onClick={(e) => {
            e.stopPropagation();
            setFocusedElement({ type: 'nested_table', tableId, rowIdx, colIdx, subtableId, nestedTableId: cell.id, contextPath });
            setSelectedCell(createCellContext(contextPath, rowIdx, colIdx));
          }}
        >
          {renderControlHeader({
            isExpanded: true,
            title: cell.name,
            showToggle: true,
            onToggle: () => toggleNestedTable(contextPath),
            className: 'nested-table-header',
            isEditing: editingNestedTableName === cell.id,
            editingValue: editingNestedTableNameValue,
            onEditStart: () => {
              setEditingNestedTableName(cell.id);
              setEditingNestedTableNameValue(cell.name);
            },
            onEditChange: (e) => setEditingNestedTableNameValue(e.target.value),
            onEditBlur: () => {
              const newName = editingNestedTableNameValue.trim();
              if (newName && newName !== cell.name) {
                // Update state first (synchronous)
                setTables(prevTables => prevTables.map(table => {
                  if (table.id !== contextPath.tableId) return table;
                  return traverseAndUpdate(table, contextPath, (structure) => ({
                    ...structure,
                    name: newName
                  }));
                }));
                // Exit edit mode after state update
                setEditingNestedTableName(null);
                // Send to backend
                const sendToBackend = async () => {
                  try {
                    const response = await authFetch(
                      `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/update-name`,
                      {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ contextPath, name: newName })
                      }
                    );
                    if (!response.ok) {
                      await fetchTables();
                    }
                  } catch (error) {
                    await fetchTables();
                  }
                };
                sendToBackend();
              } else {
                setEditingNestedTableName(null);
              }
            },
            onEditKeyDown: (e) => {
              if (e.key === 'Enter') {
                const newName = editingNestedTableNameValue.trim();
                if (newName && newName !== cell.name) {
                  // Update state first (synchronous)
                  setTables(prevTables => prevTables.map(table => {
                    if (table.id !== contextPath.tableId) return table;
                    return traverseAndUpdate(table, contextPath, (structure) => ({
                      ...structure,
                      name: newName
                    }));
                  }));
                  // Exit edit mode after state update
                  setEditingNestedTableName(null);
                  // Send to backend (don't do optimistic update again)
                  const sendToBackend = async () => {
                    try {
                      const response = await authFetch(
                        `${API_URL}/api/tables/${contextPath.tableId}/update-by-path/update-name`,
                        {
                          method: 'PUT',
                          headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ contextPath, name: newName })
                        }
                      );
                      if (!response.ok) {
                        await fetchTables(); // Rollback on error
                      }
                    } catch (error) {
                      await fetchTables(); // Rollback on error
                    }
                  };
                  sendToBackend();
                } else {
                  setEditingNestedTableName(null);
                }
              } else if (e.key === 'Escape') {
                setEditingNestedTableName(null);
              }
            },
            controlId: borderStyle !== 'minimalist' ? `nested-${cell.id}` : null,
            controlButtons: borderStyle !== 'minimalist' ? [
            {
              label: '📷',
              onClick: () => captureElementScreenshot(`.nested-table-wrapper:has([data-nested-table-id="${cell.id}"])`, cell.name, 'nested table'),
              active: true,
              title: 'Capture screenshot of this nested table'
            },
            {
              label: cell.show_headers ? 'Col ✓' : 'Col',
              // ✅ UPDATED: Now passes contextPath
              onClick: () => toggleNestedTableHeaders(contextPath),
              active: cell.show_headers,
              title: cell.show_headers ? 'Hide column headers' : 'Show column headers'
            },
            {
              label: cell.show_row_headers ? 'Row ✓' : 'Row',
              // ✅ UPDATED: Now passes contextPath
              onClick: () => toggleNestedTableRowHeaders(contextPath),
              active: cell.show_row_headers,
              title: cell.show_row_headers ? 'Hide row headers' : 'Show row headers'
            },
            ...(!readOnly && cell.show_headers ? [{
              label: '+Hdr Row',
              onClick: () => addHeaderRowAtPath(contextPath),
              active: false,
              title: 'Add header row level above existing headers',
              style: {
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                fontSize: '0.75rem',
                background: 'var(--accent-primary)',
                color: 'white',
                fontWeight: 600
              }
            }] : []),
            {
              label: subtablesEnabled[cell.id] ? 'Subtables ✓' : 'Subtables',
              onClick: () => setSubtablesEnabled(prev => ({ ...prev, [cell.id]: !prev[cell.id] })),
              active: subtablesEnabled[cell.id],
              title: subtablesEnabled[cell.id] ? 'Hide subtables column and controls' : 'Show subtables column and controls'
            },
            ...(!readOnly ? [
              {
                label: '🗑',
                onClick: () => deleteNestedTable(contextPath),
                active: false,
                title: 'Delete nested table',
                danger: true
              }
            ] : [])
          ] : []
        })}
          {borderStyle === 'minimalist' && !cell.show_headers && renderMinimalistControlsBar(`nested-${cell.id}`, [
            {
              label: '📷',
              onClick: () => captureElementScreenshot(`.nested-table-wrapper:has([data-nested-table-id="${cell.id}"])`, cell.name, 'nested table'),
              active: true,
              title: 'Capture screenshot of this nested table'
            },
            {
              label: cell.show_headers ? 'Col ✓' : 'Col',
              // ✅ UPDATED: Now passes contextPath
              onClick: () => toggleNestedTableHeaders(contextPath),
              active: cell.show_headers,
              title: cell.show_headers ? 'Hide column headers' : 'Show column headers'
            },
            {
              label: cell.show_row_headers ? 'Row ✓' : 'Row',
              // ✅ UPDATED: Now passes contextPath
              onClick: () => toggleNestedTableRowHeaders(contextPath),
              active: cell.show_row_headers,
              title: cell.show_row_headers ? 'Hide row headers' : 'Show row headers'
            },
            ...(!readOnly && cell.show_headers ? [{
              label: '+Hdr Row',
              onClick: () => addHeaderRowAtPath(contextPath),
              active: false,
              title: 'Add header row level above existing headers',
              style: {
                marginLeft: '0.5rem',
                padding: '0.25rem 0.5rem',
                fontSize: '0.75rem',
                background: 'var(--accent-primary)',
                color: 'white',
                fontWeight: 600
              }
            }] : []),
            {
              label: subtablesEnabled[cell.id] ? 'Subtables ✓' : 'Subtables',
              onClick: () => setSubtablesEnabled(prev => ({ ...prev, [cell.id]: !prev[cell.id] })),
              active: subtablesEnabled[cell.id],
              title: subtablesEnabled[cell.id] ? 'Hide subtables column and controls' : 'Show subtables column and controls'
            },
            ...(!readOnly ? [
              {
                label: '🗑',
                onClick: () => deleteNestedTable(contextPath),
                active: false,
                title: 'Delete nested table',
                danger: true
              }
            ] : [])
          ])}
          <table className="nested-table-inner" data-nested-table-id={cell.id}>
            {cell.show_headers && (
              <thead>
                {/* Render additional header rows if they exist */}
                {cell.header_rows && cell.header_rows.map((headerRow, headerRowIdx) => (
                  <tr key={`header-row-${headerRowIdx}`}>
                    {/* Only render corner cells in first header row, with rowspan to cover all header rows + main header row */}
                    {headerRowIdx === 0 && subtablesEnabled[cell.id] && cell.show_row_headers && (() => {
                      const allSubs = getAllTableSubtables(cell);
                      const anyExp = allSubs.some(st => st.expanded !== false);
                      return (
                      <th className="row-controls" rowSpan={(cell.header_rows?.length || 0) + 1} colSpan={2}>
                        {allSubs.length > 0 && (
                          <button
                            onClick={() => {
                              const ids = allSubs.filter(st => anyExp ? st.expanded !== false : st.expanded === false).map(st => st.id);
                              if (ids.length > 0) bulkSetExpanded(ids, !anyExp, (id) => extendPath(contextPath, { type: 'subtable', id }));
                            }}
                            title={anyExp ? 'Hide all subtables' : 'Show all subtables'}
                            style={{ fontSize: '0.65rem', padding: '2px 5px', border: `1px solid ${theme === 'dark' ? '#666' : '#ccc'}`, borderRadius: '3px', background: theme === 'dark' ? '#3a3a3a' : '#f5f5f5', color: theme === 'dark' ? '#ccc' : '#555', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            {anyExp ? '▼ Hide all' : '▶ Show all'}
                          </button>
                        )}
                      </th>
                      );
                    })()}
                    {headerRowIdx === 0 && subtablesEnabled[cell.id] && !cell.show_row_headers && (() => {
                      const allSubs = getAllTableSubtables(cell);
                      const anyExp = allSubs.some(st => st.expanded !== false);
                      return (
                      <th className="row-controls" rowSpan={(cell.header_rows?.length || 0) + 1}>
                        {allSubs.length > 0 && (
                          <button
                            onClick={() => {
                              const ids = allSubs.filter(st => anyExp ? st.expanded !== false : st.expanded === false).map(st => st.id);
                              if (ids.length > 0) bulkSetExpanded(ids, !anyExp, (id) => extendPath(contextPath, { type: 'subtable', id }));
                            }}
                            title={anyExp ? 'Hide all subtables' : 'Show all subtables'}
                            style={{ fontSize: '0.65rem', padding: '2px 5px', border: `1px solid ${theme === 'dark' ? '#666' : '#ccc'}`, borderRadius: '3px', background: theme === 'dark' ? '#3a3a3a' : '#f5f5f5', color: theme === 'dark' ? '#ccc' : '#555', cursor: 'pointer', whiteSpace: 'nowrap' }}
                          >
                            {anyExp ? '▼ Hide all' : '▶ Show all'}
                          </button>
                        )}
                      </th>
                      );
                    })()}
                    {headerRowIdx === 0 && !subtablesEnabled[cell.id] && cell.show_row_headers && (
                      <th className="table-row-header-cell" rowSpan={(cell.header_rows?.length || 0) + 1}></th>
                    )}
                    {headerRow.map((headerCell, colIdx) => {
                      const colWidth = cell.column_widths?.[colIdx] || 150;
                      const mergeInfo = cell.header_rows_merged?.[`${headerRowIdx},${colIdx}`];
                      const colspan = mergeInfo?.colspan || 1;
                      const rowspan = mergeInfo?.rowspan || 1;

                      // Skip cells covered by merges
                      let isCovered = false;
                      if (cell.header_rows_merged) {
                        for (const [key, merge] of Object.entries(cell.header_rows_merged)) {
                          const [mergeRow, mergeCol] = key.split(',').map(Number);
                          if (mergeRow !== headerRowIdx || mergeCol !== colIdx) {
                            if (headerRowIdx >= mergeRow && headerRowIdx < mergeRow + merge.rowspan &&
                                colIdx >= mergeCol && colIdx < mergeCol + merge.colspan) {
                              isCovered = true;
                              break;
                            }
                          }
                        }
                      }

                      if (isCovered) return null;

                      return (
                        <th
                          key={`header-${headerRowIdx}-${colIdx}`}
                          className="table-column-header-cell"
                          colSpan={colspan}
                          rowSpan={rowspan}
                          onContextMenu={(e) => {
                            if (!readOnly) {
                              e.preventDefault();
                              setSelectedHeaderCell({
                                contextPath,
                                headerRowIdx,
                                colIdx,
                                x: e.clientX,
                                y: e.clientY
                              });
                            }
                          }}
                          style={{
                            width: `${colWidth * colspan}px`,
                            minWidth: `${colWidth * colspan}px`,
                            position: 'relative',
                            cursor: !readOnly ? 'context-menu' : 'default',
                            textAlign: 'center',
                            ...(borderStyle !== 'minimalist' && {
                              fontWeight: 600,
                              padding: '0.75rem',
                              fontSize: '0.85rem'
                            }),
                            ...getColumnBorderStyle(cell.column_border_styles, colIdx)
                          }}
                        >
                          {readOnly ? (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>{headerCell || ''}</div>
                          ) : (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                              <input
                                type="text"
                                value={headerCell || ''}
                                onChange={(e) => {
                                const newValue = e.target.value;
                                // Optimistic update
                                setTables(prevTables => prevTables.map(table => {
                                  if (table.id !== contextPath.tableId) return table;
                                  return traverseAndUpdate(table, contextPath, (structure) => {
                                    const updatedHeaderRows = [...(structure.header_rows || [])];
                                    updatedHeaderRows[headerRowIdx] = [...updatedHeaderRows[headerRowIdx]];
                                    updatedHeaderRows[headerRowIdx][colIdx] = newValue;
                                    return { ...structure, header_rows: updatedHeaderRows };
                                  });
                                }));

                                // Send to backend
                                authFetch(`${API_URL}/api/tables/${contextPath.tableId}/update-header-row-cell-at-path`, {
                                  method: 'PUT',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    contextPath,
                                    header_row_index: headerRowIdx,
                                    col_index: colIdx,
                                    value: newValue
                                  })
                                }).catch(error => {
                                  console.error('Error updating header row cell:', error);
                                  fetchTables();
                                });
                              }}
                                className={borderStyle === 'minimalist' ? 'minimalist-header-input' : ''}
                                style={{
                                  border: 'none',
                                  background: 'transparent',
                                  padding: 0,
                                  fontSize: 'inherit',
                                  fontFamily: 'inherit',
                                  color: 'inherit',
                                  fontWeight: 'inherit',
                                  outline: 'none',
                                  width: '100%',
                                  boxSizing: 'border-box',
                                  textAlign: 'center'
                                }}
                              />
                            </div>
                          )}
                        </th>
                      );
                    })}
                    {!readOnly && (
                      <th style={{ border: 'none', background: 'transparent', width: '20px', padding: '2px' }}>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            removeHeaderRowAtPath(contextPath, headerRowIdx);
                          }}
                          title="Remove this header row"
                          style={{
                            padding: '2px 4px',
                            fontSize: '0.65rem',
                            background: 'transparent',
                            color: 'var(--text-secondary)',
                            border: 'none',
                            cursor: 'pointer',
                            opacity: 0.5,
                            transition: 'opacity 0.2s'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                          onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                        >
                          ×
                        </button>
                      </th>
                    )}
                  </tr>
                ))}
                {/* Render regular header row inside the same thead */}
                {renderTableHeaderRow({
              columnHeaders: cell.column_headers,
              numCols: cell.num_cols,
              showRowHeaders: cell.show_row_headers,
              showControls: !!subtablesEnabled[cell.id],
              skipCornerCells: !!cell.header_rows, // Skip if header rows exist (they're rowspanned from above)
              returnRowOnly: true, // Return just <tr> to include in same <thead>
              theme: 'default',
              sizing: 'small',
              isEditable: !readOnly,
              onHeaderEdit: (colIdx, newValue) => {
                // ✅ UPDATED: Now passes contextPath instead of legacy params
                console.log('[Nested Table] onHeaderEdit called with colIdx:', colIdx, 'newValue:', newValue, 'contextPath:', contextPath);
                updateNestedTableColumnHeader(contextPath, colIdx, newValue);
              },
              showHeaderMenu: !readOnly,
              onInsertColumn: (colIdx, direction) => {
                // Convert 'before'/'after' to 'left'/'right' for backend
                const backendDirection = direction === 'before' ? 'left' : 'right';
                insertNestedTableColumn(contextPath, colIdx, backendDirection);
                setShowNestedTableColMenu(null);
              },
              onMoveColumn: (colIdx, direction) => {
                moveNestedTableColumn(contextPath, colIdx, direction);
              },
              onDeleteColumn: (colIdx) => {
                deleteNestedTableColumn(contextPath, colIdx);
              },
              onCopyColumn: (colIdx) => {
                copyNestedTableColumn(tableId, cell.id, colIdx);
              },
              onPasteColumn: (colIdx) => {
                pasteNestedTableColumn(tableId, cell.id, colIdx);
              },
              copiedColumn: copiedColumn,
              showColMenu: showNestedTableColMenu,
              setShowColMenu: setShowNestedTableColMenu,
              menuIdentifier: { nestedTableId: cell.id },
              rowHeaderWidth: cell.row_header_width || 120,
              onRowHeaderResize: !readOnly ? (newWidth) => {
                // Manual resize (legacy API - only works for depth <= 1)
                const depth = getDepth(contextPath);
                if (depth <= 1) {
                  authFetch(`http://localhost:8000/api/tables/${tableId}/nested-table/${cell.id}/row-header-width`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ width: newWidth })
                  }).then(() => fetchTables());
                }
                // Note: Manual resize skipped for depth > 1
              } : null,
              sortColumn: cell.sort_column,
              sortDirection: cell.sort_direction,
              onSort: (colIdx) => toggleSort(colIdx, tableId, null, cell.id),
              controlsId: `nested-${cell.id}`,
              doneColumn: cell.done_column,
              onSetDoneColumn: !readOnly ? async (colIdx) => {
                try {
                  // Optimistic update: immediately update local state
                  setTables(prevTables => prevTables.map(t => {
                    if (t.id !== tableId) return t;

                    const updatedTable = JSON.parse(JSON.stringify(t));

                    // Recursive function to find and update nested table
                    const updateNestedTableDoneColumn = (rows) => {
                      if (!rows) return false;

                      for (let r = 0; r < rows.length; r++) {
                        for (let c = 0; c < rows[r].length; c++) {
                          const nestedCell = rows[r][c];
                          if (nestedCell && nestedCell.type === 'nested_table' && nestedCell.id === cell.id) {
                            // Toggle: if clicking same column, unset; otherwise set to new column
                            nestedCell.done_column = nestedCell.done_column === colIdx ? null : colIdx;
                            return true;
                          }
                        }
                      }
                      return false;
                    };

                    updateNestedTableDoneColumn(updatedTable.rows);
                    return updatedTable;
                  }));

                  // Then sync with backend
                  await authFetch(`${API_URL}/api/tables/${tableId}/nested-table/${cell.id}/done-column`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ col_idx: colIdx })
                  });
                } catch (error) {
                  console.error('[Nested Table Done Column] Error:', error);
                  // Revert on error
                  await fetchTables();
                }
              } : null,
              columnBorderStyles: cell.column_border_styles,
              onSetColumnBorderStyle: !readOnly ? (colIdx, borderStyle) => {
                setColumnBorderStyleAtPath(contextPath, colIdx, borderStyle);
              } : null,
              showColBorderSubmenu: showColBorderSubmenu,
              setShowColBorderSubmenu: setShowColBorderSubmenu,
              borderSubmenuTimeout: borderSubmenuTimeout,
              setBorderSubmenuTimeout: setBorderSubmenuTimeout,
              translationColumns: cell.translation_columns,
              allSubtablesForToggle: getAllTableSubtables(cell),
              onBulkExpand: (ids) => bulkSetExpanded(ids, true, (id) => extendPath(contextPath, { type: 'subtable', id })),
              onBulkCollapse: (ids) => bulkSetExpanded(ids, false, (id) => extendPath(contextPath, { type: 'subtable', id })),
              extraButtons: borderStyle === 'minimalist' ? [
                {
                  label: cell.show_headers ? 'Col ✓' : 'Col',
                  // ✅ UPDATED: Now passes contextPath
              onClick: () => toggleNestedTableHeaders(contextPath),
                  active: cell.show_headers,
                  title: cell.show_headers ? 'Hide column headers' : 'Show column headers'
                },
                {
                  label: cell.show_row_headers ? 'Row ✓' : 'Row',
                  // ✅ UPDATED: Now passes contextPath
              onClick: () => toggleNestedTableRowHeaders(contextPath),
                  active: cell.show_row_headers,
                  title: cell.show_row_headers ? 'Hide row headers' : 'Show row headers'
                },
                ...(!readOnly ? [{
                  label: '🗑',
                  onClick: () => deleteNestedTable(contextPath),
                  active: false,
                  title: 'Delete nested table',
                  danger: true
                }] : [])
              ] : null
            })}
              </thead>
            )}
            <tbody>
              {sortRows(cell.rows, cell.sort_column, cell.sort_direction, cell.done_column).map((rowData, displayIdx) => {
              const { row: nestedRow, originalIndex: nestedRowIdx } = rowData;
                const rowKey = `row-${nestedRowIdx}`;
                const nestedSubtables = cell.row_subtables?.[rowKey] || [];
                const hasExpandedSubtables = nestedSubtables.some(st => st.expanded !== false);
                const parentRowBg = depthShading && hasExpandedSubtables
                  ? getDepthBackground(depth + 1, theme, colorScheme, true, true, customColor)
                  : undefined;

                return (
                  <React.Fragment key={nestedRowIdx}>
                    <tr>
                      {/* Row controls column for toggle and add subtable buttons */}
                      {subtablesEnabled[cell.id] && renderRowControls({
                        subtables: nestedSubtables,
                        onToggleSubtable: (id) => toggleSubtable(extendPath(contextPath, { type: 'subtable', id })),
                        onShowAllSubtables: (ids) => bulkSetExpanded(ids, true, (id) => extendPath(contextPath, { type: 'subtable', id })),
                        onHideAllSubtables: (ids) => bulkSetExpanded(ids, false, (id) => extendPath(contextPath, { type: 'subtable', id })),
                        backgroundColor: parentRowBg,
                        onAddSubtable: () => setShowSubtableModal({
                          tableId,
                          rowIndex: nestedRowIdx,
                          nestedTableId: cell.id
                        }),
                        addButtonClassName: 'btn-add-subtable-small',
                        addButtonTitle: 'Add subtable to this row',
                        readOnly,
                        parentId: cell.id,
                        rowBorderStyles: cell.row_border_styles,
                        rowIndex: nestedRowIdx
                      })}
                      {cell.show_row_headers && (
                        <td
                          className="table-row-header-cell"
                          style={{
                            fontWeight: 600,
                            padding: '0.3rem',
                            paddingRight: showCellBackgrounds ? '1rem' : '0.3rem',
                            width: `${cell.row_header_width || 120}px`,
                            minWidth: `${cell.row_header_width || 120}px`,
                            fontSize: '0.75rem',
                            position: 'relative',
                            verticalAlign: 'middle',
                            ...getRowBorderStyle(cell.row_border_styles, nestedRowIdx)
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                            {readOnly ? (
                              <div style={{ flex: 1, fontSize: '0.75rem', fontWeight: 600 }}>
                                {(cell.row_headers || [])[nestedRowIdx] ? (
                                  <LatexText text={(cell.row_headers || [])[nestedRowIdx]} />
                                ) : (
                                  <span style={{ opacity: 0.4 }}>(empty)</span>
                                )}
                              </div>
                            ) : (
                              <div style={{ flex: 1 }}>
                                <EditableLatexHeader
                                  value={(cell.row_headers || [])[nestedRowIdx]}
                                  onSave={async (newValue) => {
                                    const currentValue = (cell.row_headers || [])[nestedRowIdx];
                                    if (newValue !== currentValue) {
                                      // ✅ UPDATED: Now passes contextPath instead of legacy params
                                      await updateNestedTableRowHeader(contextPath, nestedRowIdx, newValue);
                                    }

                                    // Auto-resize row header column to fit content
                                    const canvas = document.createElement('canvas');
                                    const context = canvas.getContext('2d');
                                    context.font = '600 0.75rem sans-serif';

                                    const allHeaders = (cell.row_headers || cell.rows.map((_, i) => `Row ${i + 1}`)).map((header, i) =>
                                      i === nestedRowIdx ? newValue : header
                                    );

                                    const maxWidth = allHeaders.reduce((max, header) => {
                                      const width = context.measureText(header || '').width;
                                      return Math.max(max, width);
                                    }, 0);

                                    const newWidth = Math.max(80, Math.min(300, Math.ceil(maxWidth + 80)));

                                    // Auto-resize row header width (legacy API - only works for depth <= 1)
                                    const depth = getDepth(contextPath);
                                    if (depth <= 1) {
                                      await authFetch(`${API_URL}/api/tables/${tableId}/nested-table/${cell.id}/row-header-width`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ width: newWidth })
                                      });
                                      await fetchTables();
                                    }
                                    // Note: Width auto-resize skipped for depth > 1 (minor cosmetic limitation)
                                  }}
                                  fontSize="0.75rem"
                                  onSort={null}
                                  textAlign='left'
                                />
                              </div>
                            )}
                            <div style={{ position: 'relative' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowNestedTableRowMenu(
                                    showNestedTableRowMenu?.tableId === tableId &&
                                    showNestedTableRowMenu?.nestedTableId === cell.id &&
                                    showNestedTableRowMenu?.rowIdx === nestedRowIdx
                                      ? null
                                      : { tableId, nestedTableId: cell.id, rowIdx: nestedRowIdx }
                                  );
                                }}
                                className="btn-insert-menu"
                                title="Insert row"
                              >
                                ≡
                              </button>
                              {showNestedTableRowMenu?.tableId === tableId &&
                               showNestedTableRowMenu?.nestedTableId === cell.id &&
                               showNestedTableRowMenu?.rowIdx === nestedRowIdx && (
                                <div className="insert-menu" style={{ overflow: 'visible' }} onClick={(e) => e.stopPropagation()}>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  insertNestedTableRow(contextPath, nestedRowIdx, 'above');
                                  setShowNestedTableRowMenu(null);
                                }}
                              >
                                ↑ Insert Above
                              </button>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  insertNestedTableRow(contextPath, nestedRowIdx, 'below');
                                  setShowNestedTableRowMenu(null);
                                }}
                              >
                                ↓ Insert Below
                              </button>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  moveNestedTableRow(contextPath, nestedRowIdx, 'up');
                                  setShowNestedTableRowMenu(null);
                                }}
                                disabled={cell.sort_column != null || nestedRowIdx === 0}
                                style={{ opacity: (cell.sort_column != null || nestedRowIdx === 0) ? 0.5 : 1 }}
                                title={cell.sort_column != null ? "Cannot move rows when sorting is active" : ""}
                              >
                                ⬆ Move Up
                              </button>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  moveNestedTableRow(contextPath, nestedRowIdx, 'down');
                                  setShowNestedTableRowMenu(null);
                                }}
                                disabled={cell.sort_column != null || nestedRowIdx === cell.num_rows - 1}
                                style={{ opacity: (cell.sort_column != null || nestedRowIdx === cell.num_rows - 1) ? 0.5 : 1 }}
                                title={cell.sort_column != null ? "Cannot move rows when sorting is active" : ""}
                              >
                                ⬇ Move Down
                              </button>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  copyNestedTableRow(tableId, cell.id, nestedRowIdx);
                                  setShowNestedTableRowMenu(null);
                                }}
                              >
                                📋 Copy Row
                              </button>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  pasteNestedTableRow(tableId, cell.id, nestedRowIdx);
                                  setShowNestedTableRowMenu(null);
                                }}
                                disabled={!copiedRow}
                                style={{ opacity: copiedRow ? 1 : 0.5 }}
                                title={copiedRow ? "Paste copied row below this row" : "No row copied"}
                              >
                                📄 Paste Row
                              </button>
                              {/* Row Border Styles */}
                              <div style={{ borderTop: '1px solid var(--border-primary)', margin: '4px 0', paddingTop: '4px' }} />
                              <div
                                style={{ position: 'relative', overflow: 'visible' }}
                                onMouseEnter={() => {
                                  if (rowBorderSubmenuTimeout) {
                                    clearTimeout(rowBorderSubmenuTimeout);
                                    setRowBorderSubmenuTimeout(null);
                                  }
                                  setShowRowBorderSubmenu({ tableId, nestedTableId: cell.id, rowIdx: nestedRowIdx });
                                }}
                                onMouseLeave={() => {
                                  const timeout = setTimeout(() => {
                                    setShowRowBorderSubmenu(null);
                                  }, 300);
                                  setRowBorderSubmenuTimeout(timeout);
                                }}
                              >
                                <button
                                  className="insert-menu-item"
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    background: showRowBorderSubmenu?.tableId === tableId && showRowBorderSubmenu?.nestedTableId === cell.id && showRowBorderSubmenu?.rowIdx === nestedRowIdx ? 'var(--bg-hover)' : undefined
                                  }}
                                >
                                  <span>🎨 Border Style</span>
                                  <span>▶</span>
                                </button>
                                {(showRowBorderSubmenu?.tableId === tableId && showRowBorderSubmenu?.nestedTableId === cell.id && showRowBorderSubmenu?.rowIdx === nestedRowIdx) && (
                                  <div
                                    className="insert-menu"
                                    style={{
                                      position: 'absolute',
                                      left: '100%',
                                      top: 0,
                                      marginLeft: '2px',
                                      minWidth: '150px',
                                      zIndex: 10000
                                    }}
                                    onMouseEnter={() => {
                                      if (rowBorderSubmenuTimeout) {
                                        clearTimeout(rowBorderSubmenuTimeout);
                                        setRowBorderSubmenuTimeout(null);
                                      }
                                    }}
                                    onMouseLeave={() => {
                                      const timeout = setTimeout(() => {
                                        setShowRowBorderSubmenu(null);
                                      }, 300);
                                      setRowBorderSubmenuTimeout(timeout);
                                    }}
                                  >
                                    <button
                                      className="insert-menu-item"
                                      onClick={() => {
                                        setRowBorderStyleAtPath(contextPath, nestedRowIdx, 'none');
                                        setShowNestedTableRowMenu(null);
                                        setShowRowBorderSubmenu(null);
                                      }}
                                      style={{
                                        background: !cell.row_border_styles?.[nestedRowIdx] ? 'var(--accent-light-bg)' : undefined
                                      }}
                                    >
                                      None
                                    </button>
                                    <button
                                      className="insert-menu-item"
                                      onClick={() => {
                                        setRowBorderStyleAtPath(contextPath, nestedRowIdx, 'double-top');
                                        setShowNestedTableRowMenu(null);
                                        setShowRowBorderSubmenu(null);
                                      }}
                                      style={{
                                        background: cell.row_border_styles?.[nestedRowIdx] === 'double-top' ? 'var(--accent-light-bg)' : undefined
                                      }}
                                    >
                                      ═ Double Above
                                    </button>
                                    <button
                                      className="insert-menu-item"
                                      onClick={() => {
                                        setRowBorderStyleAtPath(contextPath, nestedRowIdx, 'double-bottom');
                                        setShowNestedTableRowMenu(null);
                                        setShowRowBorderSubmenu(null);
                                      }}
                                      style={{
                                        background: cell.row_border_styles?.[nestedRowIdx] === 'double-bottom' ? 'var(--accent-light-bg)' : undefined
                                      }}
                                    >
                                      ═ Double Below
                                    </button>
                                    <button
                                      className="insert-menu-item"
                                      onClick={() => {
                                        setRowBorderStyleAtPath(contextPath, nestedRowIdx, 'thick-top');
                                        setShowNestedTableRowMenu(null);
                                        setShowRowBorderSubmenu(null);
                                      }}
                                      style={{
                                        background: cell.row_border_styles?.[nestedRowIdx] === 'thick-top' ? 'var(--accent-light-bg)' : undefined
                                      }}
                                    >
                                      ▬ Thick Above
                                    </button>
                                    <button
                                      className="insert-menu-item"
                                      onClick={() => {
                                        setRowBorderStyleAtPath(contextPath, nestedRowIdx, 'thick-bottom');
                                        setShowNestedTableRowMenu(null);
                                        setShowRowBorderSubmenu(null);
                                      }}
                                      style={{
                                        background: cell.row_border_styles?.[nestedRowIdx] === 'thick-bottom' ? 'var(--accent-light-bg)' : undefined
                                      }}
                                    >
                                      ▬ Thick Below
                                    </button>
                                  </div>
                                )}
                              </div>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  if (window.confirm('Delete this row?')) {
                                    deleteNestedTableRow(contextPath, nestedRowIdx);
                                    setShowNestedTableRowMenu(null);
                                  }
                                }}
                                style={{ color: '#ef4444' }}
                              >
                                🗑 Delete Row
                              </button>
                            </div>
                              )}
                            </div>
                          </div>
                        </td>
                      )}
                      {nestedRow.map((nestedCell, nestedColIdx) => {
                        // Check if cell contains a nested table (expanded or collapsed)
                        const hasNestedTable = nestedCell && typeof nestedCell === 'object' && nestedCell.type === 'nested_table';

                        // Check if ANY cell in this row has a nested table, expanded chart, or done picker
                        const rowHasNestedTable = nestedRow.some(c => c && typeof c === 'object' && c.type === 'nested_table');
                        const rowHasExpandedChart = nestedRow.some(c => c && typeof c === 'object' && c.type === 'chart' && c.expanded);
                        const rowHasDonePicker = nestedRow.some(c => c && typeof c === 'object' && c.type === 'done');
                        const rowHasSpecialContent = rowHasNestedTable || rowHasExpandedChart || rowHasDonePicker;

                        // Always use stored column width to ensure resize handles remain accessible
                        // For expanded nested tables/charts, the content will overflow/expand as needed via CSS
                        const colWidth = cell.column_widths?.[nestedColIdx] || 150;
                        const rowHeight = rowHasSpecialContent ? null : (cell.row_heights?.[nestedRowIdx] || 40);

                        // Create unique key that changes when nested table is toggled or border styles change
                        const cellKey = hasNestedTable
                          ? `${nestedColIdx}-${nestedCell.id}-${nestedCell.expanded ? 'exp' : 'col'}-${JSON.stringify(cell.column_border_styles || {})}`
                          : nestedColIdx;

                        return (
                          <td
                            key={cellKey}
                            style={{
                              width: `${colWidth}px`,
                              minWidth: `${colWidth}px`,
                              ...(rowHeight && density !== 'sub-compact' && { height: `${rowHeight}px` }),
                              ...getColumnBorderStyle(cell.column_border_styles, nestedColIdx),
                              ...getRowBorderStyle(cell.row_border_styles, nestedRowIdx)
                            }}
                          >
                            {/* Use renderCell with context path for all cell types */}
                            {renderCell(tableId, nestedCell, nestedRowIdx, nestedColIdx, contextPath)}
                            {/* Column resize handle */}
                            {!readOnly && (
                              <ResizeHandle
                                direction="col"
                                {...resizeElementOptimized('column', nestedColIdx, { tableId, nestedTableId: cell.id })}
                              />
                            )}
                            {/* Row resize handle */}
                            {!readOnly && nestedRowIdx < cell.rows.length - 1 && (
                              <ResizeHandle
                                direction="row"
                                {...resizeElementOptimized('row', nestedRowIdx, { tableId, nestedTableId: cell.id })}
                              />
                            )}
                          </td>
                        );
                      })}
                    </tr>
                    {/* Render expanded subtables for this row */}
                    {nestedSubtables.filter(st => st.expanded !== false).map(subtable => (
                      <tr key={subtable.id} className="subtable-row">
                        <td colSpan={nestedRow.length + 1 + (cell.show_row_headers ? 1 : 0)} style={{ padding: 0 }}>
                          <div style={{ width: 0, overflow: 'visible' }}>
                            <div style={{ width: 'max-content' }}>
                              {renderSubtablesRecursive(tableId, subtable, 1, contextPath)}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    } else {
      // Show icon when collapsed
      return (
        <div className="nested-table-collapsed" onClick={() => toggleNestedTable(contextPath)}>
          <span className="table-icon">⊞</span>
          <span className="nested-table-label">{cell.name}</span>
        </div>
      );
    }
  };

  // ============================================================
  // CHART CONFIGURATION CONSTANTS
  // ============================================================
  const getChartLayout = (xData) => {
    const isDark = theme === 'dark';
    return {
      width: xData.length > 7 ? 300 + (xData.length - 7) * 30 : 300,
      height: 250,
      margin: { l: 40, r: 20, t: 20, b: 40 },
      xaxis: {
        showgrid: true,
        title: 'X',
        gridcolor: isDark ? '#444' : '#e5e7eb',
        color: isDark ? '#e0e0e0' : '#333'
      },
      yaxis: {
        showgrid: true,
        title: 'Y',
        gridcolor: isDark ? '#444' : '#e5e7eb',
        color: isDark ? '#e0e0e0' : '#333'
      },
      plot_bgcolor: isDark ? '#3a3a3a' : 'white',
      paper_bgcolor: isDark ? '#3a3a3a' : 'white',
      font: { color: isDark ? '#e0e0e0' : '#333' }
    };
  };

  // Helper function to create colored chart traces (black for rising, red for declining)
  const createColoredChartTraces = (xData, yData) => {
    const traces = [];
    for (let i = 0; i < yData.length - 1; i++) {
      const isRising = yData[i + 1] >= yData[i];
      traces.push({
        x: [xData[i], xData[i + 1]],
        y: [yData[i], yData[i + 1]],
        type: 'scatter',
        mode: 'lines+markers',
        line: { color: isRising ? 'black' : 'red', width: 2 },
        marker: { color: isRising ? 'black' : 'red', size: 6 },
        showlegend: false,
        hoverinfo: 'y'
      });
    }
    return traces;
  };

  // ============================================================
  // IMAGE RENDERER (for embedded images)
  // ============================================================
  const renderImageCell = (imageData) => {
    // Parse image format: {IMAGE:url} or {IMAGE:data:image/png;base64,...}
    const imageMatch = imageData.match(/^\{IMAGE:(.*)\}$/s);
    if (!imageMatch) return null;

    const imageSrc = imageMatch[1].trim();

    return (
      <div style={{
        padding: '0.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100px'
      }}>
        <img
          src={imageSrc}
          alt="Cell image"
          style={{
            maxWidth: '100%',
            maxHeight: '400px',
            height: 'auto',
            width: 'auto',
            objectFit: 'contain',
            borderRadius: '4px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
          }}
          onError={(e) => {
            e.target.style.display = 'none';
            e.target.nextSibling.style.display = 'block';
          }}
        />
        <div style={{
          display: 'none',
          padding: '1rem',
          color: '#ef4444',
          fontSize: '0.85rem',
          textAlign: 'center'
        }}>
          ⚠️ Failed to load image
        </div>
      </div>
    );
  };

  // ============================================================
  // PLOTLY CHART RENDERER (for embedded Plotly JSON charts)
  // ============================================================
  const renderPlotlyChartCell = (plotlyJson) => {
    try {
      const plotlyData = JSON.parse(plotlyJson);

      // Detect if this is a polar/radar chart and adjust margins
      const isPolarChart = plotlyData.layout?.polar !== undefined;

      // Ensure proper margins to prevent label cutoff
      // Use Math.max to ensure we have at least minimum margins
      const originalMargin = plotlyData.layout?.margin || {};
      const minMargins = isPolarChart
        ? { l: 80, r: 80, t: 60, b: 40 } // Extra left/right margins for radar charts
        : { l: 60, r: 40, t: 60, b: 40 }; // Standard margins with space for title

      const mergedLayout = {
        ...plotlyData.layout,
        autosize: true,
        // Override layout dimensions to ensure it fits in cell
        width: plotlyData.layout?.width || 500,
        height: plotlyData.layout?.height || 400,
        paper_bgcolor: 'transparent',
        plot_bgcolor: 'transparent',
        margin: {
          l: Math.max(originalMargin.l || 0, minMargins.l),
          r: Math.max(originalMargin.r || 0, minMargins.r),
          t: Math.max(originalMargin.t || 0, minMargins.t),
          b: Math.max(originalMargin.b || 0, minMargins.b)
        },
        font: {
          ...plotlyData.layout?.font,
          color: theme === 'dark' ? '#e0e0e0' : '#333333'
        }
      };

      return (
        <div style={{
          padding: '0.5rem',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100px',
          position: 'relative'
        }}>
          <Plot
            data={plotlyData.data || []}
            layout={mergedLayout}
            config={{
              displayModeBar: true,
              displaylogo: false,
              modeBarButtonsToRemove: ['toImage'],
              responsive: true,
              modeBarButtonsToAdd: [],
              modeBarPosition: 'right', // Position modebar vertically on right side
              orientation: 'v' // Vertical orientation
            }}
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      );
    } catch (error) {
      console.error('Error rendering Plotly chart:', error);
      return (
        <div style={{
          padding: '1rem',
          color: '#ef4444',
          fontSize: '0.85rem',
          textAlign: 'center'
        }}>
          ⚠️ Error rendering chart: {error.message}
        </div>
      );
    }
  };

  // Unified ChartCell component - renders chart in expanded or collapsed state
  const renderChartCell = (cell, tableId, rowIdx, colIdx, options = {}) => {
    const { subtableId = null, nestedTableId = null, isReadOnly = false, contextPath = null } = options;

    // Use contextPath if provided, otherwise create from legacy parameters
    const actualContextPath = contextPath || createContextPath(tableId, subtableId, nestedTableId);

    // Get X and Y data (use new format or fall back to old format)
    const xData = cell.xData || Array.from({length: (cell.yData || cell.data || []).length}, (_, i) => i);
    const yData = cell.yData || cell.data || [];

    // Theme: match color scheme
    const isDark = theme === 'dark';

    // Get theme colors based on colorScheme
    const getThemeColors = () => {
      if (colorScheme === 'custom') {
        return deriveCustomPalette(customColor);
      }
      const colors = {
        blue: { light: '#eff6ff', lightAlt: '#dbeafe', medium: '#bfdbfe', primary: '#3b82f6', dark: '#1e40af', darkBg: '#1e3a5f', darkBorder: '#60a5fa' },
        green: { light: '#d1fae5', lightAlt: '#a7f3d0', medium: '#6ee7b7', primary: '#10b981', dark: '#047857', darkBg: '#064e3b', darkBorder: '#34d399' },
        purple: { light: '#f3e8ff', lightAlt: '#e9d5ff', medium: '#d8b4fe', primary: '#a855f7', dark: '#7e22ce', darkBg: '#581c87', darkBorder: '#c084fc' },
        orange: { light: '#ffedd5', lightAlt: '#fed7aa', medium: '#fdba74', primary: '#f97316', dark: '#c2410c', darkBg: '#7c2d12', darkBorder: '#fb923c' },
        grey: { light: '#f3f4f6', lightAlt: '#e5e7eb', medium: '#d1d5db', primary: '#6b7280', dark: '#4b5563', darkBg: '#374151', darkBorder: '#9ca3af' }
      };
      return colors[colorScheme] || colors.blue;
    };

    const themeColors = getThemeColors();

    const chartTheme = isReadOnly ? {
      background: isDark ? themeColors.darkBg : themeColors.light,
      border: `2px solid ${isDark ? themeColors.darkBorder : themeColors.primary}`,
      dataTableBg: isDark ? themeColors.darkBg : themeColors.lightAlt,
      dataTableBorder: isDark ? themeColors.darkBorder : themeColors.primary,
      headerBg: isDark ? themeColors.darkBorder : themeColors.medium,
      headerBorder: isDark ? themeColors.darkBorder : themeColors.primary,
      rowBorder: isDark ? themeColors.darkBorder : themeColors.medium,
      buttonColor: isDark ? themeColors.darkBorder : themeColors.dark,
      sparklineColor: isDark ? themeColors.darkBorder : themeColors.primary,
      inputBorder: isDark ? themeColors.darkBorder : themeColors.medium
    } : isDark ? {
      background: '#2d2d2d',
      border: '1px solid #444',
      dataTableBg: '#3a3a3a',
      dataTableBorder: '#555',
      headerBg: '#444',
      headerBorder: '#555',
      rowBorder: '#444',
      buttonColor: themeColors.darkBorder,
      sparklineColor: themeColors.primary,
      inputBorder: themeColors.darkBorder
    } : {
      background: '#f9fafb',
      border: '1px solid #e5e7eb',
      dataTableBg: themeColors.lightAlt,
      dataTableBorder: themeColors.primary,
      headerBg: themeColors.medium,
      headerBorder: themeColors.primary,
      rowBorder: themeColors.medium,
      buttonColor: themeColors.dark,
      sparklineColor: themeColors.primary,
      inputBorder: themeColors.medium
    };

    // Use updateCellGeneric with context path for deep nesting support
    const updateCellFn = (updatedCell) => {
      updateCellGeneric(tableId, rowIdx, colIdx, updatedCell, { contextPath: actualContextPath });
    };

    // Determine toggle chart function parameters
    const onToggleChart = () => {
      // Pass contextPath for deep nesting support
      toggleChart(tableId, rowIdx, colIdx, cell, subtableId, nestedTableId, actualContextPath);
    };

    if (cell.expanded) {
      // Expanded chart with data table
      return (
        <div
          style={{ padding: '0.5rem', background: chartTheme.background, borderRadius: '4px', border: chartTheme.border, position: 'relative', opacity: isReadOnly ? 0.95 : 1 }}
          onClick={(e) => {
            e.stopPropagation();
            setFocusedElement({ type: 'chart', tableId, rowIdx, colIdx, subtableId, nestedTableId, contextPath: actualContextPath });
            // Also update selectedCell so arrow keys work correctly
            setSelectedCell({
              tableId,
              rowIdx,
              colIdx,
              subtableId,
              nestedTableId,
              isSubtable: !!subtableId
            });
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
            <button
              onClick={onToggleChart}
              className="btn-nested-toggle"
              style={{ fontSize: '0.75rem', padding: '0.2rem 0.4rem' }}
            >
              ▼
            </button>
            <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
              {cell.title}{isReadOnly ? ' (Read-Only)' : ''}
            </span>
            {!isReadOnly && (
              <button
                onClick={async (e) => {
                  e.stopPropagation();
                  console.log('DELETE BUTTON CLICKED - chart expanded');
                  await updateCellFn(null);
                }}
                className="btn-delete-small"
                style={{ marginLeft: 'auto' }}
                title="Delete chart"
              >
                ×
              </button>
            )}
          </div>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
            {/* Chart */}
            <div style={{ flex: '0 0 auto' }}>
              <Plot
                data={createColoredChartTraces(xData, yData)}
                layout={getChartLayout(xData)}
                config={{ displayModeBar: false }}
              />
            </div>

            {/* Collapsible Data Table */}
            <div style={{ flex: cell.showDataTable ? '1 1 auto' : '0 0 auto', minWidth: cell.showDataTable ? '200px' : 'auto', marginRight: '8px' }}>
              <div style={{
                background: chartTheme.dataTableBg,
                borderRadius: '4px',
                border: `1px solid ${chartTheme.dataTableBorder}`,
                padding: '0.5rem'
              }}>
                <button
                  onClick={() => {
                    const updatedCell = {...cell, showDataTable: !cell.showDataTable};
                    updateCellFn(updatedCell);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontWeight: 600,
                    fontSize: '0.85rem',
                    color: chartTheme.buttonColor,
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.25rem',
                    marginBottom: cell.showDataTable ? '0.5rem' : '0',
                    outlineOffset: '-2px'
                  }}
                >
                  {cell.showDataTable ? '▼' : '▶'} Data
                </button>
                {cell.showDataTable && (
                  <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: chartTheme.headerBg, borderBottom: `2px solid ${chartTheme.headerBorder}`, position: 'sticky', top: 0, zIndex: 10 }}>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>X</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left', fontWeight: 600 }}>Y</th>
                          {!isReadOnly && <th style={{ padding: '0.5rem', width: '40px' }}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {xData.map((x, idx) => (
                          <tr key={idx} style={{ borderBottom: `1px solid ${chartTheme.rowBorder}` }}>
                            <td style={{ padding: isReadOnly ? '0.5rem' : '0.25rem' }}>
                              {isReadOnly ? (
                                <span style={{ fontSize: '0.85rem', color: '#666' }}>{x}</span>
                              ) : (
                                <input
                                  type="number"
                                  value={x}
                                  onChange={(e) => {
                                    const newXData = [...xData];
                                    const val = e.target.value;
                                    // Allow empty string or valid numbers (including while typing)
                                    newXData[idx] = val === '' ? 0 : parseFloat(val);
                                    const updatedCell = {...cell, xData: newXData};
                                    updateCellFn(updatedCell);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.stopPropagation()}
                                  style={{
                                    width: '100%',
                                    padding: '0.25rem',
                                    border: `1px solid ${chartTheme.inputBorder}`,
                                    borderRadius: '2px',
                                    fontSize: '0.85rem'
                                  }}
                                />
                              )}
                            </td>
                            <td style={{ padding: isReadOnly ? '0.5rem' : '0.25rem' }}>
                              {isReadOnly ? (
                                <span style={{ fontSize: '0.85rem', color: '#666' }}>{yData[idx]}</span>
                              ) : (
                                <input
                                  type="number"
                                  value={yData[idx]}
                                  onChange={(e) => {
                                    const newYData = [...yData];
                                    const val = e.target.value;
                                    // Allow empty string or valid numbers (including while typing)
                                    newYData[idx] = val === '' ? 0 : parseFloat(val);
                                    const updatedCell = {...cell, yData: newYData};
                                    updateCellFn(updatedCell);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.stopPropagation()}
                                  style={{
                                    width: '100%',
                                    padding: '0.25rem',
                                    border: `1px solid ${chartTheme.inputBorder}`,
                                    borderRadius: '2px',
                                    fontSize: '0.85rem'
                                  }}
                                />
                              )}
                            </td>
                            {!isReadOnly && (
                              <td style={{ padding: '0.25rem', textAlign: 'center' }}>
                                <button
                                  onClick={() => {
                                    const newXData = xData.filter((_, i) => i !== idx);
                                    const newYData = yData.filter((_, i) => i !== idx);
                                    const updatedCell = {...cell, xData: newXData, yData: newYData};
                                    updateCellFn(updatedCell);
                                  }}
                                  style={{
                                    background: 'transparent',
                                    border: 'none',
                                    cursor: 'pointer',
                                    color: '#ef4444',
                                    fontSize: '1rem',
                                    padding: '0',
                                    lineHeight: 1
                                  }}
                                  title="Delete row"
                                >
                                  ×
                                </button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {isReadOnly ? (
                      <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: chartTheme.buttonColor, fontStyle: 'italic' }}>
                        (Read-Only - Edit from source table)
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          const newXData = [...xData, (xData.length > 0 ? xData[xData.length - 1] + 1 : 1)];
                          const newYData = [...yData, 0];
                          const updatedCell = {...cell, xData: newXData, yData: newYData};
                          updateCellFn(updatedCell);
                        }}
                        style={{
                          marginTop: '0.5rem',
                          padding: '0.5rem',
                          background: chartTheme.buttonColor,
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          width: '100%',
                          color: 'white'
                        }}
                      >
                        + Add Row
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    } else {
      // Collapsed chart (sparkline)
      return (
        <div style={{ position: 'relative', padding: '0.5rem' }}>
          <div
            className="chart-collapsed"
            onClick={onToggleChart}
            style={{
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              opacity: isReadOnly ? 0.9 : 1
            }}
          >
            <span style={{ fontSize: '0.75rem', fontWeight: 600 }}>📈</span>
            <div className="sparkline-container">
              <Sparklines data={yData} width={100} height={30}>
                <SparklinesLine color={chartTheme.sparklineColor} />
              </Sparklines>
            </div>
          </div>
        </div>
      );
    }
  };

  // ============================================================
  // DATE CELL HANDLER (Phase 5)
  // ============================================================
  const renderDateCell = (cell, tableId, rowIdx, colIdx, options = {}) => {
    const { subtableId = null, nestedTableId = null, contextPath = null } = options;
    const isSubtable = !!subtableId;

    // Use contextPath if provided, otherwise create from legacy parameters
    const actualContextPath = contextPath || createContextPath(tableId, subtableId, nestedTableId);

    return (
      <div
        style={{ padding: '0.5rem', position: 'relative' }}
        onClick={(e) => {
          e.stopPropagation();
          setFocusedElement({ type: 'datepicker', tableId, rowIdx, colIdx, subtableId, nestedTableId });
          setSelectedCell({
            tableId,
            rowIdx,
            colIdx,
            subtableId,
            nestedTableId,
            isSubtable: !!subtableId
          });
        }}
      >
        <DatePicker
          selected={cell.value ? new Date(cell.value) : new Date()}
          onChange={(date) => {
            // Use updateCellGeneric with context path for deep nesting support
            const updatedCell = {
              ...cell,
              value: date.toISOString()
            };
            updateCellGeneric(tableId, rowIdx, colIdx, updatedCell, { contextPath: actualContextPath });
          }}
          showTimeSelect
          timeFormat="h:mm aa"
          timeIntervals={15}
          dateFormat="MM/dd/yyyy h:mm aa"
          className="date-picker-input"
          popperPlacement="bottom-start"
          open={isDatePickerOpen(tableId, rowIdx, colIdx, subtableId, nestedTableId)}
          onInputClick={() => toggleDatePicker(tableId, rowIdx, colIdx, subtableId, nestedTableId)}
        />
        {isDatePickerOpen(tableId, rowIdx, colIdx, subtableId, nestedTableId) && (
          <button
            onClick={async (e) => {
              e.stopPropagation();
              // Use updateCellGeneric with context path for deep nesting support
              await updateCellGeneric(tableId, rowIdx, colIdx, null, { contextPath: actualContextPath });
            }}
            className="btn-delete-small"
            style={{ position: 'absolute', top: '0.25rem', right: '0.25rem', zIndex: 1000 }}
            title="Delete date picker"
          >
            ×
          </button>
        )}
      </div>
    );
  };

  // ============================================================
  // TABLE REFERENCE CELL HANDLER (Phase 5)
  // ============================================================
  const renderReferenceCell = (cell, tableId, rowIdx, colIdx, options = {}) => {
    const { subtableId = null, nestedTableId = null, contextPath = null, readOnly = false } = options;
    const isSubtable = !!subtableId;

    // Use contextPath if provided, otherwise create from legacy parameters
    const actualContextPath = contextPath || createContextPath(tableId, subtableId, nestedTableId);

    // Find the referenced table
    const referencedTable = tables.find(t => t.id === cell.table_id);

    if (!referencedTable) {
      return (
        <div style={{ padding: '0.5rem', color: '#ef4444', fontSize: '0.85rem' }}>
          ⚠️ Referenced table not found
        </div>
      );
    }

    if (cell.expanded) {
      // Show full read-only table when expanded
      return (
        <div
          className="nested-table-wrapper reference-table-wrapper"
          onClick={(e) => {
            e.stopPropagation();
            setFocusedElement({ type: 'table_reference', tableId, rowIdx, colIdx, subtableId, nestedTableId: null });
            setSelectedCell({
              tableId,
              rowIdx,
              colIdx,
              subtableId,
              nestedTableId: null,
              isSubtable: !!subtableId
            });
          }}
        >
          {renderControlHeader({
            isExpanded: true,
            title: cell.table_name,
            icon: '🔗',
            showToggle: false,
            className: 'nested-table-header',
            controlId: borderStyle !== 'minimalist' ? `ref-nested-${cell.id || `${rowIdx}-${colIdx}`}` : null,
            controlButtons: borderStyle !== 'minimalist' ? [
            {
              label: '🗑',
              onClick: async (e) => {
                e.stopPropagation();
                // Use updateCellGeneric with context path for deep nesting support
                await updateCellGeneric(tableId, rowIdx, colIdx, null, { contextPath: actualContextPath });
              },
              danger: true,
              title: 'Delete table reference'
            }
          ] : []
        })}
          {borderStyle === 'minimalist' && !referencedTable.show_headers && renderMinimalistControlsBar(`ref-nested-${cell.id || `${rowIdx}-${colIdx}`}`, [
            {
              label: '🗑',
              onClick: async (e) => {
                e.stopPropagation();
                await updateCellGeneric(tableId, rowIdx, colIdx, null, { contextPath: actualContextPath });
              },
              danger: true,
              title: 'Delete table reference'
            }
          ])}
          <table className="nested-table-inner">
            {referencedTable.show_headers && renderTableHeaderRow({
              columnHeaders: referencedTable.column_headers,
              numCols: referencedTable.num_cols,
              showRowHeaders: referencedTable.show_row_headers,
              showControls: false,
              theme: 'default',
              sizing: 'medium',
              translationColumns: referencedTable.translation_columns
            })}
            <tbody>
              {referencedTable.rows.map((refRow, refRowIdx) => {
                const rowSubtables = referencedTable.row_subtables?.[`row-${refRowIdx}`] || [];

                return (
                <React.Fragment key={refRowIdx}>
                  <tr>
                    {/* No row controls for referenced tables (showControls: false in header) */}
                    {referencedTable.show_row_headers && (
                      <td className="table-row-header-cell" style={{ padding: '0.5rem', fontSize: '0.85rem', fontWeight: 600 }}>
                        <LatexText text={(referencedTable.row_headers || [])[refRowIdx] || `Row ${refRowIdx + 1}`} />
                      </td>
                    )}
                    {refRow.map((refCell, refColIdx) => (
                      <td
                        key={refColIdx}
                        className="reference-table-cell"
                      >
                        {renderCell(referencedTable.id, refCell, refRowIdx, refColIdx, false, null, null)}
                      </td>
                    ))}
                  </tr>
                  {/* Render expanded subtables only */}
                  {rowSubtables.filter(st => st.expanded !== false).length > 0 && (
                    <tr>
                      <td
                        colSpan={referencedTable.num_cols + (referencedTable.show_row_headers ? 1 : 0) + 1}
                        style={{ padding: 0, border: 'none' }}
                      >
                        {rowSubtables
                          .filter(subtable => subtable.expanded !== false)
                          .map((subtable) =>
                            renderSubtablesRecursive(referencedTable.id, subtable, 1, null)
                          )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
              })}
            </tbody>
          </table>
        </div>
      );
    }

    // Show collapsed reference (just the link)
    return (
      <div
        className="reference-table-collapsed"
        onClick={(e) => {
          e.stopPropagation();
          toggleTableReference(tableId, rowIdx, colIdx, cell, { contextPath: actualContextPath });
        }}
      >
        <span style={{ fontSize: '1rem' }}>🔗</span>
        <span className="reference-table-name">{cell.table_name}</span>
      </div>
    );
  };

  // ============================================================
  // COLOR PICKER CELL HANDLER
  // ============================================================
  const renderColorPickerCell = (cell, tableId, rowIdx, colIdx, options = {}) => {
    const { subtableId = null, nestedTableId = null, contextPath = null } = options;

    // Use contextPath if provided, otherwise create from legacy parameters
    const actualContextPath = contextPath || createContextPath(tableId, subtableId, nestedTableId);

    const currentColor = cell.value || '#3b82f6';

    // Calculate contrasting text color (white for dark backgrounds, black for light)
    const getContrastColor = (hexColor) => {
      const r = parseInt(hexColor.slice(1, 3), 16);
      const g = parseInt(hexColor.slice(3, 5), 16);
      const b = parseInt(hexColor.slice(5, 7), 16);
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      return luminance > 0.5 ? '#000000' : '#ffffff';
    };

    const textColor = getContrastColor(currentColor);

    return (
      <div
        className="color-picker-cell"
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          backgroundColor: currentColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          minHeight: '40px'
        }}
        onClick={(e) => {
          e.stopPropagation();
          setFocusedElement({ type: 'colorpicker', tableId, rowIdx, colIdx, subtableId, nestedTableId });
          setSelectedCell({
            tableId,
            rowIdx,
            colIdx,
            subtableId,
            nestedTableId,
            isSubtable: !!subtableId
          });
        }}
      >
        <input
          type="color"
          value={currentColor}
          onChange={(e) => {
            const updatedCell = {
              ...cell,
              value: e.target.value
            };
            updateCellGeneric(tableId, rowIdx, colIdx, updatedCell, { contextPath: actualContextPath });
          }}
          onClick={(e) => e.stopPropagation()}
          onBlur={(e) => {
            // Close color picker when clicking away
            e.target.blur();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.target.blur();
            }
          }}
          style={{
            opacity: 0,
            position: 'absolute',
            width: '100%',
            height: '100%',
            cursor: 'pointer',
            left: 0,
            top: 0,
            zIndex: 1
          }}
        />
        <button
          className="color-picker-delete"
          onClick={async (e) => {
            e.stopPropagation();
            await updateCellGeneric(tableId, rowIdx, colIdx, null, { contextPath: actualContextPath });
          }}
          title="Delete color picker"
          style={{
            position: 'absolute',
            top: '4px',
            right: '4px',
            padding: '0.2rem 0.4rem',
            fontSize: '0.9rem',
            backgroundColor: textColor === '#ffffff' ? 'rgba(0,0,0,0.6)' : 'rgba(255,255,255,0.6)',
            color: textColor,
            border: 'none',
            borderRadius: '3px',
            cursor: 'pointer',
            opacity: 0,
            transition: 'opacity 0.2s',
            zIndex: 2,
            pointerEvents: 'auto'
          }}
        >
          ×
        </button>
      </div>
    );
  };

  // ============================================================
  // PRIORITY PICKER CELL HANDLER
  // ============================================================
  const renderPriorityPickerCell = (cell, tableId, rowIdx, colIdx, options = {}) => {
    const { subtableId = null, nestedTableId = null, contextPath = null } = options;

    // Use contextPath if provided, otherwise create from legacy parameters
    const actualContextPath = contextPath || createContextPath(tableId, subtableId, nestedTableId);

    const priorities = {
      'high': { label: 'High', color: '#ef4444', bgColor: '#fee2e2' },
      'medium': { label: 'Medium', color: '#f97316', bgColor: '#ffedd5' },
      'low': { label: 'Low', color: '#eab308', bgColor: '#fef9c3' }
    };

    const currentPriority = cell.value || 'medium';
    const priorityInfo = priorities[currentPriority] || priorities['medium'];

    return (
      <div
        style={{
          padding: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
        onClick={(e) => {
          e.stopPropagation();
          setFocusedElement({ type: 'priority', tableId, rowIdx, colIdx, subtableId, nestedTableId });
          setSelectedCell({
            tableId,
            rowIdx,
            colIdx,
            subtableId,
            nestedTableId,
            isSubtable: !!subtableId
          });
        }}
      >
        <select
          value={currentPriority}
          onChange={async (e) => {
            if (e.target.value === 'remove') {
              // Remove the priority picker
              await updateCellGeneric(tableId, rowIdx, colIdx, null, { contextPath: actualContextPath });
            } else {
              // Update priority value
              const updatedCell = {
                ...cell,
                value: e.target.value
              };
              updateCellGeneric(tableId, rowIdx, colIdx, updatedCell, { contextPath: actualContextPath });
            }
          }}
          style={{
            padding: '0.5rem 0.8rem',
            border: `2px solid ${priorityInfo.color}`,
            borderRadius: '6px',
            backgroundColor: priorityInfo.bgColor,
            color: priorityInfo.color,
            fontWeight: '600',
            fontSize: '0.9rem',
            cursor: 'pointer',
            outline: 'none',
            flex: 1
          }}
        >
          {Object.entries(priorities).map(([key, info]) => (
            <option key={key} value={key}>
              {info.label}
            </option>
          ))}
          <option value="remove">Remove Priority</option>
        </select>
      </div>
    );
  };

  // ============================================================
  // DONE PICKER CELL HANDLER
  // ============================================================
  const renderDonePickerCell = (cell, tableId, rowIdx, colIdx, options = {}) => {
    const { subtableId = null, nestedTableId = null, contextPath = null } = options;

    // Use contextPath if provided, otherwise create from legacy parameters
    const actualContextPath = contextPath || createContextPath(tableId, subtableId, nestedTableId);

    const doneOptions = {
      'thumbs-up': { label: '👍 Done!', icon: '👍', color: '#10b981', bgColor: '#d1fae5' },
      'checkmark': { label: '✓ Done!', icon: '✓', color: '#059669', bgColor: '#a7f3d0' },
      'animated-check': { label: '✓ Done!', icon: '✓', color: '#047857', bgColor: '#6ee7b7' }
    };

    const currentDone = cell.value || 'checkmark';
    const doneInfo = doneOptions[currentDone] || doneOptions['checkmark'];

    return (
      <div
        style={{
          padding: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}
        onClick={(e) => {
          e.stopPropagation();
          setFocusedElement({ type: 'done', tableId, rowIdx, colIdx, subtableId, nestedTableId });
          setSelectedCell({
            tableId,
            rowIdx,
            colIdx,
            subtableId,
            nestedTableId,
            isSubtable: !!subtableId
          });
        }}
      >
        <select
          value={currentDone}
          onChange={(e) => {
            if (e.target.value === 'remove') {
              // Remove the done picker (no await - optimistic update)
              updateCellGeneric(tableId, rowIdx, colIdx, null, { contextPath: actualContextPath });
            } else {
              // Update done value (no await - optimistic update)
              const updatedCell = {
                ...cell,
                value: e.target.value
              };
              updateCellGeneric(tableId, rowIdx, colIdx, updatedCell, { contextPath: actualContextPath });
            }
          }}
          style={{
            padding: '0.5rem 0.8rem',
            border: `2px solid ${doneInfo.color}`,
            borderRadius: '6px',
            backgroundColor: doneInfo.bgColor,
            color: doneInfo.color,
            fontWeight: '600',
            fontSize: '0.9rem',
            cursor: 'pointer',
            outline: 'none',
            flex: 1,
            animation: currentDone === 'animated-check' ? 'pulse 2s ease-in-out infinite' : 'none'
          }}
        >
          {Object.entries(doneOptions).map(([key, info]) => (
            <option key={key} value={key}>
              {info.label}
            </option>
          ))}
          <option value="remove">Remove Done</option>
        </select>
      </div>
    );
  };

  // ============================================================
  // UNIFIED TABLE HEADER COMPONENT (Phase 2b)
  // Consolidates all table header rendering logic
  // ============================================================
  const renderTableHeaderRow = (options = {}) => {
    const {
      columnHeaders,
      numCols,
      showRowHeaders = false,
      showControls = true,
      theme = 'default', // 'default' (gray) or 'amber'
      sizing = 'medium', // 'small' (0.75rem, 80px), 'medium' (0.85rem, 100px), 'large' (0.75rem, 120px)
      isEditable = false, // If true, enables column header editing and resizing
      onHeaderEdit = null, // Function to call when header is edited: (colIdx, newValue) => {}
      onHeaderResize = null, // Function to call for resize: (colIdx, event) => {}
      columnWidths = null, // Array of column widths for resizable headers
      showHeaderMenu = false, // If true, shows insert column menu button
      onInsertColumn = null, // Function to call for column insertion: (colIdx, direction) => {}
      onMoveColumn = null, // Function to call for column movement: (colIdx, direction) => {}
      onDeleteColumn = null, // Function to call for column deletion: (colIdx) => {}
      onCopyColumn = null, // Function to call for column copy: (colIdx) => {}
      onPasteColumn = null, // Function to call for column paste: (colIdx, direction) => {}
      copiedColumn = null, // Copied column data to enable/disable paste button
      showColMenu = null, // State variable tracking which column menu is open: {tableId, colIdx} or {subtableId, colIdx} or {nestedTableId, colIdx}
      setShowColMenu = null, // State setter function
      menuIdentifier = null, // Object identifying this table (e.g., {tableId: 'abc'} or {subtableId: 'xyz'})
      rowHeaderWidth = null, // Width of the row header column
      onRowHeaderResize = null, // Function to call for row header resize: (newWidth) => {}
      sortColumn = null, // Current sort column index
      sortDirection = null, // Current sort direction: 'asc' or 'desc'
      onSort = null, // Function to call when column header is clicked for sorting: (colIdx) => {}
      extraButtons = null, // Extra buttons to display in minimalist mode (array of {label, onClick, active, title})
      controlsId = null, // Unique identifier for this table's controls (e.g., 'subtable-123' or 'nested-456')
      doneColumn = null, // Current column index that has "Done↓" enabled
      onSetDoneColumn = null, // Function to call to set/unset done column: (colIdx) => {}
      skipCornerCells = false, // If true, don't render corner cells (they're rowspanned from header rows above)
      returnRowOnly = false, // If true, return just the <tr> without <thead> wrapper
      columnBorderStyles = null, // Column border styles object: {colIdx: 'double-left', ...}
      onSetColumnBorderStyle = null, // Function to call to set column border style: (colIdx, borderStyle) => {}
      showColBorderSubmenu = null, // State variable tracking which column's border submenu is open
      setShowColBorderSubmenu = null, // State setter function for border submenu
      borderSubmenuTimeout = null, // Timeout for delayed border submenu closing
      setBorderSubmenuTimeout = null, // State setter function for border submenu timeout
      translationColumns = null, // Translation column configuration: {colIdx: {sourceColumn, sourceLang, targetLang}, ...}
      allSubtablesForToggle = null, // Array of all subtables in this table/subtable for table-wide show/hide
      onBulkExpand = null, // (ids) => {} — bulk show subtables
      onBulkCollapse = null, // (ids) => {} — bulk hide subtables
    } = options;

    // Theme configurations
    const themes = {
      default: { background: '#f8f9fa', textColor: 'inherit', borderColor: '#ddd' },
      amber: { background: '#3b82f6', textColor: 'white', borderColor: '#3b82f6' }
    };

    const sizes = {
      small: { fontSize: '0.75rem', rowHeaderWidth: '80px', padding: '0.4rem' },
      medium: { fontSize: '0.85rem', rowHeaderWidth: '100px', padding: '0.5rem' },
      large: { fontSize: '0.75rem', rowHeaderWidth: '120px', padding: '0.75rem' }
    };

    const currentTheme = themes[theme];
    const currentSize = sizes[sizing];

    // Generate column headers if not provided
    const headers = columnHeaders || Array.from({ length: numCols }, (_, i) => `Column ${i + 1}`);

    const headerRow = (
        <tr>
          {!skipCornerCells && showControls && (
            <th className="row-controls">
              {allSubtablesForToggle && allSubtablesForToggle.length > 0 && (() => {
                const anyExp = allSubtablesForToggle.some(st => st.expanded !== false);
                return (
                  <button
                    onClick={() => {
                      if (anyExp && onBulkCollapse) {
                        const ids = allSubtablesForToggle.filter(st => st.expanded !== false).map(st => st.id);
                        onBulkCollapse(ids);
                      } else if (!anyExp && onBulkExpand) {
                        const ids = allSubtablesForToggle.filter(st => st.expanded === false).map(st => st.id);
                        onBulkExpand(ids);
                      }
                    }}
                    title={anyExp ? 'Hide all subtables' : 'Show all subtables'}
                    style={{
                      fontSize: '0.65rem',
                      padding: '2px 5px',
                      border: `1px solid ${theme === 'dark' ? '#666' : '#ccc'}`,
                      borderRadius: '3px',
                      background: theme === 'dark' ? '#3a3a3a' : '#f5f5f5',
                      color: theme === 'dark' ? '#ccc' : '#555',
                      cursor: 'pointer',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {anyExp ? '▼ Hide all' : '▶ Show all'}
                  </button>
                );
              })()}
            </th>
          )}
          {!skipCornerCells && showRowHeaders && (
            <th
              className="table-row-header-cell"
              style={{
                ...(theme === 'amber' && { background: currentTheme.background, color: currentTheme.textColor }),
                width: rowHeaderWidth ? `${rowHeaderWidth}px` : currentSize.rowHeaderWidth,
                minWidth: rowHeaderWidth ? `${rowHeaderWidth}px` : currentSize.rowHeaderWidth,
                fontSize: currentSize.fontSize,
                position: 'relative'
              }}
            >
              {onRowHeaderResize && (
                <ResizeHandle
                  direction="col"
                  onResize={onRowHeaderResize}
                />
              )}
            </th>
          )}
          {headers.map((header, colIdx) => (
            <th
              key={colIdx}
              className="table-column-header-cell"
              style={{
                ...(theme === 'amber' && { background: currentTheme.background, color: currentTheme.textColor }),
                // Don't override font styles in minimalist mode - let CSS handle it
                ...(borderStyle !== 'minimalist' && {
                  fontWeight: 600,
                  padding: currentSize.padding,
                  fontSize: currentSize.fontSize
                }),
                ...(columnWidths && columnWidths[colIdx] ? { width: `${columnWidths[colIdx]}px` } : {}),
                position: 'relative',
                ...getColumnBorderStyle(columnBorderStyles, colIdx)
              }}
            >
              {isEditable ? (
                <>
                  <div
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.25rem', flex: 1 }}
                  >
                    <EditableLatexHeader
                      value={header}
                      onSave={(newValue) => onHeaderEdit && onHeaderEdit(colIdx, newValue)}
                      fontSize={currentSize.fontSize}
                      onSort={onSort ? () => onSort(colIdx) : null}
                      textAlign='center'
                    />
                    {translationColumns?.[colIdx] && (
                      <span style={{ fontSize: '0.7rem', opacity: 0.7, marginLeft: '4px' }}>
                        🌐
                      </span>
                    )}
                    {onSort && sortColumn === colIdx && sortDirection && (
                      <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                  {onHeaderResize && (
                    <div
                      className="column-resizer"
                      onMouseDown={(e) => onHeaderResize(colIdx, e)}
                    ></div>
                  )}
                  {showHeaderMenu && onInsertColumn && setShowColMenu && menuIdentifier && (
                    <>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Check if this menu is already open
                          const isOpen = showColMenu &&
                            Object.keys(menuIdentifier).every(key => showColMenu[key] === menuIdentifier[key]) &&
                            showColMenu.colIdx === colIdx;
                          setShowColMenu(isOpen ? null : { ...menuIdentifier, colIdx });
                        }}
                        className="btn-insert-menu"
                        title="Insert column"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          right: '2px'
                        }}
                      >
                        ≡
                      </button>
                      {showColMenu &&
                       Object.keys(menuIdentifier).every(key => showColMenu[key] === menuIdentifier[key]) &&
                       showColMenu.colIdx === colIdx && (
                        <div className="insert-menu" style={{ right: 0, left: 'auto', overflow: 'visible' }} onClick={(e) => e.stopPropagation()}>
                          {onSort && (
                            <>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  onSort(colIdx);
                                  setShowColMenu(null);
                                }}
                                disabled={sortColumn === colIdx && sortDirection === 'asc'}
                                style={{
                                  opacity: sortColumn === colIdx && sortDirection === 'asc' ? 0.5 : 1
                                }}
                              >
                                ↑ Sort Ascending
                              </button>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  onSort(colIdx);
                                  setShowColMenu(null);
                                }}
                                disabled={sortColumn === colIdx && sortDirection === 'desc'}
                                style={{
                                  opacity: sortColumn === colIdx && sortDirection === 'desc' ? 0.5 : 1
                                }}
                              >
                                ↓ Sort Descending
                              </button>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  onSort(colIdx);
                                  setShowColMenu(null);
                                }}
                                disabled={sortColumn !== colIdx}
                                style={{
                                  opacity: sortColumn !== colIdx ? 0.5 : 1
                                }}
                              >
                                ✕ Clear Sort
                              </button>
                            </>
                          )}
                          {onSetDoneColumn && (
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                console.log('[Done Column Button] Clicked! colIdx:', colIdx, 'current doneColumn:', doneColumn);
                                onSetDoneColumn(colIdx);
                                setShowColMenu(null);
                              }}
                              style={{
                                background: doneColumn === colIdx ? 'var(--accent-primary)' : undefined,
                                color: doneColumn === colIdx ? 'white' : undefined
                              }}
                            >
                              {doneColumn === colIdx ? 'Done↓ ✓' : 'Done↓'}
                            </button>
                          )}
                          <button className="insert-menu-item" onClick={() => {
                            onInsertColumn(colIdx, 'before');
                            setShowColMenu(null);
                          }}>
                            ← Insert Left
                          </button>
                          <button className="insert-menu-item" onClick={() => {
                            onInsertColumn(colIdx, 'after');
                            setShowColMenu(null);
                          }}>
                            → Insert Right
                          </button>
                          {onMoveColumn && (
                            <>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  onMoveColumn(colIdx, 'left');
                                  setShowColMenu(null);
                                }}
                                disabled={colIdx === 0}
                                style={{ opacity: colIdx === 0 ? 0.5 : 1 }}
                              >
                                ⬅ Move Left
                              </button>
                              <button
                                className="insert-menu-item"
                                onClick={() => {
                                  onMoveColumn(colIdx, 'right');
                                  setShowColMenu(null);
                                }}
                                disabled={colIdx === numCols - 1}
                                style={{ opacity: colIdx === numCols - 1 ? 0.5 : 1 }}
                              >
                                ➡ Move Right
                              </button>
                            </>
                          )}
                          {onCopyColumn && (
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                onCopyColumn(colIdx);
                                setShowColMenu(null);
                              }}
                            >
                              📋 Copy Column
                            </button>
                          )}
                          {onPasteColumn && (
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                onPasteColumn(colIdx);
                                setShowColMenu(null);
                              }}
                              disabled={!copiedColumn}
                              style={{ opacity: copiedColumn ? 1 : 0.5 }}
                              title={copiedColumn ? "Paste copied column into this column" : "No column copied"}
                            >
                              📄 Paste Column
                            </button>
                          )}
                          {/* Translation Setup */}
                          <div style={{ borderTop: '1px solid var(--border-primary)', margin: '4px 0' }} />
                          <button
                            className="insert-menu-item"
                            onClick={() => {
                              setShowTranslationDialog({ ...menuIdentifier, colIdx });
                              setShowColMenu(null);
                            }}
                            style={{
                              background: translationColumns?.[colIdx] ? 'var(--accent-light-bg)' : undefined
                            }}
                          >
                            🌐 {translationColumns?.[colIdx] ? 'Edit Translation' : 'Setup Translation'}
                          </button>
                          {onSetColumnBorderStyle && (
                            <div
                              style={{ position: 'relative', overflow: 'visible' }}
                              onMouseEnter={() => {
                                if (borderSubmenuTimeout) {
                                  clearTimeout(borderSubmenuTimeout);
                                  setBorderSubmenuTimeout(null);
                                }
                                setShowColBorderSubmenu({ ...menuIdentifier, colIdx });
                              }}
                              onMouseLeave={() => {
                                const timeout = setTimeout(() => {
                                  setShowColBorderSubmenu(null);
                                }, 300);
                                setBorderSubmenuTimeout(timeout);
                              }}
                            >
                              <button
                                className="insert-menu-item"
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  background: showColBorderSubmenu && Object.keys(menuIdentifier).every(key => showColBorderSubmenu[key] === menuIdentifier[key]) && showColBorderSubmenu.colIdx === colIdx ? 'var(--bg-hover)' : undefined
                                }}
                              >
                                <span>🎨 Border Style</span>
                                <span>▶</span>
                              </button>
                              {(showColBorderSubmenu && Object.keys(menuIdentifier).every(key => showColBorderSubmenu[key] === menuIdentifier[key]) && showColBorderSubmenu.colIdx === colIdx) && (
                                <div
                                  className="insert-menu"
                                  style={{
                                    position: 'absolute',
                                    left: '100%',
                                    top: 0,
                                    marginLeft: '2px',
                                    minWidth: '150px',
                                    zIndex: 10000
                                  }}
                                  onMouseEnter={() => {
                                    if (borderSubmenuTimeout) {
                                      clearTimeout(borderSubmenuTimeout);
                                      setBorderSubmenuTimeout(null);
                                    }
                                  }}
                                  onMouseLeave={() => {
                                    const timeout = setTimeout(() => {
                                      setShowColBorderSubmenu(null);
                                    }, 300);
                                    setBorderSubmenuTimeout(timeout);
                                  }}
                                >
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      onSetColumnBorderStyle(colIdx, 'none');
                                      setShowColMenu(null);
                                      setShowColBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: !columnBorderStyles?.[colIdx] ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    None
                                  </button>
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      onSetColumnBorderStyle(colIdx, 'double-left');
                                      setShowColMenu(null);
                                      setShowColBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: columnBorderStyles?.[colIdx] === 'double-left' ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    ║ Double Left
                                  </button>
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      onSetColumnBorderStyle(colIdx, 'double-right');
                                      setShowColMenu(null);
                                      setShowColBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: columnBorderStyles?.[colIdx] === 'double-right' ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    ║ Double Right
                                  </button>
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      onSetColumnBorderStyle(colIdx, 'thick-left');
                                      setShowColMenu(null);
                                      setShowColBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: columnBorderStyles?.[colIdx] === 'thick-left' ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    ▌Thick Left
                                  </button>
                                  <button
                                    className="insert-menu-item"
                                    onClick={() => {
                                      onSetColumnBorderStyle(colIdx, 'thick-right');
                                      setShowColMenu(null);
                                      setShowColBorderSubmenu(null);
                                    }}
                                    style={{
                                      background: columnBorderStyles?.[colIdx] === 'thick-right' ? 'var(--accent-light-bg)' : undefined
                                    }}
                                  >
                                    ▌Thick Right
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                          {onDeleteColumn && (
                            <button
                              className="insert-menu-item"
                              onClick={() => {
                                if (window.confirm('Delete this column?')) {
                                  onDeleteColumn(colIdx);
                                  setShowColMenu(null);
                                }
                              }}
                              style={{ color: '#ef4444' }}
                            >
                              🗑 Delete Column
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}
                </>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <LatexText text={header} />
                  {onSort && sortColumn === colIdx && sortDirection && (
                    <span style={{ fontSize: '0.8rem', opacity: 0.7 }}>
                      {sortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              )}
            </th>
          ))}
          {borderStyle === 'minimalist' && extraButtons && extraButtons.length > 0 && controlsId && (
            <th
              className="minimalist-controls-header"
              style={{
                textAlign: 'right',
                paddingRight: '0.5rem',
                whiteSpace: 'nowrap',
                position: 'relative',
                overflow: 'visible'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    maxWidth: expandedControls[controlsId] ? '500px' : '0px',
                    opacity: expandedControls[controlsId] ? 1 : 0,
                    overflow: 'hidden',
                    transition: 'max-width 0.3s ease, opacity 0.3s ease',
                    marginRight: '0.25rem'
                  }}
                >
                  {extraButtons.map((btn, idx) => (
                    <button
                      key={idx}
                      onClick={btn.onClick}
                      className="minimalist-header-btn"
                      title={btn.title}
                      style={{
                        marginLeft: '0.5rem',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        background: btn.danger ? 'var(--accent-primary)' : (btn.active ? 'var(--accent-primary)' : 'rgba(0,0,0,0.1)'),
                        color: btn.danger ? 'white' : (btn.active ? 'white' : 'var(--text-primary)'),
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontWeight: 600,
                        flexShrink: 0
                      }}
                    >
                      {btn.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={() => setExpandedControls(prev => ({ ...prev, [controlsId]: !prev[controlsId] }))}
                  className="minimalist-header-btn"
                  title={expandedControls[controlsId] ? 'Hide controls' : 'Show controls'}
                  style={{
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    background: 'rgba(0,0,0,0.1)',
                    color: 'var(--text-primary)',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    flexShrink: 0,
                    lineHeight: '1',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  {expandedControls[controlsId] ? '◀' : <span style={{ transform: 'rotate(90deg)', display: 'inline-block', fontSize: '1rem' }}>≡</span>}
                </button>
              </div>
            </th>
          )}
        </tr>
    );

    return returnRowOnly ? headerRow : <thead>{headerRow}</thead>;
  };

  // ============================================================
  // UNIFIED CONTROL HEADER COMPONENT (Phase 3)
  // Consolidates control headers for subtables, nested tables, and references
  // ============================================================
  const renderControlHeader = (options = {}) => {
    const {
      isExpanded = true,
      title,
      icon = null,
      onToggle,
      actions = [],
      className = 'subtable-header',
      isEditing = false,
      editingValue = '',
      onEditStart = null,
      onEditChange = null,
      onEditBlur = null,
      onEditKeyDown = null,
      showToggle = true,  // New parameter to control collapse button visibility
      controlButtons = [],  // New parameter for slider control buttons
      controlId = null  // ID for tracking expanded state
    } = options;

    return (
      <div className={className}>
        {showToggle && onToggle && (
          <button
            onClick={onToggle}
            className="btn-toggle"
          >
            {isExpanded ? '▼' : '▶'}
          </button>
        )}
        {isEditing ? (
          <input
            type="text"
            value={editingValue}
            onChange={onEditChange}
            onBlur={onEditBlur}
            onKeyDown={onEditKeyDown}
            autoFocus
            style={{
              fontSize: '1rem',
              fontWeight: '600',
              padding: '0.2rem 0.4rem',
              border: '2px solid var(--accent-primary)',
              borderRadius: '4px',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              marginLeft: '0.5rem'
            }}
          />
        ) : (
          <span
            className="subtable-name"
            onClick={onEditStart}
            style={{ cursor: onEditStart ? 'pointer' : 'default' }}
            title={onEditStart ? 'Click to edit name' : ''}
          >
            {icon && `${icon} `}{title}
          </span>
        )}
        {/* Inline slider control buttons */}
        {controlButtons.length > 0 && controlId && (
          <div style={{ display: 'flex', alignItems: 'center', marginLeft: '0.5rem' }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                maxWidth: expandedControls[controlId] ? '500px' : '0px',
                opacity: expandedControls[controlId] ? 1 : 0,
                overflow: 'hidden',
                transition: 'max-width 0.3s ease, opacity 0.3s ease',
                marginRight: '0.25rem'
              }}
            >
              {controlButtons.map((btn, idx) => (
                <button
                  key={idx}
                  onClick={btn.onClick}
                  className="minimalist-header-btn"
                  title={btn.title}
                  style={{
                    marginLeft: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    fontSize: '0.75rem',
                    background: btn.danger ? 'var(--accent-primary)' : (btn.active ? 'var(--accent-primary)' : 'rgba(0,0,0,0.1)'),
                    color: btn.danger ? 'white' : (btn.active ? 'white' : 'var(--text-primary)'),
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: 600,
                    flexShrink: 0
                  }}
                >
                  {btn.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => setExpandedControls(prev => ({ ...prev, [controlId]: !prev[controlId] }))}
              className="minimalist-header-btn"
              title={expandedControls[controlId] ? 'Hide controls' : 'Show controls'}
              style={{
                padding: '0.25rem 0.5rem',
                fontSize: '0.75rem',
                background: 'var(--accent-primary)',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontWeight: 600,
                flexShrink: 0,
                lineHeight: '1',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {expandedControls[controlId] ? '◀' : <span style={{ transform: 'rotate(90deg)', display: 'inline-block', fontSize: '1rem' }}>≡</span>}
            </button>
          </div>
        )}
        {actions.length > 0 && (
          <div style={{ display: 'flex', gap: '0.25rem', marginLeft: 'auto' }}>
            {actions.map((action, idx) => (
              <button
                key={idx}
                onClick={action.onClick}
                className={action.className || 'btn-toggle'}
                style={action.style || { fontSize: '0.75rem', padding: '0.2rem 0.4rem' }}
                title={action.title}
              >
                {action.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Helper function to render row controls (subtable toggles and add buttons)
  const renderRowControls = (options = {}) => {
    const {
      subtables = [],
      onToggleSubtable,
      onShowAllSubtables,
      onHideAllSubtables,
      onAddSubtable,
      addButtonClassName = 'btn-add-subtable',
      addButtonTitle = 'Add subtable',
      readOnly = false,
      backgroundColor = undefined,
      parentId = null,  // ID of the parent table/subtable/nested table to check subtablesEnabled
      rowBorderStyles = null,  // Row border styles object
      rowIndex = null  // Row index for applying border styles
    } = options;

    const hasSubtables = subtables.length > 0;
    const hasExpandedSubtables = subtables.some(st => st.expanded !== false);

    // In minimalist mode, don't apply background color
    let cellBg = borderStyle === 'minimalist' ? undefined : backgroundColor;

    // Highlight the cell if there are expanded subtables
    if (hasExpandedSubtables && !cellBg) {
      // Apply highlight based on theme
      if (theme === 'dark') {
        cellBg = 'rgba(102, 126, 234, 0.15)'; // Light blue highlight for dark theme
      } else {
        cellBg = 'rgba(102, 126, 234, 0.1)'; // Subtle blue highlight for light theme
      }
    }

    // Check if subtables are enabled for this parent
    const showSubtables = parentId ? subtablesEnabled[parentId] : true;

    if (!showSubtables) {
      return null;  // Don't render the column at all if subtables are disabled
    }

    return (
      <td className="row-controls" style={{
        backgroundColor: cellBg,
        width: 'auto',
        minWidth: '50px',
        ...(rowBorderStyles && rowIndex !== null && getRowBorderStyle(rowBorderStyles, rowIndex))
      }}>
        {hasSubtables ? (
          <SubtableDropdown
            subtables={subtables}
            onToggle={onToggleSubtable}
            onShowAll={onShowAllSubtables}
            onHideAll={onHideAllSubtables}
            onAdd={onAddSubtable}
            readOnly={readOnly}
            theme={theme}
          />
        ) : (
          !readOnly && (
            <button
              onClick={onAddSubtable}
              className={addButtonClassName}
              title={addButtonTitle}
            >
              +
            </button>
          )
        )}
      </td>
    );
  };

  // Helper function to render a cell from a referenced table (read-only)
  const renderReferencedCell = (cell, tableId, rowIdx, colIdx, subtableId = null, nestedTableId = null) => {
    // Handle datepicker - show as read-only text
    if (cell && typeof cell === 'object' && cell.type === 'date') {
      return (
        <div style={{ padding: '0.5rem', fontSize: '0.85rem', color: '#666' }}>
          📅 {new Date(cell.value).toLocaleString()}
        </div>
      );
    }

    // Handle nested table - make it expandable and reflect its state
    if (cell && typeof cell === 'object' && cell.type === 'nested_table') {
      // Build contextPath from legacy parameters
      const contextPath = createContextPath(tableId, subtableId, nestedTableId);
      const nestedContextPath = extendPath(contextPath, { type: 'nested', id: cell.id });

      if (cell.expanded) {
        // Show expanded nested table in read-only mode
        return (
          <div
            className="nested-table-wrapper reference-table-wrapper"
            style={{ opacity: 0.95 }}
            onClick={(e) => {
              e.stopPropagation();
              setFocusedElement({ type: 'nested_table', tableId, rowIdx, colIdx, subtableId, nestedTableId });
              setSelectedCell({
                tableId,
                rowIdx,
                colIdx,
                subtableId,
                nestedTableId,
                isSubtable: !!subtableId
              });
            }}
          >
            {renderControlHeader({
              isExpanded: true,
              title: cell.name,
              showToggle: true,
              onToggle: () => toggleNestedTable(nestedContextPath),
              className: 'nested-table-header'
            })}
            <table className="nested-table-inner">
              {cell.show_headers && renderTableHeaderRow({
                columnHeaders: cell.column_headers,
                numCols: cell.num_cols,
                showRowHeaders: cell.show_row_headers,
                showControls: false,
                theme: 'amber',
                sizing: 'small',
                translationColumns: cell.translation_columns
              })}
              <tbody>
                {cell.rows.map((nestedRow, nestedRowIdx) => (
                  <tr key={nestedRowIdx}>
                    {cell.show_row_headers && (
                      <td
                        style={{
                          background: '#3b82f6',
                          fontWeight: 600,
                          padding: '0.3rem',
                          fontSize: '0.75rem',
                          color: '#78350f'
                        }}
                      >
                        {(cell.row_headers || [])[nestedRowIdx] ? (
                          <LatexText text={(cell.row_headers || [])[nestedRowIdx]} />
                        ) : (
                          <span style={{ opacity: 0.4 }}>(empty)</span>
                        )}
                      </td>
                    )}
                    {nestedRow.map((nestedCell, nestedColIdx) => (
                      <td
                        key={nestedColIdx}
                        style={{
                          padding: '0.25rem',
                          border: '1px solid #3b82f6',
                          background: '#eff6ff',
                          fontSize: '0.8rem'
                        }}
                      >
                        {renderReferencedCell(nestedCell, tableId, nestedRowIdx, nestedColIdx, subtableId, cell.id)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      } else {
        // Show collapsed with toggle button
        return (
          <div
            className="nested-table-collapsed"
            onClick={() => toggleNestedTable(nestedContextPath)}
            style={{ cursor: 'pointer', opacity: 0.9 }}
          >
            <span className="table-icon">⊞</span>
            <span className="nested-table-label">{cell.name}</span>
          </div>
        );
      }
    }

    // Handle chart - make it expandable and reflect its state
    if (cell && typeof cell === 'object' && cell.type === 'chart') {
      return renderChartCell(cell, tableId, rowIdx, colIdx, { subtableId, nestedTableId, isReadOnly: true });
    }

    // Handle table reference - show as read-only to avoid infinite loops
    if (cell && typeof cell === 'object' && cell.type === 'table_reference') {
      return (
        <div className="nested-table-collapsed reference-table-collapsed" style={{
          cursor: 'default',
          opacity: 0.7
        }}>
          <span className="table-icon">🔗</span>
          <span className="nested-table-label">{cell.table_name} (Read-Only)</span>
        </div>
      );
    }

    // Regular text - show as read-only
    return (
      <div style={{ padding: '0.75rem', fontSize: '0.9rem', color: '#555' }}>
        {cell || ''}
      </div>
    );
  };

  // Helper function to render a cell (can be text input, nested table, chart, or date)
  // Supports both old signature and new contextPath-based signature
  // Helper function to get border style CSS for a column
  const getColumnBorderStyle = (columnBorderStyles, colIdx) => {
    const style = columnBorderStyles?.[colIdx];
    if (!style || style === 'none') return {};

    // Determine border color based on current border style and theme
    let borderColor;
    if (borderStyle === 'dark') {
      borderColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
    } else {
      // For all other border styles, use the standard border color
      borderColor = 'var(--border-primary)';
    }

    const borderStyles = {
      'double-left': { borderLeft: `3px double ${borderColor}` },
      'double-right': { borderRight: `3px double ${borderColor}` },
      'thick-left': { borderLeft: `4px solid ${borderColor}` },
      'thick-right': { borderRight: `4px solid ${borderColor}` }
    };

    return borderStyles[style] || {};
  };

  const getRowBorderStyle = (rowBorderStyles, rowIdx) => {
    const style = rowBorderStyles?.[rowIdx];
    if (!style || style === 'none') return {};

    // Determine border color based on current border style and theme
    let borderColor;
    if (borderStyle === 'dark') {
      borderColor = theme === 'dark' ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)';
    } else {
      // For all other border styles, use the standard border color
      borderColor = 'var(--border-primary)';
    }

    const borderStyles = {
      'double-top': { borderTop: `3px double ${borderColor}` },
      'double-bottom': { borderBottom: `3px double ${borderColor}` },
      'thick-top': { borderTop: `4px solid ${borderColor}` },
      'thick-bottom': { borderBottom: `4px solid ${borderColor}` }
    };

    return borderStyles[style] || {};
  };

  const renderCell = (tableId, cell, rowIdx, colIdx, isSubtableOrContextPath = false, subtableId = null, nestedTableId = null) => {
    // Handle both old and new calling styles
    let contextPath, actualSubtableId, actualNestedTableId, isSubtable;

    if (isSubtableOrContextPath && typeof isSubtableOrContextPath === 'object' && isSubtableOrContextPath.tableId) {
      // New style: called with contextPath
      contextPath = isSubtableOrContextPath;
      const extracted = extractLegacyParams(contextPath);
      actualSubtableId = extracted.subtableId;
      actualNestedTableId = extracted.nestedTableId;
      isSubtable = !!actualSubtableId;
    } else {
      // Old style: called with individual parameters
      isSubtable = isSubtableOrContextPath;
      actualSubtableId = subtableId;
      actualNestedTableId = nestedTableId;
      contextPath = createContextPath(tableId, subtableId, nestedTableId);
    }

    // Check if table is read-only
    const table = tables.find(t => t.id === tableId);
    const readOnly = isTableReadOnly(table);

    // Check if cell is a datepicker
    if (cell && typeof cell === 'object' && cell.type === 'date') {
      return renderDateCell(cell, tableId, rowIdx, colIdx, { subtableId: actualSubtableId, nestedTableId: actualNestedTableId, contextPath });
    }

    // Check if cell is a chart
    if (cell && typeof cell === 'object' && cell.type === 'chart') {
      return renderChartCell(cell, tableId, rowIdx, colIdx, { subtableId: actualSubtableId, nestedTableId: actualNestedTableId, contextPath, isReadOnly: readOnly });
    }

    // Check if cell contains embedded Plotly chart JSON (format: {PLOTLY_CHART:{...}})
    if (cell && typeof cell === 'string' && cell.trim().startsWith('{PLOTLY_CHART:')) {
      const plotlyJsonMatch = cell.match(/^\{PLOTLY_CHART:(.*)\}$/s);
      if (plotlyJsonMatch && plotlyJsonMatch[1]) {
        return renderPlotlyChartCell(plotlyJsonMatch[1]);
      }
    }

    // Check if cell contains embedded image (format: {IMAGE:url} or {IMAGE:data:image/png;base64,...})
    if (cell && typeof cell === 'string' && cell.trim().startsWith('{IMAGE:')) {
      return renderImageCell(cell.trim());
    }

    // Check if cell is a nested table
    if (cell && typeof cell === 'object' && cell.type === 'nested_table') {
      // Use unified renderer with context path
      return renderNestedTable(tableId, cell, rowIdx, colIdx, contextPath);
    }

    // Check if cell is a subtable reference
    if (cell && typeof cell === 'object' && cell.type === 'subtable') {
      // Subtables are shown in the row controls column, not in the cell
      // Display a label or icon to indicate there's a subtable
      return (
        <div style={{
          padding: '0.5rem',
          color: 'var(--text-secondary)',
          fontStyle: 'italic',
          textAlign: 'center'
        }}>
          📋 Subtable
        </div>
      );
    }

    // Check if cell is a table reference
    if (cell && typeof cell === 'object' && cell.type === 'table_reference') {
      return renderReferenceCell(cell, tableId, rowIdx, colIdx, { subtableId: actualSubtableId, nestedTableId: actualNestedTableId, contextPath, readOnly });
    }

    // Check if cell is a color picker
    if (cell && typeof cell === 'object' && cell.type === 'color') {
      return renderColorPickerCell(cell, tableId, rowIdx, colIdx, { subtableId: actualSubtableId, nestedTableId: actualNestedTableId, contextPath });
    }

    // Check if cell is a priority picker
    if (cell && typeof cell === 'object' && cell.type === 'priority') {
      return renderPriorityPickerCell(cell, tableId, rowIdx, colIdx, { subtableId: actualSubtableId, nestedTableId: actualNestedTableId, contextPath });
    }

    // Check if cell is a done picker
    if (cell && typeof cell === 'object' && cell.type === 'done') {
      return renderDonePickerCell(cell, tableId, rowIdx, colIdx, { subtableId: actualSubtableId, nestedTableId: actualNestedTableId, contextPath });
    }

    // Regular text input
    const cellKey = getCellKey(tableId, rowIdx, colIdx, contextPath);

    // Auto-resize textarea function
    const handleTextareaResize = (e) => {
      e.target.style.height = 'auto';
      // Force reflow for Firefox by reading offsetHeight before scrollHeight
      // eslint-disable-next-line no-unused-expressions
      e.target.offsetHeight;
      if (density === 'sub-compact') {
        const minHeight = parseFloat(getComputedStyle(e.target).fontSize) * 1.1;
        e.target.style.height = Math.max(e.target.scrollHeight, minHeight) + 'px';
      } else {
        e.target.style.height = e.target.scrollHeight + 'px';
      }
    };

    // Check if cell contains markdown syntax
    const hasMarkdown = cell && typeof cell === 'string' && (
      cell.includes('**') || cell.includes('__') ||
      (cell.includes('[') && cell.includes('](')) ||
      cell.includes('`') ||  // Inline code
      (cell.match(/(?<!\*)\*(?!\*)/) || cell.match(/(?<!_)_(?!_)/))  // Single * or _ (not double)
    );

    return (
      <div className="cell-container" style={{ position: 'relative' }}>
        {/* Markdown preview overlay - only show for cells with markdown syntax */}
        {hasMarkdown && !readOnly && (
          <div
            className="markdown-preview-overlay"
            data-table-id={tableId}
            data-row={rowIdx}
            data-col={colIdx}
            contentEditable={false}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              padding: 'var(--table-padding-y, 0.5rem) var(--table-padding-x, 0.5rem)',
              cursor: 'text',
              pointerEvents: 'auto',
              opacity: 1,
              transition: 'opacity 0.15s',
              zIndex: 1,
              background: 'var(--bg-cell, transparent)',
              overflow: 'auto',
              wordWrap: 'break-word',
              fontSize: '0.85rem',
              fontFamily: 'inherit',
              lineHeight: '1.5',
              userSelect: 'none',
              WebkitUserSelect: 'none'
            }}
            onClick={(e) => {
              // When clicked, focus the textarea
              const textarea = e.currentTarget.parentElement.querySelector('textarea');
              if (textarea) {
                textarea.focus();
              }
            }}
            dangerouslySetInnerHTML={{ __html: parseSimpleMarkdown(cell) }}
          />
        )}
        <EditableCellTextarea
          key={cellKey}
          cellValue={cell}
          density={density}
          data-table-id={tableId}
          data-row={rowIdx}
          data-col={colIdx}
          {...(subtableId && { 'data-subtable-id': subtableId })}
          {...(nestedTableId && { 'data-nested-table-id': nestedTableId })}
          readOnly={readOnly}
          disabled={readOnly}
          onFocus={(e) => {
            // Hide markdown preview when focused, show textarea text
            const preview = e.target.parentElement.querySelector('.markdown-preview-overlay');
            if (preview) {
              preview.style.opacity = '0';
              preview.style.pointerEvents = 'none';
            }
            e.target.style.color = 'var(--text-primary, #333)'; // Show textarea text
            console.log('[onFocus] Textarea value:', e.target.value, 'color:', e.target.style.color);
          }}
          onBlur={(e) => {
            // Show markdown preview when unfocused (after a small delay to let click events fire)
            setTimeout(() => {
              const preview = e.target.parentElement.querySelector('.markdown-preview-overlay');
              if (preview && !e.target.matches(':focus')) {
                preview.style.opacity = '1';
                preview.style.pointerEvents = 'auto';
                e.target.style.color = 'transparent'; // Hide textarea text
              }
            }, 100);

            console.log('[onBlur] Fired for', tableId, rowIdx, colIdx, 'value:', e.target.value, 'flag:', creatingSpecialCell.current);
            // Skip persist if we're in the middle of creating a special cell (chart, nested table, etc.)
            if (creatingSpecialCell.current) {
              console.log('[onBlur] Skipping persist - creating special cell');
              return;
            }

            // Persist to backend when user clicks outside the cell
            const finalValue = e.target.value;
            console.log('[onBlur] Calling persistCellValue with:', finalValue);
            persistCellValue(tableId, rowIdx, colIdx, finalValue, { contextPath });
          }}
          onKeyDown={(e) => {
            // Persist to backend when user presses Enter (with Shift+Enter for new lines)
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              const finalValue = e.target.value;
              persistCellValue(tableId, rowIdx, colIdx, finalValue, { contextPath });
              e.target.blur(); // Remove focus from textarea
            }
          }}
          onInput={handleTextareaResize}
          rows={density === 'sub-compact' ? 1 : undefined}
          ref={(textarea) => {
            if (textarea) {
              if (density === 'sub-compact') {
                // For sub-compact, start with minimal height
                textarea.style.height = 'auto';
                const minHeight = parseFloat(getComputedStyle(textarea).fontSize) * 1.1; // 1em * line-height
                textarea.style.height = Math.max(textarea.scrollHeight, minHeight) + 'px';
              } else {
                textarea.style.height = 'auto';
                textarea.style.height = textarea.scrollHeight + 'px';
              }
              // Hide textarea text initially if there's markdown and preview exists
              // But keep the caret visible
              // Only hide if textarea is not currently focused
              if (hasMarkdown && !readOnly && document.activeElement !== textarea) {
                textarea.style.color = 'transparent';
                textarea.style.caretColor = 'var(--text-primary, #000)';
              } else if (hasMarkdown && !readOnly && document.activeElement === textarea) {
                // If focused, ensure text is visible
                textarea.style.color = 'var(--text-primary, #333)';
                textarea.style.caretColor = 'var(--text-primary, #000)';
              }
            }
          }}
          placeholder={showDefaultCellPlaceholders ? `R${rowIdx + 1}C${colIdx + 1}` : ''}
          onContextMenu={(e) => {
            if (readOnly) {
              e.preventDefault();
              return;
            }
            e.preventDefault();
            e.stopPropagation(); // Stop event from bubbling to parent tables
            const cellContext = createCellContext(contextPath, rowIdx, colIdx);
            setSelectedCell(cellContext);
            setShowContextMenu(true);
          }}
          onClick={(e) => {
            e.stopPropagation();
            const cellData = { tableId, rowIdx, colIdx, isSubtable, subtableId, nestedTableId };
            setSelectedCell(cellData);
          }}
        />
        {showContextMenu &&
         selectedCell &&
         selectedCell.tableId === tableId &&
         selectedCell.rowIdx === rowIdx &&
         selectedCell.colIdx === colIdx &&
         selectedCell.isSubtable === isSubtable &&
         selectedCell.subtableId === actualSubtableId &&
         selectedCell.nestedTableId === actualNestedTableId &&
         !cell && (
          <div
            className="cell-context-menu"
            onClick={(e) => e.stopPropagation()} // Prevent closing when clicking menu
          >
            <button
              onMouseDown={(e) => {
                // Set flag BEFORE blur fires
                creatingSpecialCell.current = true;
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (contextPath) {
                  createNestedTable(contextPath, rowIdx, colIdx);
                  setShowContextMenu(false); // Close menu after action
                }
                // Reset flag after a short delay
                setTimeout(() => {
                  creatingSpecialCell.current = false;
                }, 100);
              }}
              className="context-menu-item"
            >
              📊 Add Nested Table
            </button>
            <button
              onMouseDown={(e) => {
                // Set flag BEFORE blur fires (blur happens on mousedown, not click)
                console.log('[Create Chart button] Setting creatingSpecialCell flag to true (mousedown)');
                creatingSpecialCell.current = true;
              }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (contextPath) {
                  createChart(contextPath, rowIdx, colIdx);
                  setShowContextMenu(false);
                }
                // Reset flag after a short delay to allow chart creation to complete
                setTimeout(() => {
                  console.log('[Create Chart button] Resetting creatingSpecialCell flag to false');
                  creatingSpecialCell.current = false;
                }, 100);
              }}
              className="context-menu-item"
            >
              📈 Create Chart
            </button>
            <button
              onMouseDown={() => { creatingSpecialCell.current = true; }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                createDatePicker(tableId, rowIdx, colIdx, actualSubtableId, actualNestedTableId, contextPath);
                setShowContextMenu(false);
                setTimeout(() => { creatingSpecialCell.current = false; }, 100);
              }}
              className="context-menu-item"
            >
              📅 Add Date Picker
            </button>
            <button
              onMouseDown={() => { creatingSpecialCell.current = true; }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                createColorPicker(tableId, rowIdx, colIdx, actualSubtableId, actualNestedTableId, contextPath);
                setShowContextMenu(false);
                setTimeout(() => { creatingSpecialCell.current = false; }, 100);
              }}
              className="context-menu-item"
            >
              🎨 Add Color Picker
            </button>
            <button
              onMouseDown={() => { creatingSpecialCell.current = true; }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                createPriorityPicker(tableId, rowIdx, colIdx, actualSubtableId, actualNestedTableId, contextPath);
                setShowContextMenu(false);
                setTimeout(() => { creatingSpecialCell.current = false; }, 100);
              }}
              className="context-menu-item"
            >
              ⚡ Add Priority Picker
            </button>
            <button
              onMouseDown={() => { creatingSpecialCell.current = true; }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                createDonePicker(tableId, rowIdx, colIdx, actualSubtableId, actualNestedTableId, contextPath);
                setShowContextMenu(false);
                setTimeout(() => { creatingSpecialCell.current = false; }, 100);
              }}
              className="context-menu-item"
            >
              ✓ Add Done Picker
            </button>
            <button
              onMouseDown={() => { creatingSpecialCell.current = true; }}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openTableReferenceModal(tableId, rowIdx, colIdx, actualSubtableId, actualNestedTableId, contextPath);
                setTimeout(() => { creatingSpecialCell.current = false; }, 100);
              }}
              className="context-menu-item"
            >
              🔗 Add Table Reference
            </button>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="app"><div className="loading">Loading...</div></div>;
  }

  return (
    <div className="app">
      <FileExplorer
        ref={fileExplorerRef}
        isOpen={explorerOpen}
        onToggle={() => setExplorerOpen(!explorerOpen)}
        onFileSelect={async (selection) => {
          console.log('Selection from FileExplorer:', selection);

          if (selection.type === 'file') {
            // User clicked on a file node - load that file
            if (selection.filename !== currentFile) {
              console.log('Switching to file:', selection.filename);
              try {
                await authFetch(`${API_URL}/api/files/load/${selection.filename}`, {
                  method: 'POST'
                });
                setCurrentFile(selection.filename);
                await fetchTabs();
                console.log('File loaded:', selection.filename);
              } catch (error) {
                console.error('Error loading file:', error);
              }
            }
          } else if (selection.type === 'my-table') {
            // Check if we need to switch files first
            if (selection.filename && selection.filename !== currentFile) {
              console.log('Switching from', currentFile, 'to', selection.filename);
              try {
                // Load the new file
                await authFetch(`${API_URL}/api/files/load/${selection.filename}`, {
                  method: 'POST'
                });
                setCurrentFile(selection.filename);

                // Fetch tabs for the new file
                const tabsResponse = await authFetch(`${API_URL}/api/tabs`);
                const tabsData = await tabsResponse.json();
                const newTabs = Array.isArray(tabsData) ? tabsData : (tabsData?.tabs || []);

                setTabs(newTabs);

                // Find the tab containing this table in the newly loaded tabs
                const tabWithTable = newTabs.find(tab =>
                  tab.tables && tab.tables.some(t => t.id === selection.id)
                );

                if (tabWithTable) {
                  setActiveTabId(tabWithTable.id);
                  // Update tables state with the active tab's tables
                  const newTabTables = JSON.parse(JSON.stringify(tabWithTable.tables));
                  setTables(newTabTables);
                  console.log('Switched to tab:', tabWithTable.name, 'with', newTabTables.length, 'tables');

                  // Wait for React to render, then scroll
                  setTimeout(() => {
                    const scrollToTable = (retries = 0) => {
                      const tableElement = document.querySelector(`[data-table-id="${selection.id}"]`);
                      if (tableElement) {
                        tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      } else if (retries < 10) {
                        setTimeout(() => scrollToTable(retries + 1), 150);
                      } else {
                        console.warn('Could not find table element after 10 retries:', selection.id);
                      }
                    };
                    scrollToTable();
                  }, 300);
                } else {
                  console.warn('Could not find tab containing table:', selection.id);
                }
              } catch (error) {
                console.error('Error switching files:', error);
              }
            } else {
              // Same file - just find the tab containing this table and switch to it
              const tabWithTable = tabs.find(tab =>
                tab.tables && tab.tables.some(t => t.id === selection.id)
              );
              if (tabWithTable) {
                setActiveTabId(tabWithTable.id);
                console.log('Switched to tab:', tabWithTable.name);

                // Scroll the table into view after a short delay to allow React to render
                // Use a longer delay and retry mechanism since tab switching needs time
                const scrollToTable = (retries = 0) => {
                  const tableElement = document.querySelector(`[data-table-id="${selection.id}"]`);
                  if (tableElement) {
                    tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    console.log('Scrolled to table:', selection.id);
                  } else if (retries < 5) {
                    // Retry after another delay
                    setTimeout(() => scrollToTable(retries + 1), 100);
                  } else {
                    console.warn('Could not find table element to scroll to after retries:', selection.id);
                  }
                };
                setTimeout(() => scrollToTable(), 200);
              } else {
                console.warn('Could not find tab containing table:', selection.id);
              }
            }
          } else if (selection.type === 'shared-in') {
            // Table shared with user - use the provided table data
            if (selection.data) {
              // Check if this shared table is already open in a tab
              const existingTab = tabs.find(tab =>
                tab.tables && tab.tables.some(t => t.id === selection.data.id)
              );

              if (existingTab) {
                // Tab already exists, just switch to it
                setActiveTabId(existingTab.id);
              } else {
                // Create a new tab with the shared table
                console.log('Would create new tab for shared table:', selection.data.name);
                // Note: Creating tabs is handled by the createTab function
                // For now, just log that we received a shared table
              }
            }
          } else if (selection.type === 'shared-out') {
            // Table shared by user - find the tab containing this table
            const tabWithTable = tabs.find(tab =>
              tab.tables && tab.tables.some(t => t.id === selection.tableId)
            );
            if (tabWithTable) {
              setActiveTabId(tabWithTable.id);
              console.log('Switched to tab:', tabWithTable.name);

              // Scroll the table into view with retry mechanism
              const scrollToTable = (retries = 0) => {
                const tableElement = document.querySelector(`[data-table-id="${selection.tableId}"]`);
                if (tableElement) {
                  tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  console.log('Scrolled to table:', selection.tableId);
                } else if (retries < 5) {
                  setTimeout(() => scrollToTable(retries + 1), 100);
                } else {
                  console.warn('Could not find table element to scroll to after retries:', selection.tableId);
                }
              };
              setTimeout(() => scrollToTable(), 200);
            }
          }
        }}
        onRefresh={async () => {
          // Refresh the app's data when explorer is refreshed
          try {
            await fetchTabs();
            await fetchAvailableFiles();
            console.log('[FileExplorer] Refresh completed');
          } catch (error) {
            console.error('[FileExplorer] Refresh error:', error);
          }
        }}
      />
      <div className="main-content" style={{ marginLeft: explorerOpen ? '280px' : '40px', transition: 'margin-left 0.3s ease' }}>
      <header className="app-header">
        <div className="app-header-content" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '16px' }}>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
              {companyLogo && (
                <div
                  style={{
                    background: logoBgEnabled ? logoBgColor : 'transparent',
                    borderRadius: '6px',
                    padding: logoBgEnabled ? '6px 10px' : '0',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  <img
                    src={companyLogo}
                    alt="Company logo"
                    style={{ maxHeight: '56px', maxWidth: '260px', objectFit: 'contain', display: 'block' }}
                  />
                </div>
              )}
              <h1 style={{ margin: 0 }}>
                Organized Tables
              </h1>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                maxWidth: toolbarOpen ? '800px' : '0px',
                opacity: toolbarOpen ? 1 : 0,
                overflow: toolbarOpen ? 'visible' : 'hidden',
                transition: 'max-width 0.3s ease, opacity 0.3s ease',
                marginRight: toolbarOpen ? '12px' : '0',
                whiteSpace: 'nowrap'
              }}>
                {/* Shared Tables Buttons */}
                <button
                  onClick={() => setShowSharedTablesView(true)}
              style={{
                padding: '6px 12px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              📥 Shared With Me
            </button>
            <button
              onClick={() => setShowSharingWithOthersView(true)}
              style={{
                padding: '6px 12px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              📤 Sharing with Others
            </button>

            {/* File Dropdown */}
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setShowFileDropdown(!showFileDropdown)}
                style={{
                  padding: '6px 12px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                📁 File <span style={{ fontSize: '10px' }}>▾</span>
              </button>
              {showFileDropdown && (
                <div style={{
                  position: 'absolute',
                  top: '100%',
                  right: 0,
                  marginTop: '4px',
                  background: 'var(--bg-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  zIndex: 1000,
                  minWidth: '220px',
                  padding: '8px'
                }}>
                  {/* Current File Display */}
                  <div style={{
                    padding: '8px 12px',
                    marginBottom: '8px',
                    background: 'var(--bg-secondary)',
                    borderRadius: '4px',
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    fontWeight: '500'
                  }}>
                    Current: {currentFile}
                  </div>

                  {/* File Selector */}
                  <div style={{ padding: '0 4px', marginBottom: '8px' }}>
                    <label style={{ fontSize: '12px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>
                      Switch File
                    </label>
                    <select
                      style={{
                        width: '100%',
                        padding: '6px 8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        fontSize: '13px'
                      }}
                      value={currentFile}
                      onChange={(e) => { loadFile(e.target.value); setShowFileDropdown(false); }}
                    >
                      {availableFiles?.map(file => (
                        <option key={file} value={file}>{file}</option>
                      ))}
                    </select>
                  </div>

                  {/* Divider */}
                  <div style={{ height: '1px', background: 'var(--border-color)', margin: '8px 0' }} />

                  {/* File Management */}
                  <div style={{ marginBottom: '8px' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '4px 12px', fontWeight: '500', textTransform: 'uppercase' }}>
                      File Management
                    </div>
                    <button
                      onClick={() => { renameFile(); setShowFileDropdown(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      ✏️ Rename
                    </button>
                    <button
                      onClick={() => { duplicateFile(); setShowFileDropdown(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      📋 Duplicate
                    </button>
                    <button
                      onClick={() => { deleteFile(); setShowFileDropdown(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: '#ef4444',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      🗑️ Delete
                    </button>
                  </div>

                  {/* Divider */}
                  <div style={{ height: '1px', background: 'var(--border-color)', margin: '8px 0' }} />

                  {/* File Operations */}
                  <div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', padding: '4px 12px', fontWeight: '500', textTransform: 'uppercase' }}>
                      Operations
                    </div>
                    <button
                      onClick={() => { saveCurrentFile(); setShowFileDropdown(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={() => { createNewFile(); setShowFileDropdown(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      📄 New File
                    </button>
                    <button
                      onClick={() => { exportFile(); setShowFileDropdown(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      📤 Export File
                    </button>
                    <label
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        margin: 0,
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      📥 Import File
                      <input
                        type="file"
                        accept=".json"
                        onChange={(e) => { importFile(e); setShowFileDropdown(false); }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <label
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        margin: 0,
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      📊 Import Excel
                      <input
                        type="file"
                        accept=".xlsx,.xls"
                        onChange={(e) => { importFromExcel(e); setShowFileDropdown(false); }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <label
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        margin: 0,
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      📄 Import HTML/MD
                      <input
                        type="file"
                        accept=".html,.htm,.md,.markdown"
                        onChange={(e) => { importFromHierarchical(e); setShowFileDropdown(false); }}
                        style={{ display: 'none' }}
                      />
                    </label>
                    <button
                      onClick={() => { exportBackup(); setShowFileDropdown(false); }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        border: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      📦 Export Backup
                    </button>
                    <label
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        background: 'transparent',
                        textAlign: 'left',
                        cursor: 'pointer',
                        color: 'var(--text-primary)',
                        borderRadius: '4px',
                        margin: 0,
                        fontSize: '13px'
                      }}
                      onMouseOver={(e) => e.target.style.background = 'var(--bg-secondary)'}
                      onMouseOut={(e) => e.target.style.background = 'transparent'}
                    >
                      📥 Import Backup
                      <input
                        type="file"
                        accept=".json"
                        onChange={(e) => { importBackup(e); setShowFileDropdown(false); }}
                        style={{ display: 'none' }}
                      />
                    </label>
                  </div>
                </div>
              )}
            </div>

            {/* Settings Button */}
            <button
              onClick={() => setShowSettingsModal(true)}
              style={{
                padding: '6px 12px',
                background: 'var(--bg-secondary)',
                color: 'var(--text-primary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '500'
              }}
            >
              ⚙️ Settings
            </button>
              </div>
              <button
                onClick={() => setToolbarOpen(!toolbarOpen)}
                title={toolbarOpen ? 'Hide controls' : 'Show controls'}
                aria-label={toolbarOpen ? 'Hide controls' : 'Show controls'}
                style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  background: 'var(--accent-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  flexShrink: 0,
                  lineHeight: '1',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {toolbarOpen ? '◀' : <span style={{ transform: 'rotate(90deg)', display: 'inline-block', fontSize: '1rem' }}>≡</span>}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Settings Modal */}
      {showSettingsModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }} onClick={() => setShowSettingsModal(false)}>
          <div style={{
            background: 'var(--bg-primary)',
            borderRadius: '8px',
            padding: '24px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80vh',
            overflowY: 'auto',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>Display Settings</h2>
              <button
                onClick={() => setShowSettingsModal(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  padding: '0 8px'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>View</label>
                <select
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  value={viewMode}
                  onChange={(e) => setViewMode(e.target.value)}
                >
                  <option value="tables">Tables</option>
                  <option value="shopify">Shopify</option>
                  <option value="warehouse">Warehouse</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>Theme</label>
                <select
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                >
                  <option value="light">Light</option>
                  <option value="dark">Dark</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>Color</label>
                <select
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  value={colorScheme}
                  onChange={(e) => setColorScheme(e.target.value)}
                >
                  <option value="blue">Blue</option>
                  <option value="green">Green</option>
                  <option value="purple">Purple</option>
                  <option value="orange">Orange</option>
                  <option value="grey">Grey</option>
                  <option value="custom">Custom</option>
                </select>
                {colorScheme === 'custom' && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      style={{
                        width: '36px',
                        height: '32px',
                        padding: '0',
                        border: '1px solid var(--border-color)',
                        borderRadius: '4px',
                        background: 'var(--bg-secondary)',
                        cursor: 'pointer'
                      }}
                      aria-label="Custom color picker"
                    />
                    <input
                      type="text"
                      value={customColor}
                      onChange={(e) => {
                        const v = e.target.value;
                        setCustomColor(v);
                      }}
                      placeholder="#2A9DBF"
                      style={{
                        flex: 1,
                        padding: '6px 8px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        fontSize: '13px',
                        fontFamily: 'monospace'
                      }}
                      aria-label="Custom color hex code"
                    />
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>Company Logo</label>
                {companyLogo && (
                  <div
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      borderRadius: '4px',
                      border: '1px solid var(--border-color)',
                      background: logoBgEnabled ? logoBgColor : 'var(--bg-secondary)',
                      padding: '4px',
                      maxWidth: '230px'
                    }}
                  >
                    <img
                      src={companyLogo}
                      alt="Company logo preview"
                      style={{ maxHeight: '64px', maxWidth: '220px', objectFit: 'contain', display: 'block' }}
                    />
                  </div>
                )}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      if (file.size > 1024 * 1024) {
                        alert('Logo image must be under 1 MB. Please choose a smaller file.');
                        e.target.value = '';
                        return;
                      }
                      const reader = new FileReader();
                      reader.onload = () => {
                        try {
                          setCompanyLogo(reader.result);
                        } catch (err) {
                          alert('Could not save logo: ' + (err?.message || err));
                        }
                      };
                      reader.onerror = () => alert('Could not read the selected file.');
                      reader.readAsDataURL(file);
                      e.target.value = '';
                    }}
                    style={{ fontSize: '13px', color: 'var(--text-secondary)' }}
                    aria-label="Upload company logo"
                  />
                  {companyLogo && (
                    <button
                      type="button"
                      onClick={() => setCompanyLogo(null)}
                      style={{
                        padding: '6px 12px',
                        borderRadius: '4px',
                        border: '1px solid var(--border-color)',
                        background: 'var(--bg-secondary)',
                        color: 'var(--text-primary)',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                  <input
                    type="checkbox"
                    id="logoBgToggle"
                    checked={logoBgEnabled}
                    onChange={(e) => setLogoBgEnabled(e.target.checked)}
                    style={{ cursor: 'pointer' }}
                  />
                  <label
                    htmlFor="logoBgToggle"
                    style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', cursor: 'pointer' }}
                  >
                    Logo background
                  </label>
                  <input
                    type="color"
                    value={logoBgColor}
                    onChange={(e) => setLogoBgColor(e.target.value)}
                    disabled={!logoBgEnabled}
                    style={{
                      width: '32px',
                      height: '28px',
                      padding: '0',
                      border: '1px solid var(--border-color)',
                      borderRadius: '4px',
                      background: 'var(--bg-secondary)',
                      cursor: logoBgEnabled ? 'pointer' : 'not-allowed',
                      opacity: logoBgEnabled ? 1 : 0.5
                    }}
                    aria-label="Logo background color"
                  />
                  <input
                    type="text"
                    value={logoBgColor}
                    onChange={(e) => setLogoBgColor(e.target.value)}
                    disabled={!logoBgEnabled}
                    placeholder="#ffffff"
                    style={{
                      width: '90px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      border: '1px solid var(--border-color)',
                      background: 'var(--bg-secondary)',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      fontFamily: 'monospace',
                      opacity: logoBgEnabled ? 1 : 0.5
                    }}
                    aria-label="Logo background color hex"
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="graphPaperToggle"
                  checked={graphPaper}
                  onChange={(e) => setGraphPaper(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label
                  htmlFor="graphPaperToggle"
                  style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', cursor: 'pointer' }}
                >
                  Graph Paper Background
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="cellPlaceholdersToggle"
                  checked={showDefaultCellPlaceholders}
                  onChange={(e) => setShowDefaultCellPlaceholders(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label
                  htmlFor="cellPlaceholdersToggle"
                  style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', cursor: 'pointer' }}
                >
                  Show Default Cell Placeholders (RxCy)
                </label>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  id="metallicFinishToggle"
                  checked={metallicFinish}
                  onChange={(e) => setMetallicFinish(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <label
                  htmlFor="metallicFinishToggle"
                  style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)', cursor: 'pointer' }}
                >
                  Metallic Finish (Headers)
                </label>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>Density</label>
                <select
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  value={density}
                  onChange={(e) => {
                    console.log('[Density Change] From:', density, 'To:', e.target.value);
                    setDensity(e.target.value);
                  }}
                >
                  <option value="sub-compact">Sub-Compact</option>
                  <option value="compact">Compact</option>
                  <option value="normal">Normal</option>
                  <option value="comfortable">Comfortable</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>Borders</label>
                <select
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  value={borderStyle}
                  onChange={(e) => setBorderStyle(e.target.value)}
                >
                  <option value="bordered">Bordered</option>
                  <option value="dark">Dark Borders</option>
                  <option value="minimal">Minimal</option>
                  <option value="striped">Striped</option>
                  <option value="minimalist">Minimalist</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>Depth Shading</label>
                <select
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  value={depthShading ? 'on' : 'off'}
                  onChange={(e) => setDepthShading(e.target.value === 'on')}
                >
                  <option value="off">Off</option>
                  <option value="on">On</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>Cell Backgrounds</label>
                <select
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  value={showCellBackgrounds ? 'on' : 'off'}
                  onChange={(e) => setShowCellBackgrounds(e.target.value === 'on')}
                >
                  <option value="off">Off</option>
                  <option value="on">On</option>
                </select>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: 'var(--text-secondary)' }}>Connector Lines</label>
                <select
                  style={{
                    padding: '8px 12px',
                    borderRadius: '4px',
                    border: '1px solid var(--border-color)',
                    background: 'var(--bg-secondary)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                  value={showConnectorLines ? 'on' : 'off'}
                  onChange={(e) => setShowConnectorLines(e.target.value === 'on')}
                >
                  <option value="off">Off</option>
                  <option value="on">On</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Bar */}
      <div className="tab-bar">
        <div className="tabs-container">
          {tabs.map(tab => (
            <div
              key={tab.id}
              className={`tab ${activeTabId === tab.id ? 'active' : ''}`}
              onClick={() => {
                if (editingTabId !== tab.id) {
                  switchTab(tab.id);
                }
              }}
            >
              {editingTabId === tab.id ? (
                <input
                  type="text"
                  defaultValue={tab.name}
                  autoFocus
                  onBlur={(e) => {
                    renameTab(tab.id, e.target.value);
                    setEditingTabId(null);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      renameTab(tab.id, e.target.value);
                      setEditingTabId(null);
                    } else if (e.key === 'Escape') {
                      setEditingTabId(null);
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="tab-name-input"
                />
              ) : (
                <>
                  <span
                    className="tab-name"
                    onDoubleClick={() => setEditingTabId(tab.id)}
                  >
                    {tab.name}
                  </span>
                  {tabs.length > 1 && (
                    <button
                      className="tab-delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm(`Delete tab "${tab.name}"?`)) {
                          deleteTab(tab.id);
                        }
                      }}
                      title="Delete tab"
                    >
                      ×
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
          <button
            className="tab-create-btn"
            onClick={() => createTab()}
            title="Create new tab"
          >
            +
          </button>
        </div>
      </div>

      <div className="container" ref={tablesContainerRef} data-screenshot-container>
        {viewMode === 'tables' && (
          <>
            {/* Create Table Form */}
            <div className="create-table-section">
          <h2>Create New Table</h2>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={newTableName}
              onChange={(e) => setNewTableName(e.target.value)}
              placeholder="Table Name"
              className="compact-input"
              style={{ flex: '1 1 150px', minWidth: '150px' }}
            />
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem' }}>
              Rows:
              <input
                type="number"
                min="1"
                max="20"
                value={newTableRows}
                onChange={(e) => setNewTableRows(parseInt(e.target.value))}
                className="compact-input"
                style={{ width: '50px' }}
              />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.85rem' }}>
              Cols:
              <input
                type="number"
                min="1"
                max="20"
                value={newTableCols}
                onChange={(e) => setNewTableCols(parseInt(e.target.value))}
                className="compact-input"
                style={{ width: '50px' }}
              />
            </label>
            <button
              onClick={createTable}
              disabled={isCreatingTable}
              className="btn-toggle"
              style={{
                padding: '0.3rem 0.6rem',
                fontSize: '0.85rem'
              }}
            >
              Create
            </button>
          </div>
        </div>

        {/* Generate Table with Claude */}
        <div className="create-table-section" style={{ marginTop: '2rem' }}>
          <h2>✨ Generate Table with AI</h2>
          <div className="form-group">
            <label>
              Describe the table you want to create:
              <textarea
                value={claudePrompt}
                onChange={(e) => setClaudePrompt(e.target.value)}
                placeholder="e.g., Create a weekly meal planner with columns for day, breakfast, lunch, dinner, and notes"
                rows="3"
                style={{
                  width: '100%',
                  padding: '0.5rem',
                  fontSize: '1rem',
                  border: '1px solid var(--border)',
                  borderRadius: '4px',
                  background: 'var(--bg-primary)',
                  color: 'var(--text-primary)',
                  fontFamily: 'inherit',
                  resize: 'vertical'
                }}
                disabled={generatingTable}
              />
            </label>
          </div>
          <button
            onClick={generateTableWithClaude}
            className="btn-primary"
            disabled={generatingTable || !claudePrompt.trim()}
            style={{
              opacity: generatingTable || !claudePrompt.trim() ? 0.5 : 1,
              cursor: generatingTable || !claudePrompt.trim() ? 'not-allowed' : 'pointer'
            }}
          >
            {generatingTable ? '⏳ Generating...' : '✨ Generate Table with Claude'}
          </button>
          {generatingTable && (
            <p style={{ marginTop: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
              Claude is creating your table... This may take a few seconds.
            </p>
          )}
        </div>

        {/* Tables List */}
        <div className="tables-section">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '1.5rem' }}>
            <h2 style={{ margin: 0 }}>Tables ({tables.length})</h2>
            {tables.length > 0 && (
              <select
                onChange={(e) => {
                  const tableId = e.target.value;
                  if (tableId) {
                    const element = document.querySelector(`[data-table-id="${tableId}"]`);
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }
                  // Reset dropdown to placeholder
                  e.target.value = "";
                }}
                value=""
                style={{
                  padding: '6px 12px',
                  background: 'var(--bg-secondary)',
                  color: 'var(--text-primary)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '4px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  outline: 'none'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--accent-primary)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--border-color)'}
              >
                <option value="" disabled>Go to table...</option>
                {tables.map(table => (
                  <option key={table.id} value={table.id}>
                    {table.name || `Table ${table.id}`}
                  </option>
                ))}
              </select>
            )}
          </div>
          {tables.length === 0 ? (
            <p className="no-tables">No tables yet. Create one above!</p>
          ) : (
            tables.map(table => {
              // Safety check - ensure table has rows
              if (!table || !table.rows) {
                console.error('Invalid table data:', table);
                return null;
              }

              return (
                <div key={table.id} className="table-container" data-table-id={table.id}>
                  <div className="table-header">
                    {editingTableName === table.id ? (
                      <input
                        type="text"
                        value={editingTableNameValue}
                        onChange={(e) => setEditingTableNameValue(e.target.value)}
                        autoFocus
                        onBlur={() => {
                          const newName = editingTableNameValue.trim();
                          setEditingTableName(null);
                          if (newName && newName !== table.name) {
                            updateTableName(table.id, newName);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const newName = editingTableNameValue.trim();
                            setEditingTableName(null);
                            if (newName && newName !== table.name) {
                              updateTableName(table.id, newName);
                            }
                          } else if (e.key === 'Escape') {
                            setEditingTableName(null);
                          }
                        }}
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          padding: '0.25rem 0.5rem',
                          border: '2px solid var(--accent-primary)',
                          borderRadius: '4px',
                          background: 'var(--bg-primary)',
                          color: 'var(--text-primary)'
                        }}
                      />
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', width: '100%' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button
                          onClick={() => setCollapsedTables(prev => ({ ...prev, [table.id]: !prev[table.id] }))}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '1.2rem',
                            padding: '0',
                            lineHeight: '1',
                            color: 'var(--text-primary)'
                          }}
                          title={collapsedTables[table.id] ? 'Expand table' : 'Collapse table'}
                        >
                          {collapsedTables[table.id] ? '▶' : '▼'}
                        </button>
                        <h3
                          onClick={() => {
                            if (!table.isShared) {
                              setEditingTableNameValue(table.name);
                              setEditingTableName(table.id);
                            }
                          }}
                          style={{ cursor: table.isShared ? 'default' : 'pointer', margin: 0 }}
                          title={table.isShared ? `Shared by ${table.sharedBy}` : "Click to edit table name"}
                        >
                          {table.name}
                        </h3>
                        {table.isShared && (
                          <span style={{
                            padding: '4px 10px',
                            background: 'var(--accent-primary)',
                            color: 'white',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: '600'
                          }}>
                            📥 {table.sharedPermission === 'write' ? 'Can Edit' : 'Read Only'} by {table.sharedBy}
                          </span>
                        )}
                        {/* Inline slider controls for all modes */}
                        <div style={{ display: 'flex', alignItems: 'center', marginLeft: '0.5rem' }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                maxWidth: expandedControls[`main-table-${table.id}`] ? '2000px' : '0px',
                                opacity: expandedControls[`main-table-${table.id}`] ? 1 : 0,
                                overflow: expandedControls[`main-table-${table.id}`] ? 'visible' : 'hidden',
                                transition: 'max-width 0.3s ease, opacity 0.3s ease',
                                marginRight: '0.25rem'
                              }}
                            >
                              <button
                                onClick={() => captureTableScreenshot(table.id, table.name)}
                                className="minimalist-header-btn"
                                title="Capture screenshot of this table"
                                style={{
                                  marginLeft: '0.5rem',
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.75rem',
                                  background: 'var(--accent-primary)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}
                              >
                                📸
                              </button>
                              <button
                                onClick={() => toggleTableHeaders(table.id)}
                                className="minimalist-header-btn"
                                title={table.show_headers ? 'Hide column headers' : 'Show column headers'}
                                style={{
                                  marginLeft: '0.5rem',
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.75rem',
                                  background: table.show_headers ? 'var(--accent-primary)' : 'rgba(0,0,0,0.1)',
                                  color: table.show_headers ? 'white' : 'var(--text-primary)',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}
                              >
                                {table.show_headers ? 'Col ✓' : 'Col'}
                              </button>
                              <button
                                onClick={() => toggleTableRowHeaders(table.id)}
                                className="minimalist-header-btn"
                                title={table.show_row_headers ? 'Hide row headers' : 'Show row headers'}
                                style={{
                                  marginLeft: '0.5rem',
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.75rem',
                                  background: table.show_row_headers ? 'var(--accent-primary)' : 'rgba(0,0,0,0.1)',
                                  color: table.show_row_headers ? 'white' : 'var(--text-primary)',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}
                              >
                                {table.show_row_headers ? 'Row ✓' : 'Row'}
                              </button>
                              <button
                                onClick={() => addHeaderRow(table.id)}
                                className="minimalist-header-btn"
                                title="Add header row level above existing headers"
                                style={{
                                  marginLeft: '0.5rem',
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.75rem',
                                  background: 'var(--accent-primary)',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}
                              >
                                +Hdr Row
                              </button>
                              <button
                                onClick={() => setSubtablesEnabled(prev => ({ ...prev, [table.id]: !prev[table.id] }))}
                                className="minimalist-header-btn"
                                title={subtablesEnabled[table.id] ? 'Hide subtables column and controls' : 'Show subtables column and controls'}
                                style={{
                                  marginLeft: '0.5rem',
                                  padding: '0.25rem 0.5rem',
                                  fontSize: '0.75rem',
                                  background: subtablesEnabled[table.id] ? 'var(--accent-primary)' : 'rgba(0,0,0,0.1)',
                                  color: subtablesEnabled[table.id] ? 'white' : 'var(--text-primary)',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontWeight: 600,
                                  flexShrink: 0
                                }}
                              >
                                {subtablesEnabled[table.id] ? 'Subtables ✓' : 'Subtables'}
                              </button>
                              {!isTableReadOnly(table) && (
                                <div style={{ position: 'relative', display: 'inline-block', marginLeft: '0.5rem' }}>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowTableMenu(showTableMenu === table.id ? null : table.id);
                                    }}
                                    className="minimalist-header-btn"
                                    title="Table actions"
                                    style={{
                                      padding: '0.375rem 0.5rem',
                                      fontSize: '0.75rem',
                                      background: 'var(--accent-primary)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: 600,
                                      flexShrink: 0,
                                      lineHeight: '1',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center'
                                    }}
                                  >
                                    ≡
                                  </button>
                                  {showTableMenu === table.id && (
                                    <div className="insert-menu" style={{ right: 0, left: 'auto' }} onClick={(e) => e.stopPropagation()}>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          moveTable(table.id, 'top');
                                          setShowTableMenu(null);
                                        }}
                                      >
                                        Move to Top
                                      </button>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          moveTable(table.id, 'up');
                                          setShowTableMenu(null);
                                        }}
                                      >
                                        Move Up
                                      </button>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          moveTable(table.id, 'down');
                                          setShowTableMenu(null);
                                        }}
                                      >
                                        Move Down
                                      </button>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          moveTable(table.id, 'bottom');
                                          setShowTableMenu(null);
                                        }}
                                      >
                                        Move to Bottom
                                      </button>
                                    </div>
                                  )}
                                </div>
                              )}
                              {!table.isShared && (
                                <>
                                  <button
                                    onClick={() => setShowShareModal(table)}
                                    className="minimalist-header-btn"
                                    title="Share this table"
                                    style={{
                                      marginLeft: '0.5rem',
                                      padding: '0.375rem 0.5rem',
                                      fontSize: '0.75rem',
                                      background: 'var(--accent-primary)',
                                      color: 'white',
                                      border: 'none',
                                      borderRadius: '4px',
                                      cursor: 'pointer',
                                      fontWeight: 600,
                                      flexShrink: 0,
                                      lineHeight: '1'
                                    }}
                                  >
                                    Share
                                  </button>
                                  {tableShares[table.id] && tableShares[table.id].length > 0 && (
                                    <div style={{ position: 'relative', display: 'inline-block', marginLeft: '0.5rem' }}>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setShowShareDropdown(showShareDropdown === table.id ? null : table.id);
                                        }}
                                        className="minimalist-header-btn"
                                        title={`Shared with ${tableShares[table.id].length} user${tableShares[table.id].length > 1 ? 's' : ''}`}
                                        style={{
                                          padding: '0.375rem 0.5rem',
                                          fontSize: '0.75rem',
                                          background: 'var(--accent-primary)',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: 'pointer',
                                          fontWeight: 600,
                                          flexShrink: 0,
                                          lineHeight: '1'
                                        }}
                                      >
                                        Shared ({tableShares[table.id].length}) ▾
                                      </button>
                                      {showShareDropdown === table.id && (
                                        <div
                                          style={{
                                            position: 'absolute',
                                            top: '100%',
                                            left: 0,
                                            marginTop: '4px',
                                            background: 'var(--bg-primary)',
                                            border: '1px solid var(--border-color)',
                                            borderRadius: '4px',
                                            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                            zIndex: 1000,
                                            minWidth: '200px',
                                            padding: '8px'
                                          }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <div style={{
                                            fontSize: '11px',
                                            color: 'var(--text-secondary)',
                                            padding: '4px 8px',
                                            borderBottom: '1px solid var(--border-color)',
                                            marginBottom: '4px',
                                            fontWeight: 600
                                          }}>
                                            SHARED WITH
                                          </div>
                                          {tableShares[table.id].map((share) => (
                                            <div
                                              key={share.id}
                                              style={{
                                                padding: '6px 8px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                borderRadius: '4px',
                                                marginBottom: '2px'
                                              }}
                                            >
                                              <span style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                                                {share.shared_with_username}
                                              </span>
                                              <span style={{
                                                fontSize: '11px',
                                                padding: '2px 6px',
                                                background: share.permission === 'write' ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                                                color: share.permission === 'write' ? 'white' : 'var(--text-secondary)',
                                                borderRadius: '8px',
                                                fontWeight: 600
                                              }}>
                                                {share.permission === 'write' ? 'Can Edit' : 'Read Only'}
                                              </span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  {!isTableReadOnly(table) && (
                                    <button
                                      onClick={() => deleteTable(table.id)}
                                      className="minimalist-header-btn"
                                      title="Delete this table"
                                      style={{
                                        marginLeft: '0.5rem',
                                        padding: '0.25rem 0.5rem',
                                        fontSize: '0.75rem',
                                        background: 'var(--accent-primary)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        fontWeight: 600,
                                        flexShrink: 0
                                      }}
                                    >
                                      🗑️ Delete
                                    </button>
                                  )}
                                </>
                              )}
                              {table.isShared && table.sharedPermission === 'read' && (
                                <span style={{
                                  marginLeft: '0.5rem',
                                  fontSize: '0.75rem',
                                  padding: '0.25rem 0.5rem',
                                  color: '#666',
                                  fontStyle: 'italic'
                                }}>
                                  Read-only access
                                </span>
                              )}
                              {table.isShared && (
                                <button
                                  onClick={() => {
                                    const newHiddenIds = [...hiddenSharedTableIds, table.id];
                                    setHiddenSharedTableIds(newHiddenIds);
                                    localStorage.setItem('hiddenSharedTables', JSON.stringify(newHiddenIds));
                                    // Remove from current view
                                    setTables(tables.filter(t => t.id !== table.id));
                                  }}
                                  className="minimalist-header-btn"
                                  title="Remove this shared table from view"
                                  style={{
                                    marginLeft: '0.5rem',
                                    padding: '0.25rem 0.5rem',
                                    fontSize: '0.75rem',
                                    background: '#ff9800',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: 600,
                                    flexShrink: 0,
                                    lineHeight: '1',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  Remove from View
                                </button>
                              )}
                            </div>
                            <button
                              onClick={() => setExpandedControls(prev => ({ ...prev, [`main-table-${table.id}`]: !prev[`main-table-${table.id}`] }))}
                              className="minimalist-header-btn"
                              title={expandedControls[`main-table-${table.id}`] ? 'Hide controls' : 'Show controls'}
                              style={{
                                marginLeft: '0.25rem',
                                padding: '0.25rem 0.5rem',
                                fontSize: '0.75rem',
                                background: 'var(--accent-primary)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontWeight: 600,
                                flexShrink: 0,
                                lineHeight: '1',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative',
                                zIndex: 1
                              }}
                            >
                              {expandedControls[`main-table-${table.id}`] ? '◀' : <span style={{ transform: 'rotate(90deg)', display: 'inline-block', fontSize: '1rem' }}>≡</span>}
                            </button>
                          </div>
                        </div>
                        {/* Table description in separate collapsible - second row */}
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            maxWidth: expandedControls[`desc-${table.id}`] ? '800px' : '0px',
                            opacity: expandedControls[`desc-${table.id}`] ? 1 : 0,
                            overflow: 'hidden',
                            transition: 'max-width 0.3s ease, opacity 0.3s ease',
                            marginRight: '0.25rem'
                          }}
                        >
                          {/* Table description - shows rendered Markdown when not editing, textarea when editing */}
                          {editingTableDescription === table.id ? (
                              <textarea
                                ref={(el) => {
                                  if (el) {
                                    // Auto-adjust height to fit content
                                    el.style.height = 'auto';
                                    el.style.height = Math.max(32, el.scrollHeight) + 'px';
                                    el.focus();
                                  }
                                }}
                                value={editingTableDescriptionValue}
                                onChange={(e) => {
                                  setEditingTableDescriptionValue(e.target.value);
                                  // Auto-adjust height on change
                                  e.target.style.height = 'auto';
                                  e.target.style.height = Math.max(32, e.target.scrollHeight) + 'px';
                                }}
                                onBlur={() => {
                                  const newDesc = editingTableDescriptionValue;
                                  if (newDesc !== (table.description || '')) {
                                    updateTableDescription(table.id, newDesc);
                                  }
                                  setEditingTableDescription(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && e.shiftKey) {
                                    // Allow Shift+Enter for new lines
                                    return;
                                  } else if (e.key === 'Enter') {
                                    e.preventDefault();
                                    const newDesc = editingTableDescriptionValue;
                                    if (newDesc !== (table.description || '')) {
                                      updateTableDescription(table.id, newDesc);
                                    }
                                    setEditingTableDescription(null);
                                  } else if (e.key === 'Escape') {
                                    setEditingTableDescription(null);
                                  }
                                }}
                                onMouseUp={(e) => {
                                  // Detect when user finishes resizing (mouseup after resize)
                                  const newWidth = e.target.offsetWidth;
                                  const currentWidth = table.description_width || 400;
                                  if (Math.abs(newWidth - currentWidth) > 5) {
                                    // Width changed significantly, save it
                                    updateTableDescriptionWidth(table.id, newWidth);
                                  }
                                }}
                                placeholder="Add description... (supports *italic*, **bold**, `code`, [links](url))"
                                disabled={isTableReadOnly(table)}
                                style={{
                                  marginLeft: '0.75rem',
                                  padding: '0.375rem 0.5rem',
                                  fontSize: '0.75rem',
                                  background: 'var(--bg-input)',
                                  color: 'var(--text-primary)',
                                  border: '1px solid var(--border-primary)',
                                  borderRadius: '4px',
                                  flexShrink: 0,
                                  width: `${table.description_width || 400}px`,
                                  fontFamily: 'inherit',
                                  resize: 'horizontal',
                                  minWidth: '200px',
                                  maxWidth: '800px',
                                  minHeight: '2rem',
                                  overflow: 'hidden',
                                  boxSizing: 'border-box'
                                }}
                              />
                            ) : (
                              <div
                                onClick={() => {
                                  if (!isTableReadOnly(table)) {
                                    setEditingTableDescription(table.id);
                                    setEditingTableDescriptionValue(table.description || '');
                                  }
                                }}
                                style={{
                                  marginLeft: '0.75rem',
                                  padding: '0.375rem 0.5rem',
                                  fontSize: '0.75rem',
                                  background: 'var(--bg-input)',
                                  color: 'var(--text-primary)',
                                  border: '1px solid var(--border-primary)',
                                  borderRadius: '4px',
                                  flexShrink: 0,
                                  width: `${table.description_width || 400}px`,
                                  minWidth: '200px',
                                  maxWidth: '800px',
                                  minHeight: '2rem',
                                  boxSizing: 'border-box',
                                  cursor: isTableReadOnly(table) ? 'default' : 'text',
                                  fontStyle: table.description ? 'normal' : 'italic',
                                  opacity: table.description ? 1 : 0.6,
                                  overflowWrap: 'break-word'
                                }}
                                dangerouslySetInnerHTML={{
                                  __html: table.description ? renderMarkdown(table.description) : 'Add description... (supports <em>italic</em>, <strong>bold</strong>, <code>code</code>, links)'
                                }}
                              />
                            )}
                          </div>
                          <button
                            onClick={() => setExpandedControls(prev => ({ ...prev, [`desc-${table.id}`]: !prev[`desc-${table.id}`] }))}
                            className="minimalist-header-btn"
                            title={expandedControls[`desc-${table.id}`] ? 'Hide description' : 'Show description'}
                            style={{
                              padding: '0.375rem 0.5rem',
                              fontSize: '0.75rem',
                              background: 'var(--accent-primary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: 600,
                              flexShrink: 0,
                              lineHeight: '1',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {expandedControls[`desc-${table.id}`] ? '◀' : '📝'}
                          </button>

                          {/* AI Assistant Button */}
                          <button
                            onClick={() => setExpandedControls(prev => ({ ...prev, [`ai-${table.id}`]: !prev[`ai-${table.id}`] }))}
                            className="minimalist-header-btn"
                            title={expandedControls[`ai-${table.id}`] ? 'Hide AI Assistant' : 'Show AI Assistant'}
                            style={{
                              marginLeft: '0.5rem',
                              padding: '0.375rem 0.5rem',
                              fontSize: '0.75rem',
                              background: 'var(--accent-primary)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: 600,
                              flexShrink: 0,
                              lineHeight: '1',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            {expandedControls[`ai-${table.id}`] ? '◀' : '🤖'}
                          </button>
                        </div>

                        {/* AI Assistant Content */}
                        {expandedControls[`ai-${table.id}`] && (
                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginTop: '0.5rem' }}>
                          <div
                            style={{
                              flex: expandedControls[`ai-${table.id}`] ? 1 : 0,
                              maxWidth: expandedControls[`ai-${table.id}`] ? '800px' : '0',
                              opacity: expandedControls[`ai-${table.id}`] ? 1 : 0,
                              overflow: 'hidden',
                              transition: 'max-width 0.3s ease, opacity 0.3s ease',
                              marginRight: '0.25rem'
                            }}
                          >
                            {/* AI Assistant conversation and input */}
                            <div style={{
                              marginLeft: '0.75rem',
                              padding: '0.5rem',
                              fontSize: '0.75rem',
                              background: 'var(--bg-input)',
                              color: 'var(--text-primary)',
                              border: '1px solid var(--accent-primary)',
                              borderRadius: '4px',
                              flexShrink: 0,
                              width: '600px',
                              minWidth: '300px',
                              maxWidth: '800px',
                              boxSizing: 'border-box'
                            }}>
                              {/* Conversation history */}
                              {tableAIAssistant[table.id]?.conversationHistory?.length > 0 && (
                                <div style={{
                                  maxHeight: '200px',
                                  overflowY: 'auto',
                                  marginBottom: '0.5rem',
                                  padding: '0.5rem',
                                  background: 'var(--bg-secondary)',
                                  borderRadius: '4px'
                                }}>
                                  {tableAIAssistant[table.id].conversationHistory.map((msg, idx) => (
                                    <div key={idx} style={{
                                      marginBottom: '0.5rem',
                                      padding: '0.25rem',
                                      borderLeft: msg.role === 'user' ? '2px solid var(--accent-primary)' : '2px solid #10b981',
                                      paddingLeft: '0.5rem'
                                    }}>
                                      <div style={{ fontWeight: 'bold', fontSize: '0.7rem', opacity: 0.7 }}>
                                        {msg.role === 'user' ? 'You' : 'AI'}
                                      </div>
                                      <div>{msg.content}</div>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Input area */}
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <input
                                  type="text"
                                  placeholder="Ask AI to modify this table... (e.g., 'Add a nested table in row 0')"
                                  value={tableAIAssistant[table.id]?.input || ''}
                                  onChange={(e) => {
                                    setTableAIAssistant(prev => ({
                                      ...prev,
                                      [table.id]: {
                                        ...prev[table.id],
                                        input: e.target.value
                                      }
                                    }));
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !tableAIAssistant[table.id]?.loading) {
                                      const prompt = tableAIAssistant[table.id]?.input?.trim();
                                      if (prompt) {
                                        console.log('[AI Assistant UI] Table name:', table.name, 'ID:', table.id);
                                        handleAIAssistantPrompt(table.id, prompt);
                                      }
                                    }
                                  }}
                                  disabled={tableAIAssistant[table.id]?.loading}
                                  style={{
                                    flex: 1,
                                    padding: '0.375rem 0.5rem',
                                    fontSize: '0.75rem',
                                    background: 'var(--bg-primary)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--border-primary)',
                                    borderRadius: '4px',
                                    fontFamily: 'inherit'
                                  }}
                                />
                                <button
                                  onClick={() => {
                                    const prompt = tableAIAssistant[table.id]?.input?.trim();
                                    if (prompt && !tableAIAssistant[table.id]?.loading) {
                                      console.log('[AI Assistant UI Send Button] Table name:', table.name, 'ID:', table.id);
                                      console.log('[AI Assistant UI Send Button] All table IDs in tables array:', tables.map(t => ({name: t.name, id: t.id})));
                                      handleAIAssistantPrompt(table.id, prompt);
                                    }
                                  }}
                                  disabled={!tableAIAssistant[table.id]?.input?.trim() || tableAIAssistant[table.id]?.loading}
                                  style={{
                                    padding: '0.375rem 0.75rem',
                                    fontSize: '0.75rem',
                                    background: 'var(--accent-primary)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: tableAIAssistant[table.id]?.loading ? 'wait' : 'pointer',
                                    fontWeight: 600,
                                    opacity: (!tableAIAssistant[table.id]?.input?.trim() || tableAIAssistant[table.id]?.loading) ? 0.5 : 1
                                  }}
                                >
                                  {tableAIAssistant[table.id]?.loading ? '...' : 'Send'}
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                        )}
                      </div>
                    )}
                  </div>
                  {!collapsedTables[table.id] && (
                  <div className="table-wrapper">
                    <table className="data-table">
                      {table.show_headers && (
                        <thead>
                          {/* Render additional header rows if they exist */}
                          {table.header_rows && table.header_rows.map((headerRow, headerRowIdx) => (
                            <tr key={`header-row-${headerRowIdx}`}>
                              {/* Only render corner cells in first header row, with rowspan to cover all header rows + main header row */}
                              {headerRowIdx === 0 && subtablesEnabled[table.id] && table.show_row_headers && (() => {
                                const allSubs = getAllTableSubtables(table);
                                const anyExpanded = allSubs.some(st => st.expanded !== false);
                                return (
                                <th
                                  className="row-controls"
                                  rowSpan={(table.header_rows?.length || 0) + 1}
                                  colSpan={2}
                                  style={{
                                    width: `${(table.row_header_width || 120) + 50}px`,
                                    minWidth: `${(table.row_header_width || 120) + 50}px`,
                                    position: 'relative'
                                  }}
                                >
                                  {allSubs.length > 0 && (
                                    <button
                                      onClick={() => {
                                        const ids = allSubs
                                          .filter(st => anyExpanded ? st.expanded !== false : st.expanded === false)
                                          .map(st => st.id);
                                        if (ids.length > 0) {
                                          bulkSetExpanded(ids, !anyExpanded, (id) => ({tableId: table.id, path: [{type: 'subtable', id}]}));
                                        }
                                      }}
                                      title={anyExpanded ? 'Hide all subtables' : 'Show all subtables'}
                                      style={{
                                        fontSize: '0.65rem',
                                        padding: '2px 5px',
                                        border: `1px solid ${theme === 'dark' ? '#666' : '#ccc'}`,
                                        borderRadius: '3px',
                                        background: theme === 'dark' ? '#3a3a3a' : '#f5f5f5',
                                        color: theme === 'dark' ? '#ccc' : '#555',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {anyExpanded ? '▼ Hide all' : '▶ Show all'}
                                    </button>
                                  )}
                                  {!isTableReadOnly(table) && (
                                    <ResizeHandle
                                      direction="col"
                                      onResizePreview={(newWidth) => {
                                        setTables(prevTables => prevTables.map(t =>
                                          t.id === table.id ? { ...t, row_header_width: newWidth - 50 } : t
                                        ));
                                      }}
                                      onResizeCommit={(newWidth) => {
                                        authFetch(`${API_URL}/api/tables/${table.id}/row-header-width`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ width: newWidth - 50 })
                                        }).catch(err => {
                                          console.error('[ResizeHandle] Failed to save row header width:', err);
                                          fetchTables();
                                        });
                                      }}
                                    />
                                  )}
                                </th>
                                );
                              })()}
                              {headerRowIdx === 0 && subtablesEnabled[table.id] && !table.show_row_headers && (() => {
                                const allSubs = getAllTableSubtables(table);
                                const anyExpanded = allSubs.some(st => st.expanded !== false);
                                return (
                                <th className="row-controls" rowSpan={(table.header_rows?.length || 0) + 1}>
                                  {allSubs.length > 0 && (
                                    <button
                                      onClick={() => {
                                        const ids = allSubs
                                          .filter(st => anyExpanded ? st.expanded !== false : st.expanded === false)
                                          .map(st => st.id);
                                        if (ids.length > 0) {
                                          bulkSetExpanded(ids, !anyExpanded, (id) => ({tableId: table.id, path: [{type: 'subtable', id}]}));
                                        }
                                      }}
                                      title={anyExpanded ? 'Hide all subtables' : 'Show all subtables'}
                                      style={{
                                        fontSize: '0.65rem',
                                        padding: '2px 5px',
                                        border: `1px solid ${theme === 'dark' ? '#666' : '#ccc'}`,
                                        borderRadius: '3px',
                                        background: theme === 'dark' ? '#3a3a3a' : '#f5f5f5',
                                        color: theme === 'dark' ? '#ccc' : '#555',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {anyExpanded ? '▼ Hide all' : '▶ Show all'}
                                    </button>
                                  )}
                                </th>
                                );
                              })()}
                              {headerRowIdx === 0 && !subtablesEnabled[table.id] && table.show_row_headers && (
                                <th
                                  className="table-row-header-cell"
                                  rowSpan={(table.header_rows?.length || 0) + 1}
                                  style={{
                                    width: `${table.row_header_width || 120}px`,
                                    minWidth: `${table.row_header_width || 120}px`,
                                    position: 'relative'
                                  }}
                                >
                                  {!isTableReadOnly(table) && (
                                    <ResizeHandle
                                      direction="col"
                                      onResizePreview={(newWidth) => {
                                        // Optimistic update during drag
                                        setTables(prevTables => prevTables.map(t =>
                                          t.id === table.id ? { ...t, row_header_width: newWidth } : t
                                        ));
                                      }}
                                      onResizeCommit={(newWidth) => {
                                        // Save to backend only once when drag completes
                                        authFetch(`${API_URL}/api/tables/${table.id}/row-header-width`, {
                                          method: 'PUT',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({ width: newWidth })
                                        }).catch(err => {
                                          console.error('[ResizeHandle] Failed to save row header width:', err);
                                          fetchTables(); // Rollback on error
                                        });
                                      }}
                                    />
                                  )}
                                </th>
                              )}
                              {headerRow.map((headerCell, colIdx) => {
                                const colWidth = table.column_widths?.[colIdx] || 150;
                                const mergeInfo = table.header_rows_merged?.[`${headerRowIdx},${colIdx}`];
                                const colspan = mergeInfo?.colspan || 1;
                                const rowspan = mergeInfo?.rowspan || 1;

                                // Skip cells that are covered by a merge from another cell
                                let isCovered = false;
                                if (table.header_rows_merged) {
                                  for (const [key, merge] of Object.entries(table.header_rows_merged)) {
                                    const [mergeRow, mergeCol] = key.split(',').map(Number);
                                    if (mergeRow !== headerRowIdx || mergeCol !== colIdx) {
                                      if (headerRowIdx >= mergeRow && headerRowIdx < mergeRow + merge.rowspan &&
                                          colIdx >= mergeCol && colIdx < mergeCol + merge.colspan) {
                                        isCovered = true;
                                        break;
                                      }
                                    }
                                  }
                                }

                                if (isCovered) return null;

                                return (
                                  <th
                                    key={`header-${headerRowIdx}-${colIdx}`}
                                    className="table-column-header-cell"
                                    colSpan={colspan}
                                    rowSpan={rowspan}
                                    onContextMenu={(e) => {
                                      if (!isTableReadOnly(table)) {
                                        e.preventDefault();
                                        setSelectedHeaderCell({
                                          tableId: table.id,
                                          headerRowIdx,
                                          colIdx,
                                          x: e.clientX,
                                          y: e.clientY
                                        });
                                      }
                                    }}
                                    style={{
                                      width: `${colWidth * colspan}px`,
                                      minWidth: `${colWidth * colspan}px`,
                                      position: 'relative',
                                      cursor: !isTableReadOnly(table) ? 'context-menu' : 'default',
                                      textAlign: 'center',
                                      ...(borderStyle !== 'minimalist' && {
                                        fontWeight: 600,
                                        padding: '0.75rem'
                                      }),
                                      ...getColumnBorderStyle(table.column_border_styles, colIdx)
                                    }}
                                  >
                                    {isTableReadOnly(table) ? (
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        {headerCell || ''}
                                      </div>
                                    ) : (
                                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                        <input
                                          type="text"
                                          value={headerCell || ''}
                                          onChange={(e) => {
                                          const newValue = e.target.value;
                                          // Update header row cell
                                          const updatedHeaderRows = [...(table.header_rows || [])];
                                          updatedHeaderRows[headerRowIdx] = [...updatedHeaderRows[headerRowIdx]];
                                          updatedHeaderRows[headerRowIdx][colIdx] = newValue;

                                          // Optimistic update
                                          setTables(prevTables => prevTables.map(t =>
                                            t.id === table.id ? { ...t, header_rows: updatedHeaderRows } : t
                                          ));

                                          // Send to backend
                                          authFetch(`${API_URL}/api/tables/${table.id}/update-header-row-cell`, {
                                            method: 'PUT',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({
                                              header_row_index: headerRowIdx,
                                              col_index: colIdx,
                                              value: newValue
                                            })
                                          }).catch(error => {
                                            console.error('Error updating header row cell:', error);
                                            fetchTables();
                                          });
                                        }}
                                          className={borderStyle === 'minimalist' ? 'minimalist-header-input' : ''}
                                          style={{
                                            border: 'none',
                                            background: 'transparent',
                                            padding: 0,
                                            fontSize: 'inherit',
                                            fontFamily: 'inherit',
                                            color: 'inherit',
                                            fontWeight: 'inherit',
                                            outline: 'none',
                                            width: '100%',
                                            boxSizing: 'border-box',
                                            textAlign: 'center'
                                          }}
                                        />
                                      </div>
                                    )}
                                  </th>
                                );
                              })}
                              {!isTableReadOnly(table) && (
                                <th style={{ border: 'none', background: 'transparent', width: '20px', padding: '2px' }}>
                                  <button
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      removeHeaderRow(table.id, headerRowIdx);
                                    }}
                                    title="Remove this header row"
                                    style={{
                                      padding: '2px 4px',
                                      fontSize: '0.65rem',
                                      background: 'transparent',
                                      color: 'var(--text-secondary)',
                                      border: 'none',
                                      cursor: 'pointer',
                                      opacity: 0.5,
                                      transition: 'opacity 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                                    onMouseLeave={(e) => e.currentTarget.style.opacity = '0.5'}
                                  >
                                    ×
                                  </button>
                                </th>
                              )}
                            </tr>
                          ))}
                          <tr>
                            {/* Only render corner cells if no header rows exist (otherwise they're rowspanned from above) */}
                            {!table.header_rows && subtablesEnabled[table.id] && (() => {
                              const allSubs = getAllTableSubtables(table);
                              const anyExpanded = allSubs.some(st => st.expanded !== false);
                              return (
                                <th className="row-controls">
                                  {allSubs.length > 0 && (
                                    <button
                                      onClick={() => {
                                        const ids = allSubs
                                          .filter(st => anyExpanded ? st.expanded !== false : st.expanded === false)
                                          .map(st => st.id);
                                        if (ids.length > 0) {
                                          bulkSetExpanded(ids, !anyExpanded, (id) => ({tableId: table.id, path: [{type: 'subtable', id}]}));
                                        }
                                      }}
                                      title={anyExpanded ? 'Hide all subtables' : 'Show all subtables'}
                                      style={{
                                        fontSize: '0.65rem',
                                        padding: '2px 5px',
                                        border: `1px solid ${theme === 'dark' ? '#666' : '#ccc'}`,
                                        borderRadius: '3px',
                                        background: theme === 'dark' ? '#3a3a3a' : '#f5f5f5',
                                        color: theme === 'dark' ? '#ccc' : '#555',
                                        cursor: 'pointer',
                                        whiteSpace: 'nowrap',
                                      }}
                                    >
                                      {anyExpanded ? '▼ Hide all' : '▶ Show all'}
                                    </button>
                                  )}
                                </th>
                              );
                            })()}
                            {!table.header_rows && table.show_row_headers && (
                              <th
                                className="table-row-header-cell"
                                style={{
                                  width: `${table.row_header_width || 120}px`,
                                  minWidth: `${table.row_header_width || 120}px`,
                                  position: 'relative'
                                }}
                              >
                                {!isTableReadOnly(table) && (
                                  <ResizeHandle
                                    direction="col"
                                    onResizePreview={(newWidth) => {
                                      // Optimistic update during drag
                                      setTables(prevTables => prevTables.map(t =>
                                        t.id === table.id ? { ...t, row_header_width: newWidth } : t
                                      ));
                                    }}
                                    onResizeCommit={(newWidth) => {
                                      // Save to backend only once when drag completes
                                      authFetch(`${API_URL}/api/tables/${table.id}/row-header-width`, {
                                        method: 'PUT',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ width: newWidth })
                                      }).catch(err => {
                                        console.error('[ResizeHandle] Failed to save row header width:', err);
                                        fetchTables(); // Rollback on error
                                      });
                                    }}
                                  />
                                )}
                              </th>
                            )}
                            {(table.column_headers || Array.from({ length: table.num_cols }, (_, i) => `Column ${i + 1}`)).map((header, colIdx) => {
                              const colWidth = table.column_widths?.[colIdx] || 150;
                              return (
                                <th
                                  key={`${colIdx}-${header}`}
                                  className="table-column-header-cell"
                                  style={{
                                    width: `${colWidth}px`,
                                    minWidth: `${colWidth}px`,
                                    position: 'relative',
                                    // Don't override font styles in minimalist mode - let CSS handle it
                                    ...(borderStyle !== 'minimalist' && {
                                      fontWeight: 600,
                                      padding: '0.75rem'
                                    }),
                                    ...getColumnBorderStyle(table.column_border_styles, colIdx)
                                  }}
                                >
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                    <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                      {isTableReadOnly(table) ? (
                                        <div
                                          style={{
                                            flex: 1,
                                            ...(borderStyle !== 'minimalist' && {
                                              padding: '0.5rem',
                                              fontWeight: 600
                                            })
                                          }}
                                        >
                                          <LatexText text={header} />
                                        </div>
                                      ) : (
                                        <div style={{ flex: 1 }}>
                                          <EditableLatexHeader
                                            value={header}
                                            onSave={(newValue) => {
                                              console.log('[Main table header] Saving:', newValue);
                                              updateColumnHeader(table.id, colIdx, newValue);
                                            }}
                                            fontSize={borderStyle !== 'minimalist' ? '1rem' : undefined}
                                            onSort={null}
                                          />
                                        </div>
                                      )}
                                      {table.translation_columns?.[colIdx] && (
                                        <span style={{ fontSize: '0.7rem', opacity: 0.7, marginLeft: '4px' }}>
                                          🌐
                                        </span>
                                      )}
                                      {table.sort_column === colIdx && table.sort_direction && (
                                        <span style={{ fontSize: '1rem', opacity: 0.7, minWidth: '1rem' }}>
                                          {table.sort_direction === 'asc' ? '↑' : '↓'}
                                        </span>
                                      )}
                                      {!isTableReadOnly(table) && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowColMenu(showColMenu?.tableId === table.id && showColMenu?.colIdx === colIdx ? null : { tableId: table.id, colIdx });
                                          }}
                                          className="btn-insert-menu"
                                          title="Insert column"
                                        >
                                          ≡
                                        </button>
                                      )}
                                    </div>
                                    {showColMenu?.tableId === table.id && showColMenu?.colIdx === colIdx && (
                                      <div className="insert-menu" style={{ right: 0, left: 'auto', overflow: 'visible' }} onClick={(e) => e.stopPropagation()}>
                                        <button
                                          className="insert-menu-item"
                                          onClick={async () => {
                                            await authFetch(`${API_URL}/api/tables/${table.id}/sort`, {
                                              method: 'PUT',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({
                                                sort_column: colIdx,
                                                sort_direction: 'asc'
                                              })
                                            });
                                            await fetchTables();
                                            setShowColMenu(null);
                                          }}
                                          disabled={table.sort_column === colIdx && table.sort_direction === 'asc'}
                                          style={{
                                            opacity: table.sort_column === colIdx && table.sort_direction === 'asc' ? 0.5 : 1
                                          }}
                                        >
                                          ↑ Sort Ascending
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={async () => {
                                            await authFetch(`${API_URL}/api/tables/${table.id}/sort`, {
                                              method: 'PUT',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({
                                                sort_column: colIdx,
                                                sort_direction: 'desc'
                                              })
                                            });
                                            await fetchTables();
                                            setShowColMenu(null);
                                          }}
                                          disabled={table.sort_column === colIdx && table.sort_direction === 'desc'}
                                          style={{
                                            opacity: table.sort_column === colIdx && table.sort_direction === 'desc' ? 0.5 : 1
                                          }}
                                        >
                                          ↓ Sort Descending
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={async () => {
                                            await authFetch(`${API_URL}/api/tables/${table.id}/sort`, {
                                              method: 'PUT',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({
                                                sort_column: null,
                                                sort_direction: null
                                              })
                                            });
                                            await fetchTables();
                                            setShowColMenu(null);
                                          }}
                                          disabled={table.sort_column !== colIdx}
                                          style={{
                                            opacity: table.sort_column !== colIdx ? 0.5 : 1
                                          }}
                                        >
                                          ✕ Clear Sort
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={async () => {
                                            try {
                                              console.log('[Done Column] Setting done column:', colIdx, 'for table:', table.id);
                                              const response = await authFetch(`${API_URL}/api/tables/${table.id}/done-column`, {
                                                method: 'PUT',
                                                headers: { 'Content-Type': 'application/json' },
                                                body: JSON.stringify({ col_idx: colIdx })
                                              });
                                              console.log('[Done Column] Response:', response);
                                              await fetchTables();
                                              setShowColMenu(null);
                                            } catch (error) {
                                              console.error('[Done Column] Error:', error);
                                            }
                                          }}
                                          style={{
                                            background: table.done_column === colIdx ? 'var(--accent-primary)' : undefined,
                                            color: table.done_column === colIdx ? 'white' : undefined
                                          }}
                                        >
                                          {table.done_column === colIdx ? 'Done↓ ✓' : 'Done↓'}
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={() => {
                                            insertColumn(table.id, colIdx, 'left');
                                            setShowColMenu(null);
                                          }}
                                        >
                                          ← Insert Left
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={() => {
                                            insertColumn(table.id, colIdx, 'right');
                                            setShowColMenu(null);
                                          }}
                                        >
                                          → Insert Right
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={() => {
                                            moveColumn(table.id, colIdx, 'left');
                                            setShowColMenu(null);
                                          }}
                                          disabled={colIdx === 0}
                                          style={{ opacity: colIdx === 0 ? 0.5 : 1 }}
                                        >
                                          ⬅ Move Left
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={() => {
                                            moveColumn(table.id, colIdx, 'right');
                                            setShowColMenu(null);
                                          }}
                                          disabled={colIdx === table.num_cols - 1}
                                          style={{ opacity: colIdx === table.num_cols - 1 ? 0.5 : 1 }}
                                        >
                                          ➡ Move Right
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={() => {
                                            copyColumn(table.id, colIdx);
                                            setShowColMenu(null);
                                          }}
                                        >
                                          📋 Copy Column
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={() => {
                                            pasteColumn(table.id, colIdx);
                                            setShowColMenu(null);
                                          }}
                                          disabled={!copiedColumn}
                                          style={{ opacity: copiedColumn ? 1 : 0.5 }}
                                          title={copiedColumn ? "Paste copied column into this column" : "No column copied"}
                                        >
                                          📄 Paste Column
                                        </button>
                                        <div
                                          style={{ position: 'relative', overflow: 'visible' }}
                                          onMouseEnter={() => {
                                            if (borderSubmenuTimeout) {
                                              clearTimeout(borderSubmenuTimeout);
                                              setBorderSubmenuTimeout(null);
                                            }
                                            setShowColBorderSubmenu({ tableId: table.id, colIdx });
                                          }}
                                          onMouseLeave={() => {
                                            const timeout = setTimeout(() => {
                                              setShowColBorderSubmenu(null);
                                            }, 300);
                                            setBorderSubmenuTimeout(timeout);
                                          }}
                                        >
                                          <button
                                            className="insert-menu-item"
                                            style={{
                                              display: 'flex',
                                              justifyContent: 'space-between',
                                              alignItems: 'center',
                                              background: showColBorderSubmenu?.tableId === table.id && showColBorderSubmenu?.colIdx === colIdx ? 'var(--bg-hover)' : undefined
                                            }}
                                          >
                                            <span>🎨 Border Style</span>
                                            <span>▶</span>
                                          </button>
                                          {(showColBorderSubmenu?.tableId === table.id && showColBorderSubmenu?.colIdx === colIdx) && (
                                            <div
                                              className="insert-menu"
                                              style={{
                                                position: 'absolute',
                                                left: '100%',
                                                top: 0,
                                                marginLeft: '2px',
                                                minWidth: '150px',
                                                zIndex: 10000
                                              }}
                                              onMouseEnter={() => {
                                                if (borderSubmenuTimeout) {
                                                  clearTimeout(borderSubmenuTimeout);
                                                  setBorderSubmenuTimeout(null);
                                                }
                                              }}
                                              onMouseLeave={() => {
                                                const timeout = setTimeout(() => {
                                                  setShowColBorderSubmenu(null);
                                                }, 300);
                                                setBorderSubmenuTimeout(timeout);
                                              }}
                                            >
                                              <button
                                                className="insert-menu-item"
                                                onClick={() => {
                                                  setColumnBorderStyle(table.id, colIdx, 'none');
                                                  setShowColMenu(null);
                                                  setShowColBorderSubmenu(null);
                                                }}
                                                style={{
                                                  background: !table.column_border_styles?.[colIdx] ? 'var(--accent-light-bg)' : undefined
                                                }}
                                              >
                                                None
                                              </button>
                                              <button
                                                className="insert-menu-item"
                                                onClick={() => {
                                                  setColumnBorderStyle(table.id, colIdx, 'double-left');
                                                  setShowColMenu(null);
                                                  setShowColBorderSubmenu(null);
                                                }}
                                                style={{
                                                  background: table.column_border_styles?.[colIdx] === 'double-left' ? 'var(--accent-light-bg)' : undefined
                                                }}
                                              >
                                                ║ Double Left
                                              </button>
                                              <button
                                                className="insert-menu-item"
                                                onClick={() => {
                                                  setColumnBorderStyle(table.id, colIdx, 'double-right');
                                                  setShowColMenu(null);
                                                  setShowColBorderSubmenu(null);
                                                }}
                                                style={{
                                                  background: table.column_border_styles?.[colIdx] === 'double-right' ? 'var(--accent-light-bg)' : undefined
                                                }}
                                              >
                                                ║ Double Right
                                              </button>
                                              <button
                                                className="insert-menu-item"
                                                onClick={() => {
                                                  setColumnBorderStyle(table.id, colIdx, 'thick-left');
                                                  setShowColMenu(null);
                                                  setShowColBorderSubmenu(null);
                                                }}
                                                style={{
                                                  background: table.column_border_styles?.[colIdx] === 'thick-left' ? 'var(--accent-light-bg)' : undefined
                                                }}
                                              >
                                                ▌Thick Left
                                              </button>
                                              <button
                                                className="insert-menu-item"
                                                onClick={() => {
                                                  setColumnBorderStyle(table.id, colIdx, 'thick-right');
                                                  setShowColMenu(null);
                                                  setShowColBorderSubmenu(null);
                                                }}
                                                style={{
                                                  background: table.column_border_styles?.[colIdx] === 'thick-right' ? 'var(--accent-light-bg)' : undefined
                                                }}
                                              >
                                                ▐ Thick Right
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                        {/* Translation Setup */}
                                        <div style={{ borderTop: '1px solid var(--border-primary)', margin: '4px 0' }} />
                                        <button
                                          className="insert-menu-item"
                                          onClick={() => {
                                            setShowTranslationDialog({ tableId: table.id, colIdx });
                                            setShowColMenu(null);
                                          }}
                                          style={{
                                            background: table.translation_columns?.[colIdx] ? 'var(--accent-light-bg)' : undefined
                                          }}
                                        >
                                          🌐 {table.translation_columns?.[colIdx] ? 'Edit Translation' : 'Setup Translation'}
                                        </button>
                                        <button
                                          className="insert-menu-item"
                                          onClick={() => {
                                            if (window.confirm('Delete this column?')) {
                                              deleteColumn(table.id, colIdx);
                                              setShowColMenu(null);
                                            }
                                          }}
                                          style={{ color: '#ef4444' }}
                                        >
                                          🗑 Delete Column
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                  <ResizeHandle
                                    direction="col"
                                    onResize={(newWidth) => resizeTableColumn(table.id, colIdx, newWidth)}
                                  />
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                      )}
                      <tbody>
                        {sortRows(table.rows, table.sort_column, table.sort_direction, table.done_column).map((rowData, displayIdx) => {
                        const { row, originalIndex } = rowData;
                        const rowKey = `row-${originalIndex}`;
                        const subtables = table.row_subtables?.[rowKey] || [];
                        const hasSubtables = subtables.length > 0;
                        const hasExpandedSubtables = subtables.some(st => st.expanded !== false);
                        const parentRowBg = depthShading && hasExpandedSubtables
                          ? getDepthBackground(1, theme, colorScheme, true, true, customColor)
                          : undefined;
                        // In minimalist mode, don't apply background color
                        const finalRowBg = borderStyle === 'minimalist' ? undefined : parentRowBg;

                        return (
                          <React.Fragment key={originalIndex}>
                            <tr>
                              {subtablesEnabled[table.id] && (
                                <td className="row-controls" style={{
                                  backgroundColor: finalRowBg,
                                  width: '50px',
                                  minWidth: '50px',
                                  maxWidth: '50px',
                                  ...getRowBorderStyle(table.row_border_styles, originalIndex)
                                }}>
                                  {hasSubtables ? (
                                    <SubtableDropdown
                                      subtables={subtables}
                                      onToggle={(id) => toggleSubtable({tableId: table.id, path: [{type: 'subtable', id}]})}
                                      onShowAll={(ids) => bulkSetExpanded(ids, true, (id) => ({tableId: table.id, path: [{type: 'subtable', id}]}))}
                                      onHideAll={(ids) => bulkSetExpanded(ids, false, (id) => ({tableId: table.id, path: [{type: 'subtable', id}]}))}
                                      onAdd={() => setShowSubtableModal({ tableId: table.id, rowIndex: originalIndex })}
                                      readOnly={isTableReadOnly(table)}
                                      theme={theme}
                                    />
                                  ) : (
                                    !isTableReadOnly(table) && (
                                      <button
                                        onClick={() => setShowSubtableModal({ tableId: table.id, rowIndex: originalIndex })}
                                        className="btn-add-subtable"
                                        title="Add subtable"
                                      >
                                        +
                                      </button>
                                    )
                                  )}
                                </td>
                              )}
                              {table.show_row_headers && (
                                <td
                                  className="table-row-header-cell"
                                  style={{
                                    fontWeight: 600,
                                    padding: '0.5rem',
                                    width: `${table.row_header_width || 120}px`,
                                    minWidth: `${table.row_header_width || 120}px`,
                                    position: 'relative',
                                    verticalAlign: 'middle',
                                    ...getRowBorderStyle(table.row_border_styles, originalIndex)
                                  }}
                                >
                                  <div style={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                    {isTableReadOnly(table) ? (
                                      <div
                                        style={{
                                          flex: 1,
                                          fontSize: borderStyle === 'minimalist' ? '1.1rem' : '1rem',
                                          fontWeight: borderStyle === 'minimalist' ? 700 : 600,
                                          ...(borderStyle !== 'minimalist' && {
                                            padding: '0.5rem'
                                          })
                                        }}
                                      >
                                        {(table.row_headers || [])[originalIndex] ? (
                                          <LatexText text={(table.row_headers || [])[originalIndex]} />
                                        ) : (
                                          <span style={{ opacity: 0.4 }}>(empty)</span>
                                        )}
                                      </div>
                                    ) : (
                                      <div style={{
                                        flex: 1,
                                        fontSize: borderStyle === 'minimalist' ? '1.1rem' : '1rem',
                                        fontWeight: borderStyle === 'minimalist' ? 700 : 600
                                      }}>
                                        <EditableLatexHeader
                                          value={(table.row_headers || [])[originalIndex]}
                                          onSave={async (newValue) => {
                                            console.log('[Main table row header] Saving:', newValue);
                                            const currentValue = (table.row_headers || [])[originalIndex];
                                            if (newValue !== currentValue) {
                                              await updateRowHeader(table.id, originalIndex, newValue);
                                            }

                                            // Auto-resize row header column to fit content
                                            const canvas = document.createElement('canvas');
                                            const context = canvas.getContext('2d');
                                            const fontSize = borderStyle === 'minimalist' ? '1.1rem' : '1rem';
                                            const fontWeight = borderStyle === 'minimalist' ? '700' : '600';
                                            context.font = `${fontWeight} ${fontSize} sans-serif`;

                                            const allHeaders = (table.row_headers || table.rows.map((_, i) => `Row ${i + 1}`)).map((header, i) =>
                                              i === originalIndex ? newValue : header
                                            );

                                            const maxWidth = allHeaders.reduce((max, header) => {
                                              const width = context.measureText(header || '').width;
                                              return Math.max(max, width);
                                            }, 0);

                                            const newWidth = Math.max(80, Math.min(300, Math.ceil(maxWidth + 80)));

                                            // Optimistic update
                                            setTables(prevTables => prevTables.map(t =>
                                              t.id === table.id ? { ...t, row_header_width: newWidth } : t
                                            ));

                                            // Save to backend
                                            authFetch(`${API_URL}/api/tables/${table.id}/row-header-width`, {
                                              method: 'PUT',
                                              headers: { 'Content-Type': 'application/json' },
                                              body: JSON.stringify({ width: newWidth })
                                            }).catch(err => {
                                              console.error('[Auto-resize] Failed to save row header width:', err);
                                              fetchTables(); // Rollback on error
                                            });
                                          }}
                                          fontSize={borderStyle === 'minimalist' ? '1.1rem' : '1rem'}
                                          onSort={null}
                                          textAlign='left'
                                        />
                                      </div>
                                    )}
                                    {!isTableReadOnly(table) && (
                                      <div style={{ position: 'relative' }}>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setShowRowMenu(showRowMenu?.tableId === table.id && showRowMenu?.rowIdx === originalIndex ? null : { tableId: table.id, rowIdx: originalIndex });
                                          }}
                                          className="btn-insert-menu"
                                          title="Insert row"
                                        >
                                          ≡
                                        </button>
                                        {showRowMenu?.tableId === table.id && showRowMenu?.rowIdx === originalIndex && (
                                          <div className="insert-menu" style={{ overflow: 'visible' }} onClick={(e) => e.stopPropagation()}>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          insertRow(table.id, originalIndex, 'above');
                                          setShowRowMenu(null);
                                        }}
                                      >
                                        ↑ Insert Above
                                      </button>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          insertRow(table.id, originalIndex, 'below');
                                          setShowRowMenu(null);
                                        }}
                                      >
                                        ↓ Insert Below
                                      </button>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          moveRow(table.id, originalIndex, 'up');
                                          setShowRowMenu(null);
                                        }}
                                        disabled={table.sort_column !== null || originalIndex === 0}
                                        style={{ opacity: (table.sort_column !== null || originalIndex === 0) ? 0.5 : 1 }}
                                        title={table.sort_column !== null ? "Cannot move rows when sorting is active" : ""}
                                      >
                                        ⬆ Move Up
                                      </button>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          moveRow(table.id, originalIndex, 'down');
                                          setShowRowMenu(null);
                                        }}
                                        disabled={table.sort_column !== null || originalIndex === table.num_rows - 1}
                                        style={{ opacity: (table.sort_column !== null || originalIndex === table.num_rows - 1) ? 0.5 : 1 }}
                                        title={table.sort_column !== null ? "Cannot move rows when sorting is active" : ""}
                                      >
                                        ⬇ Move Down
                                      </button>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          copyRow(table.id, originalIndex);
                                          setShowRowMenu(null);
                                        }}
                                      >
                                        📋 Copy Row
                                      </button>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          pasteRow(table.id, originalIndex);
                                          setShowRowMenu(null);
                                        }}
                                        disabled={!copiedRow}
                                        style={{ opacity: copiedRow ? 1 : 0.5 }}
                                        title={copiedRow ? "Paste copied row below this row" : "No row copied"}
                                      >
                                        📄 Paste Row
                                      </button>
                                      {/* Row Border Styles */}
                                      <div style={{ borderTop: '1px solid var(--border-primary)', margin: '4px 0', paddingTop: '4px' }} />
                                      <div
                                        style={{ position: 'relative', overflow: 'visible' }}
                                        onMouseEnter={() => {
                                          if (rowBorderSubmenuTimeout) {
                                            clearTimeout(rowBorderSubmenuTimeout);
                                            setRowBorderSubmenuTimeout(null);
                                          }
                                          setShowRowBorderSubmenu({ tableId: table.id, rowIdx: originalIndex });
                                        }}
                                        onMouseLeave={() => {
                                          const timeout = setTimeout(() => {
                                            setShowRowBorderSubmenu(null);
                                          }, 300);
                                          setRowBorderSubmenuTimeout(timeout);
                                        }}
                                      >
                                        <button
                                          className="insert-menu-item"
                                          style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            background: showRowBorderSubmenu?.tableId === table.id && showRowBorderSubmenu?.rowIdx === originalIndex ? 'var(--bg-hover)' : undefined
                                          }}
                                        >
                                          <span>🎨 Border Style</span>
                                          <span>▶</span>
                                        </button>
                                        {(showRowBorderSubmenu?.tableId === table.id && showRowBorderSubmenu?.rowIdx === originalIndex) && (
                                          <div
                                            className="insert-menu"
                                            style={{
                                              position: 'absolute',
                                              left: '100%',
                                              top: 0,
                                              marginLeft: '2px',
                                              minWidth: '150px',
                                              zIndex: 10000
                                            }}
                                            onMouseEnter={() => {
                                              if (rowBorderSubmenuTimeout) {
                                                clearTimeout(rowBorderSubmenuTimeout);
                                                setRowBorderSubmenuTimeout(null);
                                              }
                                            }}
                                            onMouseLeave={() => {
                                              const timeout = setTimeout(() => {
                                                setShowRowBorderSubmenu(null);
                                              }, 300);
                                              setRowBorderSubmenuTimeout(timeout);
                                            }}
                                          >
                                            <button
                                              className="insert-menu-item"
                                              onClick={() => {
                                                setRowBorderStyle(table.id, originalIndex, 'none');
                                                setShowRowMenu(null);
                                                setShowRowBorderSubmenu(null);
                                              }}
                                              style={{
                                                background: !table.row_border_styles?.[originalIndex] ? 'var(--accent-light-bg)' : undefined
                                              }}
                                            >
                                              None
                                            </button>
                                            <button
                                              className="insert-menu-item"
                                              onClick={() => {
                                                setRowBorderStyle(table.id, originalIndex, 'double-top');
                                                setShowRowMenu(null);
                                                setShowRowBorderSubmenu(null);
                                              }}
                                              style={{
                                                background: table.row_border_styles?.[originalIndex] === 'double-top' ? 'var(--accent-light-bg)' : undefined
                                              }}
                                            >
                                              ═ Double Above
                                            </button>
                                            <button
                                              className="insert-menu-item"
                                              onClick={() => {
                                                setRowBorderStyle(table.id, originalIndex, 'double-bottom');
                                                setShowRowMenu(null);
                                                setShowRowBorderSubmenu(null);
                                              }}
                                              style={{
                                                background: table.row_border_styles?.[originalIndex] === 'double-bottom' ? 'var(--accent-light-bg)' : undefined
                                              }}
                                            >
                                              ═ Double Below
                                            </button>
                                            <button
                                              className="insert-menu-item"
                                              onClick={() => {
                                                setRowBorderStyle(table.id, originalIndex, 'thick-top');
                                                setShowRowMenu(null);
                                                setShowRowBorderSubmenu(null);
                                              }}
                                              style={{
                                                background: table.row_border_styles?.[originalIndex] === 'thick-top' ? 'var(--accent-light-bg)' : undefined
                                              }}
                                            >
                                              ▬ Thick Above
                                            </button>
                                            <button
                                              className="insert-menu-item"
                                              onClick={() => {
                                                setRowBorderStyle(table.id, originalIndex, 'thick-bottom');
                                                setShowRowMenu(null);
                                                setShowRowBorderSubmenu(null);
                                              }}
                                              style={{
                                                background: table.row_border_styles?.[originalIndex] === 'thick-bottom' ? 'var(--accent-light-bg)' : undefined
                                              }}
                                            >
                                              ▬ Thick Below
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      <button
                                        className="insert-menu-item"
                                        onClick={() => {
                                          if (window.confirm('Delete this row?')) {
                                            deleteRow(table.id, originalIndex);
                                            setShowRowMenu(null);
                                          }
                                        }}
                                        style={{ color: '#ef4444' }}
                                      >
                                        🗑 Delete Row
                                      </button>
                                    </div>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              )}
                              {row.map((cell, colIdx) => {
                                // Check if cell contains a nested table (expanded or collapsed)
                                const hasNestedTable = cell && typeof cell === 'object' && cell.type === 'nested_table';
                                const isCollapsedNestedTable = hasNestedTable && !cell.expanded;

                                // Check if ANY cell in this row has a nested table, expanded chart, or done picker
                                // If so, don't apply stored row height (let the row size naturally)
                                const rowHasNestedTable = row.some(c => c && typeof c === 'object' && c.type === 'nested_table');
                                const rowHasExpandedChart = row.some(c => c && typeof c === 'object' && c.type === 'chart' && c.expanded);
                                const rowHasDonePicker = row.some(c => c && typeof c === 'object' && c.type === 'done');
                                const rowHasSpecialContent = rowHasNestedTable || rowHasExpandedChart || rowHasDonePicker;

                                // Always use stored column width to ensure resize handles remain accessible
                                // For expanded nested tables/charts, the content will overflow/expand as needed via CSS
                                const colWidth = table.column_widths?.[colIdx] || 150;
                                const rowHeight = rowHasSpecialContent ? null : (table.row_heights?.[originalIndex] || 40);

                                const isSelected = selectedCell &&
                                  selectedCell.tableId === table.id &&
                                  selectedCell.rowIdx === originalIndex &&
                                  selectedCell.colIdx === colIdx &&
                                  !selectedCell.isSubtable &&
                                  !selectedCell.subtableId &&
                                  !selectedCell.nestedTableId;

                                // Create unique key that changes when nested table is toggled
                                const cellKey = cell && typeof cell === 'object' && cell.type === 'nested_table'
                                  ? `${colIdx}-${cell.id}-${cell.expanded ? 'exp' : 'col'}`
                                  : colIdx;

                                return (
                                  <td
                                    key={cellKey}
                                    className={isSelected ? 'cell-selected' : ''}
                                    onClick={(e) => {
                                      // Only set context if clicking directly on the cell, not on nested content
                                      if (e.target === e.currentTarget || e.target.closest('td') === e.currentTarget) {
                                        const contextPath = createContextPath(table.id, null, null);
                                        const cellContext = createCellContext(contextPath, originalIndex, colIdx);
                                        setSelectedCell(cellContext);
                                      }
                                    }}
                                    style={{
                                      width: `${colWidth}px`,
                                      minWidth: `${colWidth}px`,
                                      ...(rowHeight && density !== 'sub-compact' && { height: `${rowHeight}px` }),
                                      ...getColumnBorderStyle(table.column_border_styles, colIdx),
                                      ...getRowBorderStyle(table.row_border_styles, originalIndex)
                                    }}
                                  >
                                    {renderCell(table.id, cell, originalIndex, colIdx, createContextPath(table.id, null, null))}
                                    {/* Column resize handle */}
                                    {!isTableReadOnly(table) && (
                                      <ResizeHandle
                                        direction="col"
                                        {...resizeElementOptimized('column', colIdx, { tableId: table.id })}
                                      />
                                    )}
                                    {/* Row resize handle on all cells */}
                                    {!isTableReadOnly(table) && originalIndex < table.rows.length - 1 && (
                                      <ResizeHandle
                                        direction="row"
                                        {...resizeElementOptimized('row', originalIndex, { tableId: table.id })}
                                      />
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                            {subtables.filter(st => st.expanded).map((subtable) => (
                              <tr key={subtable.id} className="subtable-row">
                                <td colSpan={row.length + 1 + (table.show_row_headers ? 1 : 0)} style={{ padding: 0 }}>
                                  <div style={{ width: 0, overflow: 'visible' }}>
                                    <div style={{ width: 'max-content' }}>
                                      {renderSubtablesRecursive(table.id, subtable, 1, createContextPath(table.id, null, null))}
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                  )}
              </div>
              );
            })
          )}
        </div>
          </>
        )}

        {viewMode === 'shopify' && (
          <ShopifyView
            onTableLoaded={async (table) => {
              // Table is already saved by backend, just reload tabs to see it
              await fetchTabs();
            }}
            setViewMode={setViewMode}
          />
        )}

        {viewMode === 'warehouse' && (
          <WarehouseView
            onTableLoaded={async (table) => {
              // Table is already saved by backend, just reload tabs to see it
              await fetchTabs();
            }}
            setViewMode={setViewMode}
          />
        )}
      </div>

      {/* Subtable Creation Modal */}
      {showSubtableModal && (
        <div className="modal-overlay" onClick={() => setShowSubtableModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Add Subtable to Row {showSubtableModal.rowIndex + 1}
              {showSubtableModal.parentSubtableId && ' (in Subtable)'}
              {showSubtableModal.nestedTableId && ' (in Nested Table)'}
            </h3>
            <div className="modal-body">
              <h4 style={{ marginTop: 0, marginBottom: '1rem', color: '#666' }}>Create New Subtable</h4>
              <button
                onClick={() => addSubtable(showSubtableModal.tableId, showSubtableModal.rowIndex, 2, 2, showSubtableModal.parentSubtableId, showSubtableModal.nestedTableId)}
                className="btn-primary"
              >
                Add 2x2 Subtable
              </button>
              <button
                onClick={() => addSubtable(showSubtableModal.tableId, showSubtableModal.rowIndex, 3, 3, showSubtableModal.parentSubtableId, showSubtableModal.nestedTableId)}
                className="btn-primary"
              >
                Add 3x3 Subtable
              </button>
              <button
                onClick={() => addSubtable(showSubtableModal.tableId, showSubtableModal.rowIndex, 2, 4, showSubtableModal.parentSubtableId, showSubtableModal.nestedTableId)}
                className="btn-primary"
              >
                Add 2x4 Subtable
              </button>
              <hr style={{ margin: '1.5rem 0', borderColor: '#ddd' }} />
              <h4 style={{ marginBottom: '1rem', color: '#666' }}>Or Reference Existing Table</h4>
              <button
                onClick={async () => {
                  // Open the table reference modal for subtable
                  try {
                    const response = await authFetch(`${API_URL}/api/tables/available-for-reference/${showSubtableModal.tableId}`);
                    const available = await response.json();
                    setAvailableTables(available);
                    // Store the subtable modal info for later use
                    setShowTableReferenceModal({
                      ...showSubtableModal,
                      isSubtableReference: true
                    });
                    setShowSubtableModal(null); // Close this modal
                  } catch (error) {
                    console.error('Error fetching available tables:', error);
                  }
                }}
                className="btn-primary"
              >
                🔗 Reference Existing Table
              </button>
            </div>
            <button onClick={() => setShowSubtableModal(null)} className="btn-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Table Reference Selection Modal */}
      {showTableReferenceModal && (
        <div className="modal-overlay" onClick={() => setShowTableReferenceModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Select Table to Reference</h3>
            <div className="modal-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {availableTables.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                  No other tables available to reference
                </p>
              ) : (
                availableTables.map(table => (
                  <button
                    key={table.id}
                    onClick={() => createTableReference(table.id, table.name)}
                    className="btn-primary"
                    style={{ marginBottom: '0.5rem' }}
                  >
                    🔗 {table.name}
                  </button>
                ))
              )}
            </div>
            <button onClick={() => setShowTableReferenceModal(null)} className="btn-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Chart Configuration Modal */}
      {showChartModal && (
        <div className="modal-overlay" onClick={() => setShowChartModal(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Create Chart</h3>
            <div className="modal-body">
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Chart Title:
                </label>
                <input
                  type="text"
                  id="chart-title"
                  placeholder="Enter chart title"
                  defaultValue="Line Chart"
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '2px solid #e5e7eb',
                    borderRadius: '4px',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
                After creating the chart, expand it to add data points using the built-in data table.
              </p>
              <button
                onClick={() => {
                  const title = document.getElementById('chart-title').value;
                  // Create chart with sample data
                  createChartWithData(
                    showChartModal.tableId,
                    showChartModal.rowIdx,
                    showChartModal.colIdx,
                    [1, 2, 3],  // Sample X data
                    [1, 4, 2],  // Sample Y data
                    title,
                    showChartModal.subtableId,
                    showChartModal.nestedTableId
                  );
                  setShowChartModal(null);
                }}
                className="btn-primary"
                style={{ width: '100%' }}
              >
                Create Chart
              </button>
            </div>
            <button onClick={() => setShowChartModal(null)} className="btn-cancel">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Delete Warning Modal */}
      {deleteWarning && (
        <div className="modal-overlay" onClick={() => setDeleteWarning(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 style={{ color: '#ef4444' }}>⚠️ Cannot Delete Table</h3>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', fontSize: '0.95rem' }}>
                The table <strong>"{deleteWarning.tableName}"</strong> is being referenced by other tables and cannot be deleted.
              </p>
              <p style={{ marginBottom: '0.5rem', fontWeight: 600 }}>Referenced in:</p>
              <div style={{
                maxHeight: '300px',
                overflowY: 'auto',
                padding: '1rem',
                background: '#f9fafb',
                borderRadius: '4px',
                border: '1px solid #e5e7eb'
              }}>
                {deleteWarning.references.map((ref, idx) => (
                  <div key={idx} style={{
                    padding: '0.75rem',
                    marginBottom: '0.5rem',
                    borderRadius: '4px',
                    border: '1px solid #e5e7eb',
                    fontSize: '0.9rem'
                  }}>
                    <div style={{ fontWeight: 600, color: '#667eea', marginBottom: '0.25rem' }}>
                      📊 {ref.referencing_table_name}
                    </div>
                    <div style={{ color: '#666', fontSize: '0.85rem' }}>
                      {ref.location}
                    </div>
                  </div>
                ))}
              </div>
              <p style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#666' }}>
                Please remove all references to this table before deleting it.
              </p>
            </div>
            <button onClick={() => setDeleteWarning(null)} className="btn-primary">
              OK
            </button>
          </div>
        </div>
      )}

      {/* Share Table Modal */}
      {showShareModal && (
        <ShareTableModal
          table={showShareModal}
          onClose={() => {
            setShowShareModal(null);
            // Refresh shares after modal closes
            fetchTableShares();
          }}
        />
      )}

      {/* Shared Tables View */}
      {showSharedTablesView && (
        <SharedTablesView
          onSelectTable={(sharedItem) => {
            // Add the shared table to the tables display
            const sharedTable = {
              ...sharedItem.table,
              isShared: true,
              sharedBy: sharedItem.owner_username,
              sharedPermission: sharedItem.permission,
              shareId: sharedItem.share_id
            };

            // Remove from hidden list if it was hidden
            const newHiddenIds = hiddenSharedTableIds.filter(id => id !== sharedTable.id);
            setHiddenSharedTableIds(newHiddenIds);
            localStorage.setItem('hiddenSharedTables', JSON.stringify(newHiddenIds));

            // Check if table is already in the list
            const existingIndex = tables.findIndex(t => t.id === sharedTable.id);
            if (existingIndex >= 0) {
              // Replace existing
              const newTables = [...tables];
              newTables[existingIndex] = sharedTable;
              setTables(newTables);
            } else {
              // Add to the list
              setTables([...tables, sharedTable]);
            }
          }}
          onClose={() => setShowSharedTablesView(false)}
          visibleSharedTableIds={tables.filter(t => t.isShared).map(t => t.id)}
        />
      )}

      {/* Sharing with Others View */}
      {showSharingWithOthersView && (
        <SharingWithOthersView
          onClose={() => setShowSharingWithOthersView(false)}
          onManageShares={(tableId, tableName) => {
            // Find the table and open its share modal
            const activeTab = tabs.find(tab => tab.id === activeTabId);
            if (activeTab) {
              const table = activeTab.tables.find(t => t.id === tableId);
              if (table) {
                setShowShareModal(table);
              }
            }
          }}
        />
      )}

      {/* Header Cell Context Menu */}
      {selectedHeaderCell && (
        <>
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 999
            }}
            onClick={() => setSelectedHeaderCell(null)}
          />
          <div
            style={{
              position: 'fixed',
              top: selectedHeaderCell.y,
              left: selectedHeaderCell.x,
              background: 'var(--bg-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              boxShadow: '0 2px 10px rgba(0,0,0,0.3)',
              zIndex: 1000,
              minWidth: '150px',
              color: 'var(--text-primary)'
            }}
          >
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border-color)',
                background: 'var(--bg-primary)'
              }}
              onClick={() => {
                if (selectedHeaderCell.contextPath) {
                  mergeHeaderCellRightAtPath(
                    selectedHeaderCell.contextPath,
                    selectedHeaderCell.headerRowIdx,
                    selectedHeaderCell.colIdx
                  );
                } else {
                  mergeHeaderCellRight(
                    selectedHeaderCell.tableId,
                    selectedHeaderCell.headerRowIdx,
                    selectedHeaderCell.colIdx
                  );
                }
                setSelectedHeaderCell(null);
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
            >
              Merge Right →
            </div>
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                borderBottom: '1px solid var(--border-color)',
                background: 'var(--bg-primary)'
              }}
              onClick={() => {
                if (selectedHeaderCell.contextPath) {
                  mergeHeaderCellDownAtPath(
                    selectedHeaderCell.contextPath,
                    selectedHeaderCell.headerRowIdx,
                    selectedHeaderCell.colIdx
                  );
                } else {
                  mergeHeaderCellDown(
                    selectedHeaderCell.tableId,
                    selectedHeaderCell.headerRowIdx,
                    selectedHeaderCell.colIdx
                  );
                }
                setSelectedHeaderCell(null);
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
            >
              Merge Down ↓
            </div>
            <div
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                background: 'var(--bg-primary)'
              }}
              onClick={() => {
                if (selectedHeaderCell.contextPath) {
                  unmergeHeaderCellAtPath(
                    selectedHeaderCell.contextPath,
                    selectedHeaderCell.headerRowIdx,
                    selectedHeaderCell.colIdx
                  );
                } else {
                  unmergeHeaderCell(
                    selectedHeaderCell.tableId,
                    selectedHeaderCell.headerRowIdx,
                    selectedHeaderCell.colIdx
                  );
                }
                setSelectedHeaderCell(null);
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-secondary)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-primary)'}
            >
              Unmerge ✖
            </div>
          </div>
        </>
      )}

      {/* Translation Setup Dialog */}
      {showTranslationDialog && (() => {
        // Helper function to find the table/subtable/nested table
        const findTableObject = () => {
          const { tableId, subtableId, nestedTableId } = showTranslationDialog;

          // Main table
          if (tableId && !subtableId && !nestedTableId) {
            return tables.find(t => t.id === tableId);
          }

          // Subtable
          if (subtableId) {
            for (const table of tables) {
              const findSubtable = (subtables) => {
                if (!subtables) return null;
                for (const rowKey in subtables) {
                  for (const st of subtables[rowKey]) {
                    if (st.id === subtableId) return st;
                    const nested = findSubtable(st.row_subtables);
                    if (nested) return nested;
                  }
                }
                return null;
              };
              const found = findSubtable(table.row_subtables);
              if (found) return found;
            }
          }

          // Nested table
          if (nestedTableId) {
            for (const table of tables) {
              const findNested = (rows) => {
                if (!rows) return null;
                for (const row of rows) {
                  for (const cell of row) {
                    if (cell && typeof cell === 'object' && cell.type === 'nested_table' && cell.id === nestedTableId) {
                      return cell;
                    }
                  }
                }
                return null;
              };
              const found = findNested(table.rows);
              if (found) return found;
            }
          }

          return null;
        };

        const tableObj = findTableObject();
        if (!tableObj) return null;

        // Check if this is a main table (translation only supported for main tables currently)
        const isMainTable = showTranslationDialog.tableId && !showTranslationDialog.subtableId && !showTranslationDialog.nestedTableId;

        if (!isMainTable) {
          // Show a message for subtables/nested tables
          return (
            <div className="modal-overlay" onClick={() => setShowTranslationDialog(null)}>
              <div className="modal-content translation-dialog" onClick={(e) => e.stopPropagation()}>
                <h3>🌐 Translation Not Supported</h3>
                <p style={{ fontSize: '0.9rem', color: 'var(--text-secondary)', marginBottom: '1rem' }}>
                  Auto-translation is currently only supported for main table columns.
                  Support for subtables and nested tables will be added in a future update.
                </p>
                <div className="dialog-buttons">
                  <button onClick={() => setShowTranslationDialog(null)} className="btn-primary">
                    OK
                  </button>
                </div>
              </div>
            </div>
          );
        }

        return (
          <TranslationSetupDialog
            isOpen={!!showTranslationDialog}
            onClose={() => setShowTranslationDialog(null)}
            onApply={(config) => {
              handleSetupTranslation(
                showTranslationDialog.tableId,
                showTranslationDialog.colIdx,
                config
              );
            }}
            currentConfig={getColumnTranslationConfig(tableObj, showTranslationDialog.colIdx)}
            numCols={tableObj.num_cols || 0}
            currentColIdx={showTranslationDialog.colIdx}
          />
        );
      })()}
      </div> {/* Close main-content */}
    </div>
  );
}

export default App;
